import React, { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState, useTransition } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import {
  getRankerDanjiCount,
  deleteRankerScreenSetting,
  getRankerFixArticles,
  getRankerFixList,
  getRankerGubunCodes,
  getRankerScreenSettings,
  saveRankerScreenSetting,
  reorderRankerScreenSettings,
} from '../../lib/api/ranker-api';
import { getRankerBjdTree } from '../../lib/api/bjd-api';
import {
  addFavorite,
  addFavoriteGroup,
  deleteFavoriteGroup,
  getFavoriteGroupComplexes,
  getFavoriteGroups,
  getFavoriteList,
  moveFavoritesToGroup,
  removeFavorite,
  removeFavoriteFromGroup,
  updateFavoriteGroup,
} from '../../lib/api/favorite-api';
import ComplexTanStackTable from '../complex-table/ComplexTanStackTable';
import RankingComplexDetailModal from './RankingComplexDetailModalV2';
import RankingCompareModalV2 from './RankingCompareModalV2';
import RankingColumnSettingPanel from './RankingColumnSettingPanel';
import RankingLeftPanel from '../sidebar/RankingLeftPanel';
import FilterWrapper from './filters/FilterWrapper';
import MoneyRangeFilter from './filters/MoneyRangeFilter';
import BuiltYearFilter from './filters/BuiltYearFilter';
import HouseholdRangeFilter from './filters/HouseholdRangeFilter';
import AreaRangeFilter from './filters/AreaRangeFilter';
import GradeRangeFilter from './filters/GradeRangeFilter';
import GubunFilter from './filters/GubunFilter';
import HouseStructureFilter from './filters/HouseStructureFilter';
import RateRangeFilter from './filters/RateRangeFilter';
import PriceRangeFilter from './filters/PriceRangeFilter';
import CountRangeFilter from './filters/CountRangeFilter';
import '../../pages/ranking/RankingPage.css';

// 선택 컬럼 헤더: 전체선택 체크박스 (AG Grid headerComponent)
function CompareSelectAllHeader(props) {
  const { checked, indeterminate, onToggle } = props || {};
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = !!indeterminate && !checked;
  }, [indeterminate, checked]);
  return (
    <input
      ref={ref}
      type="checkbox"
      className="complex-compare-checkbox"
      checked={!!checked}
      onChange={(e) => onToggle?.(e)}
      onClick={(e) => e.stopPropagation()}
      aria-label="전체 선택"
    />
  );
}

const HOUSEHOLD_OPTIONS = ['~300', '300~1000', '1000~'];
const HOUSEHOLD_MIN_LIMIT = 0;
const HOUSEHOLD_MAX_FALLBACK = 2000;
const AREA_M2_PER_PYEONG = 3.305785;
const AREA_MAX_FALLBACK_M2 = 200;
const AREA_BUCKETS_M2 = [
  { key: 'small', min: null, max: 59 },
  { key: 'mid', min: 59, max: 84 },
  { key: 'large', min: 84, max: null },
];
// 상단 퀵필터 버튼 밑 요약칩에서 버킷 선택값을 사람이 읽기 쉬운 라벨로 표기
const HOUSEHOLD_BUCKET_LABEL = { '~300': '300세대 이하', '300~1000': '300~1000세대', '1000~': '1000세대 이상' };
const AREA_BUCKET_LABEL = { small: '~59㎡', mid: '59~84㎡', large: '84㎡~' };
const FALLBACK_LANDMARK_FILTER_OPTIONS = [
  { key: 'LEGEND', label: '안전진단', sortOrder: 10 },
  { key: 'REBUILD', label: '재건축', sortOrder: 20 },
  { key: 'CAPTAIN', label: '대장', sortOrder: 30 },
  { key: 'PRESALE_RIGHT', label: '분양권', sortOrder: 40 },
  { key: 'HIGH_RANKER', label: '하이랭커', sortOrder: 50 },
  { key: 'RENTAL', label: '임대', sortOrder: 60 },
  { key: 'LANDMARK', label: '랜드마크', sortOrder: 70 },
  { key: 'RANKER', label: '랭커', sortOrder: 80 },
  { key: 'SELECTED', label: '선정', sortOrder: 90 },
];
const LANDMARK_FILTER_LABEL_MAP = FALLBACK_LANDMARK_FILTER_OPTIONS.reduce((acc, option) => {
  acc[option.key] = option.label;
  return acc;
}, {});
const GROUP_BY_OPTIONS = ['시도', '구', '동'];
// 내부 키('시도')는 SIDO 레벨 유지, 화면 표기만 '시/군'
const GROUP_BY_LABELS = { '시도': '시/군', '구': '구', '동': '동' };
const SORT_PRESET_OPTIONS = [
  // 1행
  ['지역', '급지', '가치'],
  // 2행
  ['매매가', '투자금', '적고가', '전고가'],
  // 3행
  ['적현율', '고현율', '저현율', '고저율', '변동차'],
  // 4행
  ['월간상승', '주간상승', '반기상승'],
  // 5행
  ['주간하락', '월간하락', '반기하락'],
  // 6행
  ['거래건수', '상승율', '순이익', '실수익률'],
];
const SORT_PRESET_PRIMARY_OPTIONS = [];
// 조건부 서식 연산자 라벨
const COLOR_RULE_OP_LABEL = { '>=': '이상', '>': '초과', '<=': '이하', '<': '미만', '=': '같음', between: '사이' };

// 톱니바퀴(설정) 아이콘 — SVG (feather settings)
function GearIcon({ size = 14, color = '#475569' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

// 여유매수/매도가 헤더 — 라벨 + 톱니바퀴(클릭 시 안전마진 설정). 정렬은 기존대로 유지(progressSort).
function SafeMarginHeader(props) {
  const { displayName, enableSorting, progressSort, column, marginType, onOpenMargin } = props || {};
  const [sortState, setSortState] = React.useState(column?.getSort?.() || null);
  React.useEffect(() => {
    if (!column?.addEventListener) return undefined;
    const onSortChanged = () => setSortState(column.getSort?.() || null);
    column.addEventListener('sortChanged', onSortChanged);
    return () => { try { column.removeEventListener('sortChanged', onSortChanged); } catch { /* noop */ } };
  }, [column]);
  const arrow = sortState === 'asc' ? ' ▲' : sortState === 'desc' ? ' ▼' : '';
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', width: '100%' }}>
      <span
        style={{ cursor: enableSorting ? 'pointer' : 'default', whiteSpace: 'nowrap' }}
        onClick={(e) => { if (enableSorting && progressSort) progressSort(!!e.shiftKey); }}
      >
        {displayName}{arrow}
      </span>
      <button
        type="button"
        title="안전마진 설정"
        aria-label="안전마진 설정"
        onClick={(e) => { e.stopPropagation(); onOpenMargin?.(marginType, e); }}
        style={{ border: 'none', background: 'transparent', cursor: 'pointer', lineHeight: 0, padding: 0, display: 'inline-flex', alignItems: 'center' }}
      >
        <GearIcon size={13} />
      </button>
    </div>
  );
}

// 세금 그룹 헤더 — 그룹명 + 톱니(클릭 시 주택수/생애최초 설정 팝오버). AG Grid headerGroupComponent.
function TaxGroupHeader(props) {
  const { displayName, onOpenTax } = props || {};
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', width: '100%' }}>
      <span style={{ whiteSpace: 'nowrap' }}>{displayName}</span>
      <button
        type="button"
        title="세금 설정(주택수/생애최초)"
        aria-label="세금 설정"
        onClick={(e) => { e.stopPropagation(); onOpenTax?.(e); }}
        style={{ border: 'none', background: 'transparent', cursor: 'pointer', lineHeight: 0, padding: 0, display: 'inline-flex', alignItems: 'center' }}
      >
        <GearIcon size={13} />
      </button>
    </div>
  );
}
// 표시 컬럼은 없지만 프리셋 정렬에만 사용하는 키 (월간/주간 매매변동)
const EXTRA_SORT_ONLY_KEYS = ['saleMonthlyChange', 'saleWeeklyChange'];
const EXTRA_SORT_ONLY_LABELS = {
  saleMonthlyChange: '월간 매매변동',
  saleWeeklyChange: '주간 매매변동',
};

// 정렬 추가 드롭다운에 노출할 항목(허용 목록) — 순서·라벨 고정
const SORT_ADD_ALLOWED_COLUMNS = [
  { key: 'gubun', label: '구분' },
  { key: 'region', label: '지역' },
  { key: 'livingZone', label: '생활권' },
  { key: 'useApproveDate', label: '연식' },
  { key: 'familyCnt', label: '세대수' },
  { key: 'sigunguGrade', label: '급지(지역)' },
  { key: 'dongGrade', label: '급지(생활권)' },
  { key: 'pastTopPrice', label: '최고가' },
  { key: 'prevHighPrice', label: '전고가' },
  { key: 'properHighPrice', label: '적고가★' },
  { key: 'targetPrice', label: '타겟가' },
  { key: 'articleTradePrice', label: '매매가' },
  { key: 'articleLeasePrice', label: '전세가' },
  { key: 'investPrice', label: '투자금' },
  { key: 'investRealInvestPrice', label: '실투자금' },
  { key: 'leasePriceRate', label: '전세가율' },
  { key: 'goHyunRate', label: '고현율' },
  { key: 'goJeoRate', label: '고저율' },
  { key: 'jeoHyunRate', label: '저현율' },
  { key: 'saleWeeklyChange', label: '주간 매매가 변동' },
];
// 정렬설정 드롭다운에서 선택 가능한(추가 버튼) 컬럼 키 집합.
// 이 목록에 없고 활성 프리셋(랭킹 기준)에도 없는 컬럼은 테이블 헤더 클릭으로 정렬되지 않도록 막는다.
const SORT_ADD_ALLOWED_COLUMN_KEY_SET = new Set(SORT_ADD_ALLOWED_COLUMNS.map((column) => column.key));

// appliedFilters 의 base 이름(Min/Max 제거) → 실제 테이블 컬럼 키. 기본은 동일(base === colKey)
const APPLIED_FILTER_COLUMN_KEYS = {
  tradePrice: ['articleTradePrice'],
  leasePrice: ['articleLeasePrice'],
  useApproveAge: ['useApproveDate'],
  area: ['planTypeName'],
  household: ['familyCnt'],
  investRealInvestPrice: ['investRealInvestPrice', 'realInvestPrice'],
};

// 금액(만원) 계열 필터 base — 요약 표기 시 단위제거 형식(예: 172000 → 17.2)으로 축약
const AMOUNT_FILTER_BASES = new Set([
  'tradePrice', 'leasePrice', 'investPrice', 'investRealInvestPrice',
  'complexMinTradePrice', 'complexMaxTradePrice', 'complexMinLeasePrice', 'complexMaxLeasePrice',
  'avgSaleRealPrice', 'avgLeaseRealPrice', 'risePrice', 'changeGap',
  'pastTopPrice', 'prevHighPrice', 'properHighPrice', 'prevLowPrice', 'targetPrice',
]);
const QUICK_FILTER_BUTTONS = [
  { key: 'tradePrice', label: '매매가' },
  { key: 'investPrice', label: '투자금' },
  { key: 'builtDate', label: '연차' },
  { key: 'area', label: '평형' },
  { key: 'household', label: '세대수' },
];

// 상세 필터 설정 모달: 기존 필터 컴포넌트(팝업)를 지원하는 컬럼 키 집합
// 상세 필터 설정 모달에 노출할 항목: 구분, 지역급지, 생활권급지, 실투자금, 전세가, 전세가율.
const DETAIL_FILTER_SUPPORTED_KEYS = new Set([
  'gubun',                 // 구분
  'sigunguGrade',          // 지역급지
  'dongGrade',             // 생활권급지
  'investRealInvestPrice', // 실투자금
  'articleLeasePrice',     // 전세가
  'leasePriceRate',        // 전세가율
]);
const SORT_PRESET_RULE_MAP = {
  지역순: [
    { key: 'region', direction: 'asc' },
    { key: 'sigunguGrade', direction: 'asc' },
    { key: 'dongGrade', direction: 'asc' },
  ],
  지역: [
    { key: 'region', direction: 'asc' },
    { key: 'sigunguGrade', direction: 'asc' },
    { key: 'dongGrade', direction: 'asc' },
  ],
  급지순: [
    { key: 'dongGrade', direction: 'asc' },
    { key: 'sigunguGrade', direction: 'asc' },
    { key: 'articleTradePrice', direction: 'desc' },
    { key: 'region', direction: 'asc' },
  ],
  급지: [
    { key: 'dongGrade', direction: 'asc' },
    { key: 'sigunguGrade', direction: 'asc' },
    { key: 'articleTradePrice', direction: 'desc' },
    { key: 'region', direction: 'asc' },
  ],
  가치순: [
    { key: 'recent4wAvgPrice', direction: 'desc' }, // 현재가치
    { key: 'pastAvgPriceBefore20250331', direction: 'desc' }, // 과거가치
    { key: 'articleTradePrice', direction: 'desc' },
    { key: 'dongGrade', direction: 'asc' },
    { key: 'sigunguGrade', direction: 'asc' },
  ],
  가치: [
    { key: 'recent4wAvgPrice', direction: 'desc' }, // 현재가치
    { key: 'pastAvgPriceBefore20250331', direction: 'desc' }, // 과거가치
    { key: 'articleTradePrice', direction: 'desc' },
    { key: 'dongGrade', direction: 'asc' },
    { key: 'sigunguGrade', direction: 'asc' },
  ],
  적현율순: [
    { key: 'jeokHyunRate', direction: 'asc' },
    { key: 'goHyunRate', direction: 'asc' },
    { key: 'changeGap', direction: 'desc' },
    { key: 'region', direction: 'asc' },
  ],
  적현율: [
    { key: 'jeokHyunRate', direction: 'asc' },
    { key: 'goHyunRate', direction: 'asc' },
    { key: 'changeGap', direction: 'desc' },
    { key: 'region', direction: 'asc' },
  ],
  고현율순: [
    { key: 'goHyunRate', direction: 'asc' },
    { key: 'goJeoRate', direction: 'asc' },
    { key: 'changeGap', direction: 'desc' },
    { key: 'region', direction: 'asc' },
  ],
  고현율: [
    { key: 'goHyunRate', direction: 'asc' },
    { key: 'goJeoRate', direction: 'asc' },
    { key: 'changeGap', direction: 'desc' },
    { key: 'region', direction: 'asc' },
  ],
  고저율순: [
    { key: 'goJeoRate', direction: 'asc' },
    { key: 'goHyunRate', direction: 'asc' },
    { key: 'changeGap', direction: 'desc' },
    { key: 'region', direction: 'asc' },
  ],
  고저율: [
    { key: 'goJeoRate', direction: 'asc' },
    { key: 'goHyunRate', direction: 'asc' },
    { key: 'changeGap', direction: 'desc' },
    { key: 'region', direction: 'asc' },
  ],
  저현율순: [
    { key: 'jeoHyunRate', direction: 'desc' },
    { key: 'goJeoRate', direction: 'asc' },
    { key: 'changeGap', direction: 'desc' },
    { key: 'region', direction: 'asc' },
  ],
  저현율: [
    { key: 'jeoHyunRate', direction: 'desc' },
    { key: 'goJeoRate', direction: 'asc' },
    { key: 'changeGap', direction: 'desc' },
    { key: 'region', direction: 'asc' },
  ],
  변동차순: [
    { key: 'changeGap', direction: 'desc' },
    { key: 'properHighPrice', direction: 'desc' },
    { key: 'articleTradePrice', direction: 'desc' },
    { key: 'region', direction: 'asc' },
  ],
  변동차: [
    { key: 'changeGap', direction: 'desc' },
    { key: 'properHighPrice', direction: 'desc' },
    { key: 'articleTradePrice', direction: 'desc' },
    { key: 'region', direction: 'asc' },
  ],
  적고가순: [
    { key: 'properHighPrice', direction: 'desc' },
    { key: 'articleTradePrice', direction: 'desc' },
    { key: 'pastTopPrice', direction: 'desc' },
    { key: 'region', direction: 'asc' },
  ],
  적고가: [
    { key: 'properHighPrice', direction: 'desc' },
    { key: 'articleTradePrice', direction: 'desc' },
    { key: 'pastTopPrice', direction: 'desc' },
    { key: 'region', direction: 'asc' },
  ],
  전고가순: [
    { key: 'prevHighPrice', direction: 'desc' },
    { key: 'articleTradePrice', direction: 'desc' },
    { key: 'pastTopPrice', direction: 'desc' },
    { key: 'region', direction: 'asc' },
  ],
  전고가: [
    { key: 'prevHighPrice', direction: 'desc' },
    { key: 'articleTradePrice', direction: 'desc' },
    { key: 'pastTopPrice', direction: 'desc' },
    { key: 'region', direction: 'asc' },
  ],
  매매가순: [
    { key: 'articleTradePrice', direction: 'desc' },
    { key: 'properHighPrice', direction: 'desc' },
    { key: 'articleLeasePrice', direction: 'desc' },
    { key: 'region', direction: 'asc' },
  ],
  매매가: [
    { key: 'articleTradePrice', direction: 'desc' },
    { key: 'properHighPrice', direction: 'desc' },
    { key: 'articleLeasePrice', direction: 'desc' },
    { key: 'region', direction: 'asc' },
  ],
  투자금순: [
    { key: 'articleTradePrice', direction: 'desc' },
    { key: 'properHighPrice', direction: 'desc' },
    { key: 'articleLeasePrice', direction: 'desc' },
    { key: 'region', direction: 'asc' },
  ],
  투자금: [
    { key: 'articleTradePrice', direction: 'desc' },
    { key: 'properHighPrice', direction: 'desc' },
    { key: 'articleLeasePrice', direction: 'desc' },
    { key: 'region', direction: 'asc' },
  ],
  월간상승순: [
    { key: 'saleMonthlyChange', direction: 'desc' },
    { key: 'saleWeeklyChange', direction: 'desc' },
    { key: 'articleTradePrice', direction: 'desc' },
    { key: 'region', direction: 'asc' },
  ],
  월간상승: [
    { key: 'saleMonthlyChange', direction: 'desc' },
    { key: 'saleWeeklyChange', direction: 'desc' },
    { key: 'articleTradePrice', direction: 'desc' },
    { key: 'region', direction: 'asc' },
  ],
  월간하락순: [
    { key: 'saleMonthlyChange', direction: 'asc' },
    { key: 'saleWeeklyChange', direction: 'asc' },
    { key: 'articleTradePrice', direction: 'desc' },
    { key: 'region', direction: 'asc' },
  ],
  월간하락: [
    { key: 'saleMonthlyChange', direction: 'asc' },
    { key: 'saleWeeklyChange', direction: 'asc' },
    { key: 'articleTradePrice', direction: 'desc' },
    { key: 'region', direction: 'asc' },
  ],
  주간상승순: [
    { key: 'saleWeeklyChange', direction: 'desc' },
    { key: 'articleTradePrice', direction: 'desc' },
    { key: 'region', direction: 'asc' },
  ],
  주간상승: [
    { key: 'saleWeeklyChange', direction: 'desc' },
    { key: 'articleTradePrice', direction: 'desc' },
    { key: 'region', direction: 'asc' },
  ],
  주간하락순: [
    { key: 'saleWeeklyChange', direction: 'asc' },
    { key: 'articleTradePrice', direction: 'desc' },
    { key: 'region', direction: 'asc' },
  ],
  주간하락: [
    { key: 'saleWeeklyChange', direction: 'asc' },
    { key: 'articleTradePrice', direction: 'desc' },
    { key: 'region', direction: 'asc' },
  ],
  반기상승순: [
    { key: 'saleChange6Months', direction: 'desc' },
    { key: 'articleTradePrice', direction: 'desc' },
    { key: 'region', direction: 'asc' },
  ],
  반기상승: [
    { key: 'saleChange6Months', direction: 'desc' },
    { key: 'articleTradePrice', direction: 'desc' },
    { key: 'region', direction: 'asc' },
  ],
  반기하락순: [
    { key: 'saleChange6Months', direction: 'asc' },
    { key: 'articleTradePrice', direction: 'desc' },
    { key: 'region', direction: 'asc' },
  ],
  반기하락: [
    { key: 'saleChange6Months', direction: 'asc' },
    { key: 'articleTradePrice', direction: 'desc' },
    { key: 'region', direction: 'asc' },
  ],
  거래건수순: [
    { key: 'tradeArticleCount', direction: 'desc' },
    { key: 'leaseArticleCount', direction: 'desc' },
    { key: 'region', direction: 'asc' },
  ],
  거래건수: [
    { key: 'tradeArticleCount', direction: 'desc' },
    { key: 'leaseArticleCount', direction: 'desc' },
    { key: 'region', direction: 'asc' },
  ],
  상승율순: [
    { key: 'goHyunRate', direction: 'desc' },
    { key: 'changeGap', direction: 'desc' },
    { key: 'region', direction: 'asc' },
  ],
  상승율: [
    { key: 'goHyunRate', direction: 'desc' },
    { key: 'changeGap', direction: 'desc' },
    { key: 'region', direction: 'asc' },
  ],
  순수익순: [
    { key: 'changeGap', direction: 'desc' },
    { key: 'leasePriceRate', direction: 'desc' },
    { key: 'goHyunRate', direction: 'desc' },
    { key: 'region', direction: 'asc' },
  ],
  순이익: [
    { key: 'changeGap', direction: 'desc' },
    { key: 'leasePriceRate', direction: 'desc' },
    { key: 'goHyunRate', direction: 'desc' },
    { key: 'region', direction: 'asc' },
  ],
  실수익률순: [
    { key: 'leasePriceRate', direction: 'desc' },
    { key: 'changeGap', direction: 'desc' },
    { key: 'goHyunRate', direction: 'desc' },
    { key: 'region', direction: 'asc' },
  ],
  실수익률: [
    { key: 'leasePriceRate', direction: 'desc' },
    { key: 'changeGap', direction: 'desc' },
    { key: 'goHyunRate', direction: 'desc' },
    { key: 'region', direction: 'asc' },
  ],
};

const COLUMN_SETTING_GROUPS = [
  { id: 'regionInfo', name: '지역정보', keys: ['region', 'sigunguGrade'] },
  { id: 'complexInfo', name: '단지정보', keys: ['gubun', 'buildingName', 'useApproveDate', 'useApproveAge', 'familyCnt', 'planTypeName'] },
  { id: 'complexValue', name: '단지가치', keys: [], hidden: true },
  { id: 'changeRate', name: '변동율', keys: ['goHyunRate', 'jeokHyunRate', 'goJeoRate', 'jeoHyunRate', 'changeGap'] },
  { id: 'invest', name: '투자금', keys: ['investPrice', 'investRealInvestPrice'] },
  { id: 'basePrice', name: '기준가', keys: ['pastTopPrice', 'prevHighPrice', 'properHighPrice', 'prevLowPrice', 'targetPrice'] },
  { id: 'salePrice', name: '매매가', keys: ['articleTradePrice', 'complexMinTradePrice', 'complexMaxTradePrice'] },
  { id: 'leasePrice', name: '전세가', keys: ['articleLeasePrice', 'complexMinLeasePrice', 'complexMaxLeasePrice'] },
  { id: 'saleCount', name: '매매수', keys: ['tradeArticleCount', 'saleCountWeeklyChange'] },
  { id: 'leaseCount', name: '전세수', keys: ['leaseArticleCount', 'leaseCountWeeklyChange'] },
  { id: 'leaseRate', name: '전세가율', keys: ['leasePriceRate'] },
  { id: 'profit', name: '수익률', keys: ['netProfit', 'profitRate', 'realInvestPrice', 'realProfitRate'] },
];

const DISPLAY_COLUMN_GROUPS = [
  // 그룹 표시 순서: 지역-단지-기준가-변동율-투자금-매매가-변동매매가-매매실거래가-매매수-매매실거래건수
  //  -전세가-변동전세가-전세실거래가-전세수-전세실거래건수-전세가율-상승율-안전마진-세금-공급
  { id: 'buildingNameGroup', name: '단지명', items: ['단지명'], locked: true },
  { id: 'regionInfo', name: '지역정보', items: ['지역', '지역급지(생활권)'] },
  { id: 'complexInfo', name: '단지정보', items: ['구분', '연식(연차)', '연차', '세대수(평세대수)', '임대수', '평형(구조)'] },
  { id: 'basePrice', name: '기준가', items: ['최고가', '전고가', '적고가★', '전저가', '타겟가'] },
  { id: 'changeRate', name: '변동율', items: ['고현율', '적현율★', '고저율', '저현율', '변동차'] },
  { id: 'invest', name: '투자금', items: ['투자금(실투자금)', '실투자금'] },
  { id: 'salePrice', name: '매매호가', items: ['매매가', '매매 최저가', '매매 최고가'] },
  { id: 'salePrice8', name: '매매호가 8주', items: ['최근 8주'] },
  { id: 'salePrice6', name: '매매호가 6개월', items: ['최근 6개월'] },
  { id: 'changeSalePrice', name: '변동 매매가', items: ['6개월', '8주', '4주'] },
  { id: 'saleTx', name: '매매 실거래가', items: ['매매 실거래가'] },
  { id: 'saleTx8', name: '매매 실거래가 8주', items: ['최근 8주'] },
  { id: 'saleTx6', name: '매매 실거래가 6개월', items: ['최근 6개월'] },
  { id: 'saleCount', name: '매매수', items: ['매매수', '매매비율', '주간변동'] },
  { id: 'saleCount8', name: '매매수 8주', items: ['최근 8주'] },
  { id: 'saleCount6', name: '매매수 6개월', items: ['최근 6개월'] },
  { id: 'saleTxCount', name: '매매 실거래건수', items: ['주간변동'] },
  { id: 'saleTxCount8', name: '매매 실거래건수 8주', items: ['최근 8주'] },
  { id: 'saleTxCount6', name: '매매 실거래건수 6개월', items: ['최근 6개월'] },
  { id: 'leasePrice', name: '전세가', items: ['전세가', '전세 최저가', '전세 최고가'] },
  { id: 'leasePrice8', name: '전세가 8주', items: ['최근 8주'] },
  { id: 'leasePrice6', name: '전세가 6개월', items: ['최근 6개월'] },
  { id: 'changeLeasePrice', name: '변동 전세가', items: ['6개월', '8주', '4주'] },
  { id: 'leaseTx', name: '전세 실거래가', items: ['전세 실거래가'] },
  { id: 'leaseTx8', name: '전세 실거래가 8주', items: ['최근 8주'] },
  { id: 'leaseTx6', name: '전세 실거래가 6개월', items: ['최근 6개월'] },
  { id: 'leaseCount', name: '전세수', items: ['전세수', '전세비율', '주간변동'] },
  { id: 'leaseCount8', name: '전세수 8주', items: ['최근 8주'] },
  { id: 'leaseCount6', name: '전세수 6개월', items: ['최근 6개월'] },
  { id: 'leaseTxCount', name: '전세 실거래건수', items: ['주간변동'] },
  { id: 'leaseTxCount8', name: '전세 실거래건수 8주', items: ['최근 8주'] },
  { id: 'leaseTxCount6', name: '전세 실거래건수 6개월', items: ['최근 6개월'] },
  { id: 'leaseRate', name: '전세가율 6개월', items: ['최근 6개월'] },
  { id: 'riseRate', name: '상승률', items: ['상승률', '상승가', '과거가', '초기 투자금', '수익률'] },
  { id: 'safetyMargin', name: '안전마진', items: ['희망매수가', '희망매도가', '양도차액'] },
  { id: 'tax', name: '세금', items: ['취득세', '필요경비', '양도세'] },
  { id: 'supply', name: '공급', items: ['공급량', '수요(3년)', '입주(3년)', '2026년', '2027년', '2028년'] },
  // 위 목록에 없던 그룹은 뒤에 배치
  { id: 'profit', name: '수익률', items: ['순수익', '수익률', '실투자금', '실수익률'] },
  { id: 'changeLeaseRate', name: '변동 전세가율', items: ['6개월', '최근 5개월'] },
  { id: 'complexValue', name: '단지가치', items: ['과거', '현재', '미래'], hidden: true },
];

// 다른 컬럼에 병합된 컬럼 키(생활권→지역, 세대수(평)→세대수, 급지(생활권)→지역급지, 구조→평형, 실투자금→투자금)
//  + 요청에 의한 숨김 컬럼(수익률 그룹 > 실투자금 realInvestPrice). 그룹/테이블/패널 어디서도 별도 노출 금지.
const MERGED_COLUMN_KEYS = new Set(['livingZone', 'ptpFamilyCnt', 'dongGrade', 'houseStructure', 'useApproveAge', 'realInvestPrice', 'investRealInvestPrice']);

// 프런트에서 제거된 컬럼(규제·인구): 저장된 화면설정에 남아있어도 빠른 순서 편집/테이블에 노출되지 않도록 필터링
const REMOVED_COLUMN_KEYS = new Set(['regulation', 'population']);
const REMOVED_COLUMN_LABELS = new Set(['규제', '인구']);

const DISPLAY_GROUP_ITEM_KEY_PRESET = {
  regionInfo: ['region', 'sigunguGrade'],
  buildingNameGroup: ['buildingName'],
  complexInfo: ['gubun', 'useApproveDate', 'useApproveAge', 'familyCnt', 'rentalCnt', 'planTypeName'],
  complexValue: [null, null, null],
  changeRate: ['goHyunRate', 'jeokHyunRate', 'goJeoRate', 'jeoHyunRate', 'changeGap'],
  basePrice: ['pastTopPrice', 'prevHighPrice', 'properHighPrice', 'prevLowPrice', 'targetPrice'],
  salePrice: ['articleTradePrice', 'complexMinTradePrice', 'complexMaxTradePrice'],
  salePrice8: ['articleTradePriceRecent8Toggle'],
  salePrice6: ['articleTradePriceRecent6Toggle'],
  changeSalePrice: ['saleChange6Months', 'saleChange8Weeks', 'saleChange4Weeks'],
  leasePrice: ['articleLeasePrice', 'complexMinLeasePrice', 'complexMaxLeasePrice'],
  leasePrice8: ['articleLeasePriceRecent8Toggle'],
  leasePrice6: ['articleLeasePriceRecent6Toggle'],
  changeLeasePrice: ['leaseChange6Months', 'leaseChange8Weeks', 'leaseChange4Weeks'],
  saleTx: ['avgSaleRealPrice'],
  saleTx8: ['saleTxRecent8Toggle'],
  saleTx6: ['saleTxRecent6Toggle'],
  leaseTx: ['avgLeaseRealPrice'],
  leaseTx8: ['leaseTxRecent8Toggle'],
  leaseTx6: ['leaseTxRecent6Toggle'],
  saleCount: ['tradeArticleCount', 'saleRatio', 'saleCountWeeklyChange'],
  saleCount8: ['tradeArticleCountRecent8Toggle'],
  saleCount6: ['tradeArticleCountRecent6Toggle'],
  leaseCount: ['leaseArticleCount', 'leaseRatio', 'leaseCountWeeklyChange'],
  leaseCount8: ['leaseArticleCountRecent8Toggle'],
  leaseCount6: ['leaseArticleCountRecent6Toggle'],
  saleTxCount: ['saleRealCountWeeklyChange'],
  saleTxCount8: ['saleRealCountRecent8Toggle'],
  saleTxCount6: ['saleRealCountRecent6Toggle'],
  leaseTxCount: ['leaseRealCountWeeklyChange'],
  leaseTxCount8: ['leaseRealCountRecent8Toggle'],
  leaseTxCount6: ['leaseRealCountRecent6Toggle'],
  leaseRate: ['leaseRateRecent6Toggle'],
  changeLeaseRate: ['leaseRateChange6m', 'leaseRateChangeRecent5Toggle'],
  riseRate: ['riseRate', 'risePrice', 'pastPrice', null, null],
  supply: ['supply3Years', 'demand3Years', 'movein3Years', 'supply2026', 'supply2027', 'supply2028'],
  safetyMargin: ['safeBuyPrice', 'safeSellPrice', 'capitalGain'],
  invest: ['investPrice', 'investRealInvestPrice'],
  tax: ['acquisitionTax', 'capitalGainsTax', 'brokerageFee'],
  profit: ['netProfit', 'profitRate', 'realInvestPrice', 'realProfitRate'],
};
const normalizeHeaderFilterText = (value) => String(value || '').replace(/\s+/g, '').trim();
const TABLE_HEADER_FILTER_LABELS = [
  '급지\n(지역)',
  '급지\n(생활권)',
  '구분',
  '연식',
  '연차',
  '세대수',
  '평형',
  '구조',
  '고현율',
  '적현율',
  '고저율',
  '저현율',
  '변동차',
  '투자금',
  '실투자금',
  '분양권투자금',
  '최고가',
  '전고가',
  '적고가',
  '전저가',
  '타겟가',
  '매매가',
  '매매최저가',
  '매매최고가',
  '전세가',
  '전세최저가',
  '전세최고가',
  '매매실거래가',
  '전세실거래가',
  '매매수',
  '전세수',
  '전세가율',
  '상승률',
  '상승가',
  '공급량',
  '수익률',
];
const TABLE_HEADER_FILTER_KEYS = [
  'gubun',
  'useApproveDate',
  'useApproveAge',
  'familyCnt',
  'planTypeName',
  'houseStructure',
  'goHyunRate',
  'jeokHyunRate',
  'goJeoRate',
  'jeoHyunRate',
  'changeGap',
  'pastTopPrice',
  'prevHighPrice',
  'properHighPrice',
  'prevLowPrice',
  'articleTradePrice',
  'complexMinTradePrice',
  'complexMaxTradePrice',
  'articleLeasePrice',
  'complexMinLeasePrice',
  'complexMaxLeasePrice',
  'tradeArticleCount',
  'leaseArticleCount',
  'leasePriceRate',
];
const TABLE_HEADER_FILTER_KEY_SET = new Set(TABLE_HEADER_FILTER_KEYS);
const POPUP_FILTER_KEYS = [
  'sigunguGrade', 'dongGrade', 'gubun', 'useApproveDate', 'planTypeName', 'familyCnt', 'houseStructure',
  'goHyunRate', 'jeokHyunRate', 'goJeoRate', 'jeoHyunRate', 'changeGap',
  'pastTopPrice', 'prevHighPrice', 'properHighPrice', 'prevLowPrice',
  'investPrice', 'investRealInvestPrice',
  'articleTradePrice', 'complexMinTradePrice', 'complexMaxTradePrice',
  'articleLeasePrice', 'complexMinLeasePrice', 'complexMaxLeasePrice',
  'avgSaleRealPrice', 'avgLeaseRealPrice',
  'tradeArticleCount', 'leaseArticleCount',
  'leasePriceRate', 'riseRate', 'risePrice', 'supply3Years'
];
const POPUP_FILTER_KEY_SET = new Set(POPUP_FILTER_KEYS);
const TABLE_HEADER_FILTER_LABEL_SET = new Set(TABLE_HEADER_FILTER_LABELS.map(normalizeHeaderFilterText));
const TABLE_MONEY_FILTER_KEYS = [
  'pastTopPrice',
  'prevHighPrice',
  'properHighPrice',
  'prevLowPrice',
  'changeGap',
  'articleTradePrice',
  'complexMinTradePrice',
  'complexMaxTradePrice',
  'articleLeasePrice',
  'complexMinLeasePrice',
  'complexMaxLeasePrice',
  'safeBuyPrice',
  'safeSellPrice',
  'capitalGain',
  'brokerageFee',
];
const TABLE_COUNT_FILTER_KEYS = [
  'tradeArticleCount',
  'leaseArticleCount',
];
const TABLE_NUMERIC_ALIGN_KEYS = [
  'useApproveAge',
  'familyCnt',
  'ptpFamilyCnt',
  'rentalCnt',
  'sigunguGrade',
  'dongGrade',
  'population',
  'supply3Years',
  'demand3Years',
  'movein3Years',
  'supply2026',
  'supply2027',
  'supply2028',
  'pastTopPrice',
  'prevHighPrice',
  'properHighPrice',
  'prevLowPrice',
  'articleTradePrice',
  'complexMinTradePrice',
  'complexMaxTradePrice',
  'articleLeasePrice',
  'complexMinLeasePrice',
  'complexMaxLeasePrice',
  'saleChange6Months',
  'saleChange8Weeks',
  'saleChange4Weeks',
  'leaseChange6Months',
  'leaseChange8Weeks',
  'leaseChange4Weeks',
  'avgSaleRealPrice',
  'avgLeaseRealPrice',
  'tradeArticleCount',
  'leaseArticleCount',
  'saleRatio',
  'leaseRatio',
  'saleRealCount',
  'leaseRealCount',
  'saleRealCountWeeklyChange',
  'leaseRealCountWeeklyChange',
  'leasePriceRate',
  'safeBuyPrice',
  'safeSellPrice',
  'capitalGain',
  'brokerageFee',
];

const DEFAULT_VISIBLE_COLUMN_KEYS = new Set([
  'buildingName',                      // 단지정보 > 단지명
  'region',                            // 지역정보 > 지역(생활권) — 생활권 병합
  'useApproveAge',                     // 단지정보 > 연차
  'planTypeName',                      // 단지정보 > 평형
  'realInvestPrice',                   // 수익률 > 실투자금
  'articleTradePrice',                 // 매매가 > 매매가
  'articleLeasePrice',                 // 전세가 > 전세가
  'tradeArticleCount',                 // 매매수 > 매매수
  'saleRatio',                         // 매매수 > 매매비율
  'leaseArticleCount',                 // 전세수 > 전세수
  'leaseRatio',                        // 전세수 > 전세비율
]);

const buildDefaultGroupItemsById = () => {
  const next = {};
  DISPLAY_COLUMN_GROUPS
    .filter((group) => !group.hidden)
    .forEach((group) => {
      const preset = Array.isArray(DISPLAY_GROUP_ITEM_KEY_PRESET[group.id]) ? DISPLAY_GROUP_ITEM_KEY_PRESET[group.id] : [];
      const items = Array.isArray(group.items) ? group.items : [];
      next[group.id] = items.map((label, index) => ({
        id: `${group.id}__item__${index}`,
        label: String(label || ''),
        columnKey: preset[index] ? String(preset[index]) : null,
      }));
    });
  return next;
};

const REGION_SHORT_NAME_MAP = {
  '서울특별시': '서울',
  '경기도': '경기',
  '인천광역시': '인천',
  '부산광역시': '부산',
  '대구광역시': '대구',
  '대전광역시': '대전',
  '광주광역시': '광주',
  '울산광역시': '울산',
  '세종특별자치시': '세종',
  '제주특별자치도': '제주',
  '강원특별자치도': '강원',
  '경상북도': '경북',
  '경상남도': '경남',
  '전북특별자치도': '전북',
  '전라북도': '전북',
  '전라남도': '전남',
  '충청북도': '충북',
  '충청남도': '충남',
};
// 규제지역 도움말 표 셀 스타일
const regHelpTh = {
  textAlign: 'left',
  padding: '4px 6px',
  borderBottom: '1px solid #e2e8f0',
  color: '#475569',
  fontWeight: 700,
  background: '#f8fafc',
  position: 'sticky',
  top: 0,
};
const regHelpTd = {
  padding: '4px 6px',
  borderBottom: '1px solid #f1f5f9',
  color: '#334155',
};

// ===== 부동산 규제지역 지정 현황 (고정 데이터, 2026.7.1 기준) =====
// 조정대상지역: 시도별 그룹 + 지정일자/해제일자
const ADJUSTMENT_TARGET_ZONES = [
  {
    sido: '서울특별시(25)',
    rows: [
      { region: '서초구, 강남구, 송파구, 용산구 (4개구)', date: '2017. 9. 6', release: '' },
      { region: '종로구, 중구, 성동구, 광진구, 동대문구, 중랑구, 성북구, 강북구, 도봉구, 노원구, 은평구, 서대문구, 마포구, 양천구, 강서구, 구로구, 금천구, 영등포구, 동작구, 관악구, 강동구 (21개구)', date: '2025.10.16 ', release: '' },
    ],
  },
  {
    sido: '경기도(15)',
    rows: [
      { region: '과천시, 광명시, 하남시, 의왕시', date: '2025.10.16 ', release: '' },
      { region: '성남시 분당구·수정구·중원구', date: '2025.10.16 ', release: '' },
      { region: '수원시 영통구·장안구·팔달구', date: '2025.10.16 ', release: '' },
      { region: '용인시 수지구', date: '2025.10.16 ', release: '' },
      { region: '안양시 동안구', date: '2025.10.16 ', release: '' },
      { region: '화성시 동탄구', date: '2026. 7. 1 ', release: '' },
      { region: '용인시 기흥구', date: '2026. 7. 1 ', release: '' },
      { region: '구리시', date: '2026. 7. 1 ', release: '' },
    ],
  },
];

// 같은 지정일자 연속 행의 날짜 셀을 rowSpan 으로 합치기 위한 계산
const computeDateRowSpans = (rows) => {
  const out = (rows || []).map((r) => ({ ...r, showDate: true, dateSpan: 1 }));
  for (let i = 0; i < out.length;) {
    let j = i + 1;
    while (j < out.length && out[j].date === out[i].date) j += 1;
    out[i].dateSpan = j - i;
    for (let k = i + 1; k < j; k += 1) out[k].showDate = false;
    i = j;
  }
  return out;
};

// 투기과열지구: 지정일자별 그룹
const SPECULATION_ZONES = [
  { date: '2017. 8. 3', regions: ['서울특별시 서초구, 강남구, 송파구, 용산구 (4개구)'] },
  {
    date: '2025.10.16',
    regions: [
      '서울특별시 종로구, 중구, 성동구, 광진구, 동대문구, 중랑구, 성북구, 강북구, 도봉구, 노원구, 은평구, 서대문구, 마포구, 양천구, 강서구, 구로구, 금천구, 영등포구, 동작구, 관악구, 강동구 (21개구)',
      '경기도 과천시, 광명시, 하남시, 의왕시, 성남시 분당구·수정구·중원구, 수원시 영통구·장안구·팔달구, 용인시 수지구, 안양시 동안구 (12개)',
    ],
  },
  { date: '2026. 7. 1', regions: ['화성시 동탄구, 용인시 기흥구, 구리시 (3개)'] },
];
const SPECULATION_TITLE = '투기과열지구 (40개)';

const normalizeDanjiCode = (value) => String(value || '').trim();
const normalizeYearInput = (value) => String(value || '').replace(/\D/g, '').slice(0, 4);
const normalizeNumberInput = (value) => String(value || '').replace(/\D/g, '').slice(0, 5);
const normalizeDecimalInput = (value) => {
  const sanitized = String(value || '').replace(/[^\d.]/g, '');
  const [head, ...rest] = sanitized.split('.');
  const decimal = rest.length > 0 ? `.${rest.join('')}` : '';
  return `${head}${decimal}`.slice(0, 7);
};

const toM2FromUnit = (value, unit) => {
  if (!Number.isFinite(value)) return null;
  return unit === 'pyeong' ? value * AREA_M2_PER_PYEONG : value;
};

const formatAreaValue = (value, unit) => {
  if (!Number.isFinite(value)) return '-';
  if (unit === 'pyeong') {
    const p = value / AREA_M2_PER_PYEONG;
    const rounded = Math.round(p * 10) / 10;
    return Number.isInteger(rounded) ? `${rounded}` : rounded.toFixed(1);
  }

  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? `${rounded}` : rounded.toFixed(1);
};

const getAreaBucketLabel = (bucket, unit) => {
  const left = bucket.min == null ? '' : formatAreaValue(bucket.min, unit);
  const right = bucket.max == null ? '' : formatAreaValue(bucket.max, unit);

  if (bucket.min == null) return `~${right}`;
  if (bucket.max == null) return `${left}~`;
  return `${left}~${right}`;
};

const parseAreaCandidatesM2 = (planTypeName) => {
  const source = String(planTypeName || '');
  if (!source.trim()) return [];

  const candidates = [];
  const unitRegex = /(\d+(?:\.\d+)?)\s*(m2|m²|㎡|평)/gi;
  let match = unitRegex.exec(source);
  while (match) {
    const num = Number(match[1]);
    const unitToken = String(match[2] || '').toLowerCase();
    if (Number.isFinite(num) && num > 0) {
      if (unitToken.includes('평')) candidates.push(num * AREA_M2_PER_PYEONG);
      else candidates.push(num);
    }
    match = unitRegex.exec(source);
  }

  if (candidates.length > 0) return candidates;

  // Fallback: "84A" 같은 표기 대응
  const plainNumbers = source.match(/\d+(?:\.\d+)?/g) || [];
  plainNumbers.forEach((token) => {
    const num = Number(token);
    if (!Number.isFinite(num) || num <= 0) return;
    if (num <= 40) candidates.push(num * AREA_M2_PER_PYEONG);
    else candidates.push(num);
  });

  return candidates;
};

const getPlanTypeName2Text = (item) => String(
  item?.planTypeName2
  ?? item?.plan_type_name2
  ?? ''
).trim();

const getPtpNameText = (item) => String(
  item?.ptpName
  ?? item?.ptp_name
  ?? ''
).trim();

const getItemAreaRangeM2 = (item) => {
  const minFromApi = Number(item?.ptpMinM2);
  const maxFromApi = Number(item?.ptpMaxM2);
  if (Number.isFinite(minFromApi) && Number.isFinite(maxFromApi) && maxFromApi > 0) {
    return {
      min: Math.max(0, Math.min(minFromApi, maxFromApi)),
      max: Math.max(minFromApi, maxFromApi),
    };
  }

  // 1순위: planTypeName2, 2순위: planTypeName(타입 문자열 "84A" 등)에서도 ㎡ 파싱
  let candidates = parseAreaCandidatesM2(getPlanTypeName2Text(item));
  if (candidates.length === 0) {
    candidates = parseAreaCandidatesM2(String(item?.planTypeName ?? item?.plan_type_name ?? ''));
  }
  if (candidates.length === 0) return null;

  return {
    min: Math.min(...candidates),
    max: Math.max(...candidates),
  };
};

const mergeDongList = (list = []) => {
  const map = new Map();
  list.forEach((dong) => {
    const code = String(dong?.bjdCode || '').trim();
    if (!code) return;
    if (!map.has(code)) map.set(code, dong);
  });
  return Array.from(map.values());
};

const mergeSigunguList = (list = []) => {
  const map = new Map();

  list.forEach((sigungu) => {
    const nameKey = String(sigungu?.sigunguName || '').trim();
    const codeKey = String(sigungu?.sigunguCode || '').trim();
    const key = codeKey || nameKey;
    if (!key) return;

    if (!map.has(key)) {
      map.set(key, {
        ...sigungu,
        bjdList: mergeDongList(sigungu?.bjdList || []),
      });
      return;
    }

    const prev = map.get(key);
    map.set(key, {
      ...prev,
      ...sigungu,
      sigunguName: prev?.sigunguName || sigungu?.sigunguName,
      sigunguCode: prev?.sigunguCode || sigungu?.sigunguCode,
      bjdList: mergeDongList([...(prev?.bjdList || []), ...(sigungu?.bjdList || [])]),
    });
  });

  return Array.from(map.values());
};

const getGroupSigunguList = (group) => {
  const merged = [];
  (group?.sidoList || []).forEach((sido) => {
    const sigunguList = mergeSigunguList(sido?.sigunguList || []);
    sigunguList.forEach((sigungu) => {
      merged.push({
        ...sigungu,
        sidoName: sigungu?.sidoName || sido?.sidoName || '',
        sidoCode: sigungu?.sidoCode || sido?.sidoCode || '',
      });
    });
  });

  const map = new Map();
  merged.forEach((sigungu) => {
    const key = `${String(sigungu?.sidoCode || sigungu?.sidoName || '').trim()}|${String(sigungu?.sigunguCode || sigungu?.sigunguName || '').trim()}`;
    if (!key || key === '|') return;
    if (!map.has(key)) {
      map.set(key, { ...sigungu, bjdList: mergeDongList(sigungu?.bjdList || []) });
      return;
    }

    const prev = map.get(key);
    map.set(key, {
      ...prev,
      ...sigungu,
      bjdList: mergeDongList([...(prev?.bjdList || []), ...(sigungu?.bjdList || [])]),
    });
  });

  return Array.from(map.values());
};

/*
const shouldFlattenGyeonggiInCapital = (groupName, sidoName) => {
  const g = String(groupName || '').trim();
  const s = String(sidoName || '').trim();
  return g === '수도권' && s.startsWith('경기');
};

const toCapitalSigunguLabel = (sigunguName) => {
  const raw = String(sigunguName || '').trim();
  if (!raw) return '';
  const tokens = raw.split(/\s+/).filter(Boolean);
  if (tokens.length >= 2 && tokens[tokens.length - 1].endsWith('구')) {
    return tokens[tokens.length - 1];
  }
  return raw;
};
*/
const toYear = (yyyymmdd) => {
  const text = String(yyyymmdd || '');
  if (text.length < 4) return null;
  const year = Number(text.slice(0, 4));
  return Number.isNaN(year) ? null : year;
};

const getUseApproveAge = (yyyymmdd) => {
  const year = toYear(yyyymmdd);
  if (!year) return null;
  return Math.max(0, new Date().getFullYear() - year);
};

const formatKrw = (value, useKoreanUnit = false) => {
  const number = Number(value);
  if (!Number.isFinite(number) || number <= 0) return '-';

  if (!useKoreanUnit) {
    const eokValue = number / 10000;
    const rounded = Math.round(eokValue * 100) / 100;
    return Number.isInteger(rounded)
      ? String(rounded)
      : String(rounded.toFixed(2)).replace(/\.?0+$/, '');
  }

  if (useKoreanUnit) {
    const eok = Math.floor(number / 10000);
    const man = number % 10000;

    if (eok > 0 && man > 0) {
      return `${eok.toLocaleString('ko-KR')}억${man.toLocaleString('ko-KR')}만원`;
    } else if (eok > 0) {
      return `${eok.toLocaleString('ko-KR')}억원`;
    } else {
      return `${man.toLocaleString('ko-KR')}만원`;
    }
  }

  return `${number.toLocaleString('ko-KR')}만원`;
};

const formatSignedKrw = (value, useKoreanUnit = false) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return '-';
  if (number === 0) return '0만원';
  if (number > 0) return formatKrw(number, useKoreanUnit);
  return `-${formatKrw(Math.abs(number), useKoreanUnit)}`;
};

const formatCount = (value, useKoreanUnit = false) => {
  const number = Number(value);
  if (!Number.isFinite(number) || number === 0) return '-';
  return `${number.toLocaleString('ko-KR')}${useKoreanUnit ? '건' : ''}`;
};

const formatWeeklyChange = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number) || number === 0) return '-';
  const icon = number > 0 ? '▲' : '▼';
  const color = number > 0 ? '#ff4444' : '#4444ff';
  const absValue = Math.abs(number).toLocaleString('ko-KR');
  return <span style={{ color }}>{icon} {absValue}</span>;
};

const formatPopulationKoreanUnit = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number) || number <= 0) return '-';
  const floored = Math.floor(number / 1000) * 1000;
  if (floored <= 0) return '-';
  const eok = Math.floor(floored / 100000000);
  const man = Math.floor((floored % 100000000) / 10000);
  const parts = [];
  if (eok > 0) parts.push(`${eok.toLocaleString('ko-KR')}억`);
  if (man > 0) parts.push(`${man.toLocaleString('ko-KR')}만`);
  return parts.length > 0 ? `${parts.join('')}명` : `${floored.toLocaleString('ko-KR')}명`;
};

const getSupplyYearValue = (item, year) => {
  const value = Number(item?.[`supply${year}`]);
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.round(value));
};

const getMoveinSupply3Years = (item) => (
  (() => {
    const direct = Number(item?.movein3Years ?? item?.movein_3years ?? item?.Movein3Years ?? item?.supply3Years ?? item?.supply_3years ?? item?.Supply3Years);
    if (Number.isFinite(direct)) return Math.max(0, Math.round(direct));
    return getSupplyYearValue(item, 2026)
      + getSupplyYearValue(item, 2027)
      + getSupplyYearValue(item, 2028);
  })()
);

const getDemand3Years = (item) => {
  const direct = Number(item?.demand3Years ?? item?.demand_3years ?? item?.Demand3Years);
  if (Number.isFinite(direct)) return Math.max(0, Math.round(direct));
  const population = Number(item?.population);
  if (!Number.isFinite(population) || population <= 0) return 0;
  return Math.round(population * 0.05 * 3);
};

const normalizeSupplyYears = (item) => {
  const toIntOrZero = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? Math.max(0, Math.round(n)) : 0;
  };

  return {
    ...item,
    supply3Years: toIntOrZero(item?.supply3Years ?? item?.supply_3years ?? item?.Supply3Years),
    demand3Years: toIntOrZero(item?.demand3Years ?? item?.demand_3years ?? item?.Demand3Years),
    movein3Years: toIntOrZero(item?.movein3Years ?? item?.movein_3years ?? item?.Movein3Years),
    supply2026: toIntOrZero(item?.supply2026 ?? item?.supply_2026 ?? item?.Supply2026),
    supply2027: toIntOrZero(item?.supply2027 ?? item?.supply_2027 ?? item?.Supply2027),
    supply2028: toIntOrZero(item?.supply2028 ?? item?.supply_2028 ?? item?.Supply2028),
  };
};

const formatLeaseRate = (item) => {
  const directRate = Number(item?.leasePriceRate);
  if (Number.isFinite(directRate) && directRate !== 0) return `${directRate.toFixed(1)}%`;
  const tradePrice = Number(item?.articleTradePrice);
  const leasePrice = Number(item?.articleLeasePrice);
  if (!Number.isFinite(tradePrice) || tradePrice <= 0 || !Number.isFinite(leasePrice)) return '-';
  const calculatedRate = (leasePrice / tradePrice) * 100;
  if (calculatedRate === 0) return '-';
  return `${calculatedRate.toFixed(1)}%`;
};

const toDateKey = (value) => {
  const text = String(value || '').trim();
  if (!text) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;
  const digits = text.replace(/\D/g, '');
  if (digits.length >= 8) return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)}`;
  return '';
};

const formatDateMMDD = (dateText) => {
  const date = toDateKey(dateText);
  if (!date) return '';
  return `${date.slice(5, 7)}/${date.slice(8, 10)}`;
};

const formatDateYYMM = (dateText) => {
  const date = toDateKey(dateText);
  if (!date) return '';
  return `${date.slice(2, 4)}.${date.slice(5, 7)}`;
};

// 최근 8주/6개월 첫 칼럼(토글 헤드)도 '최근 8주/최근 6개월' 텍스트 없이 날짜만 표기
//  · 주단위: 05-17 / 월단위: 26.05
const getRecentToggleLabel = ({ date, periodLabel, isMonth = false }) => {
  const formatted = isMonth ? formatDateYYMM(date) : formatDateMMDD(date);
  return formatted || periodLabel || '';
};

const toAmount = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

// 월별 전세가율(%) = 100 × 전세월값 / 매매월값
//  · docs 전세가율 공식 및 refresh_ranker_fix_list_snapshot 의 lease_rate_sum_6m(월별 100×전세/매매 합)과 동일 기준
//  · 백엔드 추가 없이 이미 내려오는 lease6/sale6 월별 데이터로 파생
const getLeaseRateAt = (item, date) => {
  const sale = getRecentPointList(item, 'sale6').find((r) => r.date === date);
  const lease = getRecentPointList(item, 'lease6').find((r) => r.date === date);
  const saleAmt = Number(sale?.amt);
  const leaseAmt = Number(lease?.amt);
  if (!Number.isFinite(saleAmt) || saleAmt <= 0 || !Number.isFinite(leaseAmt)) return null;
  return (leaseAmt / saleAmt) * 100;
};

// 월별 변동 전세가율 = dates[idx] 전세가율 − dates[idx+1] 전세가율 (0번째−1번째 = 첫 개월 변동)
const getLeaseRateChangeAt = (item, dates, idx) => {
  if (!Array.isArray(dates) || idx < 0 || idx + 1 >= dates.length) return null;
  const cur = getLeaseRateAt(item, dates[idx]);
  const prev = getLeaseRateAt(item, dates[idx + 1]);
  if (cur == null || prev == null) return null;
  return cur - prev;
};

// 6개월 변동 = 최근 5개월 월별 변동의 합
const getLeaseRateChangeSum = (item, dates) => {
  if (!Array.isArray(dates) || dates.length < 2) return null;
  let sum = 0;
  let any = false;
  for (let i = 0; i + 1 < dates.length; i += 1) {
    const d = getLeaseRateChangeAt(item, dates, i);
    if (d != null) { sum += d; any = true; }
  }
  return any ? sum : null;
};

// 매매/전세 비율(%) = 매물수 / 평세대수(ptpFamilyCnt) × 100. 최대 소수 2자리 반올림, 트레일링 0 제거.
const getCountRatioValue = (count, households) => {
  const c = Number(count);
  const h = Number(households);
  if (!Number.isFinite(c) || !Number.isFinite(h) || h <= 0) return null;
  return (c / h) * 100;
};
const formatCountRatio = (count, households) => {
  const v = getCountRatioValue(count, households);
  if (v == null) return '-';
  return `${Math.round(v * 100) / 100}%`;
};

const getRecentPointListRaw = (item, type) => {
  const source = item || {};
  const readList = (camelKey, snakeKey) => {
    const camel = source?.[camelKey];
    if (Array.isArray(camel)) return camel;
    const snake = source?.[snakeKey];
    if (Array.isArray(snake)) return snake;
    return [];
  };
  const pickList = (keys = []) => {
    for (const key of keys) {
      const value = source?.[key];
      if (Array.isArray(value)) return value;
    }
    return [];
  };
  const normalizeList = (list = []) => list
    .map((row) => {
      const date = toDateKey(row?.date || row?.baseDate || row?.base_date || row?.dt);
      const amt = toAmount(row?.amt ?? row?.amount ?? row?.price ?? row?.value ?? row?.sale_price ?? row?.lease_price);
      if (!date || amt == null) return null;
      return { date, amt };
    })
    .filter(Boolean);

  const weeklyRows = readList('weeklySnapshots', 'weekly_snapshots');
  const fromWeekly = (valueKey) => weeklyRows
    .map((row) => {
      const date = toDateKey(row?.baseDate || row?.base_date || row?.date);
      const snakeKeyMap = {
        salePrice: 'sale_price',
        leasePrice: 'lease_price',
        saleCount: 'sale_count',
        leaseCount: 'lease_count',
        saleRealCount: 'sale_real_count',
        leaseRealCount: 'lease_real_count',
      };
      const snakeKey = snakeKeyMap[valueKey] || valueKey;
      const amt = toAmount(row?.[valueKey] ?? row?.[snakeKey]);
      if (!date || amt == null) return null;
      return { date, amt };
    })
    .filter(Boolean);

  if (type === 'sale8') {
    const list = normalizeList(
      pickList(['saleRecent8Weeks', 'tradeRecent8Weeks', 'recent8WeeksTrade'])
        .concat(readList('saleRecent8Weeks', 'sale_recent8_weeks'))
        .concat(readList('tradeRecent8Weeks', 'trade_recent8_weeks'))
        .concat(readList('recent8WeeksTrade', 'recent8_weeks_trade'))
    );
    return list.length > 0 ? list : fromWeekly('salePrice');
  }
  if (type === 'lease8') {
    const list = normalizeList(
      pickList(['leaseRecent8Weeks', 'recent8WeeksLease'])
        .concat(readList('leaseRecent8Weeks', 'lease_recent8_weeks'))
        .concat(readList('recent8WeeksLease', 'recent8_weeks_lease'))
    );
    return list.length > 0 ? list : fromWeekly('leasePrice');
  }
  if (type === 'sale6') {
    const list = normalizeList(
      pickList(['saleRecent6Months', 'tradeRecent6Months', 'recent6MonthsTrade'])
        .concat(readList('saleRecent6Months', 'sale_recent6_months'))
        .concat(readList('tradeRecent6Months', 'trade_recent6_months'))
        .concat(readList('recent6MonthsTrade', 'recent6_months_trade'))
    );
    if (list.length > 0) return list;
    return fromWeekly('salePrice').filter((row) => new Date(row.date).getDay() === 3);
  }
  if (type === 'lease6') {
    const list = normalizeList(
      pickList(['leaseRecent6Months', 'recent6MonthsLease'])
        .concat(readList('leaseRecent6Months', 'lease_recent6_months'))
        .concat(readList('recent6MonthsLease', 'recent6_months_lease'))
    );
    if (list.length > 0) return list;
    return fromWeekly('leasePrice').filter((row) => new Date(row.date).getDay() === 3);
  }
  if (type === 'saleTx8') {
    const list = normalizeList(
      pickList(['saleTxRecent8Weeks'])
        .concat(readList('saleTxRecent8Weeks', 'sale_tx_recent_8_weeks'))
    );
    return list.length > 0 ? list : fromWeekly('saleRealPrice');
  }
  if (type === 'saleTx6') {
    const list = normalizeList(
      pickList(['saleTxRecent6Months'])
        .concat(readList('saleTxRecent6Months', 'sale_tx_recent_6_months'))
    );
    if (list.length > 0) return list;
    return fromWeekly('saleRealPrice').filter((row) => new Date(row.date).getDay() === 3);
  }
  if (type === 'leaseTx8') {
    const list = normalizeList(
      pickList(['leaseTxRecent8Weeks'])
        .concat(readList('leaseTxRecent8Weeks', 'lease_tx_recent_8_weeks'))
    );
    return list.length > 0 ? list : fromWeekly('leaseRealPrice');
  }
  if (type === 'leaseTx6') {
    const list = normalizeList(
      pickList(['leaseTxRecent6Months'])
        .concat(readList('leaseTxRecent6Months', 'lease_tx_recent_6_months'))
    );
    if (list.length > 0) return list;
    return fromWeekly('leaseRealPrice').filter((row) => new Date(row.date).getDay() === 3);
  }
  if (type === 'saleCount8') {
    const list = normalizeList(
      pickList(['saleCountRecent8Weeks'])
        .concat(readList('saleCountRecent8Weeks', 'sale_count_recent_8_weeks'))
    );
    return list.length > 0 ? list : fromWeekly('saleCount');
  }
  if (type === 'saleCount6') {
    const list = normalizeList(
      pickList(['saleCountRecent6Months'])
        .concat(readList('saleCountRecent6Months', 'sale_count_recent_6_months'))
    );
    if (list.length > 0) return list;
    return fromWeekly('saleCount').filter((row) => new Date(row.date).getDay() === 3);
  }
  if (type === 'leaseCount8') {
    const list = normalizeList(
      pickList(['leaseCountRecent8Weeks'])
        .concat(readList('leaseCountRecent8Weeks', 'lease_count_recent_8_weeks'))
    );
    return list.length > 0 ? list : fromWeekly('leaseCount');
  }
  if (type === 'leaseCount6') {
    const list = normalizeList(
      pickList(['leaseCountRecent6Months'])
        .concat(readList('leaseCountRecent6Months', 'lease_count_recent_6_months'))
    );
    if (list.length > 0) return list;
    return fromWeekly('leaseCount').filter((row) => new Date(row.date).getDay() === 3);
  }
  if (type === 'saleTxCount8') {
    const list = normalizeList(
      pickList(['saleTxCountRecent8Weeks', 'saleRealCountRecent8Weeks'])
        .concat(readList('saleTxCountRecent8Weeks', 'sale_tx_count_recent_8_weeks'))
        .concat(readList('saleRealCountRecent8Weeks', 'sale_real_count_recent_8_weeks'))
    );
    return list.length > 0 ? list : fromWeekly('saleRealCount');
  }
  if (type === 'saleTxCount6') {
    const list = normalizeList(
      pickList(['saleTxCountRecent6Months', 'saleRealCountRecent6Months'])
        .concat(readList('saleTxCountRecent6Months', 'sale_tx_count_recent_6_months'))
        .concat(readList('saleRealCountRecent6Months', 'sale_real_count_recent_6_months'))
    );
    if (list.length > 0) return list;
    return fromWeekly('saleRealCount').filter((row) => new Date(row.date).getDay() === 3);
  }
  if (type === 'leaseTxCount8') {
    const list = normalizeList(
      pickList(['leaseTxCountRecent8Weeks', 'leaseRealCountRecent8Weeks'])
        .concat(readList('leaseTxCountRecent8Weeks', 'lease_tx_count_recent_8_weeks'))
        .concat(readList('leaseRealCountRecent8Weeks', 'lease_real_count_recent_8_weeks'))
    );
    return list.length > 0 ? list : fromWeekly('leaseRealCount');
  }
  if (type === 'leaseTxCount6') {
    const list = normalizeList(
      pickList(['leaseTxCountRecent6Months', 'leaseRealCountRecent6Months'])
        .concat(readList('leaseTxCountRecent6Months', 'lease_tx_count_recent_6_months'))
        .concat(readList('leaseRealCountRecent6Months', 'lease_real_count_recent_6_months'))
    );
    if (list.length > 0) return list;
    return fromWeekly('leaseRealCount').filter((row) => new Date(row.date).getDay() === 3);
  }
  return [];
};

// (최근 셀 전주대비 색상은 recentCellTextColor + ComplexTanStackTable cellStyle 에서 처리)
const getRecentPointList = (item, type) => getRecentPointListRaw(item, type);

// 최근 8주/6개월 컬럼 key → getRecentPointList 타입([8주, 6개월])
const RECENT_BASE_TYPE = {
  articleTradePrice: ['sale8', 'sale6'],
  articleLeasePrice: ['lease8', 'lease6'],
  saleTx: ['saleTx8', 'saleTx6'],
  leaseTx: ['leaseTx8', 'leaseTx6'],
  tradeArticleCount: ['saleCount8', 'saleCount6'],
  leaseArticleCount: ['leaseCount8', 'leaseCount6'],
  saleRealCount: ['saleTxCount8', 'saleTxCount6'],
  leaseRealCount: ['leaseTxCount8', 'leaseTxCount6'],
  leaseRate: [null, 'leaseRate6'],
};
// 최근 8주/6개월 셀의 전주(직전 시점)대비 글자색 — ComplexTanStackTable cellStyle 용.
// columnKey 예: 'tradeArticleCountRecent8__26.06'(확장) / 'tradeArticleCountRecent8Toggle'(헤드=최신)
const recentCellTextColor = (columnKey, item) => {
  if (!columnKey || !item) return null;
  const m = String(columnKey).match(/^(.+?)Recent([86])(?:__(.+)|Toggle)$/);
  if (!m) return null;
  const pair = RECENT_BASE_TYPE[m[1]];
  if (!pair) return null;
  const type = m[2] === '8' ? pair[0] : pair[1];
  if (!type) return null;
  const list = getRecentPointListRaw(item, type);
  if (!Array.isArray(list) || list.length === 0) return null;
  const sorted = [...list].sort((a, b) => String(a?.date || '').localeCompare(String(b?.date || '')));
  const date = m[3];
  const idx = date ? sorted.findIndex((v) => v.date === date) : sorted.length - 1;
  if (idx < 0) return null;        // 해당 날짜에 데이터 없음('-') → 무색
  if (idx === 0) return '#1f2937'; // 최초 → 검정
  const cur = toAmount(sorted[idx]?.amt);
  const prev = toAmount(sorted[idx - 1]?.amt);
  if (cur == null || prev == null) return '#1f2937';
  if (cur > prev) return '#ef4444';   // 상승 빨강
  if (cur < prev) return '#3b82f6';   // 하락 파랑
  return '#1f2937';                   // 동일 검정
};

const getWeeklyPointDelta = (item, type) => {
  const list = getRecentPointList(item, type)
    .slice()
    .sort((a, b) => String(b?.date || '').localeCompare(String(a?.date || '')));
  if (list.length < 2) return null;
  return (toAmount(list[0]?.amt) ?? 0) - (toAmount(list[1]?.amt) ?? 0);
};

const collectRecentDates = (rows = [], type, limit) => {
  const dateSet = new Set();
  rows.forEach((item) => {
    getRecentPointList(item, type).forEach((point) => {
      if (point?.date) dateSet.add(point.date);
    });
  });
  return Array.from(dateSet)
    .sort((a, b) => b.localeCompare(a))
    .slice(0, limit);
};

const getSaleChangeApiAmount = (item, type) => {
  const source = item || {};
  const keyCandidates = {
    '6m': ['saleChange6Months', 'sale_change_6_months', 'tradeChange6Months', 'trade_change_6_months'],
    '8w': ['saleChange8Weeks', 'sale_change_8_weeks', 'tradeChange8Weeks', 'trade_change_8_weeks'],
    '4w': ['saleChange4Weeks', 'sale_change_4_weeks', 'tradeChange4Weeks', 'trade_change_4_weeks'],
  };
  const keys = keyCandidates[type] || [];
  for (const key of keys) {
    const value = toAmount(source?.[key]);
    if (value != null) return value;
  }
  return null;
};

const getLeaseChangeApiAmount = (item, type) => {
  const source = item || {};
  const keyCandidates = {
    '6m': ['leaseChange6Months', 'lease_change_6_months'],
    '8w': ['leaseChange8Weeks', 'lease_change_8_weeks'],
    '4w': ['leaseChange4Weeks', 'lease_change_4_weeks'],
  };
  const keys = keyCandidates[type] || [];
  for (const key of keys) {
    const value = toAmount(source?.[key]);
    if (value != null) return value;
  }
  return null;
};
const getRankingHeaderFilterText = (item, key) => {
  if (!item || !key) return '';
  switch (key) {
    case 'sigunguGrade':
    case 'dongGrade':
    case 'familyCnt':
    case 'planTypeName':
    case 'pastTopPrice':
    case 'prevHighPrice':
    case 'properHighPrice':
    case 'prevLowPrice':
    case 'investPrice':
    case 'investRealInvestPrice':
    case 'articleTradePrice':
    case 'complexMinTradePrice':
    case 'complexMaxTradePrice':
    case 'articleLeasePrice':
    case 'complexMinLeasePrice':
    case 'complexMaxLeasePrice':
    case 'avgSaleRealPrice':
    case 'avgLeaseRealPrice':
    case 'tradeArticleCount':
    case 'leaseArticleCount':
    case 'leasePriceRate':
    case 'riseRate':
    case 'risePrice':
    case 'supply3Years':
      return String(item?.[key] ?? '').trim();
    case 'useApproveDate': {
      const year = toYear(item?.useApproveDate);
      return year ? String(year) : '';
    }
    case 'useApproveAge': {
      const age = getUseApproveAge(item?.useApproveDate);
      return age == null ? '' : String(age);
    }
    case 'goHyunRate': {
      const rate = Number(item?.goHyunRate);
      return Number.isFinite(rate) ? rate.toFixed(1) : '';
    }
    case 'goJeoRate': {
      const rate = Number(item?.goJeoRate);
      return Number.isFinite(rate) ? rate.toFixed(1) : '';
    }
    case 'jeoHyunRate': {
      const rate = Number(item?.jeoHyunRate);
      return Number.isFinite(rate) ? rate.toFixed(1) : '';
    }
    case 'jeokHyunRate': {
      const rate = Number(item?.jeokHyunRate);
      return Number.isFinite(rate) ? rate.toFixed(1) : '';
    }
    case 'changeGap': {
      const gap = Number(item?.changeGap);
      return Number.isFinite(gap) ? String(gap) : '';
    }
    case 'leasePriceRate': {
      const directRate = Number(item?.leasePriceRate);
      if (Number.isFinite(directRate)) return directRate.toFixed(1);
      const tradePrice = Number(item?.articleTradePrice);
      const leasePrice = Number(item?.articleLeasePrice);
      if (!Number.isFinite(tradePrice) || tradePrice <= 0 || !Number.isFinite(leasePrice)) return '';
      return ((leasePrice / tradePrice) * 100).toFixed(1);
    }
    default:
      return String(item?.[key] ?? '').trim();
  }
};

const toShortRegionName = (sidoName) => {
  const raw = String(sidoName || '').trim();
  if (!raw) return '';
  return REGION_SHORT_NAME_MAP[raw] || raw;
};

const formatRegionLabel = (item) => {
  const shortSido = toShortRegionName(item?.sidoName);
  const sigungu = String(item?.sigunguName || '').trim();
  return [shortSido, sigungu].filter(Boolean).join(' ').trim();
};

const RANGE_FILTER_KEYS = new Set([
  'useApproveAge',
  'sigunguGrade',
  'dongGrade',
  'articleTradePrice',
  'articleLeasePrice',
  'tradeArticleCount',
  'leaseArticleCount',
  'sumSaleRealCount',
  'sumLeaseRealCount'
]);

const parseRangeFilterInput = (raw) => {
  const text = String(raw || '').trim();
  if (!text) return null;
  const normalized = text.replace(/\s+/g, '');
  const exact = normalized.match(/^(\d+(?:\.\d+)?)$/);
  if (exact) {
    const value = Number(exact[1]);
    return Number.isFinite(value) ? { min: value, max: value, valid: true } : null;
  }
  const ranged = normalized.match(/^(\d+(?:\.\d+)?)?[~-](\d+(?:\.\d+)?)?$/);
  if (!ranged) return null;
  const left = ranged[1] === undefined ? null : Number(ranged[1]);
  const right = ranged[2] === undefined ? null : Number(ranged[2]);
  const min = Number.isFinite(left) ? left : null;
  const max = Number.isFinite(right) ? right : null;
  if (min == null && max == null) return { valid: false };
  return { min, max, valid: true };
};

const matchesHeaderFilter = (item, key, rawFilter) => {
  const keyword = String(rawFilter || '').trim();
  if (!keyword) return true;

  if (RANGE_FILTER_KEYS.has(key)) {
    const parsed = parseRangeFilterInput(keyword);
    if (parsed?.valid) {
      let value;
      // Special handling for useApproveAge: filter uses year ranges but column shows age
      if (key === 'useApproveAge') {
        const year = toYear(item?.useApproveDate);
        value = year ? Number(year) : NaN;
      } else {
        value = Number(getRankingHeaderFilterText(item, key));
      }
      if (!Number.isFinite(value)) return false;
      if (parsed.min != null && value < parsed.min) return false;
      if (parsed.max != null && value > parsed.max) return false;
      return true;
    }
  }

  const value = getRankingHeaderFilterText(item, key).toLowerCase();
  return value.includes(keyword.toLowerCase());
};

const getRegionPriorityRank = (sidoName) => {
  const raw = String(sidoName || '').trim();
  if (!raw) return 9;
  if (raw === '서울특별시') return 0;
  if (raw === '경기도') return 1;
  if (raw.endsWith('광역시')) return 2;
  return 3;
};

const getRegionSortValue = (item) => {
  const rank = getRegionPriorityRank(item?.sidoName);
  const regionText = formatRegionLabel(item);
  return `${String(rank)}|${regionText}`;
};

const formatLivingZoneLabel = (item) => {
  if (item?.isAggregateRow) {
    const level = String(item?.aggregateLevel || '').toUpperCase();
    if (level === 'SIDO' || level === 'SIGUNGU') return '-';
  }
  const livingZone = String(item?.dongName || item?.bjdName || item?.hjdName || '').trim();
  return livingZone || '-';
};

// 구조 코드 → 사람이 읽는 형태 (예: 계32 → 계단식 방3 화장실2)
const STRUCTURE_TYPE_MAP = { 계: '계단식', 복: '복도식', 타: '타워형', 혼: '혼합식' };
const formatHouseStructure = (value) => {
  const raw = String(value ?? '').trim();
  if (!raw) return '-';
  const compact = raw.replace(/\s+/g, '');
  const m = compact.match(/^([가-힣A-Za-z]+)(\d)(\d)$/);
  if (!m) return raw;
  const typeText = STRUCTURE_TYPE_MAP[m[1]] || m[1];
  const roomCnt = Number(m[2]);
  const bathCnt = Number(m[3]);
  const parts = [typeText];
  if (Number.isFinite(roomCnt) && roomCnt > 0) parts.push(`방${roomCnt}`);
  if (Number.isFinite(bathCnt) && bathCnt > 0) parts.push(`화장실${bathCnt}`);
  return parts.join(' ');
};

const getGroupByLevelsFromSelection = (selectedGroupBy = []) => {
  const selected = new Set(
    (Array.isArray(selectedGroupBy) ? selectedGroupBy : [])
      .map((value) => String(value || '').trim())
      .filter(Boolean)
  );

  const levelSet = new Set();
  if (selected.has('시도') || selected.has('SIDO') || selected.has(GROUP_BY_OPTIONS[0])) levelSet.add('SIDO');
  if (selected.has('구') || selected.has('SIGUNGU') || selected.has(GROUP_BY_OPTIONS[1])) levelSet.add('SIGUNGU');
  if (selected.has('동') || selected.has('DONG') || selected.has(GROUP_BY_OPTIONS[2])) levelSet.add('DONG');

  return AGGREGATE_LEVEL_ORDER.filter((level) => levelSet.has(level));
};

const isAggregateRow = (item) => Boolean(item?.isAggregateRow);
// 지역평균 행(시/구/동 집계) 여부 — 공급 데이터는 단지별이 아닌 지역 단위에서만 제공
const isRegionAverageRow = (item) => {
  if (!isAggregateRow(item)) return false;
  const level = String(item?.aggregateLevel || '').toUpperCase();
  return level === 'SIDO' || level === 'SIGUNGU' || level === 'DONG';
};

const normalizeGroupToken = (value) => String(value || '').trim();
const AGGREGATE_LEVEL_ORDER = ['SIDO', 'SIGUNGU', 'DONG'];

const getAggregateGroupKey = (item, level) => {
  const sido = normalizeGroupToken(item?.sidoName);
  const sigungu = normalizeGroupToken(item?.sigunguName);
  const dong = normalizeGroupToken(item?.bjdName);
  const targetLevel = String(level || item?.aggregateLevel || '').toUpperCase();

  if (targetLevel === 'SIDO') return sido || '';
  if (targetLevel === 'SIGUNGU') return (sido && sigungu) ? `${sido}|${sigungu}` : '';
  if (targetLevel === 'DONG') return (sido && sigungu && dong) ? `${sido}|${sigungu}|${dong}` : '';
  return '';
};

const toValidNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const formatRatioPercent = (value) => {
  if (!Number.isFinite(value)) return '-';
  return `${value.toFixed(1)}%`;
};

const FavoriteStarIcon = ({ filled = false, className = '' }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className={className}>
    <path
      d="M12 21s-7.1-4.4-9.6-8.1C-0.1 9.3 1.1 4.7 5 3.2c2.4-.9 4.9-.2 7 2 2.1-2.2 4.6-2.9 7-2 3.9 1.5 5.1 6.1 2.6 9.7C19.1 16.6 12 21 12 21Z"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
      strokeLinecap="round"
    />
  </svg>
);

const RegionIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 21s7-5.6 7-11a7 7 0 1 0-14 0c0 5.4 7 11 7 11Z" fill="none" stroke="currentColor" strokeWidth="1.9" />
    <circle cx="12" cy="10" r="2.8" fill="none" stroke="currentColor" strokeWidth="1.9" />
  </svg>
);

const CalendarIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <rect x="3" y="5" width="18" height="16" rx="3" fill="none" stroke="currentColor" strokeWidth="1.9" />
    <path d="M8 3v4M16 3v4M3 10h18" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
  </svg>
);

const HouseholdIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="9" cy="8" r="3" fill="none" stroke="currentColor" strokeWidth="1.9" />
    <circle cx="16.5" cy="9" r="2.5" fill="none" stroke="currentColor" strokeWidth="1.9" />
    <path d="M4 19c0-3 2.3-5 5-5s5 2 5 5M13 19c0-2.3 1.7-3.9 3.8-3.9 2.1 0 3.8 1.6 3.8 3.9" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
  </svg>
);

const AreaIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M5 6h14v12H5z" fill="none" stroke="currentColor" strokeWidth="1.9" />
    <path d="M5 10h14M12 6v12" fill="none" stroke="currentColor" strokeWidth="1.9" />
  </svg>
);

const ColumnSettingIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M4 6h16M4 12h16M4 18h16" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
    <circle cx="8" cy="6" r="1.8" fill="currentColor" />
    <circle cx="15" cy="12" r="1.8" fill="currentColor" />
    <circle cx="11" cy="18" r="1.8" fill="currentColor" />
  </svg>
);

const MiniTransactionSparkline = ({ points = [] }) => {
  const validPoints = (Array.isArray(points) ? points : [])
    .map((point) => {
      const value = Number(point?.amountKrw);
      return Number.isFinite(value) ? value : null;
    })
    .filter((value) => value != null);

  if (validPoints.length < 2) return <span className="complex-mini-spark-empty">-</span>;

  const width = 86;
  const height = 22;
  const min = Math.min(...validPoints);
  const max = Math.max(...validPoints);
  const range = max - min || 1;
  const stepX = validPoints.length > 1 ? width / (validPoints.length - 1) : width;

  const linePath = validPoints.map((value, index) => {
    const x = Math.round(index * stepX * 100) / 100;
    const y = Math.round((height - ((value - min) / range) * (height - 2) - 1) * 100) / 100;
    return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
  }).join(' ');

  const areaPath = `${linePath} L ${width} ${height} L 0 ${height} Z`;

  return (
    <svg className="complex-mini-spark" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" role="img" aria-label="최근 거래 추세">
      <path d={areaPath} fill="rgba(59,130,246,0.16)" />
      <path d={linePath} fill="none" stroke="#3b82f6" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

const RegionGroupNode = React.memo(
  function RegionGroupNode({
    group,
    expanded,
    groupChecked,
    groupCodes,
    onToggleGroup,
    onToggleCodes,
    renderChildren,
    showPopulationFilters,
    groupPopulationText,
  }) {
    return (
      <div className="tree-sido">
        <div
          className="tree-sido-row"
          onClick={() => onToggleGroup(group.groupName)}
        >
          <label onClick={(e) => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center', width: '100%', gap: '6px', flex: 1, minWidth: 0 }}>
            <input
              type="checkbox"
              checked={groupChecked}
              onChange={() => onToggleCodes(groupCodes)}
              onClick={(e) => e.stopPropagation()}
            />
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', width: '100%', flex: 1, minWidth: 0 }}>
              <span>{group.groupName}</span>
              {showPopulationFilters && groupPopulationText && (
                <span style={{ fontSize: '12px', color: '#475569', marginLeft: 'auto', whiteSpace: 'nowrap' }}>
                  {groupPopulationText}
                </span>
              )}
            </span>
          </label>
          <span className="tree-expand-indicator">{expanded ? '−' : '+'}</span>
        </div>
        {expanded && (
          <div className="tree-sigungu-list">
            {renderChildren()}
          </div>
        )}
      </div>
    );
  }
);

function RankingWorkspace({ pageMode = 'ranking' }) {
  const { isLoggedIn, user } = useAuth();
  const topFilterRef = useRef(null);
  const topFilterTabRefs = useRef({});
  const columnSettingRef = useRef(null);
  const sortSettingRef = useRef(null);
  const colorSettingRef = useRef(null);
  const rankingButtonRef = useRef(null);
  const treeInitializedRef = useRef(false);
  const defaultGroupItemsById = useMemo(() => buildDefaultGroupItemsById(), []);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [nextLastKey, setNextLastKey] = useState(null);
  const [filterOptions, setFilterOptions] = useState(null);

  const [groupTree, setGroupTree] = useState([]);
  const [treeLoading, setTreeLoading] = useState(false);
  const [selectedBjdCodes, setSelectedBjdCodes] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [expandedSido, setExpandedSido] = useState({});
  const [expandedSigungu, setExpandedSigungu] = useState({});
  const [, startTreeTransition] = useTransition();

  const [builtDateFrom, setBuiltDateFrom] = useState('');
  const [builtDateTo, setBuiltDateTo] = useState('');
  const [snapshotDate, setSnapshotDate] = useState(null); // 마지막 업데이트(스냅샷) 날짜
  const [builtDateMode, setBuiltDateMode] = useState('range'); // 'range' or 'age'
  const [builtDateAgeInput, setBuiltDateAgeInput] = useState('');
  const [selectedHouseholds, setSelectedHouseholds] = useState([]);
  const [householdMinInput, setHouseholdMinInput] = useState('');
  const [householdMaxInput, setHouseholdMaxInput] = useState('');
  const [areaUnit, setAreaUnit] = useState('m2');
  const [selectedAreaBuckets, setSelectedAreaBuckets] = useState([]);
  const [areaMinInput, setAreaMinInput] = useState('');
  const [areaMaxInput, setAreaMaxInput] = useState('');
  const [tradePriceMinInput, setTradePriceMinInput] = useState('');
  const [tradePriceMaxInput, setTradePriceMaxInput] = useState('');
  const [investPriceMinInput, setInvestPriceMinInput] = useState('');
  const [investPriceMaxInput, setInvestPriceMaxInput] = useState('');
  const [landmarkFilterOptions, setLandmarkFilterOptions] = useState(FALLBACK_LANDMARK_FILTER_OPTIONS);
  const [selectedLandmarkFilters, setSelectedLandmarkFilters] = useState([]);
  const [gubunAverageOptions, setGubunAverageOptions] = useState([]);
  const [selectedGubunAverageCodes, setSelectedGubunAverageCodes] = useState([]);
  const [selectedGroupBy, setSelectedGroupBy] = useState([]);
  // 단지제외: 체크 시 개별 단지는 목록에서 빼고 지역평균(집계행)만 표시
  const [excludeDanji, setExcludeDanji] = useState(false);
  const [openTopFilterKey, setOpenTopFilterKey] = useState(null);
  const [topFilterPanelLeft, setTopFilterPanelLeft] = useState(0);
  const [favoriteOnly, setFavoriteOnly] = useState(false);

  // 필터 값 (입력 중인 값)
  const [filterValues, setFilterValues] = useState({
    tradePriceMin: '',
    tradePriceMax: '',
    investPriceMin: '',
    investPriceMax: '',
    useApproveAgeMin: '',
    useApproveAgeMax: '',
    areaMin: '',
    areaMax: '',
    householdMin: '',
    householdMax: '',
  });

  // 적용된 필터 값 (API 호출 시 사용)
  const [appliedFilters, setAppliedFilters] = useState({
    tradePriceMin: null,
    tradePriceMax: null,
    investPriceMin: null,
    investPriceMax: null,
    investRealInvestPriceMin: null,
    investRealInvestPriceMax: null,
    complexMinTradePriceMin: null,
    complexMinTradePriceMax: null,
    complexMaxTradePriceMin: null,
    complexMaxTradePriceMax: null,
    leasePriceMin: null,
    leasePriceMax: null,
    complexMinLeasePriceMin: null,
    complexMinLeasePriceMax: null,
    complexMaxLeasePriceMin: null,
    complexMaxLeasePriceMax: null,
    avgSaleRealPriceMin: null,
    avgSaleRealPriceMax: null,
    avgLeaseRealPriceMin: null,
    avgLeaseRealPriceMax: null,
    tradeArticleCountMin: null,
    tradeArticleCountMax: null,
    leaseArticleCountMin: null,
    leaseArticleCountMax: null,
    leasePriceRateMin: null,
    leasePriceRateMax: null,
    riseRateMin: null,
    riseRateMax: null,
    risePriceMin: null,
    risePriceMax: null,
    supply3YearsMin: null,
    supply3YearsMax: null,
    useApproveAgeMin: null,
    useApproveAgeMax: null,
    areaMin: null,
    areaMax: null,
    householdMin: null,
    householdMax: null,
    goHyunRateMin: null,
    goHyunRateMax: null,
    jeokHyunRateMin: null,
    jeokHyunRateMax: null,
    goJeoRateMin: null,
    goJeoRateMax: null,
    jeoHyunRateMin: null,
    jeoHyunRateMax: null,
    changeGapMin: null,
    changeGapMax: null,
    pastTopPriceMin: null,
    pastTopPriceMax: null,
    prevHighPriceMin: null,
    prevHighPriceMax: null,
    properHighPriceMin: null,
    properHighPriceMax: null,
    prevLowPriceMin: null,
    prevLowPriceMax: null,
    targetPriceMin: null,
    targetPriceMax: null,
  });

  // 상세 필터 설정 모달
  const [detailFilterOpen, setDetailFilterOpen] = useState(false);

  // 급지 필터 (복수 선택)
  const [selectedSigunguGrades, setSelectedSigunguGrades] = useState([]);
  const [selectedDongGrades, setSelectedDongGrades] = useState([]);

  // 구분 필터 (복수 선택)
  const [selectedGubuns, setSelectedGubuns] = useState([]);
  // 구조 필터 (계32 형식)
  const [selectedHouseStructure, setSelectedHouseStructure] = useState('');
  const [selectedDanjiCodeForModal, setSelectedDanjiCodeForModal] = useState('');
  const [selectedDanjiSummaryForModal, setSelectedDanjiSummaryForModal] = useState(null);
  const [compareTargets, setCompareTargets] = useState([]);
  const [compareModalOpen, setCompareModalOpen] = useState(false);
  const [columnSettingOpen, setColumnSettingOpen] = useState(false);
  const [filterPopupOpen, setFilterPopupOpen] = useState(false);
  const [filterPopupColumn, setFilterPopupColumn] = useState(null);
  const [filterPopupPosition, setFilterPopupPosition] = useState({ top: 0, left: 0 });
  const [sortSettingOpen, setSortSettingOpen] = useState(false);
  const [colorSettingOpen, setColorSettingOpen] = useState(false);
  // 조건부 서식 규칙(엑셀식): [{ id, scope:'column'|'group', targetKey, op, value, value2, color }]
  // 값이 조건을 만족하는 셀에 배경색을 칠한다. localStorage 유지.
  const [cellColorRules, setCellColorRules] = useState(() => {
    try {
      const raw = localStorage.getItem('ranker_cell_color_rules_v1');
      const parsed = raw ? JSON.parse(raw) : null;
      return Array.isArray(parsed) ? parsed : [];
    } catch { return []; }
  });
  useEffect(() => {
    try { localStorage.setItem('ranker_cell_color_rules_v1', JSON.stringify(cellColorRules || [])); } catch { /* ignore */ }
  }, [cellColorRules]);
  const addColorRule = (rule) => setCellColorRules((prev) => [...(prev || []), rule]);
  // eslint-disable-next-line no-unused-vars
  const updateColorRule = (id, patch) => setCellColorRules((prev) => (prev || []).map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const removeColorRule = (id) => setCellColorRules((prev) => (prev || []).filter((r) => r.id !== id));
  // 새 규칙 작성용 초안
  const [colorRuleDraft, setColorRuleDraft] = useState({ scope: 'column', targetKey: '', op: '>=', value: '', value2: '', color: '#fff3b0' });
  const handleAddColorRule = () => {
    const d = colorRuleDraft;
    if (!d.targetKey) return;
    const value = Number(d.value);
    const value2 = Number(d.value2);
    if (d.op === 'between') {
      if (!Number.isFinite(value) || !Number.isFinite(value2)) return;
    } else if (!Number.isFinite(value)) {
      return;
    }
    addColorRule({
      id: `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      scope: d.scope,
      targetKey: d.targetKey,
      op: d.op,
      value,
      value2: d.op === 'between' ? value2 : undefined,
      color: d.color,
    });
    setColorRuleDraft((prev) => ({ ...prev, value: '', value2: '' }));
  };
  const [selectedSortIndex, setSelectedSortIndex] = useState(null);
  const [sortDragOverIndex, setSortDragOverIndex] = useState(null);
  const [sortDragFromIndex, setSortDragFromIndex] = useState(null);
  const [columnPrefs, setColumnPrefs] = useState({ order: [], visible: {} });
  const [columnGroupPrefs, setColumnGroupPrefs] = useState({});
  const [columnGroupOrder, setColumnGroupOrder] = useState(() => DISPLAY_COLUMN_GROUPS.filter((g) => !g.hidden).map((g) => g.id));
  const [columnGroupItemsById, setColumnGroupItemsById] = useState(() => buildDefaultGroupItemsById());
  const [sortPrefs, setSortPrefs] = useState([{ key: 'region', direction: 'asc' }]);
  const [tableHeaderFilters, setTableHeaderFilters] = useState({});
  const [tableHeaderFilterSummaryMap, setTableHeaderFilterSummaryMap] = useState({});
  const [stickyHeaderFilterSummaryMap, setStickyHeaderFilterSummaryMap] = useState({});
  const [headerFilterResetToken, setHeaderFilterResetToken] = useState(0);
  const [headerFilterRemoveSignal, setHeaderFilterRemoveSignal] = useState({ key: '', token: 0 });
  const [useKoreanPriceUnit, setUseKoreanPriceUnit] = useState(false);
  const [resizeColumnsToken, setResizeColumnsToken] = useState(0);
  const useKoreanPriceUnitRef = useRef(useKoreanPriceUnit);
  useEffect(() => {
    useKoreanPriceUnitRef.current = useKoreanPriceUnit;
  }, [useKoreanPriceUnit]);

  // 안전마진(개인별) — 여유매수가/여유매도가 재계산 비율. localStorage 유지, 조회 파라미터로 전달.
  //  buy=0.02(2%), sell=0.20(20%) 기본. ref로 최신값 보장(load 가 일반 함수라서).
  const [safeMargins, setSafeMargins] = useState(() => {
    try {
      const raw = localStorage.getItem('ranker_safe_margins_v1');
      const parsed = raw ? JSON.parse(raw) : null;
      const buy = Number(parsed?.buy);
      const sell = Number(parsed?.sell);
      return {
        buy: Number.isFinite(buy) ? buy : 0.02,
        sell: Number.isFinite(sell) ? sell : 0.20,
      };
    } catch { return { buy: 0.02, sell: 0.20 }; }
  });
  const safeMarginsRef = useRef(safeMargins);
  useEffect(() => {
    safeMarginsRef.current = safeMargins;
    try { localStorage.setItem('ranker_safe_margins_v1', JSON.stringify(safeMargins)); } catch { /* ignore */ }
  }, [safeMargins]);
  // 마진 설정 팝오버 { open, type:'buy'|'sell', x, y }
  const [marginPopover, setMarginPopover] = useState({ open: false, type: null, x: 0, y: 0 });
  const marginInputRef = useRef(null);
  const openMarginPopover = useCallback((type, e) => {
    const x = e?.clientX ?? 0;
    const y = e?.clientY ?? 0;
    setMarginPopover({ open: true, type, x, y });
  }, []);
  // 세금(취득세) 개인화 설정 — 주택수/생애최초. 백엔드 재계산 연결은 추후.
  const [taxSettings, setTaxSettings] = useState(() => {
    try { return { homeCount: 1, firstTime: false, ...JSON.parse(localStorage.getItem('ranker_tax_settings_v1') || '{}') }; }
    catch { return { homeCount: 1, firstTime: false }; }
  });
  const taxSettingsRef = useRef(taxSettings);
  useEffect(() => {
    taxSettingsRef.current = taxSettings;
    try { localStorage.setItem('ranker_tax_settings_v1', JSON.stringify(taxSettings)); } catch { /* ignore */ }
  }, [taxSettings]);
  const [taxPopover, setTaxPopover] = useState({ open: false, x: 0, y: 0 });
  const openTaxPopover = useCallback((e) => {
    setTaxPopover({ open: true, x: e?.clientX ?? 0, y: e?.clientY ?? 0 });
  }, []);
  const [filterMode, setFilterMode] = useState('search'); // 'filter' or 'search'

  // appliedFilters 변경 시 데이터 다시 로드
  const prevAppliedFiltersRef = useRef(appliedFilters);
  const prevSigunguGradesRef = useRef(selectedSigunguGrades);
  const prevDongGradesRef = useRef(selectedDongGrades);
  const prevGubunRef = useRef(selectedGubuns);
  const prevHouseStructureRef = useRef(selectedHouseStructure);
  const prevSortPrefsRef = useRef([{ key: 'region', direction: 'asc' }]); // 초기값과 동일하게 설정
  const screenSettingChangeInProgressRef = useRef(false); // 화면설정 변경 중 플래그
  useEffect(() => {
    // Bootstrap이 완료되기 전에는 실행하지 않음 (bootstrap에서 초기 로드 처리)
    if (!treeInitializedRef.current) return;

    const prev = prevAppliedFiltersRef.current;
    const prevSigunguGrades = prevSigunguGradesRef.current;
    const prevDongGrades = prevDongGradesRef.current;
    const prevGubun = prevGubunRef.current;
    const prevHouseStructure = prevHouseStructureRef.current;
    const prevSortPrefs = prevSortPrefsRef.current;

    const hasFilterChanged =
      prev.tradePriceMin !== appliedFilters.tradePriceMin ||
      prev.tradePriceMax !== appliedFilters.tradePriceMax ||
      prev.investPriceMin !== appliedFilters.investPriceMin ||
      prev.investPriceMax !== appliedFilters.investPriceMax ||
      prev.investRealInvestPriceMin !== appliedFilters.investRealInvestPriceMin ||
      prev.investRealInvestPriceMax !== appliedFilters.investRealInvestPriceMax ||
      prev.complexMinTradePriceMin !== appliedFilters.complexMinTradePriceMin ||
      prev.complexMinTradePriceMax !== appliedFilters.complexMinTradePriceMax ||
      prev.complexMaxTradePriceMin !== appliedFilters.complexMaxTradePriceMin ||
      prev.complexMaxTradePriceMax !== appliedFilters.complexMaxTradePriceMax ||
      prev.leasePriceMin !== appliedFilters.leasePriceMin ||
      prev.leasePriceMax !== appliedFilters.leasePriceMax ||
      prev.complexMinLeasePriceMin !== appliedFilters.complexMinLeasePriceMin ||
      prev.complexMinLeasePriceMax !== appliedFilters.complexMinLeasePriceMax ||
      prev.complexMaxLeasePriceMin !== appliedFilters.complexMaxLeasePriceMin ||
      prev.complexMaxLeasePriceMax !== appliedFilters.complexMaxLeasePriceMax ||
      prev.avgSaleRealPriceMin !== appliedFilters.avgSaleRealPriceMin ||
      prev.avgSaleRealPriceMax !== appliedFilters.avgSaleRealPriceMax ||
      prev.avgLeaseRealPriceMin !== appliedFilters.avgLeaseRealPriceMin ||
      prev.avgLeaseRealPriceMax !== appliedFilters.avgLeaseRealPriceMax ||
      prev.tradeArticleCountMin !== appliedFilters.tradeArticleCountMin ||
      prev.tradeArticleCountMax !== appliedFilters.tradeArticleCountMax ||
      prev.leaseArticleCountMin !== appliedFilters.leaseArticleCountMin ||
      prev.leaseArticleCountMax !== appliedFilters.leaseArticleCountMax ||
      prev.leasePriceRateMin !== appliedFilters.leasePriceRateMin ||
      prev.leasePriceRateMax !== appliedFilters.leasePriceRateMax ||
      prev.riseRateMin !== appliedFilters.riseRateMin ||
      prev.riseRateMax !== appliedFilters.riseRateMax ||
      prev.risePriceMin !== appliedFilters.risePriceMin ||
      prev.risePriceMax !== appliedFilters.risePriceMax ||
      prev.supply3YearsMin !== appliedFilters.supply3YearsMin ||
      prev.supply3YearsMax !== appliedFilters.supply3YearsMax ||
      prev.useApproveAgeMin !== appliedFilters.useApproveAgeMin ||
      prev.useApproveAgeMax !== appliedFilters.useApproveAgeMax ||
      prev.areaMin !== appliedFilters.areaMin ||
      prev.areaMax !== appliedFilters.areaMax ||
      prev.householdMin !== appliedFilters.householdMin ||
      prev.householdMax !== appliedFilters.householdMax ||
      prev.goHyunRateMin !== appliedFilters.goHyunRateMin ||
      prev.goHyunRateMax !== appliedFilters.goHyunRateMax ||
      prev.jeokHyunRateMin !== appliedFilters.jeokHyunRateMin ||
      prev.jeokHyunRateMax !== appliedFilters.jeokHyunRateMax ||
      prev.goJeoRateMin !== appliedFilters.goJeoRateMin ||
      prev.goJeoRateMax !== appliedFilters.goJeoRateMax ||
      prev.jeoHyunRateMin !== appliedFilters.jeoHyunRateMin ||
      prev.jeoHyunRateMax !== appliedFilters.jeoHyunRateMax ||
      prev.changeGapMin !== appliedFilters.changeGapMin ||
      prev.changeGapMax !== appliedFilters.changeGapMax ||
      prev.pastTopPriceMin !== appliedFilters.pastTopPriceMin ||
      prev.pastTopPriceMax !== appliedFilters.pastTopPriceMax ||
      prev.prevHighPriceMin !== appliedFilters.prevHighPriceMin ||
      prev.prevHighPriceMax !== appliedFilters.prevHighPriceMax ||
      prev.properHighPriceMin !== appliedFilters.properHighPriceMin ||
      prev.properHighPriceMax !== appliedFilters.properHighPriceMax ||
      prev.prevLowPriceMin !== appliedFilters.prevLowPriceMin ||
      prev.prevLowPriceMax !== appliedFilters.prevLowPriceMax ||
      prev.targetPriceMin !== appliedFilters.targetPriceMin ||
      prev.targetPriceMax !== appliedFilters.targetPriceMax;

    const hasGradeFilterChanged =
      JSON.stringify(prevSigunguGrades) !== JSON.stringify(selectedSigunguGrades) ||
      JSON.stringify(prevDongGrades) !== JSON.stringify(selectedDongGrades) ||
      JSON.stringify(prevGubun) !== JSON.stringify(selectedGubuns) ||
      prevHouseStructure !== selectedHouseStructure;

    const hasSortChanged = JSON.stringify(prevSortPrefs) !== JSON.stringify(sortPrefs);

    // 화면설정 변경 중에는 정렬 변경을 무시 (API에서 모든 데이터를 이미 가져오므로)
    const shouldReload = hasFilterChanged || hasGradeFilterChanged || (hasSortChanged && !screenSettingChangeInProgressRef.current);

    if (shouldReload) {
      prevAppliedFiltersRef.current = appliedFilters;
      prevSigunguGradesRef.current = selectedSigunguGrades;
      prevDongGradesRef.current = selectedDongGrades;
      prevGubunRef.current = selectedGubuns;
      prevHouseStructureRef.current = selectedHouseStructure;
      prevSortPrefsRef.current = sortPrefs;
      load(selectedBjdCodes, null, true);
    } else if (hasSortChanged && screenSettingChangeInProgressRef.current) {
      // 화면설정 변경으로 인한 정렬 변경은 ref만 업데이트
      prevSortPrefsRef.current = sortPrefs;
    }
  }, [appliedFilters, selectedSigunguGrades, selectedDongGrades, selectedGubuns, selectedHouseStructure, sortPrefs]);
  const [headerFilterEnabled, setHeaderFilterEnabled] = useState(false);
  const [regionSidebarCollapsed, setRegionSidebarCollapsed] = useState(false);
  const [regionSearchInput, setRegionSearchInput] = useState('');
  const [regionSearchKeyword, setRegionSearchKeyword] = useState('');
  const [regionDanjiCount, setRegionDanjiCount] = useState(0);
  const [showRegulationFilters, setShowRegulationFilters] = useState(true);
  const [showPopulationFilters, setShowPopulationFilters] = useState(false);
  const [selectedRegulationTypes, setSelectedRegulationTypes] = useState([]);
  const [regulationHelpOpen, setRegulationHelpOpen] = useState(false);
  const [regulationHelpPos, setRegulationHelpPos] = useState({ bottom: 0, left: 0 }); // 포탈 팝오버 위치(아이콘 위쪽)
  const regulationHelpIconRef = useRef(null);
  const regulationHelpCloseTimerRef = useRef(null);

  // 검색 모드 상태
  const [searchSido, setSearchSido] = useState('');
  const [searchSigungu, setSearchSigungu] = useState('');
  const [searchDong, setSearchDong] = useState('');
  const [searchComplexName, setSearchComplexName] = useState('');
  const [appliedSearchComplexName, setAppliedSearchComplexName] = useState('');
  const [searchSigunguGradeFrom, setSearchSigunguGradeFrom] = useState('');
  const [searchSigunguGradeTo, setSearchSigunguGradeTo] = useState('');
  const [searchDongGradeFrom, setSearchDongGradeFrom] = useState('');
  const [searchDongGradeTo, setSearchDongGradeTo] = useState('');

  const [favoriteDanjiCodes, setFavoriteDanjiCodes] = useState(() => new Set());
  const [favoritePendingCodes, setFavoritePendingCodes] = useState(() => new Set());
  const [favoriteGroups, setFavoriteGroups] = useState([]);
  const [favoriteGroupsLoading, setFavoriteGroupsLoading] = useState(false);
  const [groupAverageOn, setGroupAverageOn] = useState(false); // 즐겨찾기 그룹평균 표시 토글
  const groupAvgFirstRef = useRef(true);
  const [selectedFavoriteGroupId, setSelectedFavoriteGroupId] = useState(0);
  const [favoriteGroupComplexes, setFavoriteGroupComplexes] = useState([]);
  const [favoriteGroupComplexesLoading, setFavoriteGroupComplexesLoading] = useState(false);
  const [selectedFavoriteDanjiCodes, setSelectedFavoriteDanjiCodes] = useState([]);
  const [favoriteGroupPickerOpen, setFavoriteGroupPickerOpen] = useState(false);
  const [favoriteTargetGroupId, setFavoriteTargetGroupId] = useState(0);
  const [favoriteGroupApplyLoading, setFavoriteGroupApplyLoading] = useState(false);
  const favoritePickerInitialTargetGroupIdRef = useRef(0);
  const [favoritePickerTargets, setFavoritePickerTargets] = useState([]);
  const [screenSettingItems, setScreenSettingItems] = useState([]);
  const [selectedScreenSettingId, setSelectedScreenSettingId] = useState('');
  const [screenSettingBusy, setScreenSettingBusy] = useState(false);
  const [pendingScreenSettingOrder, setPendingScreenSettingOrder] = useState(null);
  const [needDraftRefresh, setNeedDraftRefresh] = useState(false);
  const [columnSettingDraft, setColumnSettingDraft] = useState(null);
  const [initialColumnSettingDraft, setInitialColumnSettingDraft] = useState(null);
  const [selectedSortPreset, setSelectedSortPreset] = useState('');
  const [sortPresetExpanded, setSortPresetExpanded] = useState(false);
  const [saleRecentExpanded, setSaleRecentExpanded] = useState(true);
  const [leaseRecentExpanded, setLeaseRecentExpanded] = useState(true);
  const [saleRecent6Expanded, setSaleRecent6Expanded] = useState(true);
  const [leaseRecent6Expanded, setLeaseRecent6Expanded] = useState(true);
  // 전세가율 최근6개월 — lease6/sale6 월별로 파생, 항상 펼침(다른 6개월 토글과 동일)
  const [leaseRateRecent6Expanded] = useState(true);
  // 변동 전세가율 최근5개월 — 월별 전세가율 diff, 항상 펼침
  const [leaseRateChangeRecent5Expanded] = useState(true);
  const [saleTxRecentExpanded, setSaleTxRecentExpanded] = useState(true);
  const [leaseTxRecentExpanded, setLeaseTxRecentExpanded] = useState(true);
  const [saleTxRecent6Expanded, setSaleTxRecent6Expanded] = useState(true);
  const [leaseTxRecent6Expanded, setLeaseTxRecent6Expanded] = useState(true);
  const [saleCountRecentExpanded, setSaleCountRecentExpanded] = useState(true);
  const [leaseCountRecentExpanded, setLeaseCountRecentExpanded] = useState(true);
  const [saleCountRecent6Expanded, setSaleCountRecent6Expanded] = useState(true);
  const [leaseCountRecent6Expanded, setLeaseCountRecent6Expanded] = useState(true);
  const [saleTxCountRecentExpanded, setSaleTxCountRecentExpanded] = useState(true);
  const [leaseTxCountRecentExpanded, setLeaseTxCountRecentExpanded] = useState(true);
  const [saleTxCountRecent6Expanded, setSaleTxCountRecent6Expanded] = useState(true);
  const [leaseTxCountRecent6Expanded, setLeaseTxCountRecent6Expanded] = useState(true);
  const [articlePanelOpen, setArticlePanelOpen] = useState(false);
  const [articlePanelTradeType, setArticlePanelTradeType] = useState('SALE');
  const [articlePanelFixId, setArticlePanelFixId] = useState(null);
  const [articlePanelComplexName, setArticlePanelComplexName] = useState('');
  const [articlePanelLoading, setArticlePanelLoading] = useState(false);
  const [articlePanelError, setArticlePanelError] = useState('');
  const [articlePanelItems, setArticlePanelItems] = useState({ SALE: [], LEASE: [] });
  const TableComponent = ComplexTanStackTable;

  const openComparePopupWindow = (targetsForPopup = compareTargets) => {
    const safeTargets = Array.isArray(targetsForPopup) ? targetsForPopup : [];
    if (safeTargets.length < 2) {
      toast.info('비교하려면 2개 단지를 선택해 주세요.');
      return;
    }

    const key = `ranking-compare-popup:${Date.now()}:${Math.random().toString(36).slice(2)}`;
    const payload = {
      targets: safeTargets,
      createdAt: Date.now(),
    };

    try {
      sessionStorage.setItem(key, JSON.stringify(payload));
    } catch (error) {
      console.error('비교 팝업 데이터 저장 실패:', error);
      toast.error('비교 창을 열지 못했습니다.');
      return;
    }

    const popupUrl = `/compare?tab=complex&key=${encodeURIComponent(key)}`;
    window.open(popupUrl, '_blank', 'width=1680,height=980,noopener,noreferrer');
  };

  const userColumnStorageKey = useMemo(() => {
    const userKey = String(user?.userId || user?.id || 'guest').trim() || 'guest';
    return `complex-page-column-prefs:v1:${userKey}`;
  }, [user?.userId, user?.id]);
  const userSortStorageKey = useMemo(() => {
    const userKey = String(user?.userId || user?.id || 'guest').trim() || 'guest';
    return `complex-page-sort-prefs:v1:${userKey}`;
  }, [user?.userId, user?.id]);

  const normalizeGroupItemsById = (rawGroupItems) => {
    const defaultOrder = DISPLAY_COLUMN_GROUPS.filter((group) => !group.hidden).map((group) => group.id);
    const normalizedGroupItems = { ...defaultGroupItemsById };
    const source = rawGroupItems && typeof rawGroupItems === 'object' ? rawGroupItems : {};

    defaultOrder.forEach((groupId) => {
      const rawItems = Array.isArray(source[groupId]) ? source[groupId] : null;
      if (!rawItems) return;
      const nextItems = rawItems
        .map((item, index) => {
          if (!item || typeof item !== 'object') return null;
          const defaultColumnKey = defaultGroupItemsById[groupId]?.[index]?.columnKey || null;
          const columnKey = item.columnKey ? String(item.columnKey) : defaultColumnKey;
          // 병합 반영해 라벨 강제(저장된 설정도 갱신)
          let label = String(item.label || '');
          if (columnKey === 'region') label = '지역';
          else if (columnKey === 'familyCnt') label = '세대수(평세대수)';
          else if (columnKey === 'sigunguGrade') label = '지역급지(생활권)';
          else if (columnKey === 'planTypeName') label = '평형(구조)';
          else if (columnKey === 'useApproveDate') label = '연식(연차)';
          else if (columnKey === 'investPrice') label = '투자금(실투자금)';
          else if (columnKey === 'properHighPrice') label = '적고가★';
          else if (columnKey === 'jeokHyunRate') label = '적현율★';
          else if (columnKey === 'tradeArticleCount') label = '매매수';
          else if (columnKey === 'leaseArticleCount') label = '전세수';
          return {
            id: String(item.id || `${groupId}__item__${index}`),
            label,
            columnKey,
          };
        })
        .filter(Boolean)
        // 제거된 컬럼(규제·인구)은 저장된 설정에 남아있어도 걸러낸다
        .filter((it) => !REMOVED_COLUMN_KEYS.has(String(it.columnKey || ''))
          && !REMOVED_COLUMN_LABELS.has(String(it.label || '').trim()))
        // 단지명은 전용 잠금 그룹(buildingNameGroup)으로 분리됨 — 다른 그룹에 남아있으면 제거(중복 방지)
        .filter((it) => groupId === 'buildingNameGroup' || String(it.columnKey || '') !== 'buildingName')
        // 생활권(livingZone)은 지역 컬럼에 병합됨 — 저장된 설정에 별도 항목이 남아있으면 제거
        .filter((it) => String(it.columnKey || '') !== 'livingZone' && String(it.label || '').trim() !== '생활권')
        // 세대수(평)(ptpFamilyCnt)은 세대수 컬럼에 병합됨 — 저장된 설정에 별도 항목이 남아있으면 제거
        .filter((it) => String(it.columnKey || '') !== 'ptpFamilyCnt' && String(it.label || '').replace(/\s/g, '') !== '세대수(평)');
      if (nextItems.length > 0) normalizedGroupItems[groupId] = nextItems;
    });

    return normalizedGroupItems;
  };

  const parseScreenSettingPayload = (payloadText) => {
    if (payloadText == null) return null;
    if (typeof payloadText === 'object') return payloadText;
    try {
      return JSON.parse(String(payloadText || '{}'));
    } catch {
      return null;
    }
  };

  const mergeScreenSettingState = (baseState, payload) => {
    const normalizeVisibleMap = (visibleMap) => {
      const nextVisible = visibleMap && typeof visibleMap === 'object' ? { ...visibleMap } : {};
      // 단지명은 필수 표시 컬럼
      if (nextVisible.buildingName === false) {
        delete nextVisible.buildingName;
      }
      return nextVisible;
    };

    const fallback = baseState && typeof baseState === 'object' ? baseState : {
      columnPrefs: { order: [], visible: {} },
      columnGroupPrefs: {},
      columnGroupOrder: [],
      columnGroupItemsById: defaultGroupItemsById,
      sortPrefs: [],
      useKoreanPriceUnit: false,
    };
    const next = {
      columnPrefs: {
        order: Array.isArray(fallback?.columnPrefs?.order) ? [...fallback.columnPrefs.order] : [],
        visible: normalizeVisibleMap(fallback?.columnPrefs?.visible),
      },
      columnGroupPrefs: fallback?.columnGroupPrefs && typeof fallback.columnGroupPrefs === 'object'
        ? { ...fallback.columnGroupPrefs }
        : {},
      columnGroupOrder: Array.isArray(fallback?.columnGroupOrder) ? [...fallback.columnGroupOrder] : [],
      columnGroupItemsById: normalizeGroupItemsById(fallback?.columnGroupItemsById),
      sortPrefs: Array.isArray(fallback?.sortPrefs) ? [...fallback.sortPrefs] : [],
      useKoreanPriceUnit: !!fallback?.useKoreanPriceUnit,
    };

    if (!payload || typeof payload !== 'object') return next;

    if (payload.columnPrefs && typeof payload.columnPrefs === 'object') {
      next.columnPrefs = {
        order: Array.isArray(payload.columnPrefs.order)
          ? payload.columnPrefs.order.map((key) => String(key || '')).filter(Boolean)
          : [],
        visible: normalizeVisibleMap(payload.columnPrefs.visible),
      };
    }
    if (payload.columnGroupPrefs && typeof payload.columnGroupPrefs === 'object') {
      next.columnGroupPrefs = payload.columnGroupPrefs;
    }
    if (Array.isArray(payload.columnGroupOrder)) {
      next.columnGroupOrder = payload.columnGroupOrder.map((id) => String(id || '')).filter(Boolean);
    }
    if (payload.columnGroupItemsById && typeof payload.columnGroupItemsById === 'object') {
      next.columnGroupItemsById = normalizeGroupItemsById(payload.columnGroupItemsById);
    }
    // sortPrefs는 화면설정과 별도로 관리 (로컬스토리지에서 독립적으로 관리)
    // if (Array.isArray(payload.sortPrefs)) {
    //   next.sortPrefs = payload.sortPrefs.map((row) => ({
    //     key: String(row?.key || ''),
    //     direction: row?.direction === 'desc' ? 'desc' : 'asc',
    //   })).filter((row) => row.key);
    // }
    if (typeof payload.useKoreanPriceUnit === 'boolean') {
      next.useKoreanPriceUnit = payload.useKoreanPriceUnit;
    }

    return next;
  };

  const applyScreenSettingPayload = (payload) => {
    const nextState = mergeScreenSettingState({
      columnPrefs,
      columnGroupPrefs,
      columnGroupOrder,
      columnGroupItemsById,
      sortPrefs,
      useKoreanPriceUnit,
    }, payload);

    // 화면설정 변경 시작 플래그 설정
    screenSettingChangeInProgressRef.current = true;

    setColumnPrefs(nextState.columnPrefs);
    setColumnGroupPrefs(nextState.columnGroupPrefs);
    setColumnGroupOrder(nextState.columnGroupOrder);
    setColumnGroupItemsById(nextState.columnGroupItemsById);
    // 정렬 설정은 화면설정과 별도로 관리 (로컬스토리지에서 독립적으로 관리됨)
    // setSortPrefs(nextState.sortPrefs);
    setUseKoreanPriceUnit(!!nextState.useKoreanPriceUnit);

    // 다음 이벤트 루프에서 플래그 해제
    setTimeout(() => {
      screenSettingChangeInProgressRef.current = false;
    }, 0);
  };

  const buildScreenSettingPayload = () => ({
    version: 1,
    columnPrefs,
    columnGroupPrefs,
    columnGroupOrder,
    columnGroupItemsById,
    // sortPrefs는 화면설정과 별도로 관리 (로컬스토리지에서 독립적으로 관리)
    useKoreanPriceUnit,
  });

  const buildScreenSettingPayloadFromState = (state) => ({
    version: 1,
    columnPrefs: state?.columnPrefs || { order: [], visible: {} },
    columnGroupPrefs: state?.columnGroupPrefs || {},
    columnGroupOrder: Array.isArray(state?.columnGroupOrder) ? state.columnGroupOrder : [],
    columnGroupItemsById: state?.columnGroupItemsById || defaultGroupItemsById,
    // sortPrefs는 화면설정과 별도로 관리
    useKoreanPriceUnit: !!state?.useKoreanPriceUnit,
  });

  const selectedBjdCodesKey = useMemo(() => [...selectedBjdCodes].sort().join(','), [selectedBjdCodes]);
  const selectedBjdCodeSet = useMemo(() => new Set(selectedBjdCodes), [selectedBjdCodes]);
  const isFavoritePage = pageMode === 'favorite';
  const favoriteGroupCodeList = useMemo(
    () => favoriteGroupComplexes
      .map((item) => normalizeDanjiCode(item?.naverDanjiCode || item?.NaverDanjiCode))
      .filter(Boolean),
    [favoriteGroupComplexes]
  );
  const favoriteGroupCodeSet = useMemo(
    () => new Set(favoriteGroupCodeList),
    [favoriteGroupCodeList]
  );
  const selectedFavoriteDanjiCodeSet = useMemo(
    () => new Set(selectedFavoriteDanjiCodes),
    [selectedFavoriteDanjiCodes]
  );
  const hasFavoriteGroupSelection = selectedFavoriteDanjiCodes.length > 0
    && selectedFavoriteDanjiCodes.length < favoriteGroupCodeList.length;
  const selectedLandmarkFiltersKey = useMemo(
    () => [...selectedLandmarkFilters].sort().join(','),
    [selectedLandmarkFilters]
  );
  const selectedGroupByKey = useMemo(
    () => [...selectedGroupBy].sort().join(','),
    [selectedGroupBy]
  );
  const selectedGroupByLevels = useMemo(
    () => getGroupByLevelsFromSelection(selectedGroupBy),
    [selectedGroupBy]
  );
  const pageNumber = 1;
  const totalPages = 1;
  const setPageNumber = () => {};

  const householdMaxLimit = useMemo(() => {
    const maxFromItems = items.reduce((max, item) => Math.max(max, Number(item?.familyCnt || 0)), 0);
    return Math.max(HOUSEHOLD_MAX_FALLBACK, Math.ceil(maxFromItems / 100) * 100);
  }, [items]);

  const householdMinValue = householdMinInput === '' ? null : Number(householdMinInput);
  const householdMaxValue = householdMaxInput === '' ? null : Number(householdMaxInput);
  const hasManualHouseholdFilter = householdMinValue != null || householdMaxValue != null;
  const effectiveHouseholdMin = Math.max(HOUSEHOLD_MIN_LIMIT, Math.min(householdMinValue ?? HOUSEHOLD_MIN_LIMIT, householdMaxLimit));
  const effectiveHouseholdMax = Math.max(effectiveHouseholdMin, Math.min(householdMaxValue ?? householdMaxLimit, householdMaxLimit));
  const areaMaxLimitM2 = useMemo(() => {
    const maxFromItems = items.reduce((max, item) => {
      const range = getItemAreaRangeM2(item);
      return Math.max(max, Number(range?.max || 0));
    }, 0);
    return Math.max(AREA_MAX_FALLBACK_M2, Math.ceil(maxFromItems));
  }, [items]);

  const areaMinValue = areaMinInput === '' ? null : Number(areaMinInput);
  const areaMaxValue = areaMaxInput === '' ? null : Number(areaMaxInput);
  const manualAreaMinM2 = areaMinValue == null ? null : toM2FromUnit(areaMinValue, areaUnit);
  const manualAreaMaxM2 = areaMaxValue == null ? null : toM2FromUnit(areaMaxValue, areaUnit);
  const hasManualAreaFilter = manualAreaMinM2 != null || manualAreaMaxM2 != null;
  const effectiveAreaMinM2 = Math.max(0, Math.min(manualAreaMinM2 ?? 0, areaMaxLimitM2));
  const effectiveAreaMaxM2 = Math.max(effectiveAreaMinM2, Math.min(manualAreaMaxM2 ?? areaMaxLimitM2, areaMaxLimitM2));
  const landmarkFilterLabelMap = useMemo(() => {
    const map = {};
    (landmarkFilterOptions || []).forEach((option) => {
      const key = String(option?.key || '').trim();
      const label = String(option?.label || '').trim();
      if (!key || !label) return;
      map[key] = label;
    });
    return Object.keys(map).length > 0 ? map : LANDMARK_FILTER_LABEL_MAP;
  }, [landmarkFilterOptions]);
  const allLandmarkSelected = selectedLandmarkFilters.length === landmarkFilterOptions.length;
  const gubunAverageOptionRows = useMemo(() => {
    const rows = [];
    const source = Array.isArray(gubunAverageOptions) ? gubunAverageOptions : [];
    for (let i = 0; i < source.length; i += 4) {
      rows.push(source.slice(i, i + 4));
    }
    return rows;
  }, [gubunAverageOptions]);
  const landmarkSummary = selectedLandmarkFilters.length > 0
    ? landmarkFilterOptions
      .filter((opt) => selectedLandmarkFilters.includes(opt.key))
      .map((opt) => opt.label)
      .join(', ')
    : '전체';
  const groupBySummary = selectedGroupBy.length > 0 ? selectedGroupBy.map((k) => GROUP_BY_LABELS[k] || k).join(', ') : '';
  const hasBuiltDateFilter = Boolean(builtDateFrom || builtDateTo);
  const hasHouseholdFilter = hasManualHouseholdFilter || selectedHouseholds.length > 0;
  const hasAreaFilter = hasManualAreaFilter || selectedAreaBuckets.length > 0;
  const currentYear = new Date().getFullYear();
  const builtYearMin = 1960;
  const builtYearMax = currentYear;
  const builtYearFrom = builtDateFrom === '' ? builtYearMin : Math.max(builtYearMin, Math.min(builtYearMax, Number(builtDateFrom) || builtYearMin));
  const builtYearTo = builtDateTo === '' ? builtYearMax : Math.max(builtYearMin, Math.min(builtYearMax, Number(builtDateTo) || builtYearMax));
  const builtYearSafeFrom = Math.min(builtYearFrom, builtYearTo);
  const builtYearSafeTo = Math.max(builtYearFrom, builtYearTo);
  const builtDateSummaryLabel = (builtDateFrom || builtDateTo)
    ? `${builtYearSafeFrom} ~ ${builtYearSafeTo}`
    : '전체';
  const areaSliderMinM2 = Math.max(0, Math.min(areaMaxLimitM2, Number(manualAreaMinM2 ?? 0)));
  const areaSliderMaxM2 = Math.max(areaSliderMinM2, Math.min(areaMaxLimitM2, Number(manualAreaMaxM2 ?? areaMaxLimitM2)));
  const tradePriceMinValue = tradePriceMinInput === '' ? null : Number(tradePriceMinInput);
  const tradePriceMaxValue = tradePriceMaxInput === '' ? null : Number(tradePriceMaxInput);
  const hasTradePriceFilter = tradePriceMinValue != null || tradePriceMaxValue != null;
  const investPriceMinValue = investPriceMinInput === '' ? null : Number(investPriceMinInput);
  const investPriceMaxValue = investPriceMaxInput === '' ? null : Number(investPriceMaxInput);
  const hasInvestPriceFilter = investPriceMinValue != null || investPriceMaxValue != null;
  const hasRankerFilter = selectedLandmarkFilters.length > 0;
  const effectiveFavoriteOnly = isFavoritePage || favoriteOnly;
  const highRankerOnly = selectedLandmarkFilters.includes('HIGH_RANKER');
  const gubunFilters = selectedLandmarkFilters.filter((key) => Boolean(landmarkFilterLabelMap[key]));
  const hasGroupByFilter = selectedGroupBy.length > 0;
  const hasGubunAverageFilter = selectedGubunAverageCodes.length > 0;
  const regulationOnly = selectedRegulationTypes.includes('REGULATION');
  const speculationZoneOnly = selectedRegulationTypes.includes('SPECULATION');
  const landTradePermitOnly = selectedRegulationTypes.includes('LAND_TRADE_PERMIT');
  const hasRegulationTypeFilter = selectedRegulationTypes.length > 0;

  // 상단 퀵필터(매매가/투자금/연차/평형/세대수) 버튼 밑에 표기할 현재 설정값 요약
  const quickFilterChipMap = useMemo(() => {
    // 슬라이더/프리셋 상한값 — 이 값 이상이면 '상한 없음(최대치)'으로 보고 "min~" 요약 표기
    const MONEY_MAX = 200000;   // MoneyRangeFilter maxLimit (만원, = 20억)
    const HOUSEHOLD_MAX = 2000; // HouseholdRangeFilter 슬라이더 상한
    const has = (v) => v !== '' && v !== null && v !== undefined;
    const hasPos = (v) => has(v) && Number(v) > 0;
    const moneyText = (min, max) => {
      const maxBounded = has(max) && Number(max) < MONEY_MAX;
      if (hasPos(min) && maxBounded) return `${formatKrw(Number(min), true)} ~ ${formatKrw(Number(max), true)}`;
      if (hasPos(min)) return `${formatKrw(Number(min), true)}~`; // 상한이 최대치 → 요약
      if (maxBounded) return `~${formatKrw(Number(max), true)}`;
      return '';
    };
    const householdText = (min, max) => {
      const maxBounded = has(max) && Number(max) < HOUSEHOLD_MAX;
      if (hasPos(min) && maxBounded) return `${min} ~ ${max}세대`;
      if (hasPos(min)) return `${min}~`; // 상한이 최대치 → 요약
      if (maxBounded) return `~${max}세대`;
      return '';
    };
    const areaText = (min, max, unit) => {
      if (has(min) && has(max)) return `${min} ~ ${max}${unit}`;
      if (has(min)) return `${min}${unit}~`;
      if (has(max)) return `~${max}${unit}`;
      return '';
    };
    const map = {};
    if (hasTradePriceFilter) map.tradePrice = moneyText(tradePriceMinInput, tradePriceMaxInput);
    if (hasInvestPriceFilter) map.investPrice = moneyText(investPriceMinInput, investPriceMaxInput);
    if (hasBuiltDateFilter) {
      map.builtDate = has(builtDateFrom) && has(builtDateTo)
        ? `${builtDateFrom} ~ ${builtDateTo}`
        : has(builtDateFrom) ? `${builtDateFrom}~` : `~${builtDateTo}`;
    }
    if (hasHouseholdFilter) {
      map.household = hasManualHouseholdFilter
        ? householdText(householdMinInput, householdMaxInput)
        : selectedHouseholds.map((k) => HOUSEHOLD_BUCKET_LABEL[k] || k).join(', ');
    }
    if (hasAreaFilter) {
      // 평형 필터 칩은 화면 단위(평/㎡)와 무관하게 무조건 ㎡ 로 표기 (appliedFilters.area* 는 ㎡ 기준)
      const aMin = appliedFilters?.areaMin;
      const aMax = appliedFilters?.areaMax;
      const hasRange = has(aMin) || has(aMax);
      map.area = hasRange
        ? areaText(has(aMin) ? Math.round(Number(aMin)) : '', has(aMax) ? Math.round(Number(aMax)) : '', '㎡')
        : selectedAreaBuckets.map((k) => AREA_BUCKET_LABEL[k] || k).join(', ');
    }
    return map;
  }, [
    hasTradePriceFilter, tradePriceMinInput, tradePriceMaxInput,
    hasInvestPriceFilter, investPriceMinInput, investPriceMaxInput,
    hasBuiltDateFilter, builtDateFrom, builtDateTo,
    hasHouseholdFilter, hasManualHouseholdFilter, householdMinInput, householdMaxInput, selectedHouseholds,
    hasAreaFilter, selectedAreaBuckets, appliedFilters?.areaMin, appliedFilters?.areaMax,
  ]);

  const extraActiveHeaderFilterColumnIds = useMemo(() => {
    const next = [];
    if (hasBuiltDateFilter) next.push('useApproveDate');
    if (hasHouseholdFilter) next.push('familyCnt');
    return next;
  }, [hasBuiltDateFilter, hasHouseholdFilter]);

  const getSigunguCodes = (sigungu) => (sigungu?.bjdList || [])
    .map((dong) => String(dong?.bjdCode || ''))
    .filter(Boolean);

  const isRegulationArea = (sigungu) => String(sigungu?.regulation || '').trim() === '\uC870\uC815';
  const isSpeculationZone = (sigungu) => String(sigungu?.speculationZone || '').trim() === '\uD22C\uAE30\uACFC\uC5F4';
  const isLandTradePermitZone = (sigungu) => {
    const candidates = [
      sigungu?.landTradePermit,
      sigungu?.landTradePermission,
      sigungu?.landTransactionPermit,
      sigungu?.tojiTradePermit,
      sigungu?.tojiPermit,
      sigungu?.landPermit,
    ];
    return candidates.some((value) => {
      const text = String(value || '').trim();
      return text === '토지거래허가' || text === 'Y' || text === 'true' || text === '1';
    });
  };

  const getSigunguBadgeTexts = (sigungu) => {
    const badges = [];
    if (isRegulationArea(sigungu)) badges.push('\uC870\uC815');
    if (isSpeculationZone(sigungu)) badges.push('\uD22C\uAE30\uACFC\uC5F4');
    if (isLandTradePermitZone(sigungu)) badges.push('토지거래허가');
    return badges;
  };
  const getSigunguPopulationText = (sigungu) => {
    const text = formatPopulationKoreanUnit(sigungu?.population);
    return text === '-' ? null : text;
  };
  const getDongPopulationText = (dong) => {
    const text = formatPopulationKoreanUnit(dong?.population);
    return text === '-' ? null : text;
  };
  const getSidoPopulationText = (sido) => {
    const totalPopulation = (sido?.sigunguList || []).reduce((sum, sigungu) => {
      return sum + (Number(sigungu?.population) || 0);
    }, 0);
    const text = formatPopulationKoreanUnit(totalPopulation);
    return text === '-' ? null : text;
  };
  const getGroupPopulationText = (group) => {
    const totalPopulation = (group?.sidoList || []).reduce((sum, sido) => {
      return sum + (sido?.sigunguList || []).reduce((sigunguSum, sigungu) => {
        return sigunguSum + (Number(sigungu?.population) || 0);
      }, 0);
    }, 0);
    const text = formatPopulationKoreanUnit(totalPopulation);
    return text === '-' ? null : text;
  };

  const getSidoCodes = (sido) => mergeSigunguList(sido?.sigunguList || [])
    .flatMap((sigungu) => getSigunguCodes(sigungu));

  const getGroupCodes = (group) => (group?.sidoList || [])
    .flatMap((sido) => getSidoCodes(sido));

  const toggleCodes = (codes) => {
    if (!Array.isArray(codes) || codes.length === 0) return;
    setSelectedBjdCodes((prev) => {
      const nextSet = new Set(prev);
      const hasAll = codes.every((code) => nextSet.has(code));
      if (hasAll) {
        codes.forEach((code) => nextSet.delete(code));
      } else {
        codes.forEach((code) => nextSet.add(code));
      }
      return Array.from(nextSet);
    });
  };

  const allBjdCodes = useMemo(() => {
    const allCodes = groupTree.flatMap((group) => getGroupCodes(group));
    return Array.from(new Set(allCodes));
  }, [groupTree]);

  const regionSummary = useMemo(() => {
    const allCount = allBjdCodes.length;
    if (allCount === 0 || selectedBjdCodes.length === 0 || selectedBjdCodes.length === allCount) return '전체';
    return `${selectedBjdCodes.length}개 지역`;
  }, [allBjdCodes, selectedBjdCodes]);

  const hasRegionFilter = allBjdCodes.length > 0 && selectedBjdCodes.length > 0 && selectedBjdCodes.length !== allBjdCodes.length;
  const hasLeftSidebarFilter = hasRegionFilter || hasGroupByFilter || hasRegulationTypeFilter || hasGubunAverageFilter;

  // 좌측 패널 폭: 인구 표시 여부/트리 펼침 여부와 무관하게 항상 고정(인구 펼친 상태 기준 300px).
  // 인구 토글에 따라 패널 크기가 바뀌지 않도록 상수로 고정한다.
  const sidebarWidthPx = 300;
  // 최소폭이 248 이상이므로 라벨을 한 줄로 유지(narrow 축소 상태 없음).
  const sidebarNarrow = false;
  const filteredGroupTree = useMemo(() => {
    const keyword = String(regionSearchKeyword || '').trim().toLowerCase();
    const includesKeyword = (value) => String(value || '').toLowerCase().includes(keyword);

    const baseTree = (groupTree || []).map((group) => {
      const filteredSidoList = (group?.sidoList || []).map((sido) => {
        const filteredSigunguList = mergeSigunguList(sido?.sigunguList || []);
        if (filteredSigunguList.length > 0) {
          return {
            ...sido,
            sigunguList: filteredSigunguList,
          };
        }

        return null;
      }).filter(Boolean);

      if (includesKeyword(group?.groupName)) return group;
      if (filteredSidoList.length > 0) {
        return {
          ...group,
          sidoList: filteredSidoList,
        };
      }

      return null;
    }).filter(Boolean);

    if (!keyword) return baseTree;

    return baseTree.map((group) => {
      const filteredSidoList = (group?.sidoList || []).map((sido) => {
        const filteredSigunguList = mergeSigunguList(sido?.sigunguList || []).map((sigungu) => {
          const originalDongList = sigungu?.bjdList || [];
          const filteredDongList = originalDongList.filter((dong) => (
            includesKeyword(dong?.bjdName)
            || includesKeyword(dong?.bjdCode)
          ));

          const sigunguMatched = includesKeyword(sigungu?.sigunguName)
            || includesKeyword(sigungu?.sigunguCode)
            || includesKeyword(sigungu?.regulation)
            || includesKeyword(sigungu?.speculationZone);

          if (sigunguMatched) {
            return {
              ...sigungu,
              bjdList: mergeDongList(originalDongList),
            };
          }

          if (filteredDongList.length > 0) {
            return {
              ...sigungu,
              bjdList: mergeDongList(filteredDongList),
            };
          }

          return null;
        }).filter(Boolean);

        const sidoMatched = includesKeyword(sido?.sidoName) || includesKeyword(sido?.sidoCode);
        if (sidoMatched) {
          return {
            ...sido,
            sigunguList: mergeSigunguList(sido?.sigunguList || []),
          };
        }

        if (filteredSigunguList.length > 0) {
          return {
            ...sido,
            sigunguList: filteredSigunguList,
          };
        }

        return null;
      }).filter(Boolean);

      if (includesKeyword(group?.groupName)) return group;
      if (filteredSidoList.length > 0) {
        return {
          ...group,
          sidoList: filteredSidoList,
        };
      }

      return null;
    }).filter(Boolean);
  }, [groupTree, regionSearchKeyword]);
  const deferredFilteredGroupTree = useDeferredValue(filteredGroupTree);

  const filteredGroupCodesMap = useMemo(() => {
    const map = new Map();
    (filteredGroupTree || []).forEach((group) => {
      map.set(String(group?.groupName || ''), getGroupCodes(group));
    });
    return map;
  }, [filteredGroupTree]);

  const fullGroupCodesMap = useMemo(() => {
    const map = new Map();
    (groupTree || []).forEach((group) => {
      map.set(String(group?.groupName || ''), getGroupCodes(group));
    });
    return map;
  }, [groupTree]);

  const filteredSidoCodesMap = useMemo(() => {
    const map = new Map();
    (filteredGroupTree || []).forEach((group) => {
      const groupKey = String(group?.groupName || '');
      (group?.sidoList || []).forEach((sido) => {
        const sidoKey = String(sido?.sidoCode || sido?.sidoName || '');
        map.set(`${groupKey}|${sidoKey}`, getSidoCodes(sido));
      });
    });
    return map;
  }, [filteredGroupTree]);

  useEffect(() => {
    const keyword = String(regionSearchKeyword || '').trim();
    if (!keyword) return;

    const nextCodes = [];
    const nextExpandedGroups = {};
    const nextExpandedSido = {};
    const nextExpandedSigungu = {}; // 구/시는 펼치지 않음

    (filteredGroupTree || []).forEach((group) => {
      const groupName = String(group?.groupName || '').trim();
      if (groupName) nextExpandedGroups[groupName] = true;

      (group?.sidoList || []).forEach((sido) => {
        const sidoKey = String(sido?.sidoCode || sido?.sidoName || '').trim();
        if (sidoKey) nextExpandedSido[sidoKey] = true;

        (sido?.sigunguList || []).forEach((sigungu) => {
          // 구/시는 자동으로 펼치지 않음 (사용자가 + 버튼을 눌러야 함)
          // const sigunguKey = `${sidoKey}|${String(sigungu?.sigunguCode || sigungu?.sigunguName || '').trim()}`;
          // if (sigunguKey !== '|') nextExpandedSigungu[sigunguKey] = true;

          (sigungu?.bjdList || []).forEach((dong) => {
            const code = String(dong?.bjdCode || '').trim();
            if (code) nextCodes.push(code);
          });
        });
      });
    });

    const uniqueCodes = Array.from(new Set(nextCodes));
    if (uniqueCodes.length > 0) {
      setSelectedBjdCodes(uniqueCodes);
    }
    setExpandedGroups(nextExpandedGroups);
    setExpandedSido(nextExpandedSido);
    setExpandedSigungu(nextExpandedSigungu);
  }, [regionSearchKeyword, filteredGroupTree]);

  const applyRegionSearch = () => {
    const keyword = String(regionSearchInput || '').trim();
    // 같은 키워드로 재검색시에도 useEffect가 실행되도록 일시적으로 초기화
    if (keyword && keyword === regionSearchKeyword) {
      setRegionSearchKeyword('');
      setTimeout(() => setRegionSearchKeyword(keyword), 0);
    } else {
      setRegionSearchKeyword(keyword);
    }
  };

  const resetRegionSidebarFilters = () => {
    setSelectedBjdCodes([]);
    setSelectedGroupBy([]);
    setSelectedGubunAverageCodes([]);
    setSelectedRegulationTypes([]);
    setRegionSearchInput('');
    setRegionSearchKeyword('');
  };

  const loadAbortControllerRef = useRef(null);

  // bjdCodes 최적화: 구/시도 전체 선택 시 상위 코드만 전송
  const optimizeBjdCodes = (bjdCodes) => {
    if (!bjdCodes || bjdCodes.length === 0) return [];

    const codeSet = new Set(bjdCodes);
    const optimized = new Set();

    // groupTree를 순회하며 상위 코드로 대체 가능한지 확인
    (groupTree || []).forEach((group) => {
      (group?.sidoList || []).forEach((sido) => {
        (sido?.sigunguList || []).forEach((sigungu) => {
          const dongCodes = (sigungu?.bjdList || [])
            .map((dong) => String(dong?.bjdCode || '').trim())
            .filter(Boolean);

          // 모든 동이 선택되어 있으면 구 코드(5자리)만 추가
          if (dongCodes.length > 0 && dongCodes.every((code) => codeSet.has(code))) {
            const sigunguCode = String(sigungu?.bjdCode || sigungu?.sigunguCode || '').trim();
            if (sigunguCode && sigunguCode.length >= 5) {
              // 구 코드는 앞 5자리만 사용
              optimized.add(sigunguCode.substring(0, 5));
              dongCodes.forEach((code) => codeSet.delete(code));
            }
          }
        });
      });
    });

    // 남은 개별 동 코드 추가 (10자리 전체)
    codeSet.forEach((code) => {
      if (code && code.length === 10) {
        optimized.add(code);
      }
    });

    return Array.from(optimized);
  };

  const load = async (nextBjdCodes = selectedBjdCodes, keywordOverride = null, resetPage = true) => {
    // 이전 요청 취소
    if (loadAbortControllerRef.current) {
      loadAbortControllerRef.current.abort();
    }

    setLoading(true);
    setError('');

    const controller = new AbortController();
    loadAbortControllerRef.current = controller;

    // 변수를 try 블록 밖에서 선언 (catch 블록에서 사용하기 위해)
    const keyword = String(keywordOverride ?? appliedSearchComplexName ?? '').trim();
    const useFavoriteOnly = isFavoritePage ? true : favoriteOnly;
    const rawBjdCodes = isFavoritePage ? [] : nextBjdCodes;
    const requestBjdCodes = optimizeBjdCodes(rawBjdCodes);

    try {

      // 즐겨찾기 페이지에서 그룹이 선택되지 않았으면 로드 안 함
      if (isFavoritePage && (!selectedFavoriteGroupId || selectedFavoriteGroupId === 0)) {
        console.log('[Load] Skipping load - no group selected on favorites page');
        setItems([]);
        setLoading(false);
        return;
      }

      // 일반(랭킹) 페이지에서 지역이 선택되지 않았으면 전체 조회하지 않고 안내 메시지 표시
      if (!isFavoritePage && requestBjdCodes.length === 0 && !keyword) {
        setItems([]);
        setTotalCount(0);
        setHasMore(false);
        setLoading(false);
        return;
      }

      // nextLastKey를 offset으로 파싱 (문자열 -> 숫자)
      const currentOffset = resetPage ? 0 : (parseInt(nextLastKey) || 0);
      console.log('[Load] resetPage:', resetPage, 'currentOffset:', currentOffset);
      console.log('[Load] isFavoritePage:', isFavoritePage, 'selectedFavoriteGroupId:', selectedFavoriteGroupId);

      // 백엔드에서 정렬 처리
      console.log('[Load] Grade Filters:', { selectedSigunguGrades, selectedDongGrades });
      const result = await getRankerFixList({
        bjdCodes: requestBjdCodes,
        keyword,
        isFavorite: useFavoriteOnly,
        favoriteGroupId: isFavoritePage ? selectedFavoriteGroupId : null,
        groupAverage: isFavoritePage ? groupAverageOn : false,
        highRankerOnly,
        gubunFilters,
        regionAvgLevels: selectedGroupBy.map(key => {
          if (key === '시도') return 'SIDO';
          if (key === '구') return 'SIGUNGU';
          if (key === '동') return 'DONG';
          return null;
        }).filter(Boolean),
        // 단지제외: 지역평균(집계행)만 반환
        regionAvgOnly: excludeDanji,
        orderBy: sortPrefs,
        offset: currentOffset,
        pageSize: 100,
        // 안전마진(개인별) — 여유매수/매도가 조회 시점 재계산
        buyMargin: safeMarginsRef.current?.buy,
        sellMargin: safeMarginsRef.current?.sell,
        // 세금(취득세) 개인화 — 주택수/생애최초 (취득세·실투자금·순이익·실수익률 재계산)
        houseCount: taxSettingsRef.current?.homeCount,
        firstTime: taxSettingsRef.current?.firstTime,
        // 필터 파라미터
        tradePriceMin: appliedFilters.tradePriceMin,
        tradePriceMax: appliedFilters.tradePriceMax,
        investPriceMin: appliedFilters.investPriceMin,
        investPriceMax: appliedFilters.investPriceMax,
        investRealInvestPriceMin: appliedFilters.investRealInvestPriceMin,
        investRealInvestPriceMax: appliedFilters.investRealInvestPriceMax,
        complexMinTradePriceMin: appliedFilters.complexMinTradePriceMin,
        complexMinTradePriceMax: appliedFilters.complexMinTradePriceMax,
        complexMaxTradePriceMin: appliedFilters.complexMaxTradePriceMin,
        complexMaxTradePriceMax: appliedFilters.complexMaxTradePriceMax,
        leasePriceMin: appliedFilters.leasePriceMin,
        leasePriceMax: appliedFilters.leasePriceMax,
        complexMinLeasePriceMin: appliedFilters.complexMinLeasePriceMin,
        complexMinLeasePriceMax: appliedFilters.complexMinLeasePriceMax,
        complexMaxLeasePriceMin: appliedFilters.complexMaxLeasePriceMin,
        complexMaxLeasePriceMax: appliedFilters.complexMaxLeasePriceMax,
        avgSaleRealPriceMin: appliedFilters.avgSaleRealPriceMin,
        avgSaleRealPriceMax: appliedFilters.avgSaleRealPriceMax,
        avgLeaseRealPriceMin: appliedFilters.avgLeaseRealPriceMin,
        avgLeaseRealPriceMax: appliedFilters.avgLeaseRealPriceMax,
        tradeArticleCountMin: appliedFilters.tradeArticleCountMin,
        tradeArticleCountMax: appliedFilters.tradeArticleCountMax,
        leaseArticleCountMin: appliedFilters.leaseArticleCountMin,
        leaseArticleCountMax: appliedFilters.leaseArticleCountMax,
        leasePriceRateMin: appliedFilters.leasePriceRateMin,
        leasePriceRateMax: appliedFilters.leasePriceRateMax,
        riseRateMin: appliedFilters.riseRateMin,
        riseRateMax: appliedFilters.riseRateMax,
        risePriceMin: appliedFilters.risePriceMin,
        risePriceMax: appliedFilters.risePriceMax,
        supply3YearsMin: appliedFilters.supply3YearsMin,
        supply3YearsMax: appliedFilters.supply3YearsMax,
        useApproveAgeMin: appliedFilters.useApproveAgeMin,
        useApproveAgeMax: appliedFilters.useApproveAgeMax,
        areaMin: appliedFilters.areaMin,
        areaMax: appliedFilters.areaMax,
        householdMin: appliedFilters.householdMin,
        householdMax: appliedFilters.householdMax,
        sigunguGrades: selectedSigunguGrades.length > 0 ? selectedSigunguGrades.join(',') : null,
        dongGrades: selectedDongGrades.length > 0 ? selectedDongGrades.join(',') : null,
        gubunFilters: selectedGubuns.length > 0 ? selectedGubuns : [],
        houseStructure: selectedHouseStructure || null,
        // 변동율 필터 파라미터
        goHyunRateMin: appliedFilters.goHyunRateMin,
        goHyunRateMax: appliedFilters.goHyunRateMax,
        jeokHyunRateMin: appliedFilters.jeokHyunRateMin,
        jeokHyunRateMax: appliedFilters.jeokHyunRateMax,
        goJeoRateMin: appliedFilters.goJeoRateMin,
        goJeoRateMax: appliedFilters.goJeoRateMax,
        jeoHyunRateMin: appliedFilters.jeoHyunRateMin,
        jeoHyunRateMax: appliedFilters.jeoHyunRateMax,
        changeGapMin: appliedFilters.changeGapMin,
        changeGapMax: appliedFilters.changeGapMax,
        pastTopPriceMin: appliedFilters.pastTopPriceMin,
        pastTopPriceMax: appliedFilters.pastTopPriceMax,
        prevHighPriceMin: appliedFilters.prevHighPriceMin,
        prevHighPriceMax: appliedFilters.prevHighPriceMax,
        properHighPriceMin: appliedFilters.properHighPriceMin,
        properHighPriceMax: appliedFilters.properHighPriceMax,
        prevLowPriceMin: appliedFilters.prevLowPriceMin,
        prevLowPriceMax: appliedFilters.prevLowPriceMax,
        targetPriceMin: appliedFilters.targetPriceMin,
        targetPriceMax: appliedFilters.targetPriceMax,
      });

      console.log('[Load] API Response:', {
        itemsCount: result?.items?.length,
        nextLastKey: result?.nextLastKey,
        hasMore: result?.hasMore,
      });

      let newItems = Array.isArray(result?.items) ? result.items.map(normalizeSupplyYears) : [];

      // 마지막 업데이트(스냅샷 날짜)
      setSnapshotDate(result?.snapshotDate || null);

      // 백엔드에서 정렬된 데이터를 그대로 사용

      if (resetPage) {
        // Create hidden filter option rows
        const filterOptionRows = [];
        if (result?.filterOptions) {
          setFilterOptions(result.filterOptions);

          // Add hidden rows for each filter value to ensure they appear in dropdowns
          const opts = result.filterOptions;
          const maxRows = Math.max(
            (opts.sigunguGrades || []).length,
            (opts.dongGrades || []).length,
            (opts.gubuns || []).length,
            (opts.useApproveDates || []).length,
            (opts.planTypeNames || []).length,
            (opts.houseStructures || []).length
          );

          for (let i = 0; i < maxRows; i++) {
            filterOptionRows.push({
              isFilterOptionRow: true,
              naverDanjiCode: `__filter_option_${i}__`,
              sigunguGrade: opts.sigunguGrades?.[i] ? parseInt(opts.sigunguGrades[i]) : undefined,
              dongGrade: opts.dongGrades?.[i] ? parseInt(opts.dongGrades[i]) : undefined,
              gubun: opts.gubuns?.[i],
              useApproveDate: opts.useApproveDates?.[i] ? `${opts.useApproveDates[i]}-01-01` : undefined,
              planTypeName: opts.planTypeNames?.[i],
              houseStructure: opts.houseStructures?.[i],
            });
          }
        }

        setItems([...newItems, ...filterOptionRows]);
      } else {
        // 무한 스크롤: 기존 데이터에 추가
        setItems(prev => {
          // filterOptionRows 제거 (isFilterOptionRow인 항목들)
          const realItems = prev.filter(item => !item.isFilterOptionRow);
          return [...realItems, ...newItems];
        });
      }

      // nextLastKey와 hasMore는 항상 업데이트
      setNextLastKey(result?.nextLastKey || null);
      setHasMore(result?.hasMore || false);

      console.log('[Load] Updated state:', {
        newNextLastKey: result?.nextLastKey,
        newHasMore: result?.hasMore,
      });

      setTotalCount(Number(result?.totalCount || 0));
    } catch (e) {
      // AbortError는 무시 (이전 요청이 취소된 경우)
      if (e.name === 'AbortError') {
        console.log('[Load] Request aborted');
        return;
      }

      console.error('[Load] Error:', e);
      console.error('[Load] Error details:', {
        bjdCodes: requestBjdCodes,
        keyword,
        isFavorite: useFavoriteOnly,
        favoriteGroupId: isFavoritePage ? selectedFavoriteGroupId : null,
      });
      if (resetPage) {
        setItems([]);
        setTotalCount(0);
      }
      setError(e?.message || '단지 목록을 불러오지 못했습니다.');
    } finally {
      if (controller.signal.aborted) {
        console.log('[Load] Cleanup skipped - request was aborted');
      } else {
        setLoading(false);
        loadAbortControllerRef.current = null;
      }
    }
  };

  const loadMore = async () => {
    if (loading || !hasMore) return;
    await load(selectedBjdCodes, appliedSearchComplexName, false);
  };

  // 규제지역 도움말 팝오버: 아이콘 기준으로 화면 좌표 계산 후 body 포탈로 표시(조상 overflow 회피)
  const openRegulationHelp = () => {
    if (regulationHelpCloseTimerRef.current) {
      clearTimeout(regulationHelpCloseTimerRef.current);
      regulationHelpCloseTimerRef.current = null;
    }
    const el = regulationHelpIconRef.current;
    if (el) {
      const rect = el.getBoundingClientRect();
      const width = 660;
      // 오른쪽 화면 밖으로 나가지 않도록 보정
      const left = Math.min(rect.left, window.innerWidth - width - 8);
      // 아이콘 위쪽에 표시: 팝오버 하단을 아이콘 상단 바로 위에 고정하고 위로 자라게 함
      setRegulationHelpPos({ bottom: window.innerHeight - rect.top + 4, left: Math.max(8, left) });
    }
    setRegulationHelpOpen(true);
  };
  const scheduleCloseRegulationHelp = () => {
    if (regulationHelpCloseTimerRef.current) clearTimeout(regulationHelpCloseTimerRef.current);
    regulationHelpCloseTimerRef.current = setTimeout(() => setRegulationHelpOpen(false), 120);
  };
  const cancelCloseRegulationHelp = () => {
    if (regulationHelpCloseTimerRef.current) {
      clearTimeout(regulationHelpCloseTimerRef.current);
      regulationHelpCloseTimerRef.current = null;
    }
  };

  useEffect(() => {
    const bootstrap = async () => {
      setTreeLoading(true);
      try {
        const [tree, codeRows] = await Promise.all([
          getRankerBjdTree(),
          getRankerGubunCodes().catch(() => []),
        ]);
        const groups = Array.isArray(tree) ? tree : [];
        setGroupTree(groups);
        const averageCodes = (groups?.[0]?.gubunAverageCodes || [])
          .map((row) => {
            const code = String(row?.code || row?.Code || '').trim();
            const label = String(row?.label || row?.Label || '').trim();
            const sortOrder = Number(row?.sortOrder ?? row?.SortOrder);
            if (!code || !label) return null;
            return {
              code,
              label,
              sortOrder: Number.isFinite(sortOrder) ? sortOrder : 0,
            };
          })
          .filter(Boolean)
          .sort((a, b) => (
            (a.sortOrder - b.sortOrder)
            || a.label.localeCompare(b.label, 'ko-KR')
            || a.code.localeCompare(b.code)
          ));
        setGubunAverageOptions(averageCodes);

        const normalizedCodes = (Array.isArray(codeRows) ? codeRows : [])
          .map((row) => {
            const key = String(row?.code || '').trim();
            const label = String(row?.label || row?.codeName || '').trim();
            const sortOrder = Number(row?.sortOrder);
            if (!key || !label) return null;
            return {
              key,
              label,
              sortOrder: Number.isFinite(sortOrder) ? sortOrder : 0,
            };
          })
          .filter(Boolean)
          .sort((a, b) => (
            (a.sortOrder - b.sortOrder)
            || a.label.localeCompare(b.label, 'ko-KR')
            || a.key.localeCompare(b.key)
          ));
        setLandmarkFilterOptions(
          normalizedCodes.length > 0
            ? normalizedCodes
            : FALLBACK_LANDMARK_FILTER_OPTIONS
        );

        const seoul = groups.find((g) => g?.groupName === '서울권역');
        const defaultCodes = (seoul?.sidoList || [])
          .flatMap((sido) => sido?.sigunguList || [])
          .flatMap((sigungu) => sigungu?.bjdList || [])
          .map((dong) => String(dong?.bjdCode || ''))
          .filter(Boolean);

        setSelectedBjdCodes(Array.from(new Set(defaultCodes)));
        setExpandedGroups({ '서울권역': true });

        // 초기 로드 - treeInitializedRef 설정 후 두 번째 useEffect에서 자동 호출됨
        treeInitializedRef.current = true;
      } catch (e) {
        setError(e?.message || '지역 트리를 불러오지 못했습니다.');
      } finally {
        setTreeLoading(false);
      }
    };

    bootstrap();
  }, []);

  useEffect(() => {
    if (!treeInitializedRef.current) return;
    if (!hasRegulationTypeFilter) {
      // 규제 필터 해제 시, 규제 때문에 자동 확장했던 트리를 다시 접어 패널 폭을 원래대로 축소
      setExpandedGroups({});
      setExpandedSido({});
      setExpandedSigungu({});
      return;
    }

    const matchedCodes = [];
    const nextExpandedGroups = {};
    const nextExpandedSido = {};
    const nextExpandedSigungu = {};

    (groupTree || []).forEach((group) => {
      const groupName = String(group?.groupName || '').trim();
      let hasGroupMatch = false;

      (group?.sidoList || []).forEach((sido) => {
        const sidoKey = String(sido?.sidoCode || sido?.sidoName || '').trim();
        let hasSidoMatch = false;

        (sido?.sigunguList || []).forEach((sigungu) => {
          if (regulationOnly && !isRegulationArea(sigungu)) return;
          if (speculationZoneOnly && !isSpeculationZone(sigungu)) return;
          if (landTradePermitOnly && !isLandTradePermitZone(sigungu)) return;

          hasGroupMatch = true;
          hasSidoMatch = true;

          // 구/시는 자동으로 펼치지 않음 (사용자가 + 버튼을 눌러야 함)
          // const sigunguKey = `${sidoKey}|${String(sigungu?.sigunguCode || sigungu?.sigunguName || '').trim()}`;
          // if (sigunguKey !== '|') nextExpandedSigungu[sigunguKey] = true;

          (sigungu?.bjdList || []).forEach((dong) => {
            const code = String(dong?.bjdCode || '').trim();
            if (code) matchedCodes.push(code);
          });
        });

        if (hasSidoMatch && sidoKey) nextExpandedSido[sidoKey] = true;
      });

      if (hasGroupMatch && groupName) nextExpandedGroups[groupName] = true;
    });

    setSelectedBjdCodes(Array.from(new Set(matchedCodes)));
    setExpandedGroups(nextExpandedGroups);
    setExpandedSido(nextExpandedSido);
    setExpandedSigungu(nextExpandedSigungu);
  }, [
    groupTree,
    hasRegulationTypeFilter,
    regulationOnly,
    speculationZoneOnly,
    landTradePermitOnly,
  ]);

  useEffect(() => {
    if (treeLoading) return;
    if (!treeInitializedRef.current) return; // 초기화 전에는 실행하지 않음 (bootstrap에서 처리)
    load(selectedBjdCodes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBjdCodesKey, selectedLandmarkFiltersKey, selectedGroupByKey, excludeDanji, treeLoading, favoriteOnly, appliedSearchComplexName, isFavoritePage]);

  useEffect(() => {
    if (isFavoritePage) return;
    if (allBjdCodes.length === 0) {
      setRegionDanjiCount(0);
      return;
    }
    if (selectedBjdCodes.length === 0 || selectedBjdCodes.length === allBjdCodes.length) {
      setRegionDanjiCount(0);
      return;
    }
    let active = true;

    const loadRegionDanjiCount = async () => {
      try {
        const response = await getRankerDanjiCount({
          bjdCodes: selectedBjdCodes,
        });
        if (!active) return;
        setRegionDanjiCount(Number(response?.totalCount || 0));
      } catch (e) {
        if (!active) return;
        setRegionDanjiCount(0);
      }
    };

    loadRegionDanjiCount();
    return () => {
      active = false;
    };
  }, [isFavoritePage, selectedBjdCodesKey, selectedBjdCodes.length, allBjdCodes]);

  useEffect(() => {
    const validKeys = new Set((landmarkFilterOptions || []).map((option) => option?.key).filter(Boolean));
    setSelectedLandmarkFilters((prev) => prev.filter((key) => validKeys.has(key)));
  }, [landmarkFilterOptions]);

  useEffect(() => {
    let active = true;

    const loadFavorites = async () => {
      if (!isLoggedIn) return;
      try {
        const response = await getFavoriteList();
        const list = Array.isArray(response?.items)
          ? response.items
          : Array.isArray(response?.data?.items)
            ? response.data.items
            : [];

        if (!active) return;

        const next = list
          .map((item) => normalizeDanjiCode(item?.naverDanjiCode ?? item?.NaverDanjiCode))
          .filter(Boolean);

        setFavoriteDanjiCodes(new Set(next));
      } catch (favoriteError) {
        console.error('즐겨찾기 紐⑸줉 議고쉶 ?ㅽ뙣:', favoriteError);
      }
    };

    loadFavorites();

    return () => {
      active = false;
    };
  }, [isLoggedIn]);

  useEffect(() => {
    if (!isLoggedIn) {
      setFavoriteGroups([]);
      setSelectedFavoriteGroupId(0);
      setFavoriteGroupComplexes([]);
      setSelectedFavoriteDanjiCodes([]);
      return;
    }

    let active = true;

    const loadFavoriteGroups = async () => {
      setFavoriteGroupsLoading(true);
      try {
        const response = await getFavoriteGroups();
        const groups = Array.isArray(response?.items) ? response.items : [];
        if (!active) return;
        setFavoriteGroups(groups);

        if (groups.length === 0) {
          if (isFavoritePage) {
            setSelectedFavoriteGroupId(0);
            setFavoriteGroupComplexes([]);
            setSelectedFavoriteDanjiCodes([]);
          }
          return;
        }

        if (isFavoritePage) {
          setSelectedFavoriteGroupId((prev) => {
            const prevId = Number(prev);
            const exists = groups.some((group) => Number(group?.groupId) === prevId);
            if (exists) return prevId;
            return Number(groups[0]?.groupId || 0);
          });
        }
      } catch (favoriteGroupError) {
        if (!active) return;
        console.error('즐겨찾기 그룹 목록 조회 실패:', favoriteGroupError);
        setFavoriteGroups([]);
        if (isFavoritePage) {
          setSelectedFavoriteGroupId(0);
          setFavoriteGroupComplexes([]);
          setSelectedFavoriteDanjiCodes([]);
        }
      } finally {
        if (active) setFavoriteGroupsLoading(false);
      }
    };

    loadFavoriteGroups();

    return () => {
      active = false;
    };
  }, [isFavoritePage, isLoggedIn]);

  useEffect(() => {
    if (!isFavoritePage || !isLoggedIn) return;
    if (!selectedFavoriteGroupId) {
      setFavoriteGroupComplexes([]);
      setSelectedFavoriteDanjiCodes([]);
      setItems([]);
      return;
    }

    let active = true;

    const loadFavoriteGroupComplexes = async () => {
      setFavoriteGroupComplexesLoading(true);
      try {
        const response = await getFavoriteGroupComplexes(selectedFavoriteGroupId);
        const complexes = Array.isArray(response?.items) ? response.items : [];
        if (!active) return;
        setFavoriteGroupComplexes(complexes);
        setSelectedFavoriteDanjiCodes(
          complexes
            .map((item) => normalizeDanjiCode(item?.naverDanjiCode || item?.NaverDanjiCode))
            .filter(Boolean)
        );

        // 테이블 데이터도 다시 로드
        await load([], null, true);
      } catch (favoriteComplexError) {
        if (!active) return;
        console.error('즐겨찾기 ?⑥? 紐⑸줉 議고쉶 ?ㅽ뙣:', favoriteComplexError);
        setFavoriteGroupComplexes([]);
        setSelectedFavoriteDanjiCodes([]);
      } finally {
        if (active) setFavoriteGroupComplexesLoading(false);
      }
    };

    loadFavoriteGroupComplexes();

    return () => {
      active = false;
    };
  }, [isFavoritePage, isLoggedIn, selectedFavoriteGroupId]);

  // 그룹평균 토글 변경 시 목록 재조회 (마운트 시 최초 1회는 건너뜀)
  useEffect(() => {
    if (!isFavoritePage) return;
    if (groupAvgFirstRef.current) { groupAvgFirstRef.current = false; return; }
    if (!selectedFavoriteGroupId) return;
    load([], null, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupAverageOn]);

  const reloadFavoriteGroups = async () => {
    const response = await getFavoriteGroups();
    const groups = Array.isArray(response?.items) ? response.items : [];
    setFavoriteGroups(groups);
    return groups;
  };

  const handleAddFavoriteGroup = async () => {
    if (!isLoggedIn) {
      toast.info('로그인 후 이용할 수 있습니다.');
      return;
    }
    const inputName = window.prompt('새 그룹 이름');
    if (inputName == null) return;
    const groupName = String(inputName || '').trim();
    if (!groupName) {
      toast.info('그룹 이름을 입력해 주세요.');
      return;
    }

    try {
      await addFavoriteGroup(groupName);
      const groups = await reloadFavoriteGroups();
      const created = groups.find((g) => String(g?.groupName || '').trim() === groupName);

      // 즐겨찾기 페이지가 아닐 때만 모달에서 새 그룹 자동 선택
      if (created?.groupId && !isFavoritePage) {
        setFavoriteTargetGroupId(Number(created.groupId));
      }
      // 즐겨찾기 페이지에서는 현재 보고 있는 그룹 유지
      if (created?.groupId && isFavoritePage) {
        // 선택하지 않음 - 현재 그룹 유지
      }

      toast.success('그룹이 추가되었습니다.');
    } catch {
      toast.error('그룹 추가에 실패했습니다.');
    }
  };

  const handleEditFavoriteGroup = async (group) => {
    if (!isLoggedIn) {
      toast.info('로그인 후 이용할 수 있습니다.');
      return;
    }

    const groupId = Number(group?.groupId || 0);
    if (!(groupId > 0)) return;

    const currentName = String(group?.groupName || '').trim();
    const inputName = window.prompt('그룹 이름 수정', currentName);
    if (inputName == null) return;
    const groupName = String(inputName || '').trim();
    if (!groupName) {
      toast.info('그룹 이름을 입력해 주세요.');
      return;
    }

    try {
      await updateFavoriteGroup(groupId, groupName);
      const groups = await reloadFavoriteGroups();
      // 팝업에서 수정 시 favoriteTargetGroupId도 업데이트
      if (favoriteGroupPickerOpen && favoriteTargetGroupId === groupId) {
        const updated = groups.find((g) => Number(g?.groupId) === groupId);
        if (updated) setFavoriteTargetGroupId(groupId);
      }
      toast.success('그룹 이름이 수정되었습니다.');
    } catch {
      toast.error('그룹 수정에 실패했습니다.');
    }
  };

  const handleDeleteFavoriteGroup = async (group) => {
    if (!isLoggedIn) {
      toast.info('로그인 후 이용할 수 있습니다.');
      return;
    }

    const groupId = Number(group?.groupId || 0);
    if (!(groupId > 0)) return;

    const ok = window.confirm(`'${group?.groupName || ''}' 그룹을 삭제할까요?`);
    if (!ok) return;

    // 그룹에 속한 단지(즐겨찾기)도 함께 삭제할지 확인
    const deleteMembers = window.confirm(
      '이 그룹에 담긴 단지(즐겨찾기)도 함께 삭제할까요?\n\n[확인] 단지까지 완전 삭제 · [취소] 단지는 기본 그룹으로 이동'
    );

    try {
      await deleteFavoriteGroup(groupId, deleteMembers);
      const groups = await reloadFavoriteGroups();

      // 선택된 그룹이 삭제된 경우 처리
      if (groups.length === 0) {
        setSelectedFavoriteGroupId(0);
        if (favoriteGroupPickerOpen) {
          setFavoriteTargetGroupId(0);
        }
      } else if (!groups.some((g) => Number(g?.groupId) === groupId)) {
        // 즐겨찾기 페이지인 경우에만 selectedFavoriteGroupId 업데이트
        if (isFavoritePage) {
          const stillSelected = groups.some((g) => Number(g?.groupId) === Number(selectedFavoriteGroupId));
          if (!stillSelected) setSelectedFavoriteGroupId(Number(groups[0]?.groupId || 0));
        }

        // 팝업에서 선택된 그룹이 삭제된 경우
        if (favoriteGroupPickerOpen && favoriteTargetGroupId === groupId) {
          setFavoriteTargetGroupId(Number(groups[0]?.groupId || 0));
        }
      }

      toast.success('그룹이 삭제되었습니다.');
    } catch {
      toast.error('그룹 삭제에 실패했습니다.');
    }
  };

  const handleOpenFavoriteGroupPicker = (singleItem = null) => {
    if (!isLoggedIn) {
      toast.info('로그인 후 즐겨찾기를 사용할 수 있습니다.');
      return;
    }

    // 이벤트 객체가 전달된 경우 무시 (버튼 onClick에서 호출될 때)
    if (singleItem && (singleItem instanceof Event || singleItem?.nativeEvent)) {
      singleItem = null;
    }

    // 단일 단지가 전달된 경우 (모달에서 호출)
    if (singleItem) {
      const code = String(singleItem?.naverDanjiCode || singleItem?.code || '').trim();
      if (!code) {
        toast.info('단지 정보가 없습니다.');
        return;
      }
      if (favoriteGroups.length === 0) {
        toast.info('먼저 즐겨찾기 그룹을 추가해 주세요.');
        return;
      }
      // favoritePickerTargets에 저장
      setFavoritePickerTargets([singleItem]);
      const nextTargetGroupId = Number(favoriteTargetGroupId || favoriteGroups[0]?.groupId || 0);
      setFavoriteTargetGroupId(nextTargetGroupId);
      favoritePickerInitialTargetGroupIdRef.current = nextTargetGroupId;
      setFavoriteGroupPickerOpen(true);
      return;
    }

    // 기존 로직 (테이블에서 선택된 단지들)
    if (compareTargets.length === 0) {
      toast.info('선택된 단지가 없습니다.');
      return;
    }

    if (favoriteGroups.length === 0) {
      toast.info('먼저 즐겨찾기 그룹을 추가해 주세요.');
      return;
    }

    // favoritePickerTargets에 저장 (naverDanjiCode 속성 추가)
    const targetsWithNaverDanjiCode = compareTargets.map(target => {
      const naverDanjiCode = target.naverDanjiCode || target.code;
      if (!naverDanjiCode) {
        console.error('단지 코드가 없는 항목:', target);
      }
      return {
        ...target,
        naverDanjiCode,
        code: naverDanjiCode, // code도 함께 설정
      };
    });

    // 유효하지 않은 항목 필터링
    const validTargets = targetsWithNaverDanjiCode.filter(t => t.naverDanjiCode);
    if (validTargets.length === 0) {
      toast.info('단지 정보가 없습니다.');
      return;
    }

    setFavoritePickerTargets(validTargets);
    const nextTargetGroupId = Number(favoriteTargetGroupId || favoriteGroups[0]?.groupId || 0);
    setFavoriteTargetGroupId(nextTargetGroupId);
    favoritePickerInitialTargetGroupIdRef.current = nextTargetGroupId;
    setFavoriteGroupPickerOpen(true);
  };

  const handleCloseFavoriteGroupPicker = () => {
    if (favoriteGroupApplyLoading) return;
    const initialGroupId = Number(favoritePickerInitialTargetGroupIdRef.current || 0);
    const currentGroupId = Number(favoriteTargetGroupId || 0);
    const hasPendingSelectionChange = initialGroupId !== currentGroupId;
    if (hasPendingSelectionChange) {
      const ok = window.confirm('적용하지 않은 변경사항이 있습니다. 닫을까요?');
      if (!ok) return;
    }
    setFavoriteGroupPickerOpen(false);
    setFavoritePickerTargets([]);
  };

  const handleApplyFavoritesToGroup = async (mode = 'add') => {
    const targetGroupId = Number(favoriteTargetGroupId);
    const selectedTargets = favoritePickerTargets.map((target) => ({
      code: normalizeDanjiCode(target?.code || target?.naverDanjiCode),
      ptpNo: target?.ptpNo || '',
    })).filter((t) => t.code);

    if (!isLoggedIn) {
      toast.info('로그인 후 즐겨찾기를 사용할 수 있습니다.');
      return;
    }
    if (!(targetGroupId > 0)) {
      toast.info('그룹을 선택해 주세요.');
      return;
    }
    if (selectedTargets.length === 0) {
      toast.info('선택된 단지가 없습니다.');
      return;
    }

    try {
      setFavoriteGroupApplyLoading(true);

      // 현재 그룹의 단지 목록 가져오기
      const currentGroupResponse = await getFavoriteGroupComplexes(targetGroupId);
      const currentGroupItems = Array.isArray(currentGroupResponse?.items)
        ? currentGroupResponse.items.map((item) => ({
            code: normalizeDanjiCode(item?.naverDanjiCode || item?.NaverDanjiCode),
            ptpNo: item?.ptpNo || '',
          })).filter((t) => t.code)
        : [];
      const currentGroupKeys = new Set(currentGroupItems.map((t) => `${t.code}|${t.ptpNo}`));

      // 중복 체크 (단지코드 + 평형)
      const duplicateTargets = selectedTargets.filter((t) => currentGroupKeys.has(`${t.code}|${t.ptpNo}`));
      const newTargets = selectedTargets.filter((t) => !currentGroupKeys.has(`${t.code}|${t.ptpNo}`));

      if (mode !== 'move' && duplicateTargets.length > 0 && newTargets.length === 0) {
        toast.info(`선택한 ${selectedTargets.length}개 단지가 모두 이미 해당 그룹에 있습니다.`);
        setFavoriteGroupApplyLoading(false);
        return;
      }

      // ptpNo가 없는 항목은 제외
      const validTargets = selectedTargets.filter((t) => t.ptpNo);

      if (validTargets.length === 0) {
        toast.info('평형 정보가 없는 단지는 즐겨찾기에 추가할 수 없습니다.');
        setFavoriteGroupApplyLoading(false);
        return;
      }

      // 모든 단지를 선택한 그룹에 직접 추가 (groupId 포함)
      const results = await Promise.all(
        validTargets.map(async (t) => {
          try {
            const res = await addFavorite(t.code, t.ptpNo, targetGroupId);
            return { success: res?.success !== false, message: res?.message, target: t };
          } catch (err) {
            // BadRequest(400) 응답도 여기로 옴
            const errData = err?.response?.data;
            const message = errData?.message || errData?.Message || '추가 실패';
            return { success: false, message, target: t };
          }
        })
      );

      // 성공/실패 카운트
      const succeeded = results.filter((r) => r.success === true).length;
      const alreadyExists = results.filter((r) => r.success === false && r.message?.includes('이미')).length;

      console.log('[즐겨찾기 저장 결과]', { succeeded, alreadyExists, total: validTargets.length, results });

      // 즐겨찾기 그룹 목록 새로고침 (항목수 업데이트)
      await reloadFavoriteGroups();

      try {
        const response = await getFavoriteList();
        const list = Array.isArray(response?.items)
          ? response.items
          : Array.isArray(response?.data?.items)
            ? response.data.items
            : [];
        const next = list
          .map((item) => normalizeDanjiCode(item?.naverDanjiCode ?? item?.NaverDanjiCode))
          .filter(Boolean);
        setFavoriteDanjiCodes(new Set(next));
      } catch {}

      if (isFavoritePage && selectedFavoriteGroupId) {
        try {
          const response = await getFavoriteGroupComplexes(selectedFavoriteGroupId);
          const complexes = Array.isArray(response?.items) ? response.items : [];
          setFavoriteGroupComplexes(complexes);
          setSelectedFavoriteDanjiCodes(
            complexes
              .map((item) => normalizeDanjiCode(item?.naverDanjiCode || item?.NaverDanjiCode))
              .filter(Boolean)
          );
        } catch {
          setFavoriteGroupComplexes([]);
          setSelectedFavoriteDanjiCodes([]);
        }
      }

      // 결과 메시지
      if (succeeded === 0 && alreadyExists > 0) {
        toast.info(`선택한 ${validTargets.length}개 단지가 모두 이미 해당 그룹에 있습니다.`);
      } else if (succeeded > 0 && alreadyExists > 0) {
        toast.success(`${succeeded}개 단지를 추가했습니다. (${alreadyExists}개는 이미 그룹에 있습니다)`);
      } else if (succeeded > 0) {
        toast.success(`${succeeded}개 단지를 즐겨찾기 그룹에 추가했습니다.`);
      }

      favoritePickerInitialTargetGroupIdRef.current = targetGroupId;
      // 모달 닫지 않음 - 실시간 개수 확인 가능
    } catch (error) {
      console.error('Favorite group operation error:', error);
      toast.error(mode === 'move' ? '그룹 이동에 실패했습니다.' : '즐겨찾기 그룹 추가에 실패했습니다.');
    } finally {
      setFavoriteGroupApplyLoading(false);
    }
  };

  const handleRemoveFromCurrentGroup = async () => {
    if (!isLoggedIn) {
      toast.info('로그인 후 사용할 수 있습니다.');
      return;
    }
    if (!isFavoritePage || !selectedFavoriteGroupId) {
      toast.info('즐겨찾기 페이지에서만 사용할 수 있습니다.');
      return;
    }

    const selectedTargets = compareTargets.map((target) => ({
      code: normalizeDanjiCode(target?.code),
      ptpNo: target?.ptpNo || '',
    })).filter((t) => t.code && t.ptpNo);

    if (selectedTargets.length === 0) {
      toast.info('선택된 단지가 없습니다.');
      return;
    }

    if (!window.confirm(`선택한 ${selectedTargets.length}개 단지를 현재 그룹에서 제외하시겠습니까?`)) {
      return;
    }

    try {
      setFavoriteGroupApplyLoading(true);

      // 각 단지를 현재 그룹에서 제거
      const results = await Promise.all(
        selectedTargets.map(async (t) => {
          try {
            const res = await removeFavoriteFromGroup(selectedFavoriteGroupId, t.code, t.ptpNo);
            return { success: res?.success !== false, target: t };
          } catch (err) {
            console.error('Remove from group error:', err);
            return { success: false, target: t };
          }
        })
      );

      const succeeded = results.filter((r) => r.success).length;

      // 즐겨찾기 그룹 목록 새로고침
      await reloadFavoriteGroups();

      // 현재 그룹의 단지 목록 새로고침
      try {
        const response = await getFavoriteGroupComplexes(selectedFavoriteGroupId);
        const complexes = Array.isArray(response?.items) ? response.items : [];
        setFavoriteGroupComplexes(complexes);
        setSelectedFavoriteDanjiCodes(
          complexes
            .map((item) => normalizeDanjiCode(item?.naverDanjiCode || item?.NaverDanjiCode))
            .filter(Boolean)
        );
      } catch {
        setFavoriteGroupComplexes([]);
        setSelectedFavoriteDanjiCodes([]);
      }

      // 선택 해제
      setCompareTargets([]);

      // 테이블 데이터 다시 로드
      await load([], null, true);

      if (succeeded > 0) {
        toast.success(`${succeeded}개 단지를 그룹에서 제외했습니다.`);
      }
    } catch (error) {
      console.error('Remove from group error:', error);
      toast.error('그룹에서 제외하는데 실패했습니다.');
    } finally {
      setFavoriteGroupApplyLoading(false);
    }
  };

  const setFavoritePending = (code, pending) => {
    setFavoritePendingCodes((prev) => {
      const next = new Set(prev);
      if (pending) next.add(code);
      else next.delete(code);
      return next;
    });
  };

  const handleToggleFavorite = async (item, e) => {
    e?.stopPropagation();

    const code = normalizeDanjiCode(item?.naverDanjiCode);
    const ptpNo = item?.ptpNo || '';
    if (!code || favoritePendingCodes.has(code)) return;

    if (!isLoggedIn) {
      alert('로그인 후 즐겨찾기를 사용할 수 있습니다.');
      return;
    }

    const isFavorite = favoriteDanjiCodes.has(code);
    setFavoritePending(code, true);

    try {
      if (isFavorite) await removeFavorite(code, ptpNo);
      else await addFavorite(code, ptpNo);

      if (isFavorite) {
        toast.success('즐겨찾기가 해제되었습니다.');
      } else {
        toast.success('즐겨찾기 되었습니다.');
      }

      setFavoriteDanjiCodes((prev) => {
        const next = new Set(prev);
        if (isFavorite) next.delete(code);
        else next.add(code);
        return next;
      });
    } finally {
      setFavoritePending(code, false);
    }
  };

  const isCompared = (item) => {
    const fixId = item?.fixId ?? item?.fix_id;
    const ptpNo = item?.ptpNo ?? item?.ptp_no;
    return compareTargets.some((target) => target.fixId === fixId && target.ptpNo === ptpNo);
  };

  const handleToggleCompareTarget = (item, e) => {
    e?.stopPropagation();

    const code = normalizeDanjiCode(item?.naverDanjiCode || item?.naver_danji_code || item?.danji_code);
    if (!code) {
      console.error('단지 코드를 찾을 수 없음:', item);
      return;
    }

    const fixId = item?.fixId ?? item?.fix_id;
    const ptpNo = item?.ptpNo ?? item?.ptp_no;
    const name = String(item?.buildingName || '').trim() || code;
    const planTypeName = String(item?.planTypeName || '').trim();
    const address = item?.addressRoad || item?.roadAddress || item?.address || item?.jibunAddress || '';
    const familyCnt = item?.familyCnt ?? item?.households ?? item?.householdCount ?? null;
    const useApproveDate = item?.useApproveDate || item?.useApprovalDate || '';

    setCompareTargets((prev) => {
      const exists = prev.some((target) => target.fixId === fixId && target.ptpNo === ptpNo);
      if (exists) {
        return prev.filter((target) => !(target.fixId === fixId && target.ptpNo === ptpNo));
      }
      return [...prev, { code, fixId, ptpNo, name, planTypeName, address, familyCnt, useApproveDate }];
    });
  };

  useEffect(() => {
    try {
      const raw = localStorage.getItem(userColumnStorageKey);
      if (!raw) {
        const defaultVisible = {};
        const defaultGroupVisible = {};
        Object.entries(defaultGroupItemsById).forEach(([groupId, items]) => {
          let hasVisibleColumn = false;
          items.forEach((item) => {
            const visibleKey = item.columnKey || `group-item:${groupId}:${item.id}`;
            const isVisible = DEFAULT_VISIBLE_COLUMN_KEYS.has(visibleKey);
            defaultVisible[visibleKey] = isVisible;
            if (isVisible) hasVisibleColumn = true;
          });
          defaultGroupVisible[groupId] = hasVisibleColumn;
        });
        setColumnPrefs({ order: [], visible: defaultVisible });
        setColumnGroupPrefs(defaultGroupVisible);
        setColumnGroupOrder(DISPLAY_COLUMN_GROUPS.filter((group) => !group.hidden).map((group) => group.id));
        setColumnGroupItemsById(defaultGroupItemsById);
        return;
      }
      const parsed = JSON.parse(raw);
      setColumnPrefs({
        order: Array.isArray(parsed?.order) ? parsed.order.map((key) => String(key || '')).filter(Boolean) : [],
        visible: parsed?.visible && typeof parsed.visible === 'object' ? parsed.visible : {},
      });
      setColumnGroupPrefs(parsed?.groupVisible && typeof parsed.groupVisible === 'object' ? parsed.groupVisible : {});
      const parsedOrder = Array.isArray(parsed?.groupOrder) ? parsed.groupOrder.map((id) => String(id || '')).filter(Boolean) : [];
      const defaultOrder = DISPLAY_COLUMN_GROUPS.filter((group) => !group.hidden).map((group) => group.id);
      const normalizedOrder = [
        ...parsedOrder.filter((id, idx) => defaultOrder.includes(id) && parsedOrder.indexOf(id) === idx),
        ...defaultOrder.filter((id) => !parsedOrder.includes(id)),
      ];
      setColumnGroupOrder(normalizedOrder);
      const storedGroupItems = parsed?.groupItems && typeof parsed.groupItems === 'object' ? parsed.groupItems : {};
      setColumnGroupItemsById(normalizeGroupItemsById(storedGroupItems));
    } catch {
      const defaultVisible = {};
      const defaultGroupVisible = {};
      Object.entries(defaultGroupItemsById).forEach(([groupId, items]) => {
        let hasVisibleColumn = false;
        items.forEach((item) => {
          const visibleKey = item.columnKey || `group-item:${groupId}:${item.id}`;
          const isVisible = DEFAULT_VISIBLE_COLUMN_KEYS.has(visibleKey);
          defaultVisible[visibleKey] = isVisible;
          if (isVisible) hasVisibleColumn = true;
        });
        defaultGroupVisible[groupId] = hasVisibleColumn;
      });
      setColumnPrefs({ order: [], visible: defaultVisible });
      setColumnGroupPrefs(defaultGroupVisible);
      setColumnGroupOrder(DISPLAY_COLUMN_GROUPS.filter((group) => !group.hidden).map((group) => group.id));
      setColumnGroupItemsById(defaultGroupItemsById);
    }
  }, [userColumnStorageKey, defaultGroupItemsById]);

  // 정렬설정은 메모리에서만 관리 (로컬스토리지에서 불러오지 않음)
  // useEffect(() => {
  //   try {
  //     const raw = localStorage.getItem(userSortStorageKey);
  //     if (!raw) {
  //       setSortPrefs([]);
  //       return;
  //     }
  //     const parsed = JSON.parse(raw);
  //     const normalized = Array.isArray(parsed)
  //       ? parsed.map((row) => ({
  //         key: String(row?.key || ''),
  //         direction: row?.direction === 'desc' ? 'desc' : 'asc',
  //       })).filter((row) => row.key)
  //       : [];
  //     setSortPrefs(normalized);
  //   } catch {
  //     setSortPrefs([]);
  //   }
  // }, [userSortStorageKey]);

  useEffect(() => {
    let active = true;
    if (!isLoggedIn) {
      setScreenSettingItems([]);
      setSelectedScreenSettingId('');
      return () => {
        active = false;
      };
    }

    const loadScreenSettings = async () => {
      try {
        const response = await getRankerScreenSettings();
        if (!active) return;
        const rows = Array.isArray(response?.items) ? response.items : [];
        setScreenSettingItems(rows);
        setSelectedScreenSettingId('');
        const defaultVisible = {};
        const defaultGroupVisible = {};
        Object.entries(defaultGroupItemsById).forEach(([groupId, items]) => {
          let hasVisibleColumn = false;
          items.forEach((item) => {
            const visibleKey = item.columnKey || `group-item:${groupId}:${item.id}`;
            const isVisible = DEFAULT_VISIBLE_COLUMN_KEYS.has(visibleKey);
            defaultVisible[visibleKey] = isVisible;
            if (isVisible) hasVisibleColumn = true;
          });
          defaultGroupVisible[groupId] = hasVisibleColumn;
        });
        setColumnPrefs((prev) => ({ ...prev, visible: defaultVisible }));
        setColumnGroupPrefs(defaultGroupVisible);

        // 초기 로드 시 정렬은 항상 지역순으로 초기화
        setSortPrefs([{ key: 'region', direction: 'asc' }]);
      } catch (loadError) {
        if (!active) return;
        setScreenSettingItems([]);
        setSelectedScreenSettingId('');
      }
    };

    loadScreenSettings();
    return () => {
      active = false;
    };
  }, [isLoggedIn, defaultGroupItemsById]);

  // 저장 후 draft 갱신 필요 시 state 변경을 감지해서 draft 재생성
  useEffect(() => {
    if (needDraftRefresh && columnSettingOpen && !screenSettingBusy) {
      const newDraft = createCurrentScreenSettingDraft();
      setColumnSettingDraft(newDraft);
      setInitialColumnSettingDraft(JSON.parse(JSON.stringify(newDraft)));
      setNeedDraftRefresh(false);
    }
  }, [needDraftRefresh, columnSettingOpen, screenSettingBusy, columnGroupOrder, columnGroupItemsById, columnPrefs, columnGroupPrefs, sortPrefs, useKoreanPriceUnit]);

  const reloadScreenSettings = async (nextSelectedId = null) => {
    if (!isLoggedIn) return;
    const response = await getRankerScreenSettings();
    const rows = Array.isArray(response?.items) ? response.items : [];
    console.log('[RELOAD] Received from API:', rows.map(r => r.settingId));
    setScreenSettingItems(rows);

    if (nextSelectedId != null) {
      const nextIdStr = String(nextSelectedId);
      setSelectedScreenSettingId(nextIdStr);
      // 저장/수정 후 해당 설정 즉시 적용
      handleScreenSettingChange(nextIdStr, { forceApply: true });
      return;
    }
    const defaultSetting = rows.find((row) => row?.isDefault) || rows[0];
    const defaultIdStr = defaultSetting?.settingId ? String(defaultSetting.settingId) : '';
    setSelectedScreenSettingId(defaultIdStr);
    if (defaultIdStr) {
      handleScreenSettingChange(defaultIdStr, { forceApply: true });
    }

    // 새로고침 시 정렬은 항상 지역순으로 초기화
    setSortPrefs([{ key: 'region', direction: 'asc' }]);
  };

  const handleScreenSettingChange = (nextId, options = {}) => {
    const forceApply = options?.forceApply === true;
    const normalizedId = String(nextId || '');
    setSelectedScreenSettingId(normalizedId);

    // 화면설정 변경 시작 플래그 설정
    screenSettingChangeInProgressRef.current = true;

    // 전체보기: 모든 컬럼 표시
    if (normalizedId === '__all__') {
      const allVisible = {};
      const allGroupVisible = {};
      Object.entries(defaultGroupItemsById).forEach(([groupId, items]) => {
        items.forEach((item) => {
          const visibleKey = item.columnKey || `group-item:${groupId}:${item.id}`;
          allVisible[visibleKey] = true; // 모든 컬럼 표시
        });
        allGroupVisible[groupId] = true; // 모든 그룹 표시
      });
      setColumnPrefs((prev) => ({ ...prev, visible: allVisible }));
      setColumnGroupPrefs(allGroupVisible);
      // 플래그 해제
      setTimeout(() => {
        screenSettingChangeInProgressRef.current = false;
      }, 0);
      return;
    }

    // 기본 설정 (화면설정을 선택해주세요)
    if (!normalizedId) {
      const defaultVisible = {};
      const defaultGroupVisible = {};
      Object.entries(defaultGroupItemsById).forEach(([groupId, items]) => {
        let hasVisibleColumn = false;
        items.forEach((item) => {
          const visibleKey = item.columnKey || `group-item:${groupId}:${item.id}`;
          const isVisible = DEFAULT_VISIBLE_COLUMN_KEYS.has(visibleKey);
          defaultVisible[visibleKey] = isVisible;
          if (isVisible) hasVisibleColumn = true;
        });
        defaultGroupVisible[groupId] = hasVisibleColumn;
      });
      setColumnPrefs((prev) => ({ ...prev, visible: defaultVisible }));
      setColumnGroupPrefs(defaultGroupVisible);
      // 플래그 해제
      setTimeout(() => {
        screenSettingChangeInProgressRef.current = false;
      }, 0);
      return;
    }

    // 사용자 저장 설정
    const target = screenSettingItems.find((row) => String(row?.settingId || '') === normalizedId);
    const payload = parseScreenSettingPayload(target?.settingPayload);

    // 항상 실제 상태 적용
    applyScreenSettingPayload(payload);

    // 모달이 열려있으면 draft도 업데이트
    if (columnSettingDraft) {
      setColumnSettingDraft(mergeScreenSettingState({}, payload));
    }
  };

  const toolbarScreenSettingItems = useMemo(
    () => (Array.isArray(screenSettingItems) ? screenSettingItems.filter((row) => String(row?.settingId || '').trim()) : []),
    [screenSettingItems]
  );

  const handleSaveScreenSetting = async ({ mode, settingId, settingName } = {}) => {
    if (!isLoggedIn || screenSettingBusy) return;

    const normalizedMode = mode === 'overwrite' ? 'overwrite' : 'new';
    const normalizedName = String(settingName || '').trim();
    if (!normalizedName) {
      toast.info('설정 이름을 입력해 주세요.');
      return;
    }

    let savePayload;
    if (normalizedMode === 'new') {
      // 신규 등록: 모든 그룹과 항목 켜진 상태로 생성
      const allGroupsEnabled = {};
      DISPLAY_COLUMN_GROUPS.filter((g) => !g.hidden).forEach((group) => {
        allGroupsEnabled[group.id] = true;
      });

      const allItemsVisible = {};
      const allColumnOrder = [];
      Object.keys(defaultGroupItemsById).forEach((groupId) => {
        const items = defaultGroupItemsById[groupId] || [];
        items.forEach((item) => {
          if (item.columnKey) {
            allItemsVisible[item.columnKey] = true;
            allColumnOrder.push(item.columnKey);
          }
        });
      });

      savePayload = {
        version: 1,
        columnPrefs: { order: allColumnOrder, visible: allItemsVisible },
        columnGroupPrefs: allGroupsEnabled,
        columnGroupOrder: DISPLAY_COLUMN_GROUPS.filter((g) => !g.hidden).map((g) => g.id),
        columnGroupItemsById: defaultGroupItemsById,
        sortPrefs: sortPrefs || [],
        useKoreanPriceUnit: useKoreanPriceUnit,
      };
    } else {
      // 덮어쓰기: 현재 상태 사용
      savePayload = columnSettingDraft
        ? buildScreenSettingPayloadFromState(columnSettingDraft)
        : buildScreenSettingPayload();
    }

    const nextSettingId = normalizedMode === 'overwrite' ? Number(settingId) : null;
    if (normalizedMode === 'overwrite' && !(nextSettingId > 0)) {
      toast.info('덮어쓸 화면설정을 선택해 주세요.');
      return;
    }

    try {
      setScreenSettingBusy(true);
      const saved = await saveRankerScreenSetting({
        settingId: nextSettingId,
        settingName: normalizedName,
        payload: savePayload,
      });

      // 저장한 payload를 즉시 현재 state에 적용 (서버 응답 전에 반영)
      applyScreenSettingPayload(savePayload);

      // 순서 변경이 pending 상태면 함께 저장
      const hasOrderChange = pendingScreenSettingOrder !== null;
      if (hasOrderChange) {
        const settingIds = pendingScreenSettingOrder.map(s => s.settingId);
        await reorderRankerScreenSettings(settingIds);
        setPendingScreenSettingOrder(null);
      }

      await reloadScreenSettings(saved?.settingId || saved?.settingID || null);

      // 빠른 순서 편집 갱신: 저장한 payload로 draft 재생성
      if (columnSettingOpen) {
        const newDraft = mergeScreenSettingState({}, savePayload);
        setColumnSettingDraft(newDraft);
        setInitialColumnSettingDraft(JSON.parse(JSON.stringify(newDraft)));
      }

      // 알림 메시지: 순서도 함께 저장했으면 "저장되었습니다"로 통일
      if (hasOrderChange) {
        toast.success('저장되었습니다.');
      } else {
        toast.success(normalizedMode === 'overwrite' ? '화면설정을 덮어쓰기 저장했습니다.' : '화면설정을 신규 저장했습니다.');
      }
    } catch {
      toast.error('화면설정 저장에 실패했습니다.');
    } finally {
      setScreenSettingBusy(false);
    }
  };

  const handleRenameScreenSetting = async ({ settingId, settingName } = {}) => {
    const normalizedSettingId = Number(settingId);
    const normalizedName = String(settingName || '').trim();
    if (!normalizedName) {
      toast.info('설정 이름을 입력해 주세요.');
      return;
    }
    const targetSetting = screenSettingItems.find((row) => Number(row?.settingId) === normalizedSettingId);
    if (!targetSetting) return;

    if (!isLoggedIn || screenSettingBusy || !(normalizedSettingId > 0)) return;
    try {
      setScreenSettingBusy(true);
      await saveRankerScreenSetting({
        settingId: normalizedSettingId,
        settingName: normalizedName,
        payload: parseScreenSettingPayload(targetSetting?.settingPayload),
      });
      await reloadScreenSettings(normalizedSettingId);
      toast.success('화면설정 이름을 수정했습니다.');
    } catch {
      toast.error('화면설정 수정에 실패했습니다.');
    } finally {
      setScreenSettingBusy(false);
    }
  };

  const handleDeleteScreenSetting = async (settingId) => {
    const currentSettingId = Number(settingId || selectedScreenSettingId);
    if (!isLoggedIn || screenSettingBusy || !(currentSettingId > 0)) return;

    try {
      setScreenSettingBusy(true);
      await deleteRankerScreenSetting(currentSettingId);
      await reloadScreenSettings(null);
      toast.success('화면설정을 삭제했습니다.');
    } catch {
      toast.error('화면설정 삭제에 실패했습니다.');
    } finally {
      setScreenSettingBusy(false);
    }
  };

  const handleScreenSettingOrderChange = (reorderedItems) => {
    // 순서 변경을 pending 상태로 저장 (저장 버튼 누를 때까지 대기)
    setPendingScreenSettingOrder(reorderedItems);
  };

  const handleReorderScreenSetting = async (settingIds) => {
    if (!isLoggedIn || screenSettingBusy || !settingIds || settingIds.length === 0) return;

    try {
      setScreenSettingBusy(true);
      await reorderRankerScreenSettings(settingIds);
      await reloadScreenSettings(selectedScreenSettingId);
      setPendingScreenSettingOrder(null); // 저장 완료 후 pending 상태 초기화

      // 빠른 순서 편집 갱신: 플래그 설정 (useEffect에서 감지)
      if (columnSettingOpen) {
        setNeedDraftRefresh(true);
      }

      toast.success('저장되었습니다.');
    } catch (err) {
      console.error('[REORDER] Error:', err);
      toast.error('화면설정 순서 변경에 실패했습니다.');
    } finally {
      setScreenSettingBusy(false);
    }
  };

  const normalizeArticleRows = (rows, tradeType) => {
    const normalizedType = tradeType === 'LEASE' ? 'LEASE' : 'SALE';
    const list = Array.isArray(rows) ? rows : [];
    return list
      .map((row) => ({
        tradeType: normalizedType,
        rankNo: Number(row?.rankNo ?? row?.rank_no ?? 0) || 0,
        articleNo: String(row?.articleNo ?? row?.article_no ?? '').trim(),
        articleText: String(row?.articleText ?? row?.article_text ?? '').trim(),
      }))
      .sort((a, b) => a.rankNo - b.rankNo)
      .slice(0, 5);
  };

  const handleSelectRowArticle = async (row) => {
    const fixId = Number(row?.fixId ?? row?.fix_id);
    if (!Number.isFinite(fixId) || fixId <= 0) {
      setArticlePanelOpen(true);
      setArticlePanelFixId(null);
      setArticlePanelComplexName(String(row?.buildingName || '').trim());
      setArticlePanelItems({ SALE: [], LEASE: [] });
      setArticlePanelError('매물 정보를 찾을 수 없습니다.');
      return;
    }

    setArticlePanelOpen(true);
    setArticlePanelFixId(fixId);
    setArticlePanelComplexName(String(row?.buildingName || '').trim());
    setArticlePanelError('');
    setArticlePanelTradeType('SALE');
    setArticlePanelLoading(true);

    try {
      const response = await getRankerFixArticles(fixId);
      const saleRows = normalizeArticleRows(response?.saleItems ?? response?.sale_items, 'SALE');
      const leaseRows = normalizeArticleRows(response?.leaseItems ?? response?.lease_items, 'LEASE');
      setArticlePanelItems({ SALE: saleRows, LEASE: leaseRows });
    } catch {
      setArticlePanelItems({ SALE: [], LEASE: [] });
      setArticlePanelError('매물 정보를 불러오지 못했습니다.');
    } finally {
      setArticlePanelLoading(false);
    }
  };

  const parseFixArticleText = (articleText) => {
    const parts = String(articleText || '')
      .split('/')
      .map((v) => String(v || '').trim());

    const rawAmount = parts[0] || '';
    const numericAmount = Number(rawAmount.replace(/[^\d.-]/g, ''));
    const formattedAmount = Number.isFinite(numericAmount) && numericAmount > 0
      ? formatKrw(numericAmount, useKoreanPriceUnit)
      : (rawAmount || '-');

    return {
      amount: formattedAmount,
      currentFloor: parts[2] || '-',
      totalFloor: parts[3] || '-',
      direction: parts[4] || '-',
    };
  };

  const filteredItems = useMemo(() => {
    if (!Array.isArray(items)) return [];

    const fromYear = Number(builtDateFrom);
    const toYear = Number(builtDateTo);

    const filteredDetails = items
      .filter((item) => !isAggregateRow(item))
      .filter((item) => {
      // Keep filter option rows for dropdown options, but they won't display
      if (item?.isFilterOptionRow) return true;
      const year = Number(String(item?.useApproveDate || '').slice(0, 4));

      if (Number.isFinite(fromYear) && fromYear > 0 && Number.isFinite(year) && year < fromYear) return false;
      if (Number.isFinite(toYear) && toYear > 0 && Number.isFinite(year) && year > toYear) return false;

      const familyCnt = Number(item?.familyCnt || 0);

      if (hasManualHouseholdFilter) {
        if (familyCnt < effectiveHouseholdMin || familyCnt > effectiveHouseholdMax) return false;
      } else if (selectedHouseholds.length > 0) {
        const ok = selectedHouseholds.some((option) => (
          (option === '~300' && familyCnt <= 300)
          || (option === '300~1000' && familyCnt > 300 && familyCnt <= 1000)
          || (option === '1000~' && familyCnt > 1000)
        ));

        if (!ok) return false;
      }

      // 수동 평형(면적) 범위 필터는 서버(ptp_exclusive_area)에서 처리 → 클라 제외.
      // 버킷(~59/59~84/84~) 프리셋만 클라 유지.
      if (selectedAreaBuckets.length > 0) {
        const areaRange = getItemAreaRangeM2(item);
        if (!areaRange) return false;

        const matched = selectedAreaBuckets.some((bucketKey) => {
          const bucket = AREA_BUCKETS_M2.find((v) => v.key === bucketKey);
          if (!bucket) return false;

          const bucketMin = bucket.min == null ? 0 : bucket.min;
          const bucketMax = bucket.max == null ? Number.POSITIVE_INFINITY : bucket.max;
          return areaRange.max >= bucketMin && areaRange.min <= bucketMax;
        });

        if (!matched) return false;
      }

      const searchKeyword = String(appliedSearchComplexName || '').trim().toLowerCase();
      if (searchKeyword) {
        const buildingName = String(item?.buildingName || '').toLowerCase();
        if (!buildingName.includes(searchKeyword)) return false;
      }

      // 매매가, 투자금 필터는 서버 사이드에서 처리하므로 클라이언트 필터링 제거
      // hasTradePriceFilter, hasInvestPriceFilter 관련 로직 제거됨

      const activeHeaderFilters = Object.entries(tableHeaderFilters || {}).filter(([, raw]) => String(raw || '').trim());
      if (activeHeaderFilters.length > 0) {
        const matchedAll = activeHeaderFilters.every(([key, raw]) => {
          const normalizedKey = String(key || '').trim();
          return matchesHeaderFilter(item, normalizedKey, raw);
        });
        if (!matchedAll) return false;
      }

      if (isFavoritePage) {
        const code = normalizeDanjiCode(item?.naverDanjiCode || item?.NaverDanjiCode);
        if (!code || !favoriteGroupCodeSet.has(code)) return false;
        if (hasFavoriteGroupSelection && !selectedFavoriteDanjiCodeSet.has(code)) return false;
      }

      return true;
    });

    const aggregateRows = items.filter((item) => isAggregateRow(item));
    // 즐겨찾기 그룹평균 행은 그룹핑과 무관하게 항상 최상단에 표시
    const groupAvgRows = aggregateRows.filter(
      (row) => String(row?.aggregateLevel || '').toUpperCase() === 'GROUP'
    );

    // 단지제외(regionAvgOnly) 등으로 상세 단지행이 없으면, 지역평균 행을 붙일 대상이 없어
    // 아무것도 표시되지 않는다 → 지역평균 행 자체를 (레벨→지역명 순) 정렬해 그대로 노출.
    const nonGroupAggRows = aggregateRows.filter(
      (row) => String(row?.aggregateLevel || '').toUpperCase() !== 'GROUP'
    );
    const filterOptionRows = filteredDetails.filter((row) => row?.isFilterOptionRow);
    const realDetailRows = filteredDetails.filter((row) => !row?.isFilterOptionRow);
    if (realDetailRows.length === 0 && nonGroupAggRows.length > 0) {
      const sortedAgg = [...nonGroupAggRows].sort((a, b) => {
        const la = AGGREGATE_LEVEL_ORDER.indexOf(String(a?.aggregateLevel || '').toUpperCase());
        const lb = AGGREGATE_LEVEL_ORDER.indexOf(String(b?.aggregateLevel || '').toUpperCase());
        if (la !== lb) return la - lb;
        return String(a?.aggregateRegionName || '').localeCompare(String(b?.aggregateRegionName || ''), 'ko');
      });
      return [...groupAvgRows, ...sortedAgg, ...filterOptionRows];
    }

    if (aggregateRows.length === 0 || selectedGroupByLevels.length === 0) {
      return groupAvgRows.length > 0 ? [...groupAvgRows, ...filteredDetails] : filteredDetails;
    }

    const groupKeysByLevel = new Map();
    selectedGroupByLevels.forEach((level) => {
      const keySet = new Set();
      filteredDetails.forEach((row) => {
        const key = getAggregateGroupKey(row, level);
        if (key) keySet.add(key);
      });
      groupKeysByLevel.set(level, keySet);
    });

    const merged = [];
    const inserted = new Set();
    const aggregateMap = new Map(
      aggregateRows.map((row) => {
        const level = String(row?.aggregateLevel || '').toUpperCase();
        const key = getAggregateGroupKey(row, level);
        return [`${level}|${key}`, row];
      })
    );

    filteredDetails.forEach((row) => {
      selectedGroupByLevels.forEach((level) => {
        const key = getAggregateGroupKey(row, level);
        if (!key) return;
        const aggKey = `${level}|${key}`;
        if (inserted.has(aggKey)) return;
        if (!groupKeysByLevel.get(level)?.has(key)) return;
        const aggRow = aggregateMap.get(aggKey);
        if (!aggRow) return;
        inserted.add(aggKey);
        merged.push(aggRow);
      });
      merged.push(row);
    });

    return groupAvgRows.length > 0 ? [...groupAvgRows, ...merged] : merged;
  }, [
    items,
    builtDateFrom,
    builtDateTo,
    hasManualHouseholdFilter,
    effectiveHouseholdMin,
    effectiveHouseholdMax,
    selectedHouseholds,
    areaUnit,
    hasManualAreaFilter,
    effectiveAreaMinM2,
    effectiveAreaMaxM2,
    selectedAreaBuckets,
    appliedSearchComplexName,
    // 서버 사이드 필터링으로 변경되어 제거: hasTradePriceFilter, tradePriceMinValue, tradePriceMaxValue, hasInvestPriceFilter, investPriceMinValue, investPriceMaxValue
    tableHeaderFilters,
    selectedGroupByLevels,
    isFavoritePage,
    favoriteGroupCodeSet,
    hasFavoriteGroupSelection,
    selectedFavoriteDanjiCodeSet,
  ]);

  const handleSearchSubmit = () => {
    const nextKeyword = String(searchComplexName || '').trim();
    setAppliedSearchComplexName(nextKeyword);
    load(selectedBjdCodes, nextKeyword);
  };

  const getEstimatedPanelWidth = (key) => (
    key === 'region' ? 560
      : key === 'rankerFilter' ? 360
        : key === 'groupBy' ? 300
          : key === 'tradePrice' || key === 'investPrice' ? 360
          : key === 'household' ? 360
            : 420
  );

  const calculatePanelLeft = (key) => {
    const container = topFilterRef.current;
    const anchor = topFilterTabRefs.current[key];
    if (!container || !anchor) return topFilterPanelLeft;

    const containerRect = container.getBoundingClientRect();
    const anchorRect = anchor.getBoundingClientRect();
    const desiredLeft = anchorRect.left - containerRect.left;
    const estimatedPanelWidth = getEstimatedPanelWidth(key);

    const edgePadding = 8;
    const maxLeft = Math.max(edgePadding, container.clientWidth - estimatedPanelWidth - edgePadding);
    return Math.min(Math.max(desiredLeft, edgePadding), maxLeft);
  };

  const handleTopFilterToggle = (key) => {
    setOpenTopFilterKey((prev) => {
      const next = prev === key ? null : key;
      if (next) {
        setTopFilterPanelLeft(calculatePanelLeft(next));
      }
      return next;
    });
  };

  const handleFilterClearClick = (event, clearFn) => {
    event.preventDefault();
    event.stopPropagation();
    clearFn();
  };

  useEffect(() => {
    const syncPanelLeft = () => {
      if (!openTopFilterKey) return;
      setTopFilterPanelLeft(calculatePanelLeft(openTopFilterKey));
    };

    syncPanelLeft();
    window.addEventListener('resize', syncPanelLeft);
    return () => window.removeEventListener('resize', syncPanelLeft);
  }, [openTopFilterKey]);

  useEffect(() => {
    if (!openTopFilterKey) return undefined;

    const onOutsidePointerDown = (event) => {
      const container = topFilterRef.current;
      if (!container) return;
      if (!container.contains(event.target)) {
        setOpenTopFilterKey(null);
      }
    };

    document.addEventListener('mousedown', onOutsidePointerDown);
    return () => document.removeEventListener('mousedown', onOutsidePointerDown);
  }, [openTopFilterKey]);

  useEffect(() => {
    if (openTopFilterKey === 'region' || openTopFilterKey === 'rankerFilter') {
      setOpenTopFilterKey(null);
    }
  }, [openTopFilterKey]);

  const toggleExpandedGroup = (groupName) => {
    startTreeTransition(() => {
      setExpandedGroups((prev) => ({ ...prev, [groupName]: !prev[groupName] }));
    });
  };

  const toggleExpandedSido = (sidoKey) => {
    startTreeTransition(() => {
      setExpandedSido((prev) => ({ ...prev, [sidoKey]: !prev[sidoKey] }));
    });
  };

  const toggleExpandedSigungu = (sigunguKey) => {
    startTreeTransition(() => {
      setExpandedSigungu((prev) => ({ ...prev, [sigunguKey]: !prev[sigunguKey] }));
    });
  };

  const renderSigunguNode = (sigungu, parentKey, labelPrefix = '') => {
    const sigunguKey = `${parentKey}|${sigungu?.sigunguCode || sigungu?.sigunguName || ''}`;
    const sigunguCodes = getSigunguCodes(sigungu);
    const label = [labelPrefix, sigungu?.sigunguName].filter(Boolean).join(' ');
    const badgeTexts = getSigunguBadgeTexts(sigungu);
    const populationText = getSigunguPopulationText(sigungu);

    return (
      <div key={sigunguKey}>
        <div
          className="tree-sigungu-row"
          onClick={() => toggleExpandedSigungu(sigunguKey)}
        >
          <label onClick={(e) => e.stopPropagation()}>
            <input
              type="checkbox"
              checked={sigunguCodes.length > 0 && sigunguCodes.every((code) => selectedBjdCodeSet.has(code))}
              onChange={() => toggleCodes(sigunguCodes)}
              onClick={(e) => e.stopPropagation()}
            />
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', width: '100%', flex: 1, minWidth: 0 }}>
              <span>{label || '-'}</span>
              {showRegulationFilters && badgeTexts.length > 0 && (
                <span style={{ display: 'inline-flex', gap: '6px', alignItems: 'center' }}>
                  {badgeTexts.map((text) => (
                    <span
                      key={`${sigunguKey}-${text}`}
                      style={{
                        fontSize: '10px',
                        fontWeight: 800,
                        lineHeight: '1',
                        color: text === '조정'
                          ? '#dc2626'
                          : text === '투기과열'
                            ? '#2563eb'
                            : '#0f766e'
                      }}
                    >
                      {text}
                    </span>
                  ))}
                </span>
              )}
              {showPopulationFilters && populationText && (
                <span style={{ fontSize: '12px', color: '#475569', marginLeft: 'auto', whiteSpace: 'nowrap' }}>
                  {populationText}
                </span>
              )}
            </span>
          </label>
          <span className="tree-expand-indicator">{expandedSigungu[sigunguKey] ? '−' : '+'}</span>
        </div>

        {expandedSigungu[sigunguKey] && (
          <div className="tree-dong-list">
            {(sigungu.bjdList || []).map((dong) => {
              const code = String(dong?.bjdCode || '');
              const checked = selectedBjdCodeSet.has(code);

              return (
                <label key={code} className="tree-dong-item" style={{ display: 'flex', alignItems: 'center', width: '100%', gap: '6px' }}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => setSelectedBjdCodes((prev) => (checked ? prev.filter((v) => v !== code) : [...prev, code]))}
                  />
                  <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', width: '100%', flex: 1, minWidth: 0 }}>
                    <span>{dong?.bjdName || '-'}</span>
                    {showPopulationFilters && getDongPopulationText(dong) && (
                      <span style={{ fontSize: '12px', color: '#475569', whiteSpace: 'nowrap' }}>
                        {getDongPopulationText(dong)}
                      </span>
                    )}
                  </span>
                </label>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderGroupChildren = (group, isFilteredTree = true) => {
    const isSeoulGroup = String(group?.groupName || '').trim() === '서울특별시';

    if (isSeoulGroup) {
      return (group?.sidoList || []).flatMap((sido) => {
        const sidoKey = String(sido?.sidoCode || sido?.sidoName || '');
        const sigunguList = Array.isArray(sido?.sigunguList) ? sido.sigunguList : [];
        return sigunguList.map((sigungu) => {
          const sigunguKey = String(sigungu?.sigunguCode || sigungu?.sigunguName || '');
          return (
            <div key={`${sidoKey}|${sigunguKey}`} className="tree-sigungu">
              {renderSigunguNode(sigungu, sidoKey)}
            </div>
          );
        });
      });
    }

    return (group?.sidoList || []).map((sido) => {
      const sidoKey = String(sido?.sidoCode || sido?.sidoName || '');
      const sigunguList = Array.isArray(sido?.sigunguList) ? sido.sigunguList : [];
      const mapKey = `${String(group?.groupName || '')}|${sidoKey}`;
      const sidoCodes = isFilteredTree
        ? (filteredSidoCodesMap.get(mapKey) || getSidoCodes(sido))
        : getSidoCodes(sido);
      const directSigungu = sigunguList.length === 1
        && String(sido?.sidoName || '').trim()
        && String(sigunguList[0]?.sigunguName || '').trim()
        && String(sido?.sidoName || '').trim() === String(sigunguList[0]?.sigunguName || '').trim();

      if (directSigungu) {
        return (
          <div key={sidoKey} className="tree-sigungu">
            {renderSigunguNode(sigunguList[0], sidoKey)}
          </div>
        );
      }

      return (
        <div key={sidoKey} className="tree-sigungu">
          <div
            className="tree-sigungu-row"
            onClick={() => toggleExpandedSido(sidoKey)}
          >
            <label onClick={(e) => e.stopPropagation()}>
              <input
                type="checkbox"
                checked={sidoCodes.length > 0 && sidoCodes.every((code) => selectedBjdCodeSet.has(code))}
                onChange={() => toggleCodes(sidoCodes)}
                onClick={(e) => e.stopPropagation()}
              />
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', width: '100%', flex: 1, minWidth: 0 }}>
                <span>{sido?.sidoName || '-'}</span>
                {showPopulationFilters && getSidoPopulationText(sido) && (
                  <span style={{ fontSize: '12px', color: '#475569', marginLeft: 'auto', whiteSpace: 'nowrap' }}>
                    {getSidoPopulationText(sido)}
                  </span>
                )}
              </span>
            </label>
            <span className="tree-expand-indicator">{expandedSido[sidoKey] ? '−' : '+'}</span>
          </div>

          {expandedSido[sidoKey] && (
            <div className="tree-dong-list">
              {sigunguList.map((sigungu) => renderSigunguNode(sigungu, sidoKey))}
            </div>
          )}
        </div>
      );
    });
  };


  const saleRecent8Dates = useMemo(() => collectRecentDates(items, 'sale8', 8), [items]);
  const saleRecent6Dates = useMemo(() => collectRecentDates(items, 'sale6', 6), [items]);
  const leaseRecent8Dates = useMemo(() => collectRecentDates(items, 'lease8', 8), [items]);
  const leaseRecent6Dates = useMemo(() => collectRecentDates(items, 'lease6', 6), [items]);
  const saleTxRecent8Dates = useMemo(() => collectRecentDates(items, 'saleTx8', 8), [items]);
  const saleTxRecent6Dates = useMemo(() => collectRecentDates(items, 'saleTx6', 6), [items]);
  const leaseTxRecent8Dates = useMemo(() => collectRecentDates(items, 'leaseTx8', 8), [items]);
  const leaseTxRecent6Dates = useMemo(() => collectRecentDates(items, 'leaseTx6', 6), [items]);
  const saleCountRecent8Dates = useMemo(() => collectRecentDates(items, 'saleCount8', 8), [items]);
  const saleCountRecent6Dates = useMemo(() => collectRecentDates(items, 'saleCount6', 6), [items]);
  const leaseCountRecent8Dates = useMemo(() => collectRecentDates(items, 'leaseCount8', 8), [items]);
  const leaseCountRecent6Dates = useMemo(() => collectRecentDates(items, 'leaseCount6', 6), [items]);
  const saleTxCountRecent8Dates = saleCountRecent8Dates;
  const saleTxCountRecent6Dates = saleCountRecent6Dates;
  const leaseTxCountRecent8Dates = leaseCountRecent8Dates;
  const leaseTxCountRecent6Dates = leaseCountRecent6Dates;
  const saleRecent8HeadDate = saleRecent8Dates[0] || '';
  const saleRecent6HeadDate = saleRecent6Dates[0] || '';
  const leaseRecent8HeadDate = leaseRecent8Dates[0] || '';
  const leaseRecent6HeadDate = leaseRecent6Dates[0] || '';
  const saleTxRecent8HeadDate = saleTxRecent8Dates[0] || '';
  const saleTxRecent6HeadDate = saleTxRecent6Dates[0] || '';
  const leaseTxRecent8HeadDate = leaseTxRecent8Dates[0] || '';
  const leaseTxRecent6HeadDate = leaseTxRecent6Dates[0] || '';
  const saleCountRecent8HeadDate = saleCountRecent8Dates[0] || '';
  const saleCountRecent6HeadDate = saleCountRecent6Dates[0] || '';
  const leaseCountRecent8HeadDate = leaseCountRecent8Dates[0] || '';
  const leaseCountRecent6HeadDate = leaseCountRecent6Dates[0] || '';
  const saleTxCountRecent8HeadDate = saleTxCountRecent8Dates[0] || '';
  const saleTxCountRecent6HeadDate = saleTxCountRecent6Dates[0] || '';
  const leaseTxCountRecent8HeadDate = leaseTxCountRecent8Dates[0] || '';
  const leaseTxCountRecent6HeadDate = leaseTxCountRecent6Dates[0] || '';

  // 선택 컬럼 전체선택 상태/토글 (비교 대상)
  const compareSelectableItems = (filteredItems || []).filter(
    (it) => !isAggregateRow(it) && !it?.isFilterOptionRow
  );
  const allCompareSelected = compareSelectableItems.length > 0 && compareSelectableItems.every((it) => isCompared(it));
  const someCompareSelected = compareSelectableItems.some((it) => isCompared(it));
  const toggleAllCompare = () => {
    if (allCompareSelected) {
      setCompareTargets((prev) => prev.filter((t) => !compareSelectableItems.some(
        (it) => (it?.fixId ?? it?.fix_id) === t.fixId && (it?.ptpNo ?? it?.ptp_no) === t.ptpNo
      )));
      return;
    }
    setCompareTargets((prev) => {
      const next = [...prev];
      compareSelectableItems.forEach((it) => {
        const fixId = it?.fixId ?? it?.fix_id;
        const ptpNo = it?.ptpNo ?? it?.ptp_no;
        if (next.some((t) => t.fixId === fixId && t.ptpNo === ptpNo)) return;
        const code = normalizeDanjiCode(it?.naverDanjiCode || it?.naver_danji_code || it?.danji_code);
        if (!code) return;
        // 타입(planTypeName) 대신 실제 평으로 라벨 계산 (상세모달과 동일하게 면적→평 변환)
        const areaRangeM2 = getItemAreaRangeM2(it);
        let pyeongLabel = '';
        if (areaRangeM2) {
          const pMin = Math.round(areaRangeM2.min / AREA_M2_PER_PYEONG);
          const pMax = Math.round(areaRangeM2.max / AREA_M2_PER_PYEONG);
          pyeongLabel = (pMin === pMax) ? `${pMin}평` : `${pMin}~${pMax}평`;
        }
        next.push({
          code, fixId, ptpNo,
          name: String(it?.buildingName || '').trim() || code,
          planTypeName: String(it?.planTypeName || '').trim(),
          planTypeName2: String(it?.planTypeName2 || it?.plan_type_name2 || '').trim(),
          pyeongLabel,
          address: it?.addressRoad || it?.roadAddress || it?.address || it?.jibunAddress || '',
          familyCnt: it?.familyCnt ?? it?.households ?? it?.householdCount ?? null,
          useApproveDate: it?.useApproveDate || it?.useApprovalDate || '',
        });
      });
      return next;
    });
  };

  const tableColumns = [
    {
      key: 'compareSelect',
      label: '',
      sortable: false,
      fixed: true,
      width: 30,
      minWidth: 30,
      maxWidth: 30,
      resizable: false,
      className: 'complex-compare-col',
      headerClassName: 'complex-compare-head',
      headerComponent: CompareSelectAllHeader,
      headerComponentParams: {
        checked: allCompareSelected,
        indeterminate: someCompareSelected && !allCompareSelected,
        onToggle: toggleAllCompare,
      },
      render: (item) => {
        if (isAggregateRow(item)) return null;
        const checked = isCompared(item);

        return (
          <input
            type="checkbox"
            className="complex-compare-checkbox"
            checked={checked}
            onChange={(e) => handleToggleCompareTarget(item, e)}
            onClick={(e) => e.stopPropagation()}
            aria-label="비교 대상 선택"
          />
        );
      },
    },
    {
      key: 'gubun',
      label: '구분',
      sortable: true,
      filterable: true,
      sortValue: (row) => row?.gubun || '',
      render: (item) => (isAggregateRow(item) ? '평균/합계' : (item?.gubun || '')),
      className: 'complex-gubun-col',
      headerClassName: 'complex-gubun-head',
      size: 144,
      minSize: 120,
      maxSize: 280,
    },
    {
      key: 'buildingName',
      label: '단지명',
      sortable: true,
      sortValue: (row) => row?.buildingName || '',
      render: (item) => {
        if (isAggregateRow(item)) {
          return (
            <div className="complex-name-cell">
              <span className="complex-name-text">
                {item?.aggregateRegionName || item?.buildingName || '-'}
              </span>
            </div>
          );
        }

        const handleNameClick = (e) => {
          e.stopPropagation();
          const code = normalizeDanjiCode(item?.naverDanjiCode);
          if (!code) return;
          setSelectedDanjiSummaryForModal(item || null);
          setSelectedDanjiCodeForModal(code);
        };

        const handleNaverJump = (e) => {
          e.stopPropagation();
          const code = normalizeDanjiCode(item?.naverDanjiCode);
          if (!code) return;
          window.open(`https://new.land.naver.com/complexes/${code}`, '_blank', 'noopener,noreferrer');
        };

        return (
          <div className="complex-name-cell" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <button
              type="button"
              className="complex-name-naver-btn"
              title="네이버 부동산에서 보기"
              aria-label="네이버에서 보기"
              onClick={handleNaverJump}
              style={{
                flex: '0 0 auto',
                width: '17px',
                height: '17px',
                border: 'none',
                borderRadius: '4px',
                background: '#03c75a',
                color: '#fff',
                fontWeight: 800,
                fontSize: '11px',
                lineHeight: '17px',
                textAlign: 'center',
                padding: 0,
                cursor: 'pointer',
              }}
            >
              N
            </button>
            <button
              type="button"
              className="complex-name-link"
              onClick={handleNameClick}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                color: '#2563eb',
                textDecoration: 'underline',
                cursor: 'pointer',
                font: 'inherit',
                textAlign: 'left'
              }}
            >
              {item?.buildingName || '-'}
            </button>
          </div>
        );
      },
    },
    {
      key: 'region',
      label: '지역',
      sortable: true,
      sortValue: (row) => getRegionSortValue(row),
      render: (item) => {
        const dong = formatLivingZoneLabel(item);        // 동 (예: 성수동1가)
        const region = formatRegionLabel(item) || '-';   // 시/구 (예: 서울 성동구)
        const hasDong = dong && dong !== '-';
        return (
          <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '2px', lineHeight: 1.2, width: '100%' }}>
            <span style={{ fontSize: '14px', fontFamily: "'Pretendard Variable', 'Pretendard', sans-serif" }}>{hasDong ? dong : region}</span>
            {hasDong ? <span style={{ fontSize: '12px', color: '#6b7280' }}>{region}</span> : null}
          </span>
        );
      },
      className: 'complex-region-col',
      headerClassName: 'complex-region-head',
    },
    // 생활권(livingZone)은 지역 컬럼에 (생활권)으로 병합됨. 컬럼 정의는 하위호환용으로 유지(그룹에는 미포함).
    {
      key: 'livingZone',
      label: '생활권',
      sortable: true,
      sortValue: (row) => formatLivingZoneLabel(row),
      render: (item) => formatLivingZoneLabel(item),
      className: 'complex-living-zone-col',
      headerClassName: 'complex-living-zone-head',
    },
    {
      key: 'useApproveDate',
      label: '연식\r\n(연차)',
      sortable: true,
      filterable: true,
      sortValue: (row) => toYear(row?.useApproveDate) || 0,
      // 연식(위) + 연차(아래) 2줄 — 지역 컬럼 스타일과 동일
      render: (item) => {
        if (isAggregateRow(item)) return '-';
        const year = toYear(item?.useApproveDate);
        const age = getUseApproveAge(item?.useApproveDate);
        const yearText = year ? `${year}` : '-';
        const ageText = age == null ? '-' : `(${age}${useKoreanPriceUnit ? '년차' : ''})`;
        return (
          <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '2px', lineHeight: 1.2, width: '100%' }}>
            <span style={{ fontSize: '14px', fontFamily: "'Pretendard Variable', 'Pretendard', sans-serif" }}>{yearText}</span>
            <span style={{ fontSize: '12px', color: '#6b7280' }}>{ageText}</span>
          </span>
        );
      },
    },
    {
      key: 'useApproveAge',
      label: '연차',
      sortable: true,
      sortValue: (row) => getUseApproveAge(row?.useApproveDate) || 0,
      render: (item) => {
        if (isAggregateRow(item)) return '-';
        const age = getUseApproveAge(item?.useApproveDate);
        return age == null ? '-' : `${age}${useKoreanPriceUnit ? '년차' : ''}`;
      },
    },
    {
      key: 'familyCnt',
      label: '세대수\r\n(평세대수)',
      sortable: true,
      filterable: true,
      sortValue: (row) => Number(row?.familyCnt || 0),
      render: (item) => {
        const total = item?.familyCnt ? `${Number(item.familyCnt).toLocaleString('ko-KR')}${useKoreanPriceUnit ? '세대' : ''}` : '-';
        const ptp = item?.ptpFamilyCnt ? `${Number(item.ptpFamilyCnt).toLocaleString('ko-KR')}${useKoreanPriceUnit ? '세대' : ''}` : '';
        return (
          <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '2px', lineHeight: 1.2, width: '100%' }}>
            <span style={{ fontSize: '14px', fontFamily: "'Pretendard Variable', 'Pretendard', sans-serif" }}>{total}</span>
            {ptp ? <span style={{ fontSize: '12px', color: '#6b7280' }}>{ptp}</span> : null}
          </span>
        );
      },
    },
    // 세대수(평)(ptpFamilyCnt)은 세대수 컬럼에 (평세대수)로 병합됨. 컬럼 정의는 하위호환용으로 유지(그룹에는 미포함).
    {
      key: 'ptpFamilyCnt',
      label: '세대수\r\n(평)',
      sortable: true,
      sortValue: (row) => Number(row?.ptpFamilyCnt || 0),
      render: (item) => (item?.ptpFamilyCnt ? `${Number(item.ptpFamilyCnt).toLocaleString('ko-KR')}${useKoreanPriceUnit ? '세대' : ''}` : '-'),
    },
    {
      key: 'rentalCnt',
      label: '임대수',
      sortable: true,
      sortValue: (row) => Number(row?.rentalCnt || 0),
      render: (item) => (item?.rentalCnt ? `${Number(item.rentalCnt).toLocaleString('ko-KR')}${useKoreanPriceUnit ? '세대' : ''}` : '-'),
    },
    {
      key: 'sigunguGrade',
      label: '지역급지\r\n(생활권)',
      sortable: true,
      filterable: true,
      sortValue: (row) => Number(row?.sigunguGrade || 0),
      render: (item) => {
        const sigungu = item?.sigunguGrade == null ? '-' : `${item.sigunguGrade}${useKoreanPriceUnit ? '급지' : ''}`;
        const dong = item?.dongGrade == null ? '' : `${item.dongGrade}${useKoreanPriceUnit ? '급지' : ''}`;
        return (
          <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '2px', lineHeight: 1.2, width: '100%' }}>
            <span style={{ fontSize: '14px', fontFamily: "'Pretendard Variable', 'Pretendard', sans-serif" }}>{sigungu}</span>
            {dong ? <span style={{ fontSize: '12px', color: '#6b7280' }}>{dong}</span> : null}
          </span>
        );
      },
    },
    // 급지(생활권)(dongGrade)은 지역급지 컬럼에 (생활권급지)로 병합됨. 컬럼 정의는 하위호환용으로 유지(그룹에는 미포함).
    {
      key: 'dongGrade',
      label: '급지\r\n(생활권)',
      sortable: true,
      filterable: true,
      sortValue: (row) => Number(row?.dongGrade || 0),
      render: (item) => {
        if (item?.dongGrade == null) return '-';
        return `${item.dongGrade}${useKoreanPriceUnit ? '급지' : ''}`;
      },
    },
    {
      key: 'riseRate',
      label: '상승률',
      sortable: true,
      filterable: true,
      sortValue: (row) => Number(row?.riseRate || 0),
      render: (item) => {
        const rate = item?.riseRate;
        if (rate == null || rate === '') return '-';
        const numRate = Number(rate);
        if (!Number.isFinite(numRate)) return '-';
        const formatted = `${numRate.toFixed(2)}%`;
        if (numRate > 0) return <span style={{ color: '#ef4444' }}>+{formatted}</span>;
        if (numRate < 0) return <span style={{ color: '#3b82f6' }}>{formatted}</span>;
        return formatted;
      },
    },
    {
      key: 'risePrice',
      label: '상승가',
      sortable: true,
      filterable: true,
      sortValue: (row) => Number(row?.risePrice || 0),
      render: (item) => {
        const price = item?.risePrice;
        if (price == null) return '-';
        const formatted = formatKrw(price, useKoreanPriceUnit);
        if (price > 0) return <span style={{ color: '#ef4444' }}>+{formatted}</span>;
        if (price < 0) return <span style={{ color: '#3b82f6' }}>{formatted}</span>;
        return formatted;
      },
    },
    {
      key: 'pastPrice',
      label: '과거가',
      sortable: true,
      sortValue: (row) => Number(row?.pastPrice || 0),
      render: (item) => formatKrw(item?.pastPrice, useKoreanPriceUnit),
    },
    {
      key: 'supply3Years',
      label: '공급\r\n(3년)',
      sortable: true,
      filterable: true,
      sortValue: (row) => getMoveinSupply3Years(row),
      render: (item) => {
        if (!isRegionAverageRow(item)) return '-';
        const value = getMoveinSupply3Years(item);
        return `${value.toLocaleString('ko-KR')}${useKoreanPriceUnit ? '세대' : ''}`;
      },
    },
    {
      key: 'demand3Years',
      label: '수요\r\n(3년)',
      sortable: true,
      sortValue: (row) => getDemand3Years(row),
      render: (item) => {
        if (!isRegionAverageRow(item)) return '-';
        const value = getDemand3Years(item);
        return `${value.toLocaleString('ko-KR')}${useKoreanPriceUnit ? '세대' : ''}`;
      },
    },
    {
      key: 'movein3Years',
      label: '입주\r\n(3년)',
      sortable: true,
      sortValue: (row) => getMoveinSupply3Years(row),
      render: (item) => {
        if (!isRegionAverageRow(item)) return '-';
        const value = getMoveinSupply3Years(item);
        return `${value.toLocaleString('ko-KR')}${useKoreanPriceUnit ? '세대' : ''}`;
      },
    },
    {
      key: 'supply2026',
      label: '2026년',
      sortable: true,
      sortValue: (row) => getSupplyYearValue(row, 2026),
      render: (item) => (isRegionAverageRow(item) ? `${getSupplyYearValue(item, 2026).toLocaleString('ko-KR')}${useKoreanPriceUnit ? '세대' : ''}` : '-'),
    },
    {
      key: 'supply2027',
      label: '2027년',
      sortable: true,
      sortValue: (row) => getSupplyYearValue(row, 2027),
      render: (item) => (isRegionAverageRow(item) ? `${getSupplyYearValue(item, 2027).toLocaleString('ko-KR')}${useKoreanPriceUnit ? '세대' : ''}` : '-'),
    },
    {
      key: 'supply2028',
      label: '2028년',
      sortable: true,
      sortValue: (row) => getSupplyYearValue(row, 2028),
      render: (item) => (isRegionAverageRow(item) ? `${getSupplyYearValue(item, 2028).toLocaleString('ko-KR')}${useKoreanPriceUnit ? '세대' : ''}` : '-'),
    },
    {
      key: 'planTypeName',
      label: '평형\r\n(구조)',
      sortable: true,
      filterable: true,
      sortValue: (row) => getPtpNameText(row),
      render: (item) => {
        const ptp = getPtpNameText(item) || '-';
        const rawStruct = String(item?.houseStructure || '').trim();
        // 단위표기 ON 이면 풀어서(계단식 방3 화장실2), 아니면 원문 그대로(계32)
        const struct = useKoreanPriceUnit ? formatHouseStructure(item?.houseStructure) : rawStruct;
        const hasStruct = struct && struct !== '-';
        return (
          <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '2px', lineHeight: 1.2, width: '100%' }}>
            <span style={{ fontSize: '14px', fontFamily: "'Pretendard Variable', 'Pretendard', sans-serif" }}>{ptp}</span>
            {hasStruct ? <span style={{ fontSize: '12px', color: '#6b7280' }}>{struct}</span> : null}
          </span>
        );
      },
    },
    // 구조(houseStructure)은 평형 컬럼에 (구조)로 병합됨. 컬럼 정의는 하위호환용으로 유지(그룹에는 미포함).
    {
      key: 'houseStructure',
      label: '구조',
      sortable: true,
      filterable: true,
      sortValue: (row) => row?.houseStructure || '',
      render: (item) => (item?.houseStructure || '-'),
    },
    {
      key: 'pastTopPrice',
      label: '최고가',
      sortable: true,
      filterable: true,
      sortValue: (row) => Number(row?.pastTopPrice || 0),
      render: (item) => formatKrw(item?.pastTopPrice, useKoreanPriceUnit),
    },
    {
      key: 'prevHighPrice',
      label: '전고가',
      sortable: true,
      filterable: true,
      sortValue: (row) => Number(row?.prevHighPrice || 0),
      render: (item) => formatKrw(item?.prevHighPrice, useKoreanPriceUnit),
    },
    {
      key: 'properHighPrice',
      label: '적고가★',
      sortable: true,
      filterable: true,
      sortValue: (row) => Number(row?.properHighPrice || 0),
      render: (item) => formatKrw(item?.properHighPrice, useKoreanPriceUnit),
    },
    {
      key: 'prevLowPrice',
      label: '전저가',
      sortable: true,
      filterable: true,
      sortValue: (row) => Number(row?.prevLowPrice || 0),
      render: (item) => formatKrw(item?.prevLowPrice, useKoreanPriceUnit),
    },
    {
      key: 'targetPrice',
      label: '타겟가',
      sortable: true,
      sortValue: (row) => Number(row?.targetPrice || 0),
      render: (item) => formatKrw(item?.targetPrice, useKoreanPriceUnit),
    },
    {
      key: 'safeBuyPrice',
      label: '희망매수가',
      sortable: true,
      headerComponent: SafeMarginHeader,
      headerComponentParams: { marginType: 'buy', onOpenMargin: openMarginPopover },
      sortValue: (row) => Number(row?.safeBuyPrice || 0),
      render: (item) => formatKrw(item?.safeBuyPrice, useKoreanPriceUnit),
    },
    {
      key: 'safeSellPrice',
      label: '희망매도가',
      sortable: true,
      headerComponent: SafeMarginHeader,
      headerComponentParams: { marginType: 'sell', onOpenMargin: openMarginPopover },
      sortValue: (row) => Number(row?.safeSellPrice || 0),
      render: (item) => formatKrw(item?.safeSellPrice, useKoreanPriceUnit),
    },
    {
      key: 'capitalGain',
      label: '양도차액',
      sortable: true,
      sortValue: (row) => Number(row?.capitalGain || 0),
      render: (item) => {
        const value = Number(item?.capitalGain);
        if (!Number.isFinite(value) || value === 0) return '-';
        return formatSignedKrw(value, useKoreanPriceUnit);
      },
    },
    {
      key: 'netProfit',
      label: '순수익',
      sortable: true,
      sortValue: (row) => Number(row?.netProfit || 0),
      render: (item) => {
        const value = Number(item?.netProfit);
        if (!Number.isFinite(value) || value === 0) return '-';
        return formatSignedKrw(value, useKoreanPriceUnit);
      },
    },
    {
      key: 'brokerageFee',
      label: '필요경비',
      sortable: true,
      sortValue: (row) => Number(row?.brokerageFee || 0),
      render: (item) => formatKrw(item?.brokerageFee, useKoreanPriceUnit),
    },
    {
      key: 'acquisitionTax',
      label: '취득세',
      sortable: true,
      sortValue: (row) => Number(row?.acquisitionTax ?? row?.acquisition_tax ?? 0),
      render: (item) => formatKrw(item?.acquisitionTax ?? item?.acquisition_tax, useKoreanPriceUnit),
    },
    {
      key: 'capitalGainsTax',
      label: '양도세',
      sortable: true,
      sortValue: (row) => Number(row?.capitalGainsTax ?? row?.capital_gains_tax ?? 0),
      render: (item) => formatKrw(item?.capitalGainsTax ?? item?.capital_gains_tax, useKoreanPriceUnit),
    },
    {
      key: 'goHyunRate',
      label: '고현율',
      sortable: true,
      filterable: true,
      sortValue: (row) => Number(row?.goHyunRate || -1),
      render: (item) => {
        const rate = Number(item?.goHyunRate);
        if (Number.isFinite(rate) && rate !== 0) return formatRatioPercent(rate);
        return '-';
      },
    },
    {
      key: 'goJeoRate',
      label: '고저율',
      sortable: true,
      filterable: true,
      sortValue: (row) => Number(row?.goJeoRate || -1),
      render: (item) => {
        const rate = Number(item?.goJeoRate);
        if (Number.isFinite(rate) && rate !== 0) return formatRatioPercent(rate);
        return '-';
      },
    },
    {
      key: 'jeoHyunRate',
      label: '저현율',
      sortable: true,
      filterable: true,
      sortValue: (row) => Number(row?.jeoHyunRate || -1),
      render: (item) => {
        const rate = Number(item?.jeoHyunRate);
        if (Number.isFinite(rate) && rate !== 0) return formatRatioPercent(rate);
        return '-';
      },
    },
    {
      key: 'jeokHyunRate',
      label: '적현율★',
      sortable: true,
      filterable: true,
      sortValue: (row) => Number(row?.jeokHyunRate || -1),
      render: (item) => {
        const rate = Number(item?.jeokHyunRate);
        if (Number.isFinite(rate) && rate !== 0) return formatRatioPercent(rate);
        return '-';
      },
    },
    {
      key: 'changeGap',
      label: '변동차',
      sortable: true,
      filterable: true,
      sortValue: (row) => Number(row?.changeGap || -999999999),
      render: (item) => {
        const gap = Number(item?.changeGap);
        if (Number.isFinite(gap)) return formatKrw(gap, useKoreanPriceUnit);
        return '-';
      },
    },
    {
      key: 'articleTradePrice',
      label: '매매가',
      sortable: true,
      filterable: true,
      sortValue: (row) => Number(row?.articleTradePrice || 0),
      render: (item) => formatKrw(item?.articleTradePrice, useKoreanPriceUnit),
    },
    {
      key: 'complexMinTradePrice',
      label: '매매\r\n(최저)',
      sortable: true,
      filterable: true,
      sortValue: (row) => Number(row?.complexMinTradePrice ?? row?.complex_min_trade_price ?? 0),
      render: (item) => formatKrw(item?.complexMinTradePrice ?? item?.complex_min_trade_price, useKoreanPriceUnit),
    },
    {
      key: 'complexMaxTradePrice',
      label: '매매\r\n(최고)',
      sortable: true,
      filterable: true,
      sortValue: (row) => Number(row?.complexMaxTradePrice ?? row?.complex_max_trade_price ?? 0),
      render: (item) => formatKrw(item?.complexMaxTradePrice ?? item?.complex_max_trade_price, useKoreanPriceUnit),
    },
    {
      key: 'saleChange6Months',
      label: '6개월',
      sortable: true,
      sortValue: (row) => Number(getSaleChangeApiAmount(row, '6m') || 0),
      render: (item) => {
        const diff = getSaleChangeApiAmount(item, '6m');
        if (diff == null || Number(diff) === 0) return '-';
        return <span style={{ color: diff < 0 ? '#2563eb' : undefined }}>{formatSignedKrw(diff, useKoreanPriceUnit)}</span>;
      },
    },
    {
      key: 'saleChange8Weeks',
      label: '8주',
      sortable: true,
      sortValue: (row) => Number(getSaleChangeApiAmount(row, '8w') || 0),
      render: (item) => {
        const diff = getSaleChangeApiAmount(item, '8w');
        if (diff == null || Number(diff) === 0) return '-';
        return <span style={{ color: diff < 0 ? '#2563eb' : undefined }}>{formatSignedKrw(diff, useKoreanPriceUnit)}</span>;
      },
    },
    {
      key: 'saleChange4Weeks',
      label: '4주',
      sortable: true,
      sortValue: (row) => Number(getSaleChangeApiAmount(row, '4w') || 0),
      render: (item) => {
        const diff = getSaleChangeApiAmount(item, '4w');
        if (diff == null || Number(diff) === 0) return '-';
        return <span style={{ color: diff < 0 ? '#2563eb' : undefined }}>{formatSignedKrw(diff, useKoreanPriceUnit)}</span>;
      },
    },
    {
      key: 'articleTradePriceRecent8Toggle',
      label: getRecentToggleLabel({ expanded: saleRecentExpanded, date: saleRecent8HeadDate, periodLabel: '최근 8주' }),
      sortable: false,
      resizable: false,
      minSize: 98,
      maxSize: 120,
      size: 104,
      className: 'complex-recent-expand-col',
      headerClassName: 'complex-recent-expand-head',
      render: (item) => {
        if (!saleRecent8HeadDate) return '';
        const point = getRecentPointList(item, 'sale8').find((row) => row.date === saleRecent8HeadDate);
        return point ? formatKrw(point.amt, useKoreanPriceUnit) : '-';
      },
    },
    ...(
      saleRecentExpanded
        ? saleRecent8Dates.slice(1).map((date) => ({
            key: `articleTradePriceRecent8__${date}`,
            label: formatDateMMDD(date),
            sortable: false,
            minSize: 86,
            size: 92,
            sortValue: () => 0,
            render: (item) => {
              const point = getRecentPointList(item, 'sale8').find((row) => row.date === date);
              return point ? formatKrw(point.amt, useKoreanPriceUnit) : '-';
            },
          }))
        : []
    ),
    {
      key: 'articleTradePriceRecent6Toggle',
      label: getRecentToggleLabel({ expanded: saleRecent6Expanded, date: saleRecent6HeadDate, periodLabel: '최근 6개월', isMonth: true }),
      sortable: false,
      resizable: false,
      minSize: 108,
      maxSize: 126,
      size: 112,
      className: 'complex-recent-expand-col',
      headerClassName: 'complex-recent-expand-head',
      render: (item) => {
        if (!saleRecent6HeadDate) return '';
        const point = getRecentPointList(item, 'sale6').find((row) => row.date === saleRecent6HeadDate);
        return point ? formatKrw(point.amt, useKoreanPriceUnit) : '-';
      },
    },
    ...(
      saleRecent6Expanded
        ? saleRecent6Dates.slice(1).map((date) => ({
            key: `articleTradePriceRecent6__${date}`,
            label: formatDateYYMM(date),
            sortable: false,
            minSize: 86,
            size: 92,
            sortValue: () => 0,
            render: (item) => {
              const point = getRecentPointList(item, 'sale6').find((row) => row.date === date);
              return point ? formatKrw(point.amt, useKoreanPriceUnit) : '-';
            },
          }))
        : []
    ),
    {
      key: 'articleLeasePrice',
      label: '전세가',
      sortable: true,
      filterable: true,
      sortValue: (row) => Number(row?.articleLeasePrice || 0),
      render: (item) => formatKrw(item?.articleLeasePrice, useKoreanPriceUnit),
    },
    {
      key: 'investPrice',
      label: '투자금\r\n(실투자금)',
      sortable: true,
      filterable: true,
      sortValue: (row) => Number(row?.investPrice ?? row?.invest_price ?? 0),
      // 투자금(위) + 실투자금(아래) 2줄 — 실투자금 컬럼을 병합
      render: (item) => {
        const investText = formatKrw(item?.investPrice ?? item?.invest_price, useKoreanPriceUnit);
        const realFormatted = formatKrw(item?.investRealInvestPrice ?? item?.invest_real_invest_price, useKoreanPriceUnit);
        const realText = realFormatted && realFormatted !== '-' ? `(${realFormatted})` : '';
        return (
          <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '2px', lineHeight: 1.2, width: '100%' }}>
            <span style={{ fontSize: '14px', fontFamily: "'Pretendard Variable', 'Pretendard', sans-serif" }}>{investText}</span>
            {realText ? <span style={{ fontSize: '12px', color: '#6b7280' }}>{realText}</span> : null}
          </span>
        );
      },
    },
    {
      key: 'investRealInvestPrice',
      label: '실투자금',
      sortable: true,
      filterable: true,
      sortValue: (row) => Number(row?.investRealInvestPrice ?? row?.invest_real_invest_price ?? 0),
      render: (item) => formatKrw(item?.investRealInvestPrice ?? item?.invest_real_invest_price, useKoreanPriceUnit),
    },
    {
      key: 'profitRate',
      label: '수익률',
      sortable: true,
      sortValue: (row) => Number(row?.profitRate ?? row?.profit_rate ?? 0),
      render: (item) => {
        const rate = Number(item?.profitRate ?? item?.profit_rate);
        if (Number.isFinite(rate) && rate !== 0) return formatRatioPercent(rate);
        return '-';
      },
    },
    {
      key: 'realInvestPrice',
      label: '실투자금',
      sortable: true,
      filterable: true,
      sortValue: (row) => Number(row?.realInvestPrice ?? row?.real_invest_price ?? 0),
      render: (item) => formatKrw(item?.realInvestPrice ?? item?.real_invest_price, useKoreanPriceUnit),
    },
    {
      key: 'realProfitRate',
      label: '실수익률',
      sortable: true,
      sortValue: (row) => Number(row?.realProfitRate || 0),
      render: (item) => {
        const rate = Number(item?.realProfitRate);
        if (Number.isFinite(rate) && rate !== 0) return formatRatioPercent(rate);
        return '-';
      },
    },
    {
      key: 'complexMinLeasePrice',
      label: '전세\r\n(최저)',
      sortable: true,
      filterable: true,
      sortValue: (row) => Number(row?.complexMinLeasePrice ?? row?.complex_min_lease_price ?? 0),
      render: (item) => formatKrw(item?.complexMinLeasePrice ?? item?.complex_min_lease_price, useKoreanPriceUnit),
    },
    {
      key: 'complexMaxLeasePrice',
      label: '전세\r\n(최고)',
      sortable: true,
      filterable: true,
      sortValue: (row) => Number(row?.complexMaxLeasePrice ?? row?.complex_max_lease_price ?? 0),
      render: (item) => formatKrw(item?.complexMaxLeasePrice ?? item?.complex_max_lease_price, useKoreanPriceUnit),
    },
    {
      key: 'leaseChange6Months',
      label: '6개월',
      sortable: true,
      sortValue: (row) => Number(getLeaseChangeApiAmount(row, '6m') || 0),
      render: (item) => {
        const diff = getLeaseChangeApiAmount(item, '6m');
        if (diff == null || Number(diff) === 0) return '-';
        return <span style={{ color: diff < 0 ? '#2563eb' : undefined }}>{formatSignedKrw(diff, useKoreanPriceUnit)}</span>;
      },
    },
    {
      key: 'leaseChange8Weeks',
      label: '8주',
      sortable: true,
      sortValue: (row) => Number(getLeaseChangeApiAmount(row, '8w') || 0),
      render: (item) => {
        const diff = getLeaseChangeApiAmount(item, '8w');
        if (diff == null || Number(diff) === 0) return '-';
        return <span style={{ color: diff < 0 ? '#2563eb' : undefined }}>{formatSignedKrw(diff, useKoreanPriceUnit)}</span>;
      },
    },
    {
      key: 'leaseChange4Weeks',
      label: '4주',
      sortable: true,
      sortValue: (row) => Number(getLeaseChangeApiAmount(row, '4w') || 0),
      render: (item) => {
        const diff = getLeaseChangeApiAmount(item, '4w');
        if (diff == null || Number(diff) === 0) return '-';
        return <span style={{ color: diff < 0 ? '#2563eb' : undefined }}>{formatSignedKrw(diff, useKoreanPriceUnit)}</span>;
      },
    },
    {
      key: 'articleLeasePriceRecent8Toggle',
      label: getRecentToggleLabel({ expanded: leaseRecentExpanded, date: leaseRecent8HeadDate, periodLabel: '최근 8주' }),
      sortable: false,
      resizable: false,
      minSize: 98,
      maxSize: 120,
      size: 104,
      className: 'complex-recent-expand-col',
      headerClassName: 'complex-recent-expand-head',
      render: (item) => {
        if (!leaseRecent8HeadDate) return '';
        const point = getRecentPointList(item, 'lease8').find((row) => row.date === leaseRecent8HeadDate);
        return point ? formatKrw(point.amt, useKoreanPriceUnit) : '-';
      },
    },
    ...(
      leaseRecentExpanded
        ? leaseRecent8Dates.slice(1).map((date) => ({
            key: `articleLeasePriceRecent8__${date}`,
            label: formatDateMMDD(date),
            sortable: false,
            minSize: 86,
            size: 92,
            sortValue: () => 0,
            render: (item) => {
              const point = getRecentPointList(item, 'lease8').find((row) => row.date === date);
              return point ? formatKrw(point.amt, useKoreanPriceUnit) : '-';
            },
          }))
        : []
    ),
    {
      key: 'articleLeasePriceRecent6Toggle',
      label: getRecentToggleLabel({ expanded: leaseRecent6Expanded, date: leaseRecent6HeadDate, periodLabel: '최근 6개월', isMonth: true }),
      sortable: false,
      resizable: false,
      minSize: 108,
      maxSize: 126,
      size: 112,
      className: 'complex-recent-expand-col',
      headerClassName: 'complex-recent-expand-head',
      render: (item) => {
        if (!leaseRecent6HeadDate) return '';
        const point = getRecentPointList(item, 'lease6').find((row) => row.date === leaseRecent6HeadDate);
        return point ? formatKrw(point.amt, useKoreanPriceUnit) : '-';
      },
    },
    ...(
      leaseRecent6Expanded
        ? leaseRecent6Dates.slice(1).map((date) => ({
            key: `articleLeasePriceRecent6__${date}`,
            label: formatDateYYMM(date),
            sortable: false,
            minSize: 86,
            size: 92,
            sortValue: () => 0,
            render: (item) => {
              const point = getRecentPointList(item, 'lease6').find((row) => row.date === date);
              return point ? formatKrw(point.amt, useKoreanPriceUnit) : '-';
            },
          }))
        : []
    ),
    {
      key: 'avgSaleRealPrice',
      label: '매매\r\n(실거래가)',
      sortable: true,
      filterable: true,
      sortValue: (row) => Number(row?.avgSaleRealPrice || 0),
      render: (item) => formatKrw(item?.avgSaleRealPrice, useKoreanPriceUnit),
    },
    {
      key: 'saleTxRecent8Toggle',
      label: getRecentToggleLabel({ expanded: saleTxRecentExpanded, date: saleTxRecent8HeadDate, periodLabel: '최근 8주' }),
      sortable: false,
      resizable: false,
      minSize: 98,
      maxSize: 120,
      size: 104,
      className: 'complex-recent-expand-col',
      headerClassName: 'complex-recent-expand-head',
      render: (item) => {
        if (!saleTxRecent8HeadDate) return '';
        const point = getRecentPointList(item, 'saleTx8').find((row) => row.date === saleTxRecent8HeadDate);
        return point ? formatKrw(point.amt, useKoreanPriceUnit) : '-';
      },
    },
    ...(
      saleTxRecentExpanded
        ? saleTxRecent8Dates.slice(1).map((date) => ({
          key: `saleTxRecent8__${date}`,
          label: formatDateMMDD(date),
          sortable: false,
          minSize: 86,
          size: 92,
          sortValue: () => 0,
          render: (item) => {
            const point = getRecentPointList(item, 'saleTx8').find((row) => row.date === date);
            return point ? formatKrw(point.amt, useKoreanPriceUnit) : '-';
          },
        }))
        : []
    ),
    {
      key: 'saleTxRecent6Toggle',
      label: getRecentToggleLabel({ expanded: saleTxRecent6Expanded, date: saleTxRecent6HeadDate, periodLabel: '최근 6개월', isMonth: true }),
      sortable: false,
      resizable: false,
      minSize: 108,
      maxSize: 126,
      size: 112,
      className: 'complex-recent-expand-col',
      headerClassName: 'complex-recent-expand-head',
      render: (item) => {
        if (!saleTxRecent6HeadDate) return '';
        const point = getRecentPointList(item, 'saleTx6').find((row) => row.date === saleTxRecent6HeadDate);
        return point ? formatKrw(point.amt, useKoreanPriceUnit) : '-';
      },
    },
    ...(
      saleTxRecent6Expanded
        ? saleTxRecent6Dates.slice(1).map((date) => ({
          key: `saleTxRecent6__${date}`,
          label: formatDateYYMM(date),
          sortable: false,
          minSize: 86,
          size: 92,
          sortValue: () => 0,
          render: (item) => {
            const point = getRecentPointList(item, 'saleTx6').find((row) => row.date === date);
            return point ? formatKrw(point.amt, useKoreanPriceUnit) : '-';
          },
        }))
        : []
    ),
    {
      key: 'avgLeaseRealPrice',
      label: '전세\r\n(실거래가)',
      sortable: true,
      filterable: true,
      sortValue: (row) => Number(row?.avgLeaseRealPrice || 0),
      render: (item) => formatKrw(item?.avgLeaseRealPrice, useKoreanPriceUnit),
    },
    {
      key: 'leaseTxRecent8Toggle',
      label: getRecentToggleLabel({ expanded: leaseTxRecentExpanded, date: leaseTxRecent8HeadDate, periodLabel: '최근 8주' }),
      sortable: false,
      resizable: false,
      minSize: 98,
      maxSize: 120,
      size: 104,
      className: 'complex-recent-expand-col',
      headerClassName: 'complex-recent-expand-head',
      render: (item) => {
        if (!leaseTxRecent8HeadDate) return '';
        const point = getRecentPointList(item, 'leaseTx8').find((row) => row.date === leaseTxRecent8HeadDate);
        return point ? formatKrw(point.amt, useKoreanPriceUnit) : '-';
      },
    },
    ...(
      leaseTxRecentExpanded
        ? leaseTxRecent8Dates.slice(1).map((date) => ({
          key: `leaseTxRecent8__${date}`,
          label: formatDateMMDD(date),
          sortable: false,
          minSize: 86,
          size: 92,
          sortValue: () => 0,
          render: (item) => {
            const point = getRecentPointList(item, 'leaseTx8').find((row) => row.date === date);
            return point ? formatKrw(point.amt, useKoreanPriceUnit) : '-';
          },
        }))
        : []
    ),
    {
      key: 'leaseTxRecent6Toggle',
      label: getRecentToggleLabel({ expanded: leaseTxRecent6Expanded, date: leaseTxRecent6HeadDate, periodLabel: '최근 6개월', isMonth: true }),
      sortable: false,
      resizable: false,
      minSize: 108,
      maxSize: 126,
      size: 112,
      className: 'complex-recent-expand-col',
      headerClassName: 'complex-recent-expand-head',
      render: (item) => {
        if (!leaseTxRecent6HeadDate) return '';
        const point = getRecentPointList(item, 'leaseTx6').find((row) => row.date === leaseTxRecent6HeadDate);
        return point ? formatKrw(point.amt, useKoreanPriceUnit) : '-';
      },
    },
    ...(
      leaseTxRecent6Expanded
        ? leaseTxRecent6Dates.slice(1).map((date) => ({
          key: `leaseTxRecent6__${date}`,
          label: formatDateYYMM(date),
          sortable: false,
          minSize: 86,
          size: 92,
          sortValue: () => 0,
          render: (item) => {
            const point = getRecentPointList(item, 'leaseTx6').find((row) => row.date === date);
            return point ? formatKrw(point.amt, useKoreanPriceUnit) : '-';
          },
        }))
        : []
    ),
    {
      key: 'tradeArticleCount',
      label: '매매수',
      sortable: true,
      filterable: true,
      sortValue: (row) => Number(row?.tradeArticleCount || 0),
      render: (item) => formatCount(item?.tradeArticleCount, useKoreanPriceUnit),
    },
    {
      // 매매비율 = 매매수 / 평세대수(ptpFamilyCnt) × 100
      key: 'saleRatio',
      label: '매매비율',
      align: 'center', // % 수치 → 가운데 정렬
      sortable: true,
      sortValue: (row) => {
        const v = getCountRatioValue(row?.tradeArticleCount, row?.ptpFamilyCnt);
        return v == null ? -1 : v;
      },
      render: (item) => formatCountRatio(item?.tradeArticleCount, item?.ptpFamilyCnt),
    },
    {
      key: 'saleCountWeeklyChange',
      label: '주간변동',
      sortable: true,
      sortValue: (row) => Number(row?.saleCountWeeklyChange ?? row?.sale_count_weekly_change ?? 0),
      render: (item) => {
        const change = item?.saleCountWeeklyChange ?? item?.sale_count_weekly_change;
        return formatWeeklyChange(change);
      },
    },
    {
      key: 'tradeArticleCountRecent8Toggle',
      label: getRecentToggleLabel({ expanded: saleCountRecentExpanded, date: saleCountRecent8HeadDate, periodLabel: '최근 8주' }),
      sortable: false,
      resizable: false,
      minSize: 98,
      maxSize: 120,
      size: 104,
      className: 'complex-recent-expand-col',
      headerClassName: 'complex-recent-expand-head',
      render: (item) => {
        if (!saleCountRecent8HeadDate) return '';
        const point = getRecentPointList(item, 'saleCount8').find((row) => row.date === saleCountRecent8HeadDate);
        return point ? formatCount(point.amt, useKoreanPriceUnit) : '-';
      },
    },
    ...(
      saleCountRecentExpanded
        ? saleCountRecent8Dates.slice(1).map((date) => ({
          key: `tradeArticleCountRecent8__${date}`,
          label: formatDateMMDD(date),
          sortable: false,
          minSize: 86,
          size: 92,
          sortValue: () => 0,
          render: (item) => {
            const point = getRecentPointList(item, 'saleCount8').find((row) => row.date === date);
            return point ? formatCount(point.amt, useKoreanPriceUnit) : '-';
          },
        }))
        : []
    ),
    {
      key: 'tradeArticleCountRecent6Toggle',
      label: getRecentToggleLabel({ expanded: saleCountRecent6Expanded, date: saleCountRecent6HeadDate, periodLabel: '최근 6개월', isMonth: true }),
      sortable: false,
      resizable: false,
      minSize: 108,
      maxSize: 126,
      size: 112,
      className: 'complex-recent-expand-col',
      headerClassName: 'complex-recent-expand-head',
      render: (item) => {
        if (!saleCountRecent6HeadDate) return '';
        const point = getRecentPointList(item, 'saleCount6').find((row) => row.date === saleCountRecent6HeadDate);
        return point ? formatCount(point.amt, useKoreanPriceUnit) : '-';
      },
    },
    ...(
      saleCountRecent6Expanded
        ? saleCountRecent6Dates.slice(1).map((date) => ({
          key: `tradeArticleCountRecent6__${date}`,
          label: formatDateYYMM(date),
          sortable: false,
          minSize: 86,
          size: 92,
          sortValue: () => 0,
          render: (item) => {
            const point = getRecentPointList(item, 'saleCount6').find((row) => row.date === date);
            return point ? formatCount(point.amt, useKoreanPriceUnit) : '-';
          },
        }))
        : []
    ),
    {
      key: 'leaseArticleCount',
      label: '전세수',
      sortable: true,
      filterable: true,
      sortValue: (row) => Number(row?.leaseArticleCount || 0),
      render: (item) => formatCount(item?.leaseArticleCount, useKoreanPriceUnit),
    },
    {
      // 전세비율 = 전세수 / 평세대수(ptpFamilyCnt) × 100
      key: 'leaseRatio',
      label: '전세비율',
      align: 'center', // % 수치 → 가운데 정렬
      sortable: true,
      sortValue: (row) => {
        const v = getCountRatioValue(row?.leaseArticleCount, row?.ptpFamilyCnt);
        return v == null ? -1 : v;
      },
      render: (item) => formatCountRatio(item?.leaseArticleCount, item?.ptpFamilyCnt),
    },
    {
      key: 'leaseCountWeeklyChange',
      label: '주간변동',
      sortable: true,
      sortValue: (row) => Number(row?.leaseCountWeeklyChange ?? row?.lease_count_weekly_change ?? 0),
      render: (item) => {
        const change = item?.leaseCountWeeklyChange ?? item?.lease_count_weekly_change;
        return formatWeeklyChange(change);
      },
    },
    {
      key: 'leaseArticleCountRecent8Toggle',
      label: getRecentToggleLabel({ expanded: leaseCountRecentExpanded, date: leaseCountRecent8HeadDate, periodLabel: '최근 8주' }),
      sortable: false,
      resizable: false,
      minSize: 98,
      maxSize: 120,
      size: 104,
      className: 'complex-recent-expand-col',
      headerClassName: 'complex-recent-expand-head',
      render: (item) => {
        if (!leaseCountRecent8HeadDate) return '';
        const point = getRecentPointList(item, 'leaseCount8').find((row) => row.date === leaseCountRecent8HeadDate);
        return point ? formatCount(point.amt, useKoreanPriceUnit) : '-';
      },
    },
    ...(
      leaseCountRecentExpanded
        ? leaseCountRecent8Dates.slice(1).map((date) => ({
          key: `leaseArticleCountRecent8__${date}`,
          label: formatDateMMDD(date),
          sortable: false,
          minSize: 86,
          size: 92,
          sortValue: () => 0,
          render: (item) => {
            const point = getRecentPointList(item, 'leaseCount8').find((row) => row.date === date);
            return point ? formatCount(point.amt, useKoreanPriceUnit) : '-';
          },
        }))
        : []
    ),
    {
      key: 'leaseArticleCountRecent6Toggle',
      label: getRecentToggleLabel({ expanded: leaseCountRecent6Expanded, date: leaseCountRecent6HeadDate, periodLabel: '최근 6개월', isMonth: true }),
      sortable: false,
      resizable: false,
      minSize: 108,
      maxSize: 126,
      size: 112,
      className: 'complex-recent-expand-col',
      headerClassName: 'complex-recent-expand-head',
      render: (item) => {
        if (!leaseCountRecent6HeadDate) return '';
        const point = getRecentPointList(item, 'leaseCount6').find((row) => row.date === leaseCountRecent6HeadDate);
        return point ? formatCount(point.amt, useKoreanPriceUnit) : '-';
      },
    },
    ...(
      leaseCountRecent6Expanded
        ? leaseCountRecent6Dates.slice(1).map((date) => ({
          key: `leaseArticleCountRecent6__${date}`,
          label: formatDateYYMM(date),
          sortable: false,
          minSize: 86,
          size: 92,
          sortValue: () => 0,
          render: (item) => {
            const point = getRecentPointList(item, 'leaseCount6').find((row) => row.date === date);
            return point ? formatCount(point.amt, useKoreanPriceUnit) : '-';
          },
        }))
        : []
    ),
    {
      key: 'saleRealCountWeeklyChange',
      label: '주간변동',
      sortable: true,
      sortValue: (row) => Number(row?.saleRealCountWeeklyChange ?? row?.sale_real_count_weekly_change ?? 0),
      render: (item) => {
        const change = item?.saleRealCountWeeklyChange ?? item?.sale_real_count_weekly_change;
        return formatWeeklyChange(change);
      },
    },
    {
      key: 'saleRealCountRecent8Toggle',
      label: getRecentToggleLabel({ expanded: saleTxCountRecentExpanded, date: saleTxCountRecent8HeadDate, periodLabel: '최근 8주' }),
      sortable: false,
      resizable: false,
      minSize: 98,
      maxSize: 120,
      size: 104,
      className: 'complex-recent-expand-col',
      headerClassName: 'complex-recent-expand-head',
      render: (item) => {
        if (!saleTxCountRecent8HeadDate) return '';
        const point = getRecentPointList(item, 'saleTxCount8').find((row) => row.date === saleTxCountRecent8HeadDate);
        return point ? formatCount(point.amt, useKoreanPriceUnit) : '-';
      },
    },
    ...(
      saleTxCountRecentExpanded
        ? saleTxCountRecent8Dates.slice(1).map((date) => ({
          key: `saleRealCountRecent8__${date}`,
          label: formatDateMMDD(date),
          sortable: false,
          minSize: 86,
          size: 92,
          sortValue: () => 0,
          render: (item) => {
            const point = getRecentPointList(item, 'saleTxCount8').find((row) => row.date === date);
            return point ? formatCount(point.amt, useKoreanPriceUnit) : '-';
          },
        }))
        : []
    ),
    {
      key: 'saleRealCountRecent6Toggle',
      label: getRecentToggleLabel({ expanded: saleTxCountRecent6Expanded, date: saleTxCountRecent6HeadDate, periodLabel: '최근 6개월', isMonth: true }),
      sortable: false,
      resizable: false,
      minSize: 108,
      maxSize: 126,
      size: 112,
      className: 'complex-recent-expand-col',
      headerClassName: 'complex-recent-expand-head',
      render: (item) => {
        if (!saleTxCountRecent6HeadDate) return '';
        const point = getRecentPointList(item, 'saleTxCount6').find((row) => row.date === saleTxCountRecent6HeadDate);
        return point ? formatCount(point.amt, useKoreanPriceUnit) : '-';
      },
    },
    ...(
      saleTxCountRecent6Expanded
        ? saleTxCountRecent6Dates.slice(1).map((date) => ({
          key: `saleRealCountRecent6__${date}`,
          label: formatDateYYMM(date),
          sortable: false,
          minSize: 86,
          size: 92,
          sortValue: () => 0,
          render: (item) => {
            const point = getRecentPointList(item, 'saleTxCount6').find((row) => row.date === date);
            return point ? formatCount(point.amt, useKoreanPriceUnit) : '-';
          },
        }))
        : []
    ),
    {
      key: 'leaseRealCountWeeklyChange',
      label: '주간변동',
      sortable: true,
      sortValue: (row) => Number(row?.leaseRealCountWeeklyChange ?? row?.lease_real_count_weekly_change ?? 0),
      render: (item) => {
        const change = item?.leaseRealCountWeeklyChange ?? item?.lease_real_count_weekly_change;
        return formatWeeklyChange(change);
      },
    },
    {
      key: 'leaseRealCountRecent8Toggle',
      label: getRecentToggleLabel({ expanded: leaseTxCountRecentExpanded, date: leaseTxCountRecent8HeadDate, periodLabel: '최근 8주' }),
      sortable: false,
      resizable: false,
      minSize: 98,
      maxSize: 120,
      size: 104,
      className: 'complex-recent-expand-col',
      headerClassName: 'complex-recent-expand-head',
      render: (item) => {
        if (!leaseTxCountRecent8HeadDate) return '';
        const point = getRecentPointList(item, 'leaseTxCount8').find((row) => row.date === leaseTxCountRecent8HeadDate);
        return point ? formatCount(point.amt, useKoreanPriceUnit) : '-';
      },
    },
    ...(
      leaseTxCountRecentExpanded
        ? leaseTxCountRecent8Dates.slice(1).map((date) => ({
          key: `leaseRealCountRecent8__${date}`,
          label: formatDateMMDD(date),
          sortable: false,
          minSize: 86,
          size: 92,
          sortValue: () => 0,
          render: (item) => {
            const point = getRecentPointList(item, 'leaseTxCount8').find((row) => row.date === date);
            return point ? formatCount(point.amt, useKoreanPriceUnit) : '-';
          },
        }))
        : []
    ),
    {
      key: 'leaseRealCountRecent6Toggle',
      label: getRecentToggleLabel({ expanded: leaseTxCountRecent6Expanded, date: leaseTxCountRecent6HeadDate, periodLabel: '최근 6개월', isMonth: true }),
      sortable: false,
      resizable: false,
      minSize: 108,
      maxSize: 126,
      size: 112,
      className: 'complex-recent-expand-col',
      headerClassName: 'complex-recent-expand-head',
      render: (item) => {
        if (!leaseTxCountRecent6HeadDate) return '';
        const point = getRecentPointList(item, 'leaseTxCount6').find((row) => row.date === leaseTxCountRecent6HeadDate);
        return point ? formatCount(point.amt, useKoreanPriceUnit) : '-';
      },
    },
    ...(
      leaseTxCountRecent6Expanded
        ? leaseTxCountRecent6Dates.slice(1).map((date) => ({
          key: `leaseRealCountRecent6__${date}`,
          label: formatDateYYMM(date),
          sortable: false,
          minSize: 86,
          size: 92,
          sortValue: () => 0,
          render: (item) => {
            const point = getRecentPointList(item, 'leaseTxCount6').find((row) => row.date === date);
            return point ? formatCount(point.amt, useKoreanPriceUnit) : '-';
          },
        }))
        : []
    ),
    {
      key: 'leasePriceRate',
      label: '전세가율',
      align: 'center', // % 수치 → 가운데 정렬
      sortable: true,
      filterable: true,
      sortValue: (row) => Number(row?.leasePriceRate || 0),
      render: (item) => formatLeaseRate(item),
    },
    // 전세가율 최근 6개월(월별) — 매매/전세 최근6개월과 동일 패턴. 월별 = 100 × 전세월값 / 매매월값
    {
      key: 'leaseRateRecent6Toggle',
      label: getRecentToggleLabel({ expanded: leaseRateRecent6Expanded, date: leaseRecent6HeadDate, periodLabel: '최근 6개월', isMonth: true }),
      align: 'center', // 전세가율(%) → 가운데 정렬
      sortable: false,
      resizable: false,
      minSize: 108,
      maxSize: 126,
      size: 112,
      className: 'complex-recent-expand-col',
      headerClassName: 'complex-recent-expand-head',
      render: (item) => {
        if (!leaseRecent6HeadDate) return '';
        const rate = getLeaseRateAt(item, leaseRecent6HeadDate);
        return rate == null ? '-' : `${rate.toFixed(1)}%`;
      },
    },
    ...(
      leaseRateRecent6Expanded
        ? leaseRecent6Dates.slice(1).map((date) => ({
            key: `leaseRateRecent6__${date}`,
            label: formatDateYYMM(date),
            align: 'center', // 전세가율(%) → 가운데 정렬
            sortable: false,
            minSize: 86,
            size: 92,
            sortValue: () => 0,
            render: (item) => {
              const rate = getLeaseRateAt(item, date);
              return rate == null ? '-' : `${rate.toFixed(1)}%`;
            },
          }))
        : []
    ),
    {
      // 6개월 = 최근 5개월 월별 변동(전세가율 diff)의 합
      key: 'leaseRateChange6m',
      label: '6개월',
      align: 'center', // 변동 전세가율(%) → 가운데 정렬
      sortable: false,
      sortValue: () => 0,
      render: (item) => {
        const v = getLeaseRateChangeSum(item, leaseRecent6Dates);
        if (v == null) return '-';
        const sign = v > 0 ? '+' : '';
        return <span style={{ color: v < 0 ? '#2563eb' : undefined }}>{`${sign}${v.toFixed(1)}%`}</span>;
      },
    },
    // 변동 전세가율 최근 5개월(월별) — 각 월 = 해당월 전세가율 − 다음(과거)월 전세가율
    {
      key: 'leaseRateChangeRecent5Toggle',
      label: getRecentToggleLabel({ expanded: leaseRateChangeRecent5Expanded, date: leaseRecent6HeadDate, periodLabel: '최근 5개월', isMonth: true }),
      align: 'center', // 변동 전세가율(%) → 가운데 정렬
      sortable: false,
      resizable: false,
      minSize: 108,
      maxSize: 126,
      size: 112,
      className: 'complex-recent-expand-col',
      headerClassName: 'complex-recent-expand-head',
      render: (item) => {
        const v = getLeaseRateChangeAt(item, leaseRecent6Dates, 0);
        if (v == null) return '-';
        const sign = v > 0 ? '+' : '';
        return <span style={{ color: v < 0 ? '#2563eb' : undefined }}>{`${sign}${v.toFixed(1)}%`}</span>;
      },
    },
    ...(
      leaseRateChangeRecent5Expanded
        ? leaseRecent6Dates.slice(1, Math.max(1, leaseRecent6Dates.length - 1)).map((date) => {
            const idx = leaseRecent6Dates.indexOf(date);
            return {
              key: `leaseRateChangeRecent5__${date}`,
              label: formatDateYYMM(date),
              align: 'center', // 변동 전세가율(%) → 가운데 정렬
              sortable: false,
              minSize: 86,
              size: 92,
              sortValue: () => 0,
              render: (item) => {
                const v = getLeaseRateChangeAt(item, leaseRecent6Dates, idx);
                if (v == null) return '-';
                const sign = v > 0 ? '+' : '';
                return <span style={{ color: v < 0 ? '#2563eb' : undefined }}>{`${sign}${v.toFixed(1)}%`}</span>;
              },
            };
          })
        : []
    ),
  ];
  // 헤더 클릭으로 정렬 가능한 컬럼 = 정렬설정 드롭다운에 노출되는 컬럼
  // (추가 가능 목록 SORT_ADD_ALLOWED_COLUMNS + 현재 활성 프리셋의 랭킹 기준 키)
  const headerSortableKeySet = useMemo(() => {
    const presetKey = selectedSortPreset || '지역';
    const presetRules = Array.isArray(SORT_PRESET_RULE_MAP?.[presetKey]) ? SORT_PRESET_RULE_MAP[presetKey] : [];
    return new Set([
      ...SORT_ADD_ALLOWED_COLUMN_KEY_SET,
      ...presetRules.map((row) => String(row?.key || '')).filter(Boolean),
    ]);
  }, [selectedSortPreset]);
  const tableColumnsWithHeaderFilters = useMemo(
    () => tableColumns.map((column) => {
      const key = String(column?.key || '');
      const normalizedLabel = normalizeHeaderFilterText(column?.label);
      if (!key) return column;
      const isPopupFilter = POPUP_FILTER_KEY_SET.has(key);
      const isTextFilter = TABLE_HEADER_FILTER_KEY_SET.has(key) || TABLE_HEADER_FILTER_LABEL_SET.has(normalizedLabel);
      const filterable = isPopupFilter || isTextFilter;
      return {
        ...column,
        sortable: !!column?.sortable && headerSortableKeySet.has(key),
        filterable,
        filterValue: isTextFilter ? String(tableHeaderFilters?.[key] || '') : '',
        onFilterChange: isTextFilter
          ? (nextValue) => {
            const nextText = String(nextValue || '');
            setTableHeaderFilters((prev) => ({
              ...(prev || {}),
              [key]: nextText,
            }));
          }
          : undefined,
      };
    }),
    [tableColumns, tableHeaderFilters, headerSortableKeySet]
  );

  const fixedColumnKeys = useMemo(() => new Set(['compareSelect']), []);
  const fixedColumns = useMemo(
    () => tableColumnsWithHeaderFilters.filter((column) => fixedColumnKeys.has(column.key)),
    [tableColumnsWithHeaderFilters, fixedColumnKeys]
  );
  const configurableColumns = useMemo(
    () => tableColumnsWithHeaderFilters.filter((column) => {
      const key = column.key;
      // 고정 컬럼 제외
      if (fixedColumnKeys.has(key)) return false;
      // 동적 날짜 컬럼 제외 (최근 8주/6개월의 각 날짜 데이터)
      if (key.includes('Recent8__') || key.includes('Recent6__')) return false;
      return true;
    }),
    [tableColumnsWithHeaderFilters, fixedColumnKeys]
  );
  const configurableColumnMap = useMemo(
    () => new Map(configurableColumns.map((column) => [column.key, column])),
    [configurableColumns]
  );
  const defaultConfigurableOrder = useMemo(
    () => configurableColumns.map((column) => column.key),
    [configurableColumns]
  );
  const sanitizedColumnOrder = useMemo(() => {
    const next = [];
    const seen = new Set();
    columnPrefs.order.forEach((key) => {
      if (configurableColumnMap.has(key) && !seen.has(key)) {
        seen.add(key);
        next.push(key);
      }
    });
    defaultConfigurableOrder.forEach((key) => {
      if (!seen.has(key)) {
        seen.add(key);
        next.push(key);
      }
    });
    return next;
  }, [columnPrefs.order, configurableColumnMap, defaultConfigurableOrder]);
  const tableColumnMap = useMemo(
    () => new Map(tableColumnsWithHeaderFilters.map((column) => [column.key, column])),
    [tableColumnsWithHeaderFilters]
  );
  // 상세 필터 설정 모달에 나열할 필터 가능 컬럼 목록
  const detailFilterColumns = useMemo(
    () => {
      // 요청 목록(DETAIL_FILTER_SUPPORTED_KEYS) 순서대로 정렬해 표기
      const order = [...DETAIL_FILTER_SUPPORTED_KEYS];
      const list = tableColumnsWithHeaderFilters
        .filter((column) => column && column.filterable && DETAIL_FILTER_SUPPORTED_KEYS.has(column.key))
        .sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key));
      return list;
    },
    [tableColumnsWithHeaderFilters]
  );
  const mergedHeaderFilterEntries = useMemo(() => {
    const merged = new Map();
    Object.entries(tableHeaderFilterSummaryMap || {}).forEach(([key, raw]) => {
      const k = String(key || '').trim();
      const v = String(raw || '').trim();
      if (k && v) merged.set(k, v);
    });
    Object.entries(tableHeaderFilters || {}).forEach(([key, raw]) => {
      const k = String(key || '').trim();
      const v = String(raw || '').trim();
      if (k && v) merged.set(k, v);
    });
    return Array.from(merged.entries());
  }, [tableHeaderFilterSummaryMap, tableHeaderFilters]);
  useEffect(() => {
    if (mergedHeaderFilterEntries.length === 0) return;
    const next = {};
    mergedHeaderFilterEntries.forEach(([key, value]) => {
      next[key] = value;
    });
    setStickyHeaderFilterSummaryMap(next);
  }, [mergedHeaderFilterEntries]);
  const activeTableHeaderFilterEntries = useMemo(() => {
    if (mergedHeaderFilterEntries.length > 0) return mergedHeaderFilterEntries;
    return Object.entries(stickyHeaderFilterSummaryMap || {})
      .map(([key, raw]) => [String(key || '').trim(), String(raw || '').trim()])
      .filter(([key, value]) => key && value);
  }, [mergedHeaderFilterEntries, stickyHeaderFilterSummaryMap]);
  // 실제 적용된 필터를 컬럼 키 기준 요약맵으로 (그리드 필터 요약행에 표시)
  const headerFilterSummaryForTable = useMemo(() => {
    const map = {};
    // 1) 텍스트 헤더 필터
    Object.entries(tableHeaderFilters || {}).forEach(([k, v]) => {
      const text = String(v ?? '').trim();
      if (k && text) map[k] = text;
    });
    // 2) 범위(min/max) 팝업 필터
    const af = appliedFilters || {};
    const bases = new Set();
    Object.keys(af).forEach((k) => {
      if (k.endsWith('Min')) bases.add(k.slice(0, -3));
      else if (k.endsWith('Max')) bases.add(k.slice(0, -3));
    });
    bases.forEach((base) => {
      const min = af[`${base}Min`];
      const max = af[`${base}Max`];
      const hasMin = min !== null && min !== undefined && min !== '';
      const hasMax = max !== null && max !== undefined && max !== '';
      if (!hasMin && !hasMax) return;
      // 금액 계열은 단위제거 축약(172000 → 17.2), 그 외(건수/비율/연차 등)는 원값
      const fmt = AMOUNT_FILTER_BASES.has(base) ? (x) => formatKrw(Number(x), false) : (x) => String(x);
      const text = `${hasMin ? fmt(min) : ''}~${hasMax ? fmt(max) : ''}`;
      (APPLIED_FILTER_COLUMN_KEYS[base] || [base]).forEach((colKey) => { map[colKey] = text; });
    });
    // 3) 급지/구분 (복수 선택)
    if (Array.isArray(selectedSigunguGrades) && selectedSigunguGrades.length) map.sigunguGrade = selectedSigunguGrades.join(', ');
    if (Array.isArray(selectedDongGrades) && selectedDongGrades.length) map.dongGrade = selectedDongGrades.join(', ');
    if (Array.isArray(selectedGubuns) && selectedGubuns.length) map.gubun = selectedGubuns.join(', ');
    return map;
  }, [tableHeaderFilters, appliedFilters, selectedSigunguGrades, selectedDongGrades, selectedGubuns]);

  // 테이블 컬럼 헤더 안에 표시할 칩 요약 — 퀵필터(매매가/투자금/연차/평형/세대수)로 제어되는 컬럼은
  // 버튼 하단 칩으로 이미 표기되므로 컬럼 헤더 칩은 생략(중복 방지). 상세필터 모달·활성감지는 원본 사용.
  const headerFilterSummaryForColumns = useMemo(() => {
    const QUICK_FILTER_COLUMN_KEYS = ['articleTradePrice', 'investPrice', 'useApproveDate', 'planTypeName', 'familyCnt'];
    const m = { ...(headerFilterSummaryForTable || {}) };
    QUICK_FILTER_COLUMN_KEYS.forEach((k) => { delete m[k]; });
    return m;
  }, [headerFilterSummaryForTable]);

  // 필터가 하나라도 설정되어 있는지 (헤더/범위/급지·구분 + 퀵필터)
  const hasAnyActiveFilter = useMemo(() => (
    Object.keys(headerFilterSummaryForTable || {}).length > 0
    || Object.keys(quickFilterChipMap || {}).length > 0
  ), [headerFilterSummaryForTable, quickFilterChipMap]);

  const handleClearSelectedFilters = () => {
    setTableHeaderFilters({});
    setTableHeaderFilterSummaryMap({});
    setStickyHeaderFilterSummaryMap({});
    setHeaderFilterResetToken((prev) => prev + 1);
  };
  const handleRemoveSelectedFilter = (filterKey) => {
    const targetKey = String(filterKey || '').trim();
    if (!targetKey) return;
    setTableHeaderFilters((prev) => {
      const next = { ...(prev || {}) };
      delete next[targetKey];
      return next;
    });
    setTableHeaderFilterSummaryMap((prev) => {
      const next = { ...(prev || {}) };
      delete next[targetKey];
      return next;
    });
    setStickyHeaderFilterSummaryMap((prev) => {
      const next = { ...(prev || {}) };
      delete next[targetKey];
      return next;
    });
    setHeaderFilterRemoveSignal((prev) => ({ key: targetKey, token: Number(prev?.token || 0) + 1 }));
  };
  // 헤더 칩의 × — 해당 컬럼의 적용 필터를 소스별로(텍스트/범위/급지·구분) 모두 클리어
  const handleRemoveHeaderFilterColumn = useCallback((colKey) => {
    const key = String(colKey || '').trim();
    if (!key) return;

    // 1) 텍스트 헤더 필터 + 요약맵
    setTableHeaderFilters((prev) => { const n = { ...(prev || {}) }; delete n[key]; return n; });
    setTableHeaderFilterSummaryMap((prev) => { const n = { ...(prev || {}) }; delete n[key]; return n; });
    setStickyHeaderFilterSummaryMap((prev) => { const n = { ...(prev || {}) }; delete n[key]; return n; });

    // 2) 급지/구분(복수 선택)
    if (key === 'sigunguGrade') setSelectedSigunguGrades([]);
    if (key === 'dongGrade') setSelectedDongGrades([]);
    if (key === 'gubun') setSelectedGubuns([]);

    // 3) 범위(min/max) 필터 — 이 컬럼키를 포함하는 base 를 찾아 appliedFilters 및 입력상태 클리어
    const basesToClear = [];
    Object.entries(APPLIED_FILTER_COLUMN_KEYS).forEach(([base, keys]) => {
      if ((keys || []).includes(key)) basesToClear.push(base);
    });
    if (!basesToClear.includes(key)) basesToClear.push(key); // 기본: 컬럼키 == base

    setAppliedFilters((prev) => {
      const n = { ...(prev || {}) };
      basesToClear.forEach((base) => {
        if (`${base}Min` in n) n[`${base}Min`] = null;
        if (`${base}Max` in n) n[`${base}Max`] = null;
      });
      return n;
    });
    basesToClear.forEach((base) => {
      if (base === 'tradePrice') { setTradePriceMinInput(''); setTradePriceMaxInput(''); }
      if (base === 'investPrice') { setInvestPriceMinInput(''); setInvestPriceMaxInput(''); }
      if (base === 'useApproveAge') { setBuiltDateFrom(''); setBuiltDateTo(''); setBuiltDateAgeInput(''); setBuiltDateMode('range'); }
      if (base === 'household') { setHouseholdMinInput(''); setHouseholdMaxInput(''); setSelectedHouseholds([]); }
      if (base === 'area') { setAreaMinInput(''); setAreaMaxInput(''); setSelectedAreaBuckets([]); }
    });

    setHeaderFilterRemoveSignal((prev) => ({ key, token: Number(prev?.token || 0) + 1 }));
  }, []);
  // 필터 전체 초기화 — 헤더 필터 / 범위(퀵)필터 / 급지·구분 전부 클리어
  const handleClearAllFilters = useCallback(() => {
    setTableHeaderFilters({});
    setTableHeaderFilterSummaryMap({});
    setStickyHeaderFilterSummaryMap({});
    setAppliedFilters((prev) => {
      const n = { ...(prev || {}) };
      Object.keys(n).forEach((k) => { if (k.endsWith('Min') || k.endsWith('Max')) n[k] = null; });
      return n;
    });
    setSelectedSigunguGrades([]);
    setSelectedDongGrades([]);
    setSelectedGubuns([]);
    setTradePriceMinInput(''); setTradePriceMaxInput('');
    setInvestPriceMinInput(''); setInvestPriceMaxInput('');
    setBuiltDateFrom(''); setBuiltDateTo(''); setBuiltDateAgeInput(''); setBuiltDateMode('range');
    setHouseholdMinInput(''); setHouseholdMaxInput(''); setSelectedHouseholds([]);
    setAreaMinInput(''); setAreaMaxInput(''); setSelectedAreaBuckets([]);
    setHeaderFilterResetToken((prev) => prev + 1);
  }, []);
  // 퀵필터 1개만 해제 (버튼 하단 칩의 × 클릭)
  const clearQuickFilter = useCallback((key) => {
    if (key === 'tradePrice') {
      setTradePriceMinInput(''); setTradePriceMaxInput('');
      setAppliedFilters((p) => ({ ...(p || {}), tradePriceMin: null, tradePriceMax: null }));
    } else if (key === 'investPrice') {
      setInvestPriceMinInput(''); setInvestPriceMaxInput('');
      setAppliedFilters((p) => ({ ...(p || {}), investPriceMin: null, investPriceMax: null }));
    } else if (key === 'builtDate') {
      setBuiltDateFrom(''); setBuiltDateTo(''); setBuiltDateAgeInput(''); setBuiltDateMode('range');
      setAppliedFilters((p) => ({ ...(p || {}), useApproveAgeMin: null, useApproveAgeMax: null }));
    } else if (key === 'household') {
      setHouseholdMinInput(''); setHouseholdMaxInput(''); setSelectedHouseholds([]);
      setAppliedFilters((p) => ({ ...(p || {}), householdMin: null, householdMax: null }));
    } else if (key === 'area') {
      setAreaMinInput(''); setAreaMaxInput(''); setSelectedAreaBuckets([]);
      setAppliedFilters((p) => ({ ...(p || {}), areaMin: null, areaMax: null }));
    }
  }, []);
  const getGroupItemVisibleKey = (groupId, item) => {
    const mappedKey = String(item?.columnKey || '').trim();
    if (mappedKey) return mappedKey;
    const itemId = String(item?.id || '').trim();
    if (itemId) return `group-item:${groupId}:${itemId}`;
    return '';
  };
  const isAlwaysVisibleColumnKey = (columnKey) => String(columnKey || '').trim() === 'buildingName';
  const buildGroupedColumnSections = (groupOrderArg, groupPrefsArg, groupItemsArg, visibleMapArg, forPanel = false) => {
    const groupMap = new Map(
      DISPLAY_COLUMN_GROUPS
        .filter((group) => !group.hidden)
        .map((group) => [group.id, group])
    );
    const normalizedOrder = [
      ...(Array.isArray(groupOrderArg) ? groupOrderArg : []).filter((id, idx, arr) => groupMap.has(id) && arr.indexOf(id) === idx),
      ...Array.from(groupMap.keys()).filter((id) => !(Array.isArray(groupOrderArg) ? groupOrderArg : []).includes(id)),
    ];

    // 최근 8주/6개월 등 토글형 컬럼 → 펼쳤을 때 날짜별 하위 컬럼으로 확장.
    // (그룹 id에 의존하지 않도록 토글 컬럼키 기준으로 매핑 → 최상위 그룹 분리 후에도 동일 동작)
    const recentToggleExpansions = {
      articleTradePriceRecent8Toggle: { subDates: saleRecent8Dates.slice(1), expanded: saleRecentExpanded, prefix: 'articleTradePriceRecent8', isMonth: false },
      articleTradePriceRecent6Toggle: { subDates: saleRecent6Dates.slice(1), expanded: saleRecent6Expanded, prefix: 'articleTradePriceRecent6', isMonth: true },
      articleLeasePriceRecent8Toggle: { subDates: leaseRecent8Dates.slice(1), expanded: leaseRecentExpanded, prefix: 'articleLeasePriceRecent8', isMonth: false },
      articleLeasePriceRecent6Toggle: { subDates: leaseRecent6Dates.slice(1), expanded: leaseRecent6Expanded, prefix: 'articleLeasePriceRecent6', isMonth: true },
      leaseRateRecent6Toggle: { subDates: leaseRecent6Dates.slice(1), expanded: leaseRateRecent6Expanded, prefix: 'leaseRateRecent6', isMonth: true },
      leaseRateChangeRecent5Toggle: { subDates: leaseRecent6Dates.slice(1, Math.max(1, leaseRecent6Dates.length - 1)), expanded: leaseRateChangeRecent5Expanded, prefix: 'leaseRateChangeRecent5', isMonth: true },
      saleTxRecent8Toggle: { subDates: saleTxRecent8Dates.slice(1), expanded: saleTxRecentExpanded, prefix: 'saleTxRecent8', isMonth: false },
      saleTxRecent6Toggle: { subDates: saleTxRecent6Dates.slice(1), expanded: saleTxRecent6Expanded, prefix: 'saleTxRecent6', isMonth: true },
      leaseTxRecent8Toggle: { subDates: leaseTxRecent8Dates.slice(1), expanded: leaseTxRecentExpanded, prefix: 'leaseTxRecent8', isMonth: false },
      leaseTxRecent6Toggle: { subDates: leaseTxRecent6Dates.slice(1), expanded: leaseTxRecent6Expanded, prefix: 'leaseTxRecent6', isMonth: true },
      tradeArticleCountRecent8Toggle: { subDates: saleCountRecent8Dates.slice(1), expanded: saleCountRecentExpanded, prefix: 'tradeArticleCountRecent8', isMonth: false },
      tradeArticleCountRecent6Toggle: { subDates: saleCountRecent6Dates.slice(1), expanded: saleCountRecent6Expanded, prefix: 'tradeArticleCountRecent6', isMonth: true },
      leaseArticleCountRecent8Toggle: { subDates: leaseCountRecent8Dates.slice(1), expanded: leaseCountRecentExpanded, prefix: 'leaseArticleCountRecent8', isMonth: false },
      leaseArticleCountRecent6Toggle: { subDates: leaseCountRecent6Dates.slice(1), expanded: leaseCountRecent6Expanded, prefix: 'leaseArticleCountRecent6', isMonth: true },
      saleRealCountRecent8Toggle: { subDates: saleTxCountRecent8Dates.slice(1), expanded: saleTxCountRecentExpanded, prefix: 'saleRealCountRecent8', isMonth: false },
      saleRealCountRecent6Toggle: { subDates: saleTxCountRecent6Dates.slice(1), expanded: saleTxCountRecent6Expanded, prefix: 'saleRealCountRecent6', isMonth: true },
      leaseRealCountRecent8Toggle: { subDates: leaseTxCountRecent8Dates.slice(1), expanded: leaseTxCountRecentExpanded, prefix: 'leaseRealCountRecent8', isMonth: false },
      leaseRealCountRecent6Toggle: { subDates: leaseTxCountRecent6Dates.slice(1), expanded: leaseTxCountRecent6Expanded, prefix: 'leaseRealCountRecent6', isMonth: true },
    };

    return normalizedOrder.map((id) => {
      const group = groupMap.get(id);
      let groupItems = (Array.isArray(groupItemsArg?.[group.id]) ? groupItemsArg[group.id] : [])
        // 병합된 컬럼(생활권→지역, 세대수(평)→세대수)은 저장된 설정에 남아있어도 별도 노출하지 않음
        .filter((item) => !MERGED_COLUMN_KEYS.has(String(item?.columnKey || '')))
        .map((item) => {
          const visibleKey = getGroupItemVisibleKey(group.id, item);
          const alwaysVisible = isAlwaysVisibleColumnKey(item?.columnKey);
          return {
            ...item,
            visibleKey,
            alwaysVisible,
            locked: alwaysVisible,
            enabled: alwaysVisible ? true : (visibleKey ? (visibleMapArg?.[visibleKey] !== false) : true),
          };
        });

      // 토글형(최근 8주/6개월/5개월) 컬럼을 펼치면 날짜별 하위 컬럼으로 확장.
      // 그룹 id에 의존하지 않고 토글 컬럼키(recentToggleExpansions)로 처리 → 최상위 그룹 분리 후에도 동작.
      {
        const next = [];
        groupItems.forEach((item) => {
          next.push(item);
          const itemKey = String(item?.columnKey || '');
          const exp = recentToggleExpansions[itemKey];
          if (!forPanel && exp && exp.expanded && item.enabled !== false) {
            exp.subDates.forEach((date) => {
              const key = `${exp.prefix}__${date}`;
              next.push({
                id: `${group.id}__${exp.prefix}__${date}`,
                label: exp.isMonth ? formatDateYYMM(date) : formatDateMMDD(date),
                columnKey: key,
                visibleKey: key,
                enabled: true,
              });
            });
          }
        });
        groupItems = next;
      }

      const groupLocked = !!group.locked;
      return {
        id: group.id,
        name: group.name,
        locked: groupLocked,
        // 잠금 그룹(단지명)은 항상 표시 — 그룹 토글과 무관
        enabled: groupLocked ? true : (groupPrefsArg?.[group.id] !== false),
        items: groupItems,
      };
    });
  };
  const groupedColumnSections = useMemo(
    () => buildGroupedColumnSections(columnGroupOrder, columnGroupPrefs, columnGroupItemsById, columnPrefs.visible),
    [
      columnGroupOrder,
      columnGroupPrefs,
      columnGroupItemsById,
      columnPrefs.visible,
      saleRecentExpanded,
      leaseRecentExpanded,
      saleRecent6Expanded,
      leaseRecent6Expanded,
      leaseRateRecent6Expanded,
      leaseRateChangeRecent5Expanded,
      saleRecent8Dates,
      saleRecent6Dates,
      leaseRecent8Dates,
      leaseRecent6Dates,
      saleTxRecentExpanded,
      leaseTxRecentExpanded,
      saleTxRecent6Expanded,
      leaseTxRecent6Expanded,
      saleTxRecent8Dates,
      saleTxRecent6Dates,
      leaseTxRecent8Dates,
      leaseTxRecent6Dates,
      saleCountRecentExpanded,
      leaseCountRecentExpanded,
      saleCountRecent6Expanded,
      leaseCountRecent6Expanded,
      saleCountRecent8Dates,
      saleCountRecent6Dates,
      leaseCountRecent8Dates,
      leaseCountRecent6Dates,
      saleTxCountRecentExpanded,
      leaseTxCountRecentExpanded,
      saleTxCountRecent6Expanded,
      leaseTxCountRecent6Expanded,
      saleTxCountRecent8Dates,
      saleTxCountRecent6Dates,
      leaseTxCountRecent8Dates,
      leaseTxCountRecent6Dates,
    ]
  );
  const groupedColumnSectionsForPanel = useMemo(() => {
    const source = columnSettingDraft || {
      columnPrefs,
      columnGroupPrefs,
      columnGroupOrder,
      columnGroupItemsById,
    };
    return buildGroupedColumnSections(
      source.columnGroupOrder,
      source.columnGroupPrefs,
      source.columnGroupItemsById,
      source.columnPrefs?.visible,
      true // forPanel = true
    );
  }, [
    columnSettingDraft,
    columnPrefs,
    columnGroupPrefs,
    columnGroupOrder,
    columnGroupItemsById,
  ]);
  const groupedTableColumns = useMemo(() => {
    const usedColumnKeys = new Set();
    return groupedColumnSections
    .filter((group) => group.enabled !== false)
    .map((group, groupIndex, groups) => {
      const groupItems = (Array.isArray(group.items) ? group.items : []).filter((item) => item?.enabled !== false);
      const children = groupItems.map((item, index) => {
        const mappedKey = item?.columnKey ? String(item.columnKey) : '';
        const boundaryClass = [
          index === 0 ? 'complex-group-col-start' : '',
          index === groupItems.length - 1 ? 'complex-group-col-end' : '',
          // 최근 8주(파랑) / 최근 6개월(녹색) 데이터셀 글자색 구분
          mappedKey.includes('Recent8__') ? 'complex-recent8-col' : '',
          mappedKey.includes('Recent6__') ? 'complex-recent6-col' : '',
        ].filter(Boolean).join(' ');
        const source = mappedKey ? tableColumnMap.get(mappedKey) : null;
        if (source && !usedColumnKeys.has(source.key)) {
          usedColumnKeys.add(source.key);
          return {
            ...source,
            key: source.key,
            dataKey: source.key,
            className: `${source.className || ''} ${boundaryClass}`.trim(),
            headerClassName: `${source.headerClassName || ''} ${boundaryClass}`.trim(),
          };
        }

        if (mappedKey.startsWith('articleTradePriceRecent8__')) {
          const date = mappedKey.split('__')[1] || '';
          return {
            key: mappedKey,
            dataKey: mappedKey,
            label: String(item?.label || ''),
            sortable: false,
            className: boundaryClass,
            headerClassName: boundaryClass,
            render: (row) => {
              const point = getRecentPointList(row, 'sale8').find((v) => v.date === date);
              return point ? formatKrw(point.amt, useKoreanPriceUnitRef.current) : '-';
            },
          };
        }
        if (mappedKey.startsWith('articleTradePriceRecent6__')) {
          const date = mappedKey.split('__')[1] || '';
          return {
            key: mappedKey,
            dataKey: mappedKey,
            label: String(item?.label || ''),
            sortable: false,
            className: boundaryClass,
            headerClassName: boundaryClass,
            render: (row) => {
              const point = getRecentPointList(row, 'sale6').find((v) => v.date === date);
              return point ? formatKrw(point.amt, useKoreanPriceUnitRef.current) : '-';
            },
          };
        }
        if (mappedKey.startsWith('articleLeasePriceRecent8__')) {
          const date = mappedKey.split('__')[1] || '';
          return {
            key: mappedKey,
            dataKey: mappedKey,
            label: String(item?.label || ''),
            sortable: false,
            className: boundaryClass,
            headerClassName: boundaryClass,
            render: (row) => {
              const point = getRecentPointList(row, 'lease8').find((v) => v.date === date);
              return point ? formatKrw(point.amt, useKoreanPriceUnitRef.current) : '-';
            },
          };
        }
        if (mappedKey.startsWith('articleLeasePriceRecent6__')) {
          const date = mappedKey.split('__')[1] || '';
          return {
            key: mappedKey,
            dataKey: mappedKey,
            label: String(item?.label || ''),
            sortable: false,
            className: boundaryClass,
            headerClassName: boundaryClass,
            render: (row) => {
              const point = getRecentPointList(row, 'lease6').find((v) => v.date === date);
              return point ? formatKrw(point.amt, useKoreanPriceUnitRef.current) : '-';
            },
          };
        }
        if (mappedKey.startsWith('leaseRateRecent6__')) {
          const date = mappedKey.split('__')[1] || '';
          return {
            key: mappedKey,
            dataKey: mappedKey,
            label: String(item?.label || ''),
            sortable: false,
            className: boundaryClass,
            headerClassName: boundaryClass,
            render: (row) => {
              const rate = getLeaseRateAt(row, date);
              return rate == null ? '-' : `${rate.toFixed(1)}%`;
            },
          };
        }
        if (mappedKey.startsWith('leaseRateChangeRecent5__')) {
          const date = mappedKey.split('__')[1] || '';
          return {
            key: mappedKey,
            dataKey: mappedKey,
            label: String(item?.label || ''),
            sortable: false,
            className: boundaryClass,
            headerClassName: boundaryClass,
            render: (row) => {
              const idx = leaseRecent6Dates.indexOf(date);
              const v = getLeaseRateChangeAt(row, leaseRecent6Dates, idx);
              if (v == null) return '-';
              const sign = v > 0 ? '+' : '';
              return <span style={{ color: v < 0 ? '#2563eb' : undefined }}>{`${sign}${v.toFixed(1)}%`}</span>;
            },
          };
        }
        if (mappedKey.startsWith('saleTxRecent8__')) {
          const date = mappedKey.split('__')[1] || '';
          return {
            key: mappedKey,
            dataKey: mappedKey,
            label: String(item?.label || ''),
            sortable: false,
            className: boundaryClass,
            headerClassName: boundaryClass,
            render: (row) => {
              const point = getRecentPointList(row, 'saleTx8').find((v) => v.date === date);
              return point ? formatKrw(point.amt, useKoreanPriceUnitRef.current) : '-';
            },
          };
        }
        if (mappedKey.startsWith('saleTxRecent6__')) {
          const date = mappedKey.split('__')[1] || '';
          return {
            key: mappedKey,
            dataKey: mappedKey,
            label: String(item?.label || ''),
            sortable: false,
            className: boundaryClass,
            headerClassName: boundaryClass,
            render: (row) => {
              const point = getRecentPointList(row, 'saleTx6').find((v) => v.date === date);
              return point ? formatKrw(point.amt, useKoreanPriceUnitRef.current) : '-';
            },
          };
        }
        if (mappedKey.startsWith('leaseTxRecent8__')) {
          const date = mappedKey.split('__')[1] || '';
          return {
            key: mappedKey,
            dataKey: mappedKey,
            label: String(item?.label || ''),
            sortable: false,
            className: boundaryClass,
            headerClassName: boundaryClass,
            render: (row) => {
              const point = getRecentPointList(row, 'leaseTx8').find((v) => v.date === date);
              return point ? formatKrw(point.amt, useKoreanPriceUnitRef.current) : '-';
            },
          };
        }
        if (mappedKey.startsWith('leaseTxRecent6__')) {
          const date = mappedKey.split('__')[1] || '';
          return {
            key: mappedKey,
            dataKey: mappedKey,
            label: String(item?.label || ''),
            sortable: false,
            className: boundaryClass,
            headerClassName: boundaryClass,
            render: (row) => {
              const point = getRecentPointList(row, 'leaseTx6').find((v) => v.date === date);
              return point ? formatKrw(point.amt, useKoreanPriceUnitRef.current) : '-';
            },
          };
        }
        if (mappedKey.startsWith('tradeArticleCountRecent8__')) {
          const date = mappedKey.split('__')[1] || '';
          return {
            key: mappedKey,
            dataKey: mappedKey,
            label: String(item?.label || ''),
            sortable: false,
            className: boundaryClass,
            headerClassName: boundaryClass,
            render: (row) => {
              const point = getRecentPointList(row, 'saleCount8').find((v) => v.date === date);
              return point ? formatCount(point.amt, useKoreanPriceUnit) : '-';
            },
          };
        }
        if (mappedKey.startsWith('tradeArticleCountRecent6__')) {
          const date = mappedKey.split('__')[1] || '';
          return {
            key: mappedKey,
            dataKey: mappedKey,
            label: String(item?.label || ''),
            sortable: false,
            className: boundaryClass,
            headerClassName: boundaryClass,
            render: (row) => {
              const point = getRecentPointList(row, 'saleCount6').find((v) => v.date === date);
              return point ? formatCount(point.amt, useKoreanPriceUnit) : '-';
            },
          };
        }
        if (mappedKey.startsWith('leaseArticleCountRecent8__')) {
          const date = mappedKey.split('__')[1] || '';
          return {
            key: mappedKey,
            dataKey: mappedKey,
            label: String(item?.label || ''),
            sortable: false,
            className: boundaryClass,
            headerClassName: boundaryClass,
            render: (row) => {
              const point = getRecentPointList(row, 'leaseCount8').find((v) => v.date === date);
              return point ? formatCount(point.amt, useKoreanPriceUnit) : '-';
            },
          };
        }
        if (mappedKey.startsWith('leaseArticleCountRecent6__')) {
          const date = mappedKey.split('__')[1] || '';
          return {
            key: mappedKey,
            dataKey: mappedKey,
            label: String(item?.label || ''),
            sortable: false,
            className: boundaryClass,
            headerClassName: boundaryClass,
            render: (row) => {
              const point = getRecentPointList(row, 'leaseCount6').find((v) => v.date === date);
              return point ? formatCount(point.amt, useKoreanPriceUnit) : '-';
            },
          };
        }
        if (mappedKey.startsWith('saleRealCountRecent8__')) {
          const date = mappedKey.split('__')[1] || '';
          return {
            key: mappedKey,
            dataKey: mappedKey,
            label: String(item?.label || ''),
            sortable: false,
            className: boundaryClass,
            headerClassName: boundaryClass,
            render: (row) => {
              const point = getRecentPointList(row, 'saleTxCount8').find((v) => v.date === date);
              return point ? formatCount(point.amt, useKoreanPriceUnit) : '-';
            },
          };
        }
        if (mappedKey.startsWith('saleRealCountRecent6__')) {
          const date = mappedKey.split('__')[1] || '';
          return {
            key: mappedKey,
            dataKey: mappedKey,
            label: String(item?.label || ''),
            sortable: false,
            className: boundaryClass,
            headerClassName: boundaryClass,
            render: (row) => {
              const point = getRecentPointList(row, 'saleTxCount6').find((v) => v.date === date);
              return point ? formatCount(point.amt, useKoreanPriceUnit) : '-';
            },
          };
        }
        if (mappedKey.startsWith('leaseRealCountRecent8__')) {
          const date = mappedKey.split('__')[1] || '';
          return {
            key: mappedKey,
            dataKey: mappedKey,
            label: String(item?.label || ''),
            sortable: false,
            className: boundaryClass,
            headerClassName: boundaryClass,
            render: (row) => {
              const point = getRecentPointList(row, 'leaseTxCount8').find((v) => v.date === date);
              return point ? formatCount(point.amt, useKoreanPriceUnit) : '-';
            },
          };
        }
        if (mappedKey.startsWith('leaseRealCountRecent6__')) {
          const date = mappedKey.split('__')[1] || '';
          return {
            key: mappedKey,
            dataKey: mappedKey,
            label: String(item?.label || ''),
            sortable: false,
            className: boundaryClass,
            headerClassName: boundaryClass,
            render: (row) => {
              const point = getRecentPointList(row, 'leaseTxCount6').find((v) => v.date === date);
              return point ? formatCount(point.amt, useKoreanPriceUnit) : '-';
            },
          };
        }

        return {
          key: `${group.id}__empty__${item?.id || index}`,
          dataKey: null,
          label: String(item?.label || ''),
          sortable: false,
          className: boundaryClass,
          headerClassName: boundaryClass,
          render: () => '',
        };
      });

      // 단지명 잠금 그룹은 그룹 헤더 없이 단독(고정) 컬럼으로 렌더 → '단지명'/'단지명' 중복 헤더 방지
      if (group.id === 'buildingNameGroup') {
        return children[0] || null;
      }

      // 표시할 컬럼이 하나도 없는 그룹(예: 토글 OFF 상태의 최근 8주/6개월 전용 그룹)은 빈 헤더가 남지 않게 제외
      if (children.length === 0) return null;

      return {
        key: `group__${group.id}`,
        label: group.name,
        headerClassName: [
          'complex-group-header',
          groupIndex === 0 ? 'complex-group-header-start' : '',
          groupIndex === groups.length - 1 ? 'complex-group-header-end' : '',
        ].filter(Boolean).join(' '),
        // 세금 그룹 헤더에만 톱니(주택수/생애최초 설정) 컴포넌트 부착
        ...(group.id === 'tax' ? {
          headerGroupComponent: TaxGroupHeader,
          headerGroupComponentParams: { onOpenTax: openTaxPopover },
        } : {}),
        columns: children,
      };
    })
    .filter(Boolean);
  }, [groupedColumnSections, tableColumnMap, openTaxPopover]);
  const fixedGroupColumns = useMemo(() => {
    const baseChildren = Array.isArray(fixedColumns) ? fixedColumns : [];
    const children = baseChildren.map((column, index) => {
      const boundaryClass = [
        index === 0 ? 'complex-group-col-start' : '',
        index === baseChildren.length - 1 ? 'complex-group-col-end' : '',
      ].filter(Boolean).join(' ');
      return {
        ...column,
        key: column.key,
        dataKey: column.key,
        className: `${column.className || ''} ${boundaryClass}`.trim(),
        headerClassName: `${column.headerClassName || ''} ${boundaryClass}`.trim(),
      };
    });
    if (children.length === 0) return [];
    const compareColumn = children[0] || null;
    const remainChildren = children.slice(1);
    const next = [];
    if (compareColumn) {
      next.push(compareColumn);
    }
    if (remainChildren.length > 0) {
      next.push({
        key: 'group__fixed',
        label: '기본',
        headerClassName: 'complex-group-header',
        columns: remainChildren,
      });
    }
    return next;
  }, [fixedColumns]);
  const finalTableColumns = useMemo(() => {
    // 그룹 배열 얕은 복제(구분 추출 시 원본 불변 유지)
    const groups = groupedTableColumns.map((g) => (
      g && Array.isArray(g.columns) ? { ...g, columns: [...g.columns] } : g
    ));

    // 화면설정에서 구분(gubun)이 켜져 있으면(단지정보 그룹에 존재) 단지명 왼쪽으로 이동해 고정
    let gubunLeaf = null;
    const complexGroup = groups.find((g) => g?.key === 'group__complexInfo');
    if (complexGroup && Array.isArray(complexGroup.columns)) {
      const gi = complexGroup.columns.findIndex((c) => c?.key === 'gubun');
      if (gi >= 0) {
        // 좌측 고정 + 좁은 너비(구분 값이 짧아 flex로 넓어지는 것 방지)
        gubunLeaf = { ...complexGroup.columns[gi], fixed: true, width: 64, minWidth: 52, maxWidth: 84 };
        complexGroup.columns.splice(gi, 1);
      }
    }
    // 구분만 있던 그룹이 비면 제거(빈 그룹 헤더 방지)
    const cleaned = groups.filter((g) => !(g && Array.isArray(g.columns) && g.columns.length === 0));
    // 구분 리프를 맨 앞(단지명 리프 앞)에 배치
    const orderedGroups = gubunLeaf ? [gubunLeaf, ...cleaned] : cleaned;
    return [...fixedGroupColumns, ...orderedGroups];
  }, [fixedGroupColumns, groupedTableColumns]);
  // 조건부 서식 타깃 옵션: 그룹(대분류) / 리프 컬럼
  const colorTargetGroups = useMemo(
    () => DISPLAY_COLUMN_GROUPS.filter((g) => !g.hidden && !g.locked).map((g) => ({ id: g.id, name: g.name })),
    []
  );
  const colorTargetColumns = useMemo(() => {
    const out = [];
    const walk = (cols) => (Array.isArray(cols) ? cols : []).forEach((c) => {
      if (Array.isArray(c?.columns) && c.columns.length) walk(c.columns);
      else if (c?.key) out.push({ key: String(c.key), label: typeof c.label === 'string' ? c.label : String(c.key) });
    });
    walk(finalTableColumns);
    return out;
  }, [finalTableColumns]);
  const ruleTargetLabel = (r) => {
    if (r.scope === 'group') return colorTargetGroups.find((g) => g.id === r.targetKey)?.name || r.targetKey;
    return colorTargetColumns.find((c) => c.key === r.targetKey)?.label || r.targetKey;
  };
  const colorSelStyle = { height: '30px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '12px', color: '#334155', padding: '0 6px', background: '#fff' };
  const colorInpStyle = { height: '30px', minWidth: 0, width: '100%', boxSizing: 'border-box', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '12px', color: '#334155', padding: '0 6px' };
  const visibleConfigurableCount = useMemo(
    () => groupedTableColumns.reduce((acc, group) => acc + (Array.isArray(group.columns) ? group.columns.length : 0), 0),
    [groupedTableColumns]
  );
  const totalGroupedItemsCount = useMemo(
    () => DISPLAY_COLUMN_GROUPS
      .filter((group) => !group.hidden)
      .reduce((acc, group) => acc + (Array.isArray(group.items) ? group.items.length : 0), 0),
    []
  );
  const sortableColumns = useMemo(
    () => tableColumnsWithHeaderFilters
      .filter((column) => !!column.sortable)
      .map((column) => ({
        key: column.key,
        label: typeof column.label === 'string' ? column.label : column.key,
      })),
    [tableColumnsWithHeaderFilters]
  );
  const sortableColumnKeySet = useMemo(
    // 표시 컬럼이 없지만 프리셋 정렬에만 쓰이는 키(월간/주간 매매변동)도 허용
    () => new Set([...sortableColumns.map((column) => column.key), ...EXTRA_SORT_ONLY_KEYS]),
    [sortableColumns]
  );
  const sortColumnLabelMap = useMemo(
    () => {
      const map = sortableColumns.reduce((acc, column) => {
        acc[column.key] = column.label;
        return acc;
      }, {});
      Object.entries(EXTRA_SORT_ONLY_LABELS).forEach(([key, label]) => {
        if (!map[key]) map[key] = label;
      });
      return map;
    },
    [sortableColumns]
  );
  const sortPresetLocked = true;
  const activeSortPresetRules = useMemo(() => {
    // 선택된 프리셋이 없으면 지역순 1개만 적용 (최초 로드 시)
    const presetKey = selectedSortPreset || '지역';
    const rules = Array.isArray(SORT_PRESET_RULE_MAP?.[presetKey]) ? SORT_PRESET_RULE_MAP[presetKey] : [];
    const seen = new Set();
    const filteredRules = rules
      .map((row) => ({
        key: String(row?.key || ''),
        direction: row?.direction === 'desc' ? 'desc' : 'asc',
      }))
      .filter((row) => {
        if (!row.key || seen.has(row.key) || !sortableColumnKeySet.has(row.key)) return false;
        seen.add(row.key);
        return true;
      });

    // 선택된 프리셋이 없으면 지역순 첫 번째만 반환 (최초 로드)
    if (!selectedSortPreset && filteredRules.length > 0) {
      return [filteredRules[0]]; // region만
    }
    return filteredRules;
  }, [selectedSortPreset, sortableColumnKeySet]);
  const getSortPresetDescription = (presetKey) => {
    const rules = Array.isArray(SORT_PRESET_RULE_MAP?.[presetKey]) ? SORT_PRESET_RULE_MAP[presetKey] : [];
    if (rules.length === 0) return '';
    return rules
      .map((rule, index) => {
        const label = sortColumnLabelMap[rule.key] || rule.key;
        const arrow = rule.direction === 'desc' ? '↓' : '↑';
        return `${index + 1}. ${label} ${arrow}`;
      })
      .join('\n');
  };
  const sanitizedSortPrefs = useMemo(() => {
    const seen = new Set();
    const next = [];
    (Array.isArray(sortPrefs) ? sortPrefs : []).forEach((row) => {
      const key = String(row?.key || '');
      if (!key || seen.has(key) || !sortableColumnKeySet.has(key)) return;
      seen.add(key);
      next.push({
        key,
        direction: row?.direction === 'desc' ? 'desc' : 'asc',
      });
    });
    return next;
  }, [sortPrefs, sortableColumnKeySet]);

  // AG-Grid에 모든 정렬 규칙 전달 (보이지 않는 컬럼도 정렬에 영향)
  const tanstackSorting = useMemo(
    () => sanitizedSortPrefs.map((row) => ({ id: row.key, desc: row.direction === 'desc' })),
    [sanitizedSortPrefs]
  );
  const presetSortCount = activeSortPresetRules.length;
  const hasExtraSortRules = sanitizedSortPrefs.length > presetSortCount;
  const isPresetSortIndex = (index) => index < presetSortCount;

  // 정렬 프리셋 버튼 클릭 시 데이터 재로드
  const prevSortPresetRef = useRef(selectedSortPreset);
  useEffect(() => {
    if (!sortPresetLocked) return;

    // 정렬 프리셋이 실제로 변경되었을 때만 데이터 재로드
    const presetChanged = prevSortPresetRef.current !== selectedSortPreset;

    if (!presetChanged) return;

    prevSortPresetRef.current = selectedSortPreset;

    // activeSortPresetRules로 완전히 초기화 (기존 정렬 설정 제거)
    setSortPrefs(activeSortPresetRules);

    // 정렬 변경 시 offset 초기화 (load는 sortPrefs dependency에서 자동 호출됨)
    setNextLastKey('0'); // offset을 0으로 설정
    setHasMore(true);
  }, [sortPresetLocked, selectedSortPreset]);

  useEffect(() => {
    const payload = {
      order: sanitizedColumnOrder,
      visible: columnPrefs.visible,
      groupVisible: columnGroupPrefs,
      groupOrder: columnGroupOrder,
      groupItems: columnGroupItemsById,
    };
    localStorage.setItem(userColumnStorageKey, JSON.stringify(payload));
  }, [sanitizedColumnOrder, columnPrefs.visible, columnGroupPrefs, columnGroupOrder, columnGroupItemsById, userColumnStorageKey]);

  // 정렬설정은 메모리에서만 관리 (페이지 나가면 휘발)
  // useEffect(() => {
  //   localStorage.setItem(userSortStorageKey, JSON.stringify(sanitizedSortPrefs));
  // }, [sanitizedSortPrefs, userSortStorageKey]);

  const createCurrentScreenSettingDraft = () => ({
    columnPrefs: {
      order: Array.isArray(columnPrefs.order) ? [...columnPrefs.order] : [],
      visible: (() => {
        const nextVisible = columnPrefs.visible && typeof columnPrefs.visible === 'object' ? { ...columnPrefs.visible } : {};
        if (nextVisible.buildingName === false) delete nextVisible.buildingName;
        return nextVisible;
      })(),
    },
    columnGroupPrefs: columnGroupPrefs && typeof columnGroupPrefs === 'object' ? { ...columnGroupPrefs } : {},
    columnGroupOrder: Array.isArray(columnGroupOrder) ? [...columnGroupOrder] : [],
    columnGroupItemsById: normalizeGroupItemsById(columnGroupItemsById),
    // sortPrefs는 화면설정과 별도로 관리
    useKoreanPriceUnit: !!useKoreanPriceUnit,
  });

  const hasUnsavedChanges = () => {
    // 순서 변경이 pending 상태면 저장 안 된 변경사항이 있음
    if (pendingScreenSettingOrder !== null) return true;

    if (!columnSettingDraft || !initialColumnSettingDraft) return false;
    try {
      return JSON.stringify(columnSettingDraft) !== JSON.stringify(initialColumnSettingDraft);
    } catch {
      return false;
    }
  };

  const applyColumnSettingDraftAndClose = () => {
    if (columnSettingDraft) {
      setColumnPrefs(columnSettingDraft.columnPrefs || { order: [], visible: {} });
      setColumnGroupPrefs(columnSettingDraft.columnGroupPrefs || {});
      setColumnGroupOrder(Array.isArray(columnSettingDraft.columnGroupOrder) ? columnSettingDraft.columnGroupOrder : []);
      setColumnGroupItemsById(columnSettingDraft.columnGroupItemsById || defaultGroupItemsById);
      setSortPrefs(Array.isArray(columnSettingDraft.sortPrefs) ? columnSettingDraft.sortPrefs : []);
    }
    setColumnSettingDraft(null);
    setInitialColumnSettingDraft(null);
    setPendingScreenSettingOrder(null); // 모달 닫을 때 pending 순서 초기화
    setColumnSettingOpen(false);
  };

  const handleColumnSettingButtonClick = () => {
    if (!columnSettingOpen) {
      const draft = createCurrentScreenSettingDraft();
      setColumnSettingDraft(draft);
      setInitialColumnSettingDraft(JSON.parse(JSON.stringify(draft))); // Deep copy
      setColumnSettingOpen(true);
      setSortSettingOpen(false);
      return;
    }
    applyColumnSettingDraftAndClose();
  };

  const setDraftWithUpdater = (updater) => {
    setColumnSettingDraft((prev) => {
      const base = prev || createCurrentScreenSettingDraft();
      return typeof updater === 'function' ? updater(base) : base;
    });
  };

  const panelToggleColumnGroupItemVisible = (groupId, itemId) => {
    setDraftWithUpdater((prev) => {
      const groupItems = Array.isArray(prev?.columnGroupItemsById?.[groupId]) ? prev.columnGroupItemsById[groupId] : [];
      const target = groupItems.find((item) => String(item?.id || '') === String(itemId || ''));
      if (!target) return prev;
      if (isAlwaysVisibleColumnKey(target?.columnKey)) return prev;
      const visibleKey = getGroupItemVisibleKey(groupId, target);
      if (!visibleKey) return prev;
      const current = prev.columnPrefs?.visible?.[visibleKey] !== false;
      const nextVisible = {
        ...(prev.columnPrefs?.visible || {}),
        [visibleKey]: !current,
      };
      // 그룹(맨 상위)은 하위 항목 상태를 따라감: 하나도 없으면 해제, 하나라도 있으면 자동 체크.
      // 패널에 실제 표시되는 항목 기준으로만 판단:
      //  - 병합 컬럼(MERGED_COLUMN_KEYS: 생활권/세대수(평)/급지(생활권)/구조/연차)은 패널에 안 보이므로 제외
      //    (저장된 화면설정에 이 항목들이 기본표시로 남아있어 그룹이 안 꺼지던 문제)
      //  - placeholder(빈 columnKey)도 제외
      const anyItemEnabled = groupItems
        .filter((item) => !MERGED_COLUMN_KEYS.has(String(item?.columnKey || '')))
        .some((item) => {
          if (isAlwaysVisibleColumnKey(item?.columnKey)) return true;
          const mappedKey = String(item?.columnKey || '').trim();
          if (!mappedKey) return false;
          const key = getGroupItemVisibleKey(groupId, item);
          if (!key) return false;
          return nextVisible[key] !== false;
        });
      return {
        ...prev,
        columnPrefs: {
          ...(prev.columnPrefs || {}),
          visible: nextVisible,
        },
        columnGroupPrefs: {
          ...(prev.columnGroupPrefs || {}),
          [groupId]: anyItemEnabled,
        },
      };
    });
  };

  const panelToggleColumnGroupVisible = (groupId) => {
    if (groupId === 'buildingNameGroup') return; // 잠금 그룹(단지명)은 토글 불가
    setDraftWithUpdater((prev) => ({
      ...prev,
      columnGroupPrefs: {
        ...(prev.columnGroupPrefs || {}),
        [groupId]: prev.columnGroupPrefs?.[groupId] === false ? true : false,
      },
    }));
  };

  const panelMoveColumnGroupOrder = (groupId, direction) => {
    setDraftWithUpdater((prev) => {
      const order = Array.isArray(prev.columnGroupOrder) ? [...prev.columnGroupOrder] : [];
      const currentIndex = order.indexOf(groupId);
      if (currentIndex < 0) return prev;
      const nextIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
      if (nextIndex < 0 || nextIndex >= order.length) return prev;
      [order[currentIndex], order[nextIndex]] = [order[nextIndex], order[currentIndex]];
      return { ...prev, columnGroupOrder: order };
    });
  };

  const panelReorderColumnGroup = (sourceGroupId, targetGroupId) => {
    if (!sourceGroupId || !targetGroupId || sourceGroupId === targetGroupId) return;
    setDraftWithUpdater((prev) => {
      const order = Array.isArray(prev.columnGroupOrder) ? [...prev.columnGroupOrder] : [];
      const sourceIndex = order.indexOf(sourceGroupId);
      const targetIndex = order.indexOf(targetGroupId);
      if (sourceIndex < 0 || targetIndex < 0) return prev;
      const [picked] = order.splice(sourceIndex, 1);
      order.splice(targetIndex, 0, picked);
      return { ...prev, columnGroupOrder: order };
    });
  };

  const panelMoveColumnGroupItem = (groupId, itemId, direction) => {
    if (!groupId || !itemId) return;
    setDraftWithUpdater((prev) => {
      const next = { ...(prev?.columnGroupItemsById || {}) };
      const items = Array.isArray(next[groupId]) ? [...next[groupId]] : [];
      const index = items.findIndex((item) => String(item?.id || '') === String(itemId));
      if (index < 0) return prev;
      const targetIndex = direction === 'left' ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= items.length) return prev;
      [items[index], items[targetIndex]] = [items[targetIndex], items[index]];
      next[groupId] = items;
      return { ...prev, columnGroupItemsById: next };
    });
  };

  const toggleColumnVisible = (key) => {
    if (isAlwaysVisibleColumnKey(key)) return;
    setColumnPrefs((prev) => {
      const current = prev.visible[key] !== false;
      return {
        ...prev,
        visible: {
          ...prev.visible,
          [key]: !current,
        },
      };
    });
  };

  const moveColumnOrder = (targetKey, direction) => {
    setColumnPrefs((prev) => {
      const order = prev.order.length > 0 ? [...prev.order] : [...defaultConfigurableOrder];
      defaultConfigurableOrder.forEach((key) => {
        if (!order.includes(key)) order.push(key);
      });
      const currentIndex = order.indexOf(targetKey);
      if (currentIndex < 0) return prev;

      const nextIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
      if (nextIndex < 0 || nextIndex >= order.length) return prev;

      const nextOrder = [...order];
      [nextOrder[currentIndex], nextOrder[nextIndex]] = [nextOrder[nextIndex], nextOrder[currentIndex]];
      return { ...prev, order: nextOrder };
    });
  };

  const handleHeaderColumnReorder = (fromKey, toKey) => {
    if (!fromKey || !toKey || fromKey === toKey) return;
    if (fixedColumnKeys.has(fromKey) || fixedColumnKeys.has(toKey)) return;

    setColumnPrefs((prev) => {
      const order = prev.order.length > 0 ? [...prev.order] : [...defaultConfigurableOrder];
      defaultConfigurableOrder.forEach((key) => {
        if (!order.includes(key)) order.push(key);
      });

      const fromIndex = order.indexOf(fromKey);
      const toIndex = order.indexOf(toKey);
      if (fromIndex < 0 || toIndex < 0) return prev;

      const nextOrder = [...order];
      const [picked] = nextOrder.splice(fromIndex, 1);
      nextOrder.splice(toIndex, 0, picked);
      return { ...prev, order: nextOrder };
    });
  };

  const setAllColumnsVisible = (visible) => {
    setColumnPrefs((prev) => {
      const nextVisible = { ...prev.visible };
      defaultConfigurableOrder.forEach((key) => {
        nextVisible[key] = visible;
      });
      return { ...prev, visible: nextVisible };
    });
    setColumnGroupPrefs(() => {
      const next = {};
      DISPLAY_COLUMN_GROUPS.forEach((group) => {
        if (!group.hidden) next[group.id] = visible;
      });
      return next;
    });
  };

  const resetColumnPrefs = () => {
    setColumnPrefs({ order: [...defaultConfigurableOrder], visible: {} });
    setColumnGroupPrefs({});
    setColumnGroupItemsById(defaultGroupItemsById);
  };

  const addSortRule = () => {
    const used = new Set(sanitizedSortPrefs.map((row) => row.key));
    // 기본 추가 키도 허용 목록에서 선택
    const nextColumn = SORT_ADD_ALLOWED_COLUMNS.find((column) => sortableColumnKeySet.has(column.key) && !used.has(column.key));
    if (!nextColumn) return;
    setSortPrefs((prev) => [...(Array.isArray(prev) ? prev : []), { key: nextColumn.key, direction: 'asc' }]);
  };

  const removeSortRule = (index) => {
    if (isPresetSortIndex(index)) return;
    setSortPrefs((prev) => (Array.isArray(prev) ? prev.filter((_, idx) => idx !== index) : []));
  };

  const updateSortRuleKey = (index, nextKey) => {
    if (isPresetSortIndex(index)) return;
    const key = String(nextKey || '');
    if (!sortableColumnKeySet.has(key)) return;
    setSortPrefs((prev) => {
      const base = Array.isArray(prev) ? [...prev] : [];
      if (!base[index]) return prev;
      const duplicateIndex = base.findIndex((row, idx) => idx !== index && row?.key === key);
      if (duplicateIndex >= 0) return prev;
      base[index] = {
        key,
        direction: base[index]?.direction === 'desc' ? 'desc' : 'asc',
      };
      return base;
    });
  };

  const updateSortRuleDirection = (index, direction) => {
    if (isPresetSortIndex(index)) return;
    const nextDirection = direction === 'desc' ? 'desc' : 'asc';
    setSortPrefs((prev) => {
      const base = Array.isArray(prev) ? [...prev] : [];
      if (!base[index]) return prev;
      base[index] = { ...base[index], direction: nextDirection };
      return base;
    });
  };

  const moveSortRule = (index, direction) => {
    if (isPresetSortIndex(index)) return;
    setSortPrefs((prev) => {
      const base = Array.isArray(prev) ? [...prev] : [];
      if (!base[index]) return prev;
      const nextIndex = direction === 'up' ? index - 1 : index + 1;
      if (nextIndex < presetSortCount) return prev;
      if (nextIndex < 0 || nextIndex >= base.length) return prev;
      const next = [...base];
      [next[index], next[nextIndex]] = [next[nextIndex], next[index]];
      return next;
    });
  };

  const reorderSortRule = (fromIndex, toIndex) => {
    if (isPresetSortIndex(fromIndex) || isPresetSortIndex(toIndex)) return;
    if (toIndex < presetSortCount) return;
    setSortPrefs((prev) => {
      const base = Array.isArray(prev) ? [...prev] : [];
      if (fromIndex < 0 || fromIndex >= base.length) return prev;
      if (toIndex < 0 || toIndex >= base.length) return prev;
      if (fromIndex === toIndex) return prev;
      const next = [...base];
      const [movedItem] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, movedItem);
      return next;
    });
  };

  const resetSortPrefs = () => {
    setSortPrefs(sortPresetLocked ? activeSortPresetRules : []);
  };

  const handleTableSortingChange = (updater) => {
    setSortPrefs((prev) => {
      const prevArray = Array.isArray(prev) ? prev : [];
      const prevSorting = prevArray
        .filter((row) => sortableColumnKeySet.has(row.key))
        .map((row) => ({ id: row.key, desc: row.direction === 'desc' }));
      const nextSorting = typeof updater === 'function' ? updater(prevSorting) : updater;
      if (!Array.isArray(nextSorting)) return prevArray;
      const seen = new Set();
      const normalized = nextSorting
        .map((row) => ({
          key: String(row?.id || ''),
          direction: row?.desc ? 'desc' : 'asc',
        }))
        .filter((row) => {
          // 정렬설정 드롭다운에 없는 컬럼은 헤더 클릭으로 정렬되지 않도록 무시
          if (!row.key || seen.has(row.key) || !sortableColumnKeySet.has(row.key) || !headerSortableKeySet.has(row.key)) return false;
          seen.add(row.key);
          return true;
        });

      if (!sortPresetLocked) return normalized;

      // 프리셋 규칙은 앞쪽 순서를 고정하되, 헤더 클릭으로 사용자가 바꾼 방향(asc/desc)은 반영한다.
      // (예: 기본 region asc 상태에서 지역 컬럼 클릭 → region desc 로 백엔드 전달)
      const normalizedDirectionMap = new Map(normalized.map((row) => [row.key, row.direction]));
      const presetKeySet = new Set(activeSortPresetRules.map((row) => row.key));
      const mergedPresetRules = activeSortPresetRules.map((row) => ({
        key: row.key,
        direction: normalizedDirectionMap.has(row.key) ? normalizedDirectionMap.get(row.key) : row.direction,
      }));
      const extras = normalized.filter((row) => !presetKeySet.has(row.key));
      return [...mergedPresetRules, ...extras];
    });
  };

  const handleHeaderFilterTrigger = (columnKey, context) => {
    console.log('handleHeaderFilterTrigger called', { columnKey, headerFilterEnabled, context });

    if (!headerFilterEnabled) {
      console.log('headerFilterEnabled is false');
      return false;
    }

    const column = tableColumnMap.get(columnKey);
    console.log('column from tableColumnMap:', column);

    if (!column || !column.filterable) {
      console.log('column not filterable:', { column, filterable: column?.filterable });
      return false;
    }

    const rect = context?.anchorRect;
    console.log('rect:', rect);

    if (rect) {
      setFilterPopupColumn(column);

      // 필터 팝업의 예상 너비 (GradeFilter, GubunFilter 등의 일반적인 너비)
      const popupWidth = 400;
      const viewportWidth = window.innerWidth;

      // 기본 위치는 헤더 왼쪽 아래
      let leftPosition = rect.left + window.scrollX;

      // 우측으로 넘어가는지 체크
      if (leftPosition + popupWidth > viewportWidth) {
        // 헤더 오른쪽 끝에서 팝업 너비만큼 왼쪽으로 이동
        leftPosition = rect.right + window.scrollX - popupWidth;

        // 그래도 왼쪽으로 넘어가면 최소 10px 마진 유지
        if (leftPosition < 10) {
          leftPosition = 10;
        }
      }

      setFilterPopupPosition({
        top: rect.bottom + window.scrollY,
        left: leftPosition,
      });
      setFilterPopupOpen(true);
      console.log('Filter popup opened');
      return true;
    }
    console.log('No rect available');
    return false;
  };

  const handleTableHeaderClick = (columnKey) => {
    // 접이식 칼럼 토글 기능 비활성화 (항상 펼쳐진 상태 유지)
    // const key = String(columnKey || '');
    // Toggle 동작 제거됨
  };

  useEffect(() => {
    if (!columnSettingOpen && !sortSettingOpen && !colorSettingOpen) return undefined;

    const onPointerDown = (event) => {
      const inColumnPanel = columnSettingRef.current?.contains(event.target);
      const inSortPanel = sortSettingRef.current?.contains(event.target);
      const inColorPanel = colorSettingRef.current?.contains(event.target);
      if (!inColumnPanel) setColumnSettingOpen(false);
      if (!inSortPanel) setSortSettingOpen(false);
      if (!inColorPanel) setColorSettingOpen(false);
    };

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setColumnSettingOpen(false);
        setSortSettingOpen(false);
        setColorSettingOpen(false);
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [columnSettingOpen, sortSettingOpen, colorSettingOpen]);

  return (
    <div className="complex-page">
      <div
        className={`complex-layout ${regionSidebarCollapsed ? 'sidebar-collapsed' : ''}`.trim()}
        style={regionSidebarCollapsed ? undefined : { '--sidebar-width': `${sidebarWidthPx}px` }}
      >
        <RankingLeftPanel
          collapsed={regionSidebarCollapsed}
          onToggleCollapse={() => setRegionSidebarCollapsed((prev) => !prev)}
          title={isFavoritePage ? '\uC990\uACA8\uCC3E\uAE30' : '\uC9C0\uC5ED'}
          clearVisible={isFavoritePage ? hasFavoriteGroupSelection : hasLeftSidebarFilter}
          onClear={isFavoritePage
            ? () => setSelectedFavoriteDanjiCodes(favoriteGroupCodeList)
            : resetRegionSidebarFilters}
          clearText={'\uCD08\uAE30\uD654'}
          collapseText="«"
          expandText="»"
          collapsedLabel={isFavoritePage ? '즐겨찾기' : '지역설정'}
        >
          {isFavoritePage ? (
            !regionSidebarCollapsed && (
              <div className="complex-favorite-sidebar">
                <div className="complex-favorite-sidebar-section">
                  <div className="complex-favorite-sidebar-head">
                    <div className="complex-favorite-sidebar-title">{'\uC990\uACA8\uCC3E\uAE30 \uADF8\uB8F9'}</div>
                    <button type="button" className="complex-favorite-group-add-btn" onClick={handleAddFavoriteGroup}>+ 추가</button>
                  </div>
                  {favoriteGroupsLoading && (
                    <div className="tree-empty">{'\uADF8\uB8F9 \uBAA9\uB85D\uC744 \uBD88\uB7EC\uC624\uB294 \uC911\uC785\uB2C8\uB2E4.'}</div>
                  )}
                  {!favoriteGroupsLoading && favoriteGroups.length === 0 && (
                    <div className="tree-empty">{'\uB4F1\uB85D\uB41C \uC990\uACA8\uCC3E\uAE30 \uADF8\uB8F9\uC774 \uC5C6\uC2B5\uB2C8\uB2E4.'}</div>
                  )}
                  {!favoriteGroupsLoading && favoriteGroups.length > 0 && (
                    <div className="complex-favorite-group-list">
                      {favoriteGroups.map((group) => {
                        const groupId = Number(group?.groupId || 0);
                        const active = groupId > 0 && groupId === Number(selectedFavoriteGroupId);
                        return (
                          <div key={`favorite-group-${groupId}`} className="complex-favorite-group-row">
                            <button
                              type="button"
                              className={`complex-favorite-group-btn ${active ? 'active' : ''}`}
                              onClick={() => setSelectedFavoriteGroupId(groupId)}
                            >
                              <span>{group?.groupName || '-'}</span>
                              <strong>{Number(group?.favoriteCount || 0).toLocaleString('ko-KR')}</strong>
                            </button>
                            <div className="complex-favorite-group-row-actions">
                              <button
                                type="button"
                                className="complex-favorite-group-icon-btn"
                                data-tip="그룹명 편집"
                                onClick={(e) => { e.stopPropagation(); handleEditFavoriteGroup(group); }}
                                aria-label="그룹명 편집"
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M12 20h9" />
                                  <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
                                </svg>
                              </button>
                              <button
                                type="button"
                                className="complex-favorite-group-icon-btn danger"
                                data-tip="그룹 삭제"
                                onClick={(e) => { e.stopPropagation(); handleDeleteFavoriteGroup(group); }}
                                aria-label="그룹 삭제"
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M3 6h18" />
                                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                  <line x1="10" y1="11" x2="10" y2="17" />
                                  <line x1="14" y1="11" x2="14" y2="17" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {favoriteGroupComplexesLoading && (
                    <div className="tree-empty">그룹 단지를 불러오는 중입니다.</div>
                  )}
                </div>
                <div className="complex-favorite-sidebar-footer">
                  <button
                    type="button"
                    className={`complex-favorite-groupavg-btn ${groupAverageOn ? 'active' : ''}`}
                    onClick={() => setGroupAverageOn((v) => !v)}
                    aria-pressed={groupAverageOn}
                  >
                    <span className="complex-favorite-groupavg-dot" />
                    그룹평균 {groupAverageOn ? 'ON' : 'OFF'}
                  </button>
                </div>
              </div>
            )
          ) : (
          !regionSidebarCollapsed && (
            <div className="complex-sidebar-split-layout">
              <div className="complex-sidebar-section complex-sidebar-section-region">
              <div className="complex-region-sidebar-summary">
                {hasRegionFilter
                  ? `${regionSummary} · ${Number(regionDanjiCount || 0).toLocaleString('ko-KR')}개 단지`
                  : regionSummary}
              </div>
              <div style={{ display: 'flex', gap: '6px' }}>
                <input
                  type="text"
                  className="complex-region-search-input"
                  value={regionSearchInput}
                  onChange={(e) => setRegionSearchInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') applyRegionSearch();
                  }}
                  placeholder={'\uC9C0\uC5ED \uC785\uB825'}
                />
                <button
                  type="button"
                  onClick={applyRegionSearch}
                  style={{
                    height: '28px',
                    padding: '0 10px',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    background: '#fff',
                    color: '#334155',
                    fontSize: '12px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  검색
                </button>
              </div>
              <div className="complex-region-tree-wrap">
              {treeLoading && <div className="tree-empty">지역 트리를 불러오는 중입니다.</div>}
              {!treeLoading && (
                <div className="complex-region-tree">
                  {groupTree.map((group) => {
                    const groupCodes = fullGroupCodesMap.get(String(group?.groupName || '')) || [];
                    const groupChecked = groupCodes.length > 0 && groupCodes.every((code) => selectedBjdCodeSet.has(code));
                    const expanded = Boolean(expandedGroups[group.groupName]);
                    return (
                    <React.Fragment key={group.groupName}>
                      <RegionGroupNode
                        group={group}
                        expanded={expanded}
                        groupChecked={groupChecked}
                        groupCodes={groupCodes}
                        onToggleGroup={toggleExpandedGroup}
                        onToggleCodes={toggleCodes}
                        renderChildren={() => renderGroupChildren(group, false)}
                        showPopulationFilters={showPopulationFilters}
                        groupPopulationText={getGroupPopulationText(group)}
                      />
                      {false && (
                    <div key={group.groupName} className="tree-sido">
                      <div
                        className="tree-sido-row"
                        onClick={() => toggleExpandedGroup(group.groupName)}
                      >
                        <label onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={groupCodes.length > 0 && groupCodes.every((code) => selectedBjdCodeSet.has(code))}
                            onChange={() => toggleCodes(groupCodes)}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <span>{group.groupName}</span>
                        </label>
                        <span className="tree-expand-indicator">{expandedGroups[group.groupName] ? '−' : '+'}</span>
                      </div>

                      {expandedGroups[group.groupName] && (
                        <div className="tree-sigungu-list">
                          {renderGroupChildren(group, false)}
                        </div>
                      )}
                    </div>
                      )}
                    </React.Fragment>
                    );
                  })}
                </div>
              )}
              </div>
              <div style={{ display: 'grid', gap: '6px', marginTop: '8px' }}>
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', width: '100%', gap: '14px', fontSize: '12px', color: '#334155', fontWeight: 700 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      {'부동산 규제 지역'}
                      <span
                        ref={regulationHelpIconRef}
                        role="button"
                        tabIndex={0}
                        aria-label="규제지역 지정 현황 보기"
                        onClick={() => (regulationHelpOpen ? setRegulationHelpOpen(false) : openRegulationHelp())}
                        onMouseEnter={openRegulationHelp}
                        onMouseLeave={scheduleCloseRegulationHelp}
                        style={{
                          width: '15px',
                          height: '15px',
                          borderRadius: '50%',
                          border: '1px solid #fb923c',
                          color: '#c2410c',
                          background: '#fff7ed',
                          fontSize: '10px',
                          fontWeight: 800,
                          lineHeight: '13px',
                          textAlign: 'center',
                          cursor: 'pointer',
                          userSelect: 'none',
                        }}
                      >
                        ?
                      </span>
                      {regulationHelpOpen && createPortal(
                        <div
                          onMouseEnter={cancelCloseRegulationHelp}
                          onMouseLeave={scheduleCloseRegulationHelp}
                          style={{
                            position: 'fixed',
                            bottom: regulationHelpPos.bottom,
                            left: regulationHelpPos.left,
                            width: '660px',
                            maxWidth: 'calc(100vw - 16px)',
                            maxHeight: '70vh',
                            overflowY: 'auto',
                            background: '#fff',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            boxShadow: '0 6px 20px rgba(0,0,0,0.18)',
                            zIndex: 2147483647,
                            padding: '10px',
                          }}
                        >
                          <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                            {/* 조정대상지역 */}
                            <div style={{ flex: '1 1 0', minWidth: 0 }}>
                              <div style={{ fontSize: '12px', fontWeight: 800, color: '#7c2d12', marginBottom: '2px' }}>
                                조정대상지역
                              </div>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', fontWeight: 500, tableLayout: 'fixed' }}>
                                <thead>
                                  <tr>
                                    <th style={{ ...regHelpTh, width: '78px' }}>지정일자</th>
                                    <th style={regHelpTh}>지정지역</th>
                                    <th style={{ ...regHelpTh, width: '54px' }}>해제일자</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {ADJUSTMENT_TARGET_ZONES.map((grp) => (
                                    <React.Fragment key={grp.sido}>
                                      <tr>
                                        <td colSpan={3} style={{ ...regHelpTd, background: '#f1f5f9', fontWeight: 800, color: '#334155' }}>{grp.sido}</td>
                                      </tr>
                                      {computeDateRowSpans(grp.rows).map((r, i) => (
                                        <tr key={`${grp.sido}-${i}`}>
                                          {r.showDate && (
                                            <td rowSpan={r.dateSpan} style={{ ...regHelpTd, whiteSpace: 'nowrap', textAlign: 'center', verticalAlign: 'middle' }}>{r.date}</td>
                                          )}
                                          <td style={{ ...regHelpTd, wordBreak: 'keep-all' }}>{r.region}</td>
                                          <td style={{ ...regHelpTd, textAlign: 'center' }}>{r.release || ''}</td>
                                        </tr>
                                      ))}
                                    </React.Fragment>
                                  ))}
                                </tbody>
                              </table>
                            </div>

                            {/* 투기과열지구 */}
                            <div style={{ flex: '1 1 0', minWidth: 0 }}>
                              <div style={{ fontSize: '12px', fontWeight: 800, color: '#9a3412', marginBottom: '5px' }}>
                                {SPECULATION_TITLE}
                              </div>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', fontWeight: 500, tableLayout: 'fixed' }}>
                                <thead>
                                  <tr>
                                    <th style={{ ...regHelpTh, width: '72px' }}>지정일자</th>
                                    <th style={regHelpTh}>지정지역</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {SPECULATION_ZONES.map((grp, gi) => (
                                    <tr key={`spec-${gi}`}>
                                      <td style={{ ...regHelpTd, whiteSpace: 'nowrap', textAlign: 'center', verticalAlign: 'top', fontWeight: 700 }}>{grp.date}</td>
                                      <td style={{ ...regHelpTd, wordBreak: 'keep-all' }}>
                                        {grp.regions.map((line, li) => (
                                          <div key={li} style={{ marginBottom: li < grp.regions.length - 1 ? '4px' : 0 }}>{line}</div>
                                        ))}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>,
                        document.body
                      )}
                    </span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={showRegulationFilters}
                      onClick={() => setShowRegulationFilters((prev) => !prev)}
                      style={{
                        width: '36px',
                        height: '20px',
                        borderRadius: '999px',
                        border: '1px solid #fdba74',
                        background: showRegulationFilters ? '#f97316' : '#ffedd5',
                        position: 'relative',
                        cursor: 'pointer',
                        transition: 'all .15s ease',
                        padding: 0,
                      }}
                    >
                      <span
                        style={{
                          position: 'absolute',
                          top: '1px',
                          left: showRegulationFilters ? '17px' : '1px',
                          width: '16px',
                          height: '16px',
                          borderRadius: '50%',
                          background: '#fff',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.18)',
                          transition: 'left .15s ease'
                        }}
                      />
                    </button>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span>{'인구'}</span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={showPopulationFilters}
                      onClick={() => setShowPopulationFilters((prev) => !prev)}
                      style={{
                        width: '36px',
                        height: '20px',
                        borderRadius: '999px',
                        border: '1px solid #fdba74',
                        background: showPopulationFilters ? '#f97316' : '#ffedd5',
                        position: 'relative',
                        cursor: 'pointer',
                        transition: 'all .15s ease',
                        padding: 0,
                      }}
                    >
                      <span
                        style={{
                          position: 'absolute',
                          top: '1px',
                          left: showPopulationFilters ? '17px' : '1px',
                          width: '16px',
                          height: '16px',
                          borderRadius: '50%',
                          background: '#fff',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.18)',
                          transition: 'left .15s ease'
                        }}
                      />
                    </button>
                  </div>
                </div>
                <div style={{ display: 'grid', gap: '6px' }}>
                  <hr style={{ border: 0, borderTop: '1px solid #d7dde6', margin: '2px 0 4px' }} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                    <div style={{ width: sidebarNarrow ? '100%' : '72px', fontSize: '12px', color: '#334155', fontWeight: 700, whiteSpace: 'nowrap', lineHeight: '24px' }}>
                      규제지역 선택
                    </div>
                    {[
                      { key: 'REGULATION', label: '조정지역' },
                      { key: 'SPECULATION', label: '투기과열' },
                    ].map((option) => {
                      const active = selectedRegulationTypes.includes(option.key);
                      return (
                        <button
                          key={option.key}
                          type="button"
                          onClick={() => {
                            const isActive = selectedRegulationTypes.includes(option.key);
                            if (isActive) {
                              // 규제지역 필터 해제 시 선택된 지역도 해제
                              setSelectedBjdCodes([]);
                              setSelectedRegulationTypes((prev) => prev.filter((v) => v !== option.key));
                            } else {
                              // 규제지역 필터 선택
                              setSelectedRegulationTypes((prev) => [...prev, option.key]);
                            }
                          }}
                          style={{
                            height: '24px',
                            padding: '0 8px',
                            borderRadius: '0',
                            border: `1px solid ${active ? '#fb923c' : '#cbd5e1'}`,
                            background: active ? '#fff7ed' : '#fff',
                            color: active ? '#c2410c' : '#475569',
                            fontSize: '11px',
                            fontWeight: 700,
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
              <section className="complex-sidebar-ranker complex-sidebar-groupby">
                <div style={{ display: 'grid', gap: '6px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                    <div style={{ width: sidebarNarrow ? '100%' : '72px', fontSize: '12px', color: '#334155', fontWeight: 700, whiteSpace: 'nowrap', lineHeight: '24px' }}>
                      지역평균
                    </div>
                    {[
                      { key: '시도', label: '시/군' },
                      { key: '구', label: '구' },
                      { key: '동', label: '동' },
                    ].map((option) => {
                      const active = selectedGroupBy.includes(option.key);
                      return (
                        <button
                          key={option.key}
                          type="button"
                          onClick={() => {
                            setSelectedGroupBy((prev) => (
                              prev.includes(option.key)
                                ? prev.filter((v) => v !== option.key)
                                : [...prev, option.key]
                            ));
                          }}
                          style={{
                            height: '24px',
                            padding: '0 8px',
                            borderRadius: '0',
                            border: `1px solid ${active ? '#fb923c' : '#cbd5e1'}`,
                            background: active ? '#fff7ed' : '#fff',
                            color: active ? '#c2410c' : '#475569',
                            fontSize: '11px',
                            fontWeight: 700,
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {option.label}
                        </button>
                      );
                    })}
                    <label
                      title="체크 시 개별 단지는 목록에서 제외하고 지역평균만 표시"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginLeft: '4px', fontSize: '11px', fontWeight: 700, color: excludeDanji ? '#c2410c' : '#475569', cursor: 'pointer', whiteSpace: 'nowrap', lineHeight: '24px' }}
                    >
                      <input
                        type="checkbox"
                        checked={excludeDanji}
                        onChange={(e) => setExcludeDanji(e.target.checked)}
                        style={{ margin: 0, cursor: 'pointer' }}
                      />
                      단지제외
                    </label>
                  </div>
                </div>
              </section>
              </div>
            </div>
          ))}
        </RankingLeftPanel>
        <main className={`complex-main ${articlePanelOpen ? 'has_fix_article_footer' : ''}`.trim()}>
          <section className="complex-top-filter" ref={topFilterRef}>
            <div className="complex-top-filter-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '0' }}>
              <div className="complex-top-filter-search-inline" style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', width: '100%' }}>
                <div className="complex-inline-ranking-tools">
                  <div className="complex-inline-sort-buttons" role="group" aria-label="정렬기준 선택" style={{ position: 'relative' }}>
                    <button
                      type="button"
                      className={`complex-inline-chip-btn ${sortPresetExpanded ? 'active' : ''}`}
                      onClick={() => setSortPresetExpanded((prev) => !prev)}
                      style={{
                        height: '36px',
                        minHeight: '36px',
                        padding: '0 16px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '4px',
                        border: '1px solid #fb923c',
                        borderRadius: '8px',
                        background: sortPresetExpanded ? '#fff7ed' : '#fff',
                        color: '#c2410c',
                        fontSize: '14px',
                        fontWeight: '700',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      <span>랭킹</span>
                      {selectedSortPreset && <span style={{ color: '#fb923c' }}>: {selectedSortPreset}</span>}
                    </button>
                    {sortPresetExpanded && (
                      <div className="complex-inline-sort-dropdown" style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        marginTop: '4px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '6px',
                        padding: '12px',
                        background: '#fff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                        zIndex: 1000,
                        minWidth: '380px',
                        maxWidth: '500px'
                      }}>
                        {SORT_PRESET_OPTIONS.map((row, rowIndex) => (
                          <div key={`row-${rowIndex}`} style={{ display: 'flex', gap: '6px', flexWrap: 'nowrap' }}>
                            {row.map((option) => {
                              const active = selectedSortPreset === option;
                              const description = getSortPresetDescription(option);
                              return (
                                <div key={`inline-more-${option}`} style={{ position: 'relative', flex: '1', minWidth: '70px' }}>
                                  <button
                                    type="button"
                                    className={`complex-inline-chip-btn ${active ? 'active' : ''} ranking-preset-btn`}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      // 같은 프리셋 재클릭 시 해제(기본 정렬로)
                                      setSelectedSortPreset((prev) => (prev === option ? '' : option));
                                      // 선택 시 적용하고 박스는 닫는다
                                      setSortPresetExpanded(false);
                                    }}
                                    aria-label={`${option}`}
                                    style={{
                                      height: '32px',
                                      minHeight: '32px',
                                      padding: '0 12px',
                                      width: '100%',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      border: `1px solid ${active ? '#fb923c' : '#e2e8f0'}`,
                                      borderRadius: '6px',
                                      background: active ? '#fff7ed' : '#fff',
                                      color: active ? '#c2410c' : '#475569',
                                      fontSize: '13px',
                                      fontWeight: active ? '700' : '600',
                                      cursor: 'pointer',
                                      whiteSpace: 'nowrap',
                                      transition: 'all 0.2s'
                                    }}
                                  >
                                    {option}
                                  </button>
                                  {description && (
                                    <div
                                      className="ranking-tooltip"
                                      style={{
                                        position: 'absolute',
                                        top: '100%',
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        marginTop: '8px',
                                        padding: '8px 12px',
                                        background: '#1e293b',
                                        color: '#fff',
                                        fontSize: '12px',
                                        lineHeight: '1.6',
                                        borderRadius: '6px',
                                        whiteSpace: 'pre-line',
                                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.2)',
                                        opacity: 0,
                                        pointerEvents: 'none',
                                        transition: 'opacity 0.2s',
                                        zIndex: 10000,
                                        minWidth: '120px',
                                        textAlign: 'left'
                                      }}
                                    >
                                      <div style={{
                                        position: 'absolute',
                                        bottom: '100%',
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        width: 0,
                                        height: 0,
                                        borderLeft: '6px solid transparent',
                                        borderRight: '6px solid transparent',
                                        borderBottom: '6px solid #1e293b'
                                      }} />
                                      {description}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="complex-top-filter-search-input-wrap">
                  <input
                    type="text"
                    value={searchComplexName}
                    onChange={(e) => setSearchComplexName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSearchSubmit();
                    }}
                    placeholder={'\uB2E8\uC9C0\uBA85 \uC785\uB825'}
                    className="complex-top-filter-search-input"
                  />
                  {searchComplexName && (
                    <button
                      type="button"
                      className="complex-top-filter-search-input-clear"
                      onClick={() => {
                        setSearchComplexName('');
                        setAppliedSearchComplexName('');
                        load(selectedBjdCodes, '');
                      }}
                      aria-label="검색어 지우기"
                    >
                      ×
                    </button>
                  )}
                </div>
                <button
                  type="button"
                  onClick={handleSearchSubmit}
                  className="complex-top-filter-search-btn"
                  style={{
                    height: '36px',
                    minHeight: '36px',
                    padding: '0 16px',
                    border: '1px solid #fb923c',
                    background: '#fff7ed',
                    color: '#c2410c',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '700',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {'\uAC80\uC0C9'}
                </button>
                <div className="complex-filter-divider" style={{ width: '1px', height: '24px', background: '#e2e8f0' }}></div>
                {QUICK_FILTER_BUTTONS.map((filter) => {
                  const active = openTopFilterKey === filter.key;
                  const chipText = quickFilterChipMap[filter.key];
                  const hasValue = Boolean(chipText);
                  const highlight = active || hasValue;
                  return (
                    <span key={filter.key} style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                      <button
                        type="button"
                        className={`complex-inline-chip-btn filter-btn ${active ? 'active' : ''}`}
                        ref={(element) => {
                          if (element) topFilterTabRefs.current[filter.key] = element;
                        }}
                        onClick={() => handleTopFilterToggle(filter.key)}
                        style={{
                          height: '36px',
                          minHeight: '36px',
                          minWidth: '76px',
                          padding: '0 14px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: `1px solid ${highlight ? '#fb923c' : '#e2e8f0'}`,
                          borderRadius: '8px',
                          background: highlight ? '#fff7ed' : '#fff',
                          color: highlight ? '#c2410c' : '#475569',
                          fontSize: '14px',
                          fontWeight: highlight ? '700' : '600',
                          cursor: 'pointer',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {filter.label}
                      </button>
                      {hasValue && (
                        <span
                          className="complex-quick-filter-chip"
                          title={chipText}
                          style={{
                            position: 'relative',
                            display: 'inline-flex',
                            alignItems: 'center',
                            maxWidth: '150px',
                            padding: '2px 8px',
                            borderRadius: '999px',
                            background: '#fff2e8',
                            border: '1px solid #ffd0ad',
                            color: '#ea580c',
                            fontSize: '11px',
                            fontWeight: 700,
                            lineHeight: 1.4,
                          }}
                        >
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{chipText}</span>
                          <span
                            role="button"
                            tabIndex={0}
                            aria-label={`${filter.label} 필터 제거`}
                            onClick={(e) => { e.stopPropagation(); clearQuickFilter(filter.key); }}
                            onMouseDown={(e) => e.stopPropagation()}
                            style={{ position: 'absolute', top: '-6px', right: '-6px', width: '15px', height: '15px', borderRadius: '50%', background: '#ea580c', color: '#fff', fontSize: '10px', lineHeight: '15px', textAlign: 'center', cursor: 'pointer', boxShadow: '0 1px 2px rgba(0,0,0,0.2)' }}
                          >
                            ×
                          </span>
                        </span>
                      )}
                    </span>
                  );
                })}
                <button
                  type="button"
                  className="complex-top-filter-search-btn"
                  onClick={() => setDetailFilterOpen(true)}
                  title="상세 필터 설정"
                  aria-label="상세 필터 설정"
                  style={{
                    height: '36px',
                    minHeight: '36px',
                    padding: '0 8px',
                    fontSize: '14px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    whiteSpace: 'nowrap'
                  }}
                >
                  상세 필터 설정
                </button>
                <button
                  type="button"
                  className="complex-setting-btn"
                  onClick={handleClearAllFilters}
                  disabled={!hasAnyActiveFilter}
                  title={hasAnyActiveFilter ? '설정된 모든 필터를 초기화합니다' : '초기화할 필터가 없습니다'}
                  style={{
                    height: '36px',
                    minHeight: '36px',
                    padding: '0 14px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: hasAnyActiveFilter ? '1px solid #fca5a5' : '1px solid #e2e8f0',
                    borderRadius: '8px',
                    background: hasAnyActiveFilter ? '#fef2f2' : '#f8fafc',
                    color: hasAnyActiveFilter ? '#b91c1c' : '#94a3b8',
                    fontSize: '14px',
                    fontWeight: '700',
                    cursor: hasAnyActiveFilter ? 'pointer' : 'default',
                    whiteSpace: 'nowrap'
                  }}
                >
                  필터 초기화
                </button>
                <div style={{ flex: 1 }}></div>
                <button
                  type="button"
                  className="complex-setting-btn complex-column-quick-btn"
                  onClick={() => {
                    setUseKoreanPriceUnit((prev) => !prev);
                    // 단위 표기/제거로 셀 내용 길이가 바뀌므로 컬럼 너비 재설정
                    setResizeColumnsToken((t) => t + 1);
                  }}
                  title={!useKoreanPriceUnit ? '단위 표기' : '단위 제거'}
                  style={{
                    height: '36px',
                    minHeight: '36px',
                    padding: '0 14px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    background: '#fff',
                    color: '#475569',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {!useKoreanPriceUnit ? '단위표기' : '단위제거'}
                </button>
              {false && (
                  <div className="complex-column-setting-panel complex-sort-setting-panel" style={{ width: '360px' }}>
                    <div className="complex-column-setting-head">
                      <strong>조건부 서식</strong>
                      <button type="button" onClick={() => setColorSettingOpen(false)}>닫기</button>
                    </div>
                    <div className="complex-column-setting-footnote" style={{ marginBottom: '8px', color: '#64748b', fontWeight: '600', fontSize: '12px' }}>
                      값 조건을 만족하는 셀에 색을 칠합니다. (값은 표시 단위 기준 — 예: 매매가 5,000 = 5,000만원)
                    </div>

                    {/* 새 규칙 작성 */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '8px', marginBottom: '10px' }}>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <select
                          value={colorRuleDraft.scope}
                          onChange={(e) => setColorRuleDraft((p) => ({ ...p, scope: e.target.value, targetKey: '' }))}
                          style={colorSelStyle}
                        >
                          <option value="column">칼럼</option>
                          <option value="group">그룹</option>
                        </select>
                        <select
                          value={colorRuleDraft.targetKey}
                          onChange={(e) => setColorRuleDraft((p) => ({ ...p, targetKey: e.target.value }))}
                          style={{ ...colorSelStyle, flex: 1, minWidth: 0 }}
                        >
                          <option value="">{colorRuleDraft.scope === 'group' ? '그룹 선택' : '칼럼 선택'}</option>
                          {(colorRuleDraft.scope === 'group' ? colorTargetGroups : colorTargetColumns).map((o) => (
                            <option
                              key={colorRuleDraft.scope === 'group' ? o.id : o.key}
                              value={colorRuleDraft.scope === 'group' ? o.id : o.key}
                            >
                              {colorRuleDraft.scope === 'group' ? o.name : o.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                        <select
                          value={colorRuleDraft.op}
                          onChange={(e) => setColorRuleDraft((p) => ({ ...p, op: e.target.value }))}
                          style={colorSelStyle}
                        >
                          <option value=">=">이상 ≥</option>
                          <option value=">">초과 &gt;</option>
                          <option value="<=">이하 ≤</option>
                          <option value="<">미만 &lt;</option>
                          <option value="=">같음 =</option>
                          <option value="between">사이</option>
                        </select>
                        <input
                          type="number"
                          value={colorRuleDraft.value}
                          onChange={(e) => setColorRuleDraft((p) => ({ ...p, value: e.target.value }))}
                          placeholder="값"
                          style={{ ...colorInpStyle, flex: 1 }}
                        />
                        {colorRuleDraft.op === 'between' && (
                          <>
                            <span style={{ fontSize: '12px', color: '#64748b' }}>~</span>
                            <input
                              type="number"
                              value={colorRuleDraft.value2}
                              onChange={(e) => setColorRuleDraft((p) => ({ ...p, value2: e.target.value }))}
                              placeholder="값2"
                              style={{ ...colorInpStyle, flex: 1 }}
                            />
                          </>
                        )}
                        <input
                          type="color"
                          value={colorRuleDraft.color}
                          onChange={(e) => setColorRuleDraft((p) => ({ ...p, color: e.target.value }))}
                          style={{ width: '30px', height: '30px', padding: 0, border: '1px solid #e2e8f0', borderRadius: '4px', cursor: 'pointer', background: 'none', flex: '0 0 auto' }}
                          title="색상"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={handleAddColorRule}
                        disabled={!colorRuleDraft.targetKey}
                        style={{ padding: '6px', border: 'none', borderRadius: '6px', background: colorRuleDraft.targetKey ? '#2563eb' : '#cbd5e1', color: '#fff', fontWeight: 700, fontSize: '13px', cursor: colorRuleDraft.targetKey ? 'pointer' : 'default' }}
                      >
                        규칙 추가
                      </button>
                    </div>

                    {/* 규칙 목록 (위에서부터 우선 적용) */}
                    <div style={{ maxHeight: '240px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      {(cellColorRules || []).length === 0 ? (
                        <div style={{ fontSize: '12px', color: '#94a3b8', textAlign: 'center', padding: '10px 0' }}>등록된 규칙이 없습니다.</div>
                      ) : (
                        (cellColorRules || []).map((r) => (
                          <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 6px', border: '1px solid #eef2f7', borderRadius: '6px' }}>
                            <span style={{ width: '16px', height: '16px', borderRadius: '3px', border: '1px solid #cbd5e1', background: r.color, flex: '0 0 auto' }} />
                            <span style={{ flex: 1, fontSize: '12px', color: '#334155', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              [{r.scope === 'group' ? '그룹' : '칼럼'}] {ruleTargetLabel(r)} {COLOR_RULE_OP_LABEL[r.op] || r.op} {r.op === 'between' ? `${r.value}~${r.value2}` : r.value}
                            </span>
                            <button
                              type="button"
                              onClick={() => removeColorRule(r.id)}
                              style={{ fontSize: '12px', padding: '2px 8px', border: '1px solid #e2e8f0', borderRadius: '6px', background: '#fff', color: '#ef4444', cursor: 'pointer', flex: '0 0 auto' }}
                            >
                              삭제
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              <div className="complex-sort-setting-wrap" ref={sortSettingRef}>
                <button
                  type="button"
                  className={`complex-setting-btn complex-sort-setting-btn ${sortSettingOpen ? 'open' : ''}`}
                  onClick={() => {
                    setSortSettingOpen((prev) => !prev);
                    setColorSettingOpen(false);
                    setColumnSettingOpen(false);
                  }}
                  style={{
                    height: '36px',
                    minHeight: '36px',
                    padding: '0 14px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '4px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    background: '#fff',
                    color: '#475569',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap'
                  }}
                >
                  <span>정렬설정 ({sanitizedSortPrefs.length})</span>
                </button>
                {sortSettingOpen && (
                  <div className="complex-column-setting-panel complex-sort-setting-panel">
                    <div className="complex-column-setting-head">
                      <strong>정렬 기준</strong>
                      <button type="button" onClick={() => { setSortSettingOpen(false); setSelectedSortIndex(null); }}>닫기</button>
                    </div>
                    <div className="complex-column-setting-footnote" style={{ marginBottom: '8px', color: '#dc2626', fontWeight: '700', fontSize: '12px' }}>
                      드래그로 정렬 순서를 변경할 수 있습니다
                    </div>
                    {sortPresetLocked && presetSortCount > 0 && (
                      <div className="complex-column-setting-footnote" style={{ marginBottom: '8px', color: '#fb923c', fontWeight: '600' }}>
                        현재 랭킹 기준: {sanitizedSortPrefs.slice(0, presetSortCount).map((rule, idx) => {
                          const label = sortColumnLabelMap[rule.key] || rule.key;
                          return (idx === 0 ? '' : ' → ') + label + (rule.direction === 'desc' ? '↓' : '↑');
                        }).join('')}
                      </div>
                    )}
                    <div className="complex-column-setting-actions">
                      <button
                        type="button"
                        onClick={addSortRule}
                        disabled={sanitizedSortPrefs.length >= sortableColumns.length}
                      >
                        + 정렬 추가
                      </button>
                      <button type="button" onClick={resetSortPrefs} disabled={sortPresetLocked ? !hasExtraSortRules : sanitizedSortPrefs.length === 0}>초기화</button>
                    </div>
                    {sortPresetLocked && (
                      <div className="complex-column-setting-footnote">랭킹 기준 설정 정렬은 고정되며, 추가한 정렬만 수정할 수 있습니다.</div>
                    )}
                    <div className="complex-sort-setting-list">
                      {sanitizedSortPrefs.length === 0 && (
                        <div className="complex-sort-setting-empty">정렬 기준을 추가해 주세요.</div>
                      )}
                      {sanitizedSortPrefs.map((rule, index) => {
                        const presetRow = isPresetSortIndex(index);
                        const isDragOver = sortDragOverIndex === index;

                        // Determine drop position border
                        let dropBorderStyle = {};
                        if (isDragOver && sortDragFromIndex !== null) {
                          if (sortDragFromIndex < index) {
                            // Dragging down: show bottom border
                            dropBorderStyle = { borderBottom: '2px solid #fb923c' };
                          } else {
                            // Dragging up: show top border
                            dropBorderStyle = { borderTop: '2px solid #fb923c' };
                          }
                        }

                        return (
                        <div
                          key={`${rule.key}-${index}`}
                          className="complex-sort-setting-item"
                          style={{
                            cursor: presetRow ? 'default' : 'grab',
                            ...dropBorderStyle,
                            transition: 'all 0.2s ease'
                          }}
                          draggable={!presetRow}
                          onDragStart={(e) => {
                            if (presetRow) {
                              e.preventDefault();
                              return;
                            }
                            e.currentTarget.style.cursor = 'grabbing';
                            e.dataTransfer.effectAllowed = 'move';
                            e.dataTransfer.setData('text/plain', String(index));
                            setSortDragFromIndex(index);
                          }}
                          onDragEnd={(e) => {
                            e.currentTarget.style.cursor = 'grab';
                            setSortDragOverIndex(null);
                            setSortDragFromIndex(null);
                          }}
                          onDragOver={(e) => {
                            if (presetRow) return;
                            e.preventDefault();
                            e.dataTransfer.dropEffect = 'move';
                            setSortDragOverIndex(index);
                          }}
                          onDragLeave={(e) => {
                            if (presetRow) return;
                            // Only clear if we're actually leaving the item, not entering a child
                            if (e.currentTarget === e.target || !e.currentTarget.contains(e.relatedTarget)) {
                              setSortDragOverIndex(null);
                            }
                          }}
                          onDrop={(e) => {
                            if (presetRow) return;
                            e.preventDefault();
                            setSortDragOverIndex(null);
                            setSortDragFromIndex(null);
                            const fromIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);
                            if (!isNaN(fromIndex)) {
                              reorderSortRule(fromIndex, index);
                            }
                          }}
                        >
                          <span
                            style={{
                              cursor: presetRow ? 'default' : 'grab',
                              color: presetRow ? '#cbd5e1' : '#64748b',
                              fontSize: '16px',
                              userSelect: 'none',
                              padding: '0 4px'
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                          >
                            ☰
                          </span>
                          <select
                            value={rule.key}
                            onChange={(event) => updateSortRuleKey(index, event.target.value)}
                            disabled={presetRow}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {(() => {
                              // 허용 목록만 노출. 단, 현재 규칙 키(프리셋 등)가 목록에 없으면 그 항목은 유지해 표시.
                              const opts = SORT_ADD_ALLOWED_COLUMNS.filter((o) => sortableColumnKeySet.has(o.key));
                              if (rule.key && !opts.some((o) => o.key === rule.key)) {
                                opts.unshift({ key: rule.key, label: sortColumnLabelMap[rule.key] || rule.key });
                              }
                              return opts.map((o) => (
                                <option key={o.key} value={o.key}>{o.label}</option>
                              ));
                            })()}
                          </select>
                          <select
                            value={rule.direction}
                            onChange={(event) => updateSortRuleDirection(index, event.target.value)}
                            disabled={presetRow}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <option value="asc">오름차순</option>
                            <option value="desc">내림차순</option>
                          </select>
                          <div className="complex-sort-setting-order">
                            <button type="button" onClick={(e) => { e.stopPropagation(); removeSortRule(index); }} disabled={presetRow}>해제</button>
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              <div className="complex-column-setting-wrap" ref={columnSettingRef}>
                <div className="complex-column-toolbar-extra">
                  <div className="complex-toolbar-screen-setting-select-wrap">
                    <select
                      className="complex-toolbar-screen-setting-select"
                      value={String(selectedScreenSettingId || '')}
                      onChange={(e) => {
                        // 지역(또는 검색/즐겨찾기)으로 데이터가 로드되기 전에는 최근 8주/6개월 등 동적 컬럼 정보가 없어
                        // 화면설정을 적용/편집하면 컬럼 순서가 어긋나므로, 지역 선택 전에는 막는다.
                        if (!isFavoritePage && selectedBjdCodes.length === 0 && !appliedSearchComplexName) {
                          toast.info('먼저 좌측에서 지역을 선택해주세요.');
                          return;
                        }
                        if (e.target.value === '__setting__') {
                          handleColumnSettingButtonClick();
                        } else {
                          handleScreenSettingChange(e.target.value, { forceApply: true });
                        }
                      }}
                      disabled={screenSettingBusy}
                      aria-label="화면설정 선택"
                      style={{
                        height: '36px',
                        minHeight: '36px',
                        padding: '0 32px 0 12px',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        background: '#fff',
                        color: '#475569',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="__setting__">화면설정</option>
                      <option value="">기본항목</option>
                      {toolbarScreenSettingItems.map((setting) => (
                        <option key={`toolbar-screen-setting-${setting?.settingId}`} value={String(setting?.settingId || '')}>
                          {setting?.settingName || '-'}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <button
                  type="button"
                  className={`complex-setting-btn complex-column-setting-btn ${columnSettingOpen ? 'open' : ''}`}
                  onClick={handleColumnSettingButtonClick}
                  style={{
                    height: '36px',
                    minHeight: '36px',
                    padding: '0 14px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '4px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    background: '#fff',
                    color: '#475569',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap'
                  }}
                >
                  <span className="complex-setting-btn-icon"><ColumnSettingIcon /></span>
                  <span>화면선택</span>
                </button>
                {columnSettingOpen && (
                  <RankingColumnSettingPanel
                    groups={groupedColumnSectionsForPanel}
                    onClose={applyColumnSettingDraftAndClose}
                    onToggleGroup={panelToggleColumnGroupVisible}
                    onToggleItem={panelToggleColumnGroupItemVisible}
                    onMoveGroup={panelMoveColumnGroupOrder}
                    onMoveItem={panelMoveColumnGroupItem}
                    onReorderGroup={panelReorderColumnGroup}
                    screenSettingEnabled={isLoggedIn}
                    screenSettingItems={screenSettingItems}
                    selectedScreenSettingId={selectedScreenSettingId}
                    screenSettingBusy={screenSettingBusy}
                    hasUnsavedChanges={hasUnsavedChanges()}
                    onSelectScreenSetting={handleScreenSettingChange}
                    onSaveScreenSetting={handleSaveScreenSetting}
                    onDeleteScreenSettingItem={handleDeleteScreenSetting}
                    onReorderScreenSetting={handleReorderScreenSetting}
                    onScreenSettingOrderChange={handleScreenSettingOrderChange}
                  />
                )}
                {false && columnSettingOpen && (
                  <div className="complex-column-setting-panel">
                    <div className="complex-column-setting-head">
                      <strong>표시 항목 / 순서</strong>
                      <button type="button" onClick={() => setColumnSettingOpen(false)}>닫기</button>
                    </div>
                    <div className="complex-column-setting-actions">
                      <button type="button" onClick={() => setAllColumnsVisible(true)}>전체 표시</button>
                      <button type="button" onClick={() => setAllColumnsVisible(false)}>전체 숨김</button>
                      <button type="button" onClick={resetColumnPrefs}>초기화</button>
                    </div>
                    <div className="complex-column-setting-list">
                      {sanitizedColumnOrder.map((key, index) => {
                        const column = configurableColumnMap.get(key);
                        if (!column) return null;
                        const checked = columnPrefs.visible[key] !== false;
                        return (
                          <div key={key} className="complex-column-setting-item">
                            <label>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleColumnVisible(key)}
                              />
                              <span>{typeof column.label === 'string' ? column.label : key}</span>
                            </label>
                            <div className="complex-column-setting-order">
                              <button type="button" onClick={() => moveColumnOrder(key, 'up')} disabled={index === 0}>↑</button>
                              <button type="button" onClick={() => moveColumnOrder(key, 'down')} disabled={index === sanitizedColumnOrder.length - 1}>↓</button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              </div>

              {/* 두 번째 줄: 정렬 정보와 비교하기/즐겨찾기 버튼 (선택 유무와 무관하게 높이 고정 → 테이블 밀림 방지) */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px', flexWrap: 'wrap', minHeight: '32px' }}>
                {sanitizedSortPrefs.filter((rule) => rule.key !== 'region').length > 0 && (
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '6px 12px',
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '13px',
                    color: '#475569',
                    flexWrap: 'wrap'
                  }}>
                    {sanitizedSortPrefs
                      .filter((rule) => rule.key !== 'region')
                      .map((rule, index) => {
                        const label = sortColumnLabelMap[rule.key] || rule.key;
                        const arrow = rule.direction === 'desc' ? '↓' : '↑';
                        return (
                          <span key={`sort-${index}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
                            {index > 0 && <span style={{ margin: '0 2px', color: '#cbd5e1' }}>→</span>}
                            <span>{label}{arrow}</span>
                          </span>
                        );
                      })}
                  </div>
                )}
                {compareTargets.length > 0 && (() => {
                  const clearSelection = (e) => { e?.stopPropagation?.(); setCompareTargets([]); };
                  const clearXStyle = {
                    position: 'absolute',
                    top: '-7px',
                    right: '-7px',
                    width: '18px',
                    height: '18px',
                    borderRadius: '999px',
                    border: '1px solid #fecaca',
                    background: '#fff1f2',
                    color: '#be123c',
                    fontSize: '12px',
                    lineHeight: 1,
                    cursor: 'pointer',
                    padding: 0,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 2,
                  };
                  return (
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    {compareTargets.length < 10 && (
                    <div style={{ position: 'relative', display: 'inline-flex' }}>
                    <button
                      type="button"
                      onClick={() => {
                        if (compareTargets.length < 2) {
                          toast.info('비교하려면 2개 단지를 선택해 주세요.');
                          return;
                        }
                        setCompareModalOpen(true);
                      }}
                      style={{
                        height: '32px',
                        padding: '0 12px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        border: '1px solid #3b82f6',
                        background: '#eff6ff',
                        color: '#1e40af',
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      <span>↔</span>
                      <span>비교하기 {compareTargets.length}개</span>
                    </button>
                    <button type="button" onClick={clearSelection} title="선택 전체 해제" aria-label="선택 전체 해제" style={clearXStyle}>×</button>
                    </div>
                    )}
                    <div style={{ position: 'relative', display: 'inline-flex' }}>
                    <button
                      type="button"
                      onClick={handleOpenFavoriteGroupPicker}
                      style={{
                        height: '32px',
                        padding: '0 12px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        border: '1px solid #eab308',
                        background: '#fef9c3',
                        color: '#854d0e',
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      <span>★</span>
                      <span>즐겨찾기 {compareTargets.length}개</span>
                    </button>
                    <button type="button" onClick={clearSelection} title="선택 전체 해제" aria-label="선택 전체 해제" style={clearXStyle}>×</button>
                    </div>
                  </div>
                  );
                })()}
                <div
                  className="complex-region-update-date"
                  style={{
                    marginLeft: 'auto',
                    flexShrink: 0,
                    color: '#dc2626',
                    fontSize: '12px',
                    fontWeight: 700,
                    lineHeight: 1.4,
                    whiteSpace: 'nowrap',
                  }}
                >
                  최근 업데이트 : {snapshotDate ? String(snapshotDate).slice(0, 10) : '-'}
                </div>
              </div>

              {false && (<div className="complex-pagination top-right">
                <button
                  type="button"
                  onClick={() => {
                    const next = pageNumber - 1;
                    if (next < 1 || loading) return;
                    setPageNumber(next);
                    load(next, selectedBjdCodes);
                  }}
                  disabled={pageNumber <= 1 || loading}
                >
                  ?댁쟾
                </button>
                <span>{pageNumber} / {totalPages}</span>
                <button
                  type="button"
                  onClick={() => {
                    const next = pageNumber + 1;
                    if (next > totalPages || loading) return;
                    setPageNumber(next);
                    load(next, selectedBjdCodes);
                  }}
                  disabled={pageNumber >= totalPages || loading}
                >
                  ?ㅼ쓬
                </button>
              </div>)}
            </div>

            {openTopFilterKey === 'tradePrice' && (
              <article className="complex-top-filter-panel household" style={{ left: `${topFilterPanelLeft}px` }}>
                <FilterWrapper
                  FilterClass={MoneyRangeFilter}
                  value={tradePriceMinInput || tradePriceMaxInput ? `${tradePriceMinInput || ''}~${tradePriceMaxInput || ''}` : ''}
                  onChange={(value) => {
                    if (!value) {
                      setTradePriceMinInput('');
                      setTradePriceMaxInput('');
                      setAppliedFilters(prev => ({ ...prev, tradePriceMin: null, tradePriceMax: null }));
                    } else {
                      const [min, max] = value.split('~');
                      setTradePriceMinInput(min || '');
                      setTradePriceMaxInput(max || '');
                      setAppliedFilters(prev => ({
                        ...prev,
                        tradePriceMin: min ? Number(min) : null,
                        tradePriceMax: max ? Number(max) : null,
                      }));
                      setOpenTopFilterKey(null);
                    }
                  }}
                  onClose={() => setOpenTopFilterKey(null)}
                />
              </article>
            )}

            {openTopFilterKey === 'investPrice' && (
              <article className="complex-top-filter-panel household" style={{ left: `${topFilterPanelLeft}px` }}>
                <FilterWrapper
                  FilterClass={MoneyRangeFilter}
                  value={investPriceMinInput || investPriceMaxInput ? `${investPriceMinInput || ''}~${investPriceMaxInput || ''}` : ''}
                  onChange={(value) => {
                    if (!value) {
                      setInvestPriceMinInput('');
                      setInvestPriceMaxInput('');
                      setAppliedFilters(prev => ({ ...prev, investPriceMin: null, investPriceMax: null }));
                    } else {
                      const [min, max] = value.split('~');
                      setInvestPriceMinInput(min || '');
                      setInvestPriceMaxInput(max || '');
                      setAppliedFilters(prev => ({
                        ...prev,
                        investPriceMin: min ? Number(min) : null,
                        investPriceMax: max ? Number(max) : null,
                      }));
                      setOpenTopFilterKey(null);
                    }
                  }}
                  onClose={() => setOpenTopFilterKey(null)}
                />
              </article>
            )}

            {openTopFilterKey === 'region' && (
              <article className="complex-top-filter-panel region" style={{ left: `${topFilterPanelLeft}px` }}>
                <div className="complex-top-filter-panel-head">
                  <h4>지역</h4>
                  <button type="button" className="complex-top-filter-panel-close" onClick={() => setOpenTopFilterKey(null)}>×</button>
                </div>
                {treeLoading && <div className="tree-empty">지역 트리를 불러오는 중입니다.</div>}
                {!treeLoading && (
                  <div className="complex-region-tree">
                    {groupTree.map((group) => {
                      const groupCodes = fullGroupCodesMap.get(String(group?.groupName || '')) || [];
                      return (
                      <div key={group.groupName} className="tree-sido">
                        <div
                          className="tree-sido-row"
                          onClick={() => toggleExpandedGroup(group.groupName)}
                        >
                          <label onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={groupCodes.length > 0 && groupCodes.every((code) => selectedBjdCodeSet.has(code))}
                              onChange={() => toggleCodes(groupCodes)}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <span>{group.groupName}</span>
                          </label>
                          <span className="tree-expand-indicator">{expandedGroups[group.groupName] ? '−' : '+'}</span>
                        </div>

                        {expandedGroups[group.groupName] && (
                          <div className="tree-sigungu-list">
                            {renderGroupChildren(group, false)}{false && getGroupSigunguList(group).map((sigungu) => {
                              const sigunguKey = `${sigungu?.sidoCode || sigungu?.sidoName || ''}|${sigungu?.sigunguCode || sigungu?.sigunguName || ''}`;
                              const label = [sigungu?.sidoName, sigungu?.sigunguName].filter(Boolean).join(' ');
                              const sigunguCodes = getSigunguCodes(sigungu);

                              return (
                                <div key={sigunguKey || label} className="tree-sigungu">
                                  <div
                                    className="tree-sigungu-row"
                                    onClick={() => toggleExpandedSigungu(sigunguKey)}
                                  >
                                    <label onClick={(e) => e.stopPropagation()}>
                                      <input
                                        type="checkbox"
                                        checked={sigunguCodes.length > 0 && sigunguCodes.every((code) => selectedBjdCodeSet.has(code))}
                                        onChange={() => toggleCodes(sigunguCodes)}
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                      <span>{label || '-'}</span>
                                    </label>
                                    <span className="tree-expand-indicator">{expandedSigungu[sigunguKey] ? '−' : '+'}</span>
                                  </div>

                                  {expandedSigungu[sigunguKey] && (
                                    <div className="tree-dong-list">
                                      {(sigungu.bjdList || []).map((dong) => {
                                        const code = String(dong?.bjdCode || '');
                                        const checked = selectedBjdCodeSet.has(code);

                                        return (
                                          <label key={code} className="tree-dong-item">
                                            <input
                                              type="checkbox"
                                              checked={checked}
                                              onChange={() => setSelectedBjdCodes((prev) => (checked ? prev.filter((v) => v !== code) : [...prev, code]))}
                                            />
                                            <span>{dong?.bjdName || '-'}</span>
                                          </label>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      );
                    })}
                  </div>
                )}
              </article>
            )}

            {openTopFilterKey === 'builtDate' && (
              <article className="complex-top-filter-panel built-date" style={{ left: `${topFilterPanelLeft}px` }}>
                <FilterWrapper
                  FilterClass={BuiltYearFilter}
                  value={builtDateFrom || builtDateTo ? `${builtDateFrom || ''}~${builtDateTo || ''}` : ''}
                  onChange={(value) => {
                    if (!value) {
                      setBuiltDateFrom('');
                      setBuiltDateTo('');
                      setBuiltDateAgeInput('');
                      setBuiltDateMode('range');
                      setAppliedFilters(prev => ({ ...prev, useApproveAgeMin: null, useApproveAgeMax: null }));
                    } else {
                      const [from, to] = value.split('~');
                      setBuiltDateFrom(from || '');
                      setBuiltDateTo(to || '');
                      setAppliedFilters(prev => ({
                        ...prev,
                        useApproveAgeMin: from ? Number(from) : null,
                        useApproveAgeMax: to ? Number(to) : null,
                      }));
                      setOpenTopFilterKey(null);
                    }
                  }}
                  onClose={() => setOpenTopFilterKey(null)}
                />
              </article>
            )}

            {openTopFilterKey === 'household' && (
              <article className="complex-top-filter-panel household" style={{ left: `${topFilterPanelLeft}px` }}>
                <FilterWrapper
                  FilterClass={HouseholdRangeFilter}
                  value={householdMinInput || householdMaxInput ? `${householdMinInput || ''}~${householdMaxInput || ''}` : ''}
                  onChange={(value) => {
                    if (!value) {
                      setHouseholdMinInput('');
                      setHouseholdMaxInput('');
                      setSelectedHouseholds([]);
                      setAppliedFilters(prev => ({ ...prev, householdMin: null, householdMax: null }));
                    } else {
                      const [min, max] = value.split('~');
                      setHouseholdMinInput(min || '');
                      setHouseholdMaxInput(max || '');
                      setSelectedHouseholds([]);
                      setAppliedFilters(prev => ({
                        ...prev,
                        householdMin: min ? Number(min) : null,
                        householdMax: max ? Number(max) : null,
                      }));
                      setOpenTopFilterKey(null);
                    }
                  }}
                  onClose={() => setOpenTopFilterKey(null)}
                />
              </article>
            )}

            {openTopFilterKey === 'area' && (
              <article className="complex-top-filter-panel area" style={{ left: `${topFilterPanelLeft}px` }}>
                <FilterWrapper
                  FilterClass={AreaRangeFilter}
                  value={areaMinInput && areaMaxInput ? `${areaMinInput}~${areaMaxInput}~${areaUnit}` : ''}
                  onChange={(value) => {
                    if (!value) {
                      setAreaMinInput('');
                      setAreaMaxInput('');
                      setSelectedAreaBuckets([]);
                      setAppliedFilters((prev) => ({ ...prev, areaMin: null, areaMax: null }));
                    } else {
                      // value = "min~max~unit" (단위 그대로). 서버는 ㎡ 로 변환해 전송.
                      const [minStr, maxStr, unitStr] = value.split('~');
                      const u = (unitStr === 'm2') ? 'm2' : 'pyeong';
                      const factor = (u === 'pyeong') ? 3.305785 : 1;
                      setAreaMinInput(minStr || '');
                      setAreaMaxInput(maxStr || '');
                      setAreaUnit(u);
                      setSelectedAreaBuckets([]);
                      const toM2 = (v) => (v && !Number.isNaN(Number(v)) ? Math.round(Number(v) * factor) : null);
                      setAppliedFilters((prev) => ({ ...prev, areaMin: toM2(minStr), areaMax: toM2(maxStr) }));
                    }
                  }}
                  onClose={() => setOpenTopFilterKey(null)}
                />
              </article>
            )}

            {openTopFilterKey === 'rankerFilter' && (
              <article className="complex-top-filter-panel ranker-filter" style={{ left: `${topFilterPanelLeft}px` }}>
                <div className="complex-top-filter-panel-head">
                  <h4>랭커필터</h4>
                  <button type="button" className="complex-top-filter-panel-close" onClick={() => setOpenTopFilterKey(null)}>×</button>
                </div>

                <div className="complex-filter-checkbox-list">
                  <label className="complex-filter-checkbox-item parent">
                    <input
                      type="checkbox"
                      checked={allLandmarkSelected}
                      onChange={() => {
                        if (allLandmarkSelected) {
                          setSelectedLandmarkFilters([]);
                        } else {
                          setSelectedLandmarkFilters(landmarkFilterOptions.map((opt) => opt.key));
                        }
                      }}
                    />
                    <span>?쒕뱶留덊겕</span>
                  </label>

                  <div className="complex-filter-checkbox-children">
                    {landmarkFilterOptions.map((opt) => {
                      const checked = selectedLandmarkFilters.includes(opt.key);
                      return (
                        <label key={opt.key} className="complex-filter-checkbox-item child">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => {
                              setSelectedLandmarkFilters((prev) => (
                                checked ? prev.filter((v) => v !== opt.key) : [...prev, opt.key]
                              ));
                            }}
                          />
                          <span>{opt.label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </article>
            )}

            {openTopFilterKey === 'groupBy' && (
              <article className="complex-top-filter-panel group-by" style={{ left: `${topFilterPanelLeft}px` }}>
                <div className="complex-top-filter-panel-head">
                  <h4>평균/합계</h4>
                  <button type="button" className="complex-top-filter-panel-close" onClick={() => setOpenTopFilterKey(null)}>×</button>
                </div>

                <div className="complex-filter-checkbox-list">
                  {GROUP_BY_OPTIONS.map((label) => {
                    const checked = selectedGroupBy.includes(label);
                    return (
                      <label key={label} className="complex-filter-checkbox-item">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            setSelectedGroupBy((prev) => (
                              checked ? prev.filter((v) => v !== label) : [...prev, label]
                            ));
                          }}
                        />
                        <span>{GROUP_BY_LABELS[label] || label}</span>
                      </label>
                    );
                  })}
                </div>
              </article>
            )}

            {detailFilterOpen && (
              <div
                className="complex-detail-filter-overlay"
                onClick={() => { setDetailFilterOpen(false); setFilterPopupOpen(false); }}
                style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.35)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}
              >
                <div
                  data-detail-filter-panel
                  onClick={(e) => e.stopPropagation()}
                  style={{ background: '#fff', borderRadius: 12, width: 'min(360px, 96vw)', maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 12px 40px rgba(0,0,0,0.2)' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: '1px solid #eef2f7' }}>
                    <strong style={{ fontSize: 15, color: '#111827' }}>상세 필터 설정</strong>
                    <button type="button" onClick={() => setDetailFilterOpen(false)} style={{ border: 'none', background: 'transparent', fontSize: 22, lineHeight: 1, cursor: 'pointer', color: '#64748b' }}>×</button>
                  </div>
                  <div style={{ overflowY: 'auto', padding: '6px 0', flex: 1 }}>
                    {detailFilterColumns.length === 0 ? (
                      <div style={{ padding: '20px 16px', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>필터 가능한 항목이 없습니다.</div>
                    ) : (
                      detailFilterColumns.map((column) => {
                        const summary = headerFilterSummaryForTable[column.key] || '';
                        // 회색 readonly 자리표시자(예: 분양권투자금) — 클릭 불가, 준비중 표기
                        if (column.disabled) {
                          return (
                            <div
                              key={column.key}
                              title="준비중"
                              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '10px 16px', borderBottom: '1px solid #f5f7fa', background: '#f8fafc', textAlign: 'left', fontSize: 13.5, color: '#94a3b8', cursor: 'not-allowed', userSelect: 'none' }}
                            >
                              <span style={{ fontWeight: 700 }}>{String(column.label || column.key).replace(/[\r\n]+/g, ' ')}</span>
                              <span style={{ fontSize: 11, color: '#cbd5e1', fontWeight: 700 }}>준비중</span>
                            </div>
                          );
                        }
                        return (
                          <button
                            key={column.key}
                            type="button"
                            onClick={(e) => {
                              // 모달 우측 패널 바깥쪽에 필터 컴포넌트를 붙인다 (모달은 열린 채 유지)
                              const panelRect = e.currentTarget.closest('[data-detail-filter-panel]')?.getBoundingClientRect();
                              const rect = e.currentTarget.getBoundingClientRect();
                              const popupWidth = 400;
                              const anchorRight = panelRect ? panelRect.right : rect.right;
                              const anchorLeft = panelRect ? panelRect.left : rect.left;
                              let left = anchorRight + window.scrollX + 8;
                              if (left + popupWidth > window.innerWidth) {
                                left = Math.max(10, anchorLeft + window.scrollX - popupWidth - 8);
                              }
                              setFilterPopupColumn(column);
                              setFilterPopupPosition({ top: rect.top + window.scrollY, left });
                              setFilterPopupOpen(true);
                            }}
                            style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '10px 16px', border: 'none', borderBottom: '1px solid #f5f7fa', background: '#fff', cursor: 'pointer', textAlign: 'left', fontSize: 13.5, color: '#334155' }}
                          >
                            <span style={{ fontWeight: 700 }}>{String(column.label || column.key).replace(/[\r\n]+/g, ' ')}</span>
                            {summary ? (
                              <span
                                title={summary}
                                style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', maxWidth: 180, padding: '3px 10px', paddingRight: 10, borderRadius: 999, background: '#fff2e8', border: '1px solid #ffd0ad', color: '#ea580c', fontSize: 12, fontWeight: 700, overflow: 'visible' }}
                              >
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{summary}</span>
                                <span
                                  role="button"
                                  tabIndex={0}
                                  aria-label="필터 제거"
                                  onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleRemoveHeaderFilterColumn(column.key); }}
                                  onMouseDown={(e) => { e.stopPropagation(); }}
                                  style={{ position: 'absolute', top: -7, right: -7, width: 16, height: 16, borderRadius: '50%', background: '#ea580c', color: '#fff', fontSize: 11, lineHeight: '16px', textAlign: 'center', cursor: 'pointer', boxShadow: '0 1px 2px rgba(0,0,0,0.2)' }}
                                >
                                  ×
                                </span>
                              </span>
                            ) : (
                              <span style={{ fontSize: 16, color: '#cbd5e1' }}>›</span>
                            )}
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
            )}

            {false && filterMode === 'search' && (
              <div style={{ background: '#fff', border: '1px solid #eaf0f6', borderRadius: '14px', padding: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '60px minmax(220px, 1fr) auto', gap: '10px', alignItems: 'center' }}>
                  <label style={{ fontSize: '13px', fontWeight: '700', color: '#334155' }}>{'\uAC80\uC0C9'}</label>
                  <input
                    type="text"
                    value={searchComplexName}
                    onChange={(e) => setSearchComplexName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSearchSubmit();
                    }}
                    placeholder={'\uB2E8\uC9C0\uBA85 \uC785\uB825'}
                    style={{ height: '36px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 10px', fontSize: '13px' }}
                  />
                  <button
                    type="button"
                    onClick={handleSearchSubmit}
                    style={{ height: '36px', border: '1px solid #fb923c', background: '#fff7ed', color: '#c2410c', borderRadius: '8px', padding: '0 14px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' }}
                  >
                    {'\uAC80\uC0C9'}
                  </button>
                </div>
              </div>
            )}
            
          </section>

          {error && <div className="complex-error">{error}</div>}


          <div className="complex-table-area">
            {activeTableHeaderFilterEntries.length > 0 && (
              <div className="complex-table-filter-summary">
                <strong>설정된 필터 :</strong>
                <div className="complex-table-filter-summary-chips">
                  {activeTableHeaderFilterEntries.map(([key, value]) => {
                    const label = String(tableColumnMap.get(key)?.label || key).trim();
                    return (
                      <span key={`${key}-${value}`} className="complex-table-filter-chip">
                        <span>{`${label}=${value}`}</span>
                        <button
                          type="button"
                          className="complex-table-filter-chip-remove"
                          onClick={() => handleRemoveSelectedFilter(key)}
                          aria-label={`${label} 필터 제거`}
                        >
                          ×
                        </button>
                      </span>
                    );
                  })}
                </div>
                <button
                  type="button"
                  className="complex-table-filter-summary-reset"
                  onClick={handleClearSelectedFilters}
                >
                  초기화
                </button>
              </div>
            )}
            {(!isFavoritePage && selectedBjdCodes.length === 0 && !appliedSearchComplexName) ? (
              <div className="complex-region-empty-prompt">좌측에서 지역을 선택해주세요</div>
            ) : (
            <TableComponent
            key={selectedSortPreset}
            className="complex-table-wrap"
            columns={finalTableColumns}
            colorRules={cellColorRules}
            getCellTextColor={recentCellTextColor}
            rows={filteredItems}
            keepRowsOnTop={(row) => isAggregateRow(row) && String(row?.aggregateLevel || '').toUpperCase() === 'GROUP'}
            loading={loading}
            skeletonRows={10}
            showSelectColumn={false}
            onLoadMore={loadMore}
            hasMore={hasMore}
            useInfiniteScroll={true}
            totalCount={totalCount}
            rowKey={(row) => {
              // 집계행(지역평균 SIDO/SIGUNGU/DONG, 그룹평균 GROUP)은 fixId/ptpNo 가 없어
              // 지역 식별자로 고유 key 를 만들어야 row id 중복(빈 행/뚫린 행)을 막는다.
              if (row?.isAggregateRow || row?.aggregateLevel) {
                const level = String(row?.aggregateLevel || 'AGG');
                const rc = row?.bjdCode ?? row?.regionCode ?? row?.bjd_code ?? '';
                return `agg-${level}-${rc}-${row?.sidoName ?? ''}-${row?.sigunguName ?? ''}-${row?.bjdName ?? ''}`;
              }
              const fixId = row?.fixId ?? row?.fix_id;
              const ptpNo = row?.ptpNo ?? row?.ptp_no;
              const naver = row?.naverDanjiCode ?? row?.naver_danji_code ?? '';
              if (ptpNo) return `${fixId ?? naver}-${ptpNo}`;
              return `${fixId ?? naver}`;
            }}
            onRowClick={(row, _rowIndex, rowEvent) => {
              if (isAggregateRow(row)) return;
              const clickTarget = rowEvent?.event?.target;
              if (clickTarget instanceof Element && clickTarget.closest('input, label, button, a, [role="button"]')) return;
              handleToggleCompareTarget(row, rowEvent?.event);
            }}
            rowClassName={(row) => {
              const classes = [];
              // Hide filter option rows
              if (row?.isFilterOptionRow) {
                classes.push('filter-option-row-hidden');
              }
              if (isAggregateRow(row)) {
                const aggregateLevel = String(row?.aggregateLevel || '').toUpperCase();
                if (aggregateLevel === 'SIDO' || aggregateLevel === 'SIGUNGU' || aggregateLevel === 'DONG') {
                  classes.push('complex-region-average-row');
                  // 시/구/동 레벨별 배경색 구분 (눈에 편한 톤)
                  classes.push(`complex-region-average-row--${aggregateLevel.toLowerCase()}`);
                } else {
                  classes.push('complex-aggregate-row');
                }
              }
              const rowFixId = Number(row?.fixId ?? row?.fix_id);
              if (articlePanelOpen && Number.isFinite(rowFixId) && rowFixId > 0 && rowFixId === articlePanelFixId) {
                classes.push('fix_article_selected-row');
              }
              return classes.join(' ');
            }}
            onColumnReorder={undefined}
            isColumnDraggable={() => false}
            maintainColumnOrder={false}
            sorting={tanstackSorting}
            onSortingChange={handleTableSortingChange}
            onHeaderClick={handleTableHeaderClick}
            onHeaderFilterTrigger={handleHeaderFilterTrigger}
            headerFilterEnabled={headerFilterEnabled}
            extraActiveFilterColumnIds={extraActiveHeaderFilterColumnIds}
            headerFilterTargetKeys={TABLE_HEADER_FILTER_KEYS}
            moneyFilterTargetKeys={TABLE_MONEY_FILTER_KEYS}
            countFilterTargetKeys={TABLE_COUNT_FILTER_KEYS}
            numericAlignColumnKeys={TABLE_NUMERIC_ALIGN_KEYS}
            onHeaderFilterSummaryChange={setTableHeaderFilterSummaryMap}
            headerFilterSummaryMap={headerFilterSummaryForColumns}
            onRemoveHeaderFilter={handleRemoveHeaderFilterColumn}
            headerFilterResetToken={headerFilterResetToken}
            headerFilterRemoveSignal={headerFilterRemoveSignal}
            resizeColumnsToken={resizeColumnsToken}
            freezeBuildingNameColumn
            emptyText={!isFavoritePage && selectedBjdCodes.length === 0 && !appliedSearchComplexName ? "지역을 선택해 주세요." : "표시할 데이터가 없습니다."}
            showEmpty={!loading}
          />
            )}
          </div>
          {articlePanelOpen && (
            <div className="fix_article_footer-panel">
              <div className="fix_article_footer-head">
                <div className="fix_article_footer-title">
                  <strong>{`${articlePanelComplexName || '이 단지'}-단지 추천매물`}</strong>
                </div>
                <button type="button" onClick={() => setArticlePanelOpen(false)} aria-label="매물 패널 닫기">×</button>
              </div>

              <div className="fix_article_trade-toggle">
                <button
                  type="button"
                  className={articlePanelTradeType === 'SALE' ? 'active' : ''}
                  onClick={() => setArticlePanelTradeType('SALE')}
                >
                  매매
                </button>
                <button
                  type="button"
                  className={[
                    articlePanelTradeType === 'LEASE' ? 'active' : '',
                    (articlePanelItems.LEASE || []).length === 0 ? 'disabled' : '',
                  ].filter(Boolean).join(' ')}
                  onClick={() => {
                    if ((articlePanelItems.LEASE || []).length === 0) return;
                    setArticlePanelTradeType('LEASE');
                  }}
                  disabled={(articlePanelItems.LEASE || []).length === 0}
                >
                  전세
                </button>
              </div>

              {articlePanelLoading && (
                <div className="fix_article_footer-status">매물 정보를 불러오는 중입니다.</div>
              )}
              {!articlePanelLoading && articlePanelError && (
                <div className="fix_article_footer-status error">{articlePanelError}</div>
              )}

              {!articlePanelLoading && !articlePanelError && (
                  <div className="fix_article_card-list">
                    {(articlePanelItems[articlePanelTradeType] || []).map((article, index) => {
                      const articleNo = String(article?.articleNo || '').trim();
                      const parsed = parseFixArticleText(article?.articleText);
                    return (
                      <article key={`${articlePanelTradeType}-${articleNo || index}`} className="fix_article_card">
                        <div className="fix_article_card-content">
                          <div className="fix_article_card-grid">
                            <div className="fix_article_card-field"><span>금액</span><strong>{parsed.amount}</strong></div>
                              <div className="fix_article_card-field"><span>해당층</span><strong>{parsed.currentFloor}</strong></div>
                              <div className="fix_article_card-field"><span>총층</span><strong>{parsed.totalFloor}</strong></div>
                              <div className="fix_article_card-field"><span>방향</span><strong>{parsed.direction}</strong></div>
                            </div>
                            {articleNo && (
                              <a
                                href={`https://fin.land.naver.com/articles/${articleNo}`}
                              target="_blank"
                              rel="noreferrer"
                            >
                              네이버에서 보기
                            </a>
                          )}
                        </div>
                      </article>
                    );
                  })}
                  {(articlePanelItems[articlePanelTradeType] || []).length === 0 && (
                    <div className="fix_article_footer-status">표시할 매물이 없습니다.</div>
                  )}
                </div>
              )}
            </div>
          )}
        </main>
      </div>

      {filterPopupOpen && filterPopupColumn && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 3000 }}>
        {(() => {
        const columnKey = filterPopupColumn?.key;
        const isSigunguGrade = columnKey === 'sigunguGrade';
        const isDongGrade = columnKey === 'dongGrade';
        const isGubun = columnKey === 'gubun';
        const isUseApproveAge = columnKey === 'useApproveAge';
        const isUseApproveDate = columnKey === 'useApproveDate';
        const isPlanTypeName = columnKey === 'planTypeName';
        const isFamilyCnt = columnKey === 'familyCnt';
        const isHouseStructure = columnKey === 'houseStructure';
        const isGoHyunRate = columnKey === 'goHyunRate';
        const isJeokHyunRate = columnKey === 'jeokHyunRate';
        const isGoJeoRate = columnKey === 'goJeoRate';
        const isJeoHyunRate = columnKey === 'jeoHyunRate';
        const isChangeGap = columnKey === 'changeGap';
        const isPastTopPrice = columnKey === 'pastTopPrice';
        const isPrevHighPrice = columnKey === 'prevHighPrice';
        const isProperHighPrice = columnKey === 'properHighPrice';
        const isPrevLowPrice = columnKey === 'prevLowPrice';
        const isTargetPrice = columnKey === 'targetPrice';
        const isInvestPrice = columnKey === 'investPrice';
        // 투자금>실투자금(investRealInvestPrice)과 수익률>실투자금(realInvestPrice)은 같은 실투자금 필터 사용
        const isInvestRealInvestPrice = columnKey === 'investRealInvestPrice' || columnKey === 'realInvestPrice';
        const isArticleTradePrice = columnKey === 'articleTradePrice';
        const isComplexMinTradePrice = columnKey === 'complexMinTradePrice';
        const isComplexMaxTradePrice = columnKey === 'complexMaxTradePrice';
        const isArticleLeasePrice = columnKey === 'articleLeasePrice';
        const isComplexMinLeasePrice = columnKey === 'complexMinLeasePrice';
        const isComplexMaxLeasePrice = columnKey === 'complexMaxLeasePrice';
        const isAvgSaleRealPrice = columnKey === 'avgSaleRealPrice';
        const isAvgLeaseRealPrice = columnKey === 'avgLeaseRealPrice';
        const isTradeArticleCount = columnKey === 'tradeArticleCount';
        const isLeaseArticleCount = columnKey === 'leaseArticleCount';
        const isLeasePriceRate = columnKey === 'leasePriceRate';
        const isRiseRate = columnKey === 'riseRate';
        const isRisePrice = columnKey === 'risePrice';
        const isSupply3Years = columnKey === 'supply3Years';
        const isGradeFilter = isSigunguGrade || isDongGrade;
        const isCustomFilter = isGradeFilter || isGubun || isUseApproveAge || isUseApproveDate || isPlanTypeName || isFamilyCnt || isHouseStructure || isGoHyunRate || isJeokHyunRate || isGoJeoRate || isJeoHyunRate || isChangeGap || isPastTopPrice || isPrevHighPrice || isProperHighPrice || isPrevLowPrice || isTargetPrice || isInvestPrice || isInvestRealInvestPrice || isArticleTradePrice || isComplexMinTradePrice || isComplexMaxTradePrice || isArticleLeasePrice || isComplexMinLeasePrice || isComplexMaxLeasePrice || isAvgSaleRealPrice || isAvgLeaseRealPrice || isTradeArticleCount || isLeaseArticleCount || isLeasePriceRate || isRiseRate || isRisePrice || isSupply3Years;

        if (!isCustomFilter) {
          return (
            <>
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 999,
                }}
                onClick={() => setFilterPopupOpen(false)}
              />
              <div
                className="aptrank-custom-filter"
                style={{
                  position: 'absolute',
                  top: `${filterPopupPosition.top}px`,
                  left: `${filterPopupPosition.left}px`,
                  zIndex: 1000,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                  헤더 필터를 사용해주세요
                </div>
              </div>
            </>
          );
        }

        // 급지 필터
        if (isGradeFilter) {
          const currentSelectedGrades = isSigunguGrade ? selectedSigunguGrades : selectedDongGrades;
          const setCurrentSelectedGrades = isSigunguGrade ? setSelectedSigunguGrades : setSelectedDongGrades;
          // 급지는 0~26 범위 슬라이더. 선택된 급지 배열 → "min~max" 로 표시(연속 범위 가정).
          const currentValue = currentSelectedGrades.length > 0
            ? `${Math.min(...currentSelectedGrades)}~${Math.max(...currentSelectedGrades)}`
            : '';

          return (
            <>
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 999,
                }}
                onClick={() => setFilterPopupOpen(false)}
              />
              <div
                style={{
                  position: 'absolute',
                  top: `${filterPopupPosition.top}px`,
                  left: `${filterPopupPosition.left}px`,
                  zIndex: 1000,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <FilterWrapper
                  FilterClass={GradeRangeFilter}
                  value={currentValue}
                  onChange={(value) => {
                    // value는 "min~max"(급지 범위) 또는 빈 문자열 → [min..max] 급지 목록으로 확장
                    if (!value || value === '') {
                      setCurrentSelectedGrades([]);
                      return;
                    }
                    const parts = String(value).split('~');
                    let min = parts[0] !== '' && !isNaN(Number(parts[0])) ? Number(parts[0]) : 0;
                    let max = parts[1] !== '' && !isNaN(Number(parts[1])) ? Number(parts[1]) : 26;
                    min = Math.max(0, Math.min(26, Math.round(min)));
                    max = Math.max(0, Math.min(26, Math.round(max)));
                    if (min > max) { const t = min; min = max; max = t; }
                    const grades = [];
                    for (let g = min; g <= max; g += 1) grades.push(g);
                    setCurrentSelectedGrades(grades);
                  }}
                  onClose={() => setFilterPopupOpen(false)}
                />
              </div>
            </>
          );
        }

        // 구분 필터
        if (isGubun) {
          const currentValue = selectedGubuns.length > 0 ? selectedGubuns.join(',') : '';

          return (
            <>
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 999,
                }}
                onClick={() => setFilterPopupOpen(false)}
              />
              <div
                style={{
                  position: 'absolute',
                  top: `${filterPopupPosition.top}px`,
                  left: `${filterPopupPosition.left}px`,
                  zIndex: 1000,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <FilterWrapper
                  FilterClass={GubunFilter}
                  value={currentValue}
                  onChange={(value) => {
                    console.log('[GubunFilter] onChange value:', value);
                    if (!value || value === '') {
                      console.log('[GubunFilter] Empty value, clearing selection');
                      setSelectedGubuns([]);
                      return;
                    }
                    const gubuns = String(value)
                      .split(',')
                      .map((v) => v.trim())
                      .filter((v) => v !== '');
                    console.log('[GubunFilter] Parsed gubuns:', gubuns);
                    setSelectedGubuns(gubuns);
                  }}
                  onClose={() => setFilterPopupOpen(false)}
                />
              </div>
            </>
          );
        }

        // 연차 필터 (BuiltYearFilter)
        if (isUseApproveAge || isUseApproveDate) {
          const currentValue = appliedFilters.useApproveAgeMin || appliedFilters.useApproveAgeMax
            ? `${appliedFilters.useApproveAgeMin || ''}~${appliedFilters.useApproveAgeMax || ''}`
            : '';

          return (
            <>
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 999,
                }}
                onClick={() => setFilterPopupOpen(false)}
              />
              <div
                style={{
                  position: 'absolute',
                  top: `${filterPopupPosition.top}px`,
                  left: `${filterPopupPosition.left}px`,
                  zIndex: 1000,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <FilterWrapper
                  FilterClass={BuiltYearFilter}
                  value={currentValue}
                  onChange={(value) => {
                    console.log('[BuiltYearFilter] onChange value:', value);
                    if (!value || value === '') {
                      setAppliedFilters((prev) => ({ ...prev, useApproveAgeMin: null, useApproveAgeMax: null }));
                      return;
                    }
                    const parts = value.split('~');
                    if (parts.length === 2) {
                      const minStr = (parts[0] || '').trim();
                      const maxStr = (parts[1] || '').trim();
                      const min = minStr && !isNaN(Number(minStr)) ? Number(minStr) : null;
                      const max = maxStr && !isNaN(Number(maxStr)) ? Number(maxStr) : null;
                      setAppliedFilters((prev) => ({ ...prev, useApproveAgeMin: min, useApproveAgeMax: max }));
                    }
                  }}
                  onClose={() => setFilterPopupOpen(false)}
                />
              </div>
            </>
          );
        }

        // 평형 필터 (AreaRangeFilter)
        if (isPlanTypeName) {
          // 저장된 areaMin/Max 는 ㎡ → ㎡ 모드로 복원
          const currentValue = appliedFilters.areaMin || appliedFilters.areaMax
            ? `${appliedFilters.areaMin || ''}~${appliedFilters.areaMax || ''}~m2`
            : '';

          return (
            <>
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 999,
                }}
                onClick={() => setFilterPopupOpen(false)}
              />
              <div
                style={{
                  position: 'absolute',
                  top: `${filterPopupPosition.top}px`,
                  left: `${filterPopupPosition.left}px`,
                  zIndex: 1000,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <FilterWrapper
                  FilterClass={AreaRangeFilter}
                  value={currentValue}
                  onChange={(value) => {
                    console.log('[AreaRangeFilter] onChange value:', value);
                    if (!value || value === '') {
                      setAppliedFilters((prev) => ({ ...prev, areaMin: null, areaMax: null }));
                      return;
                    }
                    const parts = value.split('~');
                    if (parts.length >= 2) {
                      const minStr = (parts[0] || '').trim();
                      const maxStr = (parts[1] || '').trim();
                      const u = (parts[2] === 'm2') ? 'm2' : 'pyeong';
                      const factor = (u === 'pyeong') ? 3.305785 : 1;
                      const toM2 = (v) => (v && !isNaN(Number(v)) ? Math.round(Number(v) * factor) : null);
                      setAppliedFilters((prev) => ({ ...prev, areaMin: toM2(minStr), areaMax: toM2(maxStr) }));
                    }
                  }}
                  onClose={() => setFilterPopupOpen(false)}
                />
              </div>
            </>
          );
        }

        // 세대수 필터 (HouseholdRangeFilter)
        if (isFamilyCnt) {
          const currentValue = appliedFilters.householdMin || appliedFilters.householdMax
            ? `${appliedFilters.householdMin || ''}~${appliedFilters.householdMax || ''}`
            : '';

          return (
            <>
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 999,
                }}
                onClick={() => setFilterPopupOpen(false)}
              />
              <div
                style={{
                  position: 'absolute',
                  top: `${filterPopupPosition.top}px`,
                  left: `${filterPopupPosition.left}px`,
                  zIndex: 1000,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <FilterWrapper
                  FilterClass={HouseholdRangeFilter}
                  value={currentValue}
                  onChange={(value) => {
                    console.log('[HouseholdRangeFilter] onChange value:', value);
                    if (!value || value === '') {
                      setAppliedFilters((prev) => ({ ...prev, householdMin: null, householdMax: null }));
                      return;
                    }
                    const parts = value.split('~');
                    if (parts.length === 2) {
                      const minStr = (parts[0] || '').trim();
                      const maxStr = (parts[1] || '').trim();
                      const min = minStr && !isNaN(Number(minStr)) ? Number(minStr) : null;
                      const max = maxStr && !isNaN(Number(maxStr)) ? Number(maxStr) : null;
                      setAppliedFilters((prev) => ({ ...prev, householdMin: min, householdMax: max }));
                    }
                  }}
                  onClose={() => setFilterPopupOpen(false)}
                />
              </div>
            </>
          );
        }

        // 구조 필터 (HouseStructureFilter)
        if (isHouseStructure) {
          const currentValue = selectedHouseStructure || '';

          return (
            <>
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 999,
                }}
                onClick={() => setFilterPopupOpen(false)}
              />
              <div
                style={{
                  position: 'absolute',
                  top: `${filterPopupPosition.top}px`,
                  left: `${filterPopupPosition.left}px`,
                  zIndex: 1000,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <FilterWrapper
                  FilterClass={HouseStructureFilter}
                  value={currentValue}
                  onChange={(value) => {
                    console.log('[HouseStructureFilter] onChange value:', value);
                    if (!value || value === '') {
                      setSelectedHouseStructure('');
                      return;
                    }
                    setSelectedHouseStructure(value);
                  }}
                  onClose={() => setFilterPopupOpen(false)}
                />
              </div>
            </>
          );
        }

        // 고현율 필터 (RateRangeFilter)
        if (isGoHyunRate) {
          const currentValue = appliedFilters.goHyunRateMin || appliedFilters.goHyunRateMax
            ? `${appliedFilters.goHyunRateMin || ''}~${appliedFilters.goHyunRateMax || ''}`
            : '';

          return (
            <>
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 999,
                }}
                onClick={() => setFilterPopupOpen(false)}
              />
              <div
                style={{
                  position: 'absolute',
                  top: `${filterPopupPosition.top}px`,
                  left: `${filterPopupPosition.left}px`,
                  zIndex: 1000,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <FilterWrapper
                  FilterClass={RateRangeFilter}
                  value={currentValue}
                  onChange={(value) => {
                    console.log('[GoHyunRateFilter] onChange value:', value);
                    if (!value || value === '') {
                      setAppliedFilters((prev) => ({
                        ...prev,
                        goHyunRateMin: null,
                        goHyunRateMax: null,
                      }));
                      return;
                    }
                    const parts = value.split('~');
                    if (parts.length === 2) {
                      const minStr = (parts[0] || '').trim();
                      const maxStr = (parts[1] || '').trim();
                      const min = minStr && !isNaN(Number(minStr)) ? Number(minStr) : null;
                      const max = maxStr && !isNaN(Number(maxStr)) ? Number(maxStr) : null;
                      setAppliedFilters((prev) => ({
                        ...prev,
                        goHyunRateMin: min,
                        goHyunRateMax: max,
                      }));
                    }
                  }}
                  onClose={() => setFilterPopupOpen(false)}
                />
              </div>
            </>
          );
        }

        // 적현율 필터 (RateRangeFilter)
        if (isJeokHyunRate) {
          const currentValue = appliedFilters.jeokHyunRateMin || appliedFilters.jeokHyunRateMax
            ? `${appliedFilters.jeokHyunRateMin || ''}~${appliedFilters.jeokHyunRateMax || ''}`
            : '';

          return (
            <>
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 999,
                }}
                onClick={() => setFilterPopupOpen(false)}
              />
              <div
                style={{
                  position: 'absolute',
                  top: `${filterPopupPosition.top}px`,
                  left: `${filterPopupPosition.left}px`,
                  zIndex: 1000,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <FilterWrapper
                  FilterClass={RateRangeFilter}
                  value={currentValue}
                  onChange={(value) => {
                    console.log('[JeokHyunRateFilter] onChange value:', value);
                    if (!value || value === '') {
                      setAppliedFilters((prev) => ({
                        ...prev,
                        jeokHyunRateMin: null,
                        jeokHyunRateMax: null,
                      }));
                      return;
                    }
                    const parts = value.split('~');
                    if (parts.length === 2) {
                      const minStr = (parts[0] || '').trim();
                      const maxStr = (parts[1] || '').trim();
                      const min = minStr && !isNaN(Number(minStr)) ? Number(minStr) : null;
                      const max = maxStr && !isNaN(Number(maxStr)) ? Number(maxStr) : null;
                      setAppliedFilters((prev) => ({
                        ...prev,
                        jeokHyunRateMin: min,
                        jeokHyunRateMax: max,
                      }));
                    }
                  }}
                  onClose={() => setFilterPopupOpen(false)}
                />
              </div>
            </>
          );
        }

        // 고저율 필터 (RateRangeFilter)
        if (isGoJeoRate) {
          const currentValue = appliedFilters.goJeoRateMin || appliedFilters.goJeoRateMax
            ? `${appliedFilters.goJeoRateMin || ''}~${appliedFilters.goJeoRateMax || ''}`
            : '';

          return (
            <>
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 999,
                }}
                onClick={() => setFilterPopupOpen(false)}
              />
              <div
                style={{
                  position: 'absolute',
                  top: `${filterPopupPosition.top}px`,
                  left: `${filterPopupPosition.left}px`,
                  zIndex: 1000,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <FilterWrapper
                  FilterClass={RateRangeFilter}
                  value={currentValue}
                  onChange={(value) => {
                    console.log('[GoJeoRateFilter] onChange value:', value);
                    if (!value || value === '') {
                      setAppliedFilters((prev) => ({
                        ...prev,
                        goJeoRateMin: null,
                        goJeoRateMax: null,
                      }));
                      return;
                    }
                    const parts = value.split('~');
                    if (parts.length === 2) {
                      const minStr = (parts[0] || '').trim();
                      const maxStr = (parts[1] || '').trim();
                      const min = minStr && !isNaN(Number(minStr)) ? Number(minStr) : null;
                      const max = maxStr && !isNaN(Number(maxStr)) ? Number(maxStr) : null;
                      setAppliedFilters((prev) => ({
                        ...prev,
                        goJeoRateMin: min,
                        goJeoRateMax: max,
                      }));
                    }
                  }}
                  onClose={() => setFilterPopupOpen(false)}
                />
              </div>
            </>
          );
        }

        // 저현율 필터 (RateRangeFilter)
        if (isJeoHyunRate) {
          const currentValue = appliedFilters.jeoHyunRateMin || appliedFilters.jeoHyunRateMax
            ? `${appliedFilters.jeoHyunRateMin || ''}~${appliedFilters.jeoHyunRateMax || ''}`
            : '';

          return (
            <>
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 999,
                }}
                onClick={() => setFilterPopupOpen(false)}
              />
              <div
                style={{
                  position: 'absolute',
                  top: `${filterPopupPosition.top}px`,
                  left: `${filterPopupPosition.left}px`,
                  zIndex: 1000,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <FilterWrapper
                  FilterClass={RateRangeFilter}
                  value={currentValue}
                  onChange={(value) => {
                    console.log('[JeoHyunRateFilter] onChange value:', value);
                    if (!value || value === '') {
                      setAppliedFilters((prev) => ({
                        ...prev,
                        jeoHyunRateMin: null,
                        jeoHyunRateMax: null,
                      }));
                      return;
                    }
                    const parts = value.split('~');
                    if (parts.length === 2) {
                      const minStr = (parts[0] || '').trim();
                      const maxStr = (parts[1] || '').trim();
                      const min = minStr && !isNaN(Number(minStr)) ? Number(minStr) : null;
                      const max = maxStr && !isNaN(Number(maxStr)) ? Number(maxStr) : null;
                      setAppliedFilters((prev) => ({
                        ...prev,
                        jeoHyunRateMin: min,
                        jeoHyunRateMax: max,
                      }));
                    }
                  }}
                  onClose={() => setFilterPopupOpen(false)}
                />
              </div>
            </>
          );
        }

        // 변동차 필터 (MoneyRangeFilter - 금액)
        if (isChangeGap) {
          const currentValue = appliedFilters.changeGapMin || appliedFilters.changeGapMax
            ? `${appliedFilters.changeGapMin || ''}~${appliedFilters.changeGapMax || ''}`
            : '';

          return (
            <>
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 999,
                }}
                onClick={() => setFilterPopupOpen(false)}
              />
              <div
                style={{
                  position: 'absolute',
                  top: `${filterPopupPosition.top}px`,
                  left: `${filterPopupPosition.left}px`,
                  zIndex: 1000,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <FilterWrapper
                  FilterClass={MoneyRangeFilter}
                  value={currentValue}
                  onChange={(value) => {
                    console.log('[ChangeGapFilter] onChange value:', value);
                    if (!value || value === '') {
                      setAppliedFilters((prev) => ({
                        ...prev,
                        changeGapMin: null,
                        changeGapMax: null,
                      }));
                      return;
                    }
                    const parts = value.split('~');
                    if (parts.length === 2) {
                      const minStr = (parts[0] || '').trim();
                      const maxStr = (parts[1] || '').trim();
                      const min = minStr && !isNaN(Number(minStr)) ? Number(minStr) : null;
                      const max = maxStr && !isNaN(Number(maxStr)) ? Number(maxStr) : null;
                      setAppliedFilters((prev) => ({
                        ...prev,
                        changeGapMin: min,
                        changeGapMax: max,
                      }));
                    }
                  }}
                  onClose={() => setFilterPopupOpen(false)}
                />
              </div>
            </>
          );
        }

        // 최고가 필터
        if (isPastTopPrice) {
          const currentValue = appliedFilters.pastTopPriceMin || appliedFilters.pastTopPriceMax
            ? `${appliedFilters.pastTopPriceMin || ''}~${appliedFilters.pastTopPriceMax || ''}`
            : '';

          return (
            <>
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 999,
                }}
                onClick={() => setFilterPopupOpen(false)}
              />
              <div
                style={{
                  position: 'absolute',
                  top: `${filterPopupPosition.top}px`,
                  left: `${filterPopupPosition.left}px`,
                  zIndex: 1000,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <FilterWrapper
                  FilterClass={PriceRangeFilter}
                  value={currentValue}
                  onChange={(value) => {
                    console.log('[PastTopPriceFilter] onChange value:', value);
                    if (!value || value === '') {
                      setAppliedFilters((prev) => ({
                        ...prev,
                        pastTopPriceMin: null,
                        pastTopPriceMax: null,
                      }));
                      return;
                    }
                    const parts = value.split('~');
                    if (parts.length === 2) {
                      const minStr = (parts[0] || '').trim();
                      const maxStr = (parts[1] || '').trim();
                      const min = minStr && !isNaN(Number(minStr)) ? Number(minStr) : null;
                      const max = maxStr && !isNaN(Number(maxStr)) ? Number(maxStr) : null;
                      setAppliedFilters((prev) => ({
                        ...prev,
                        pastTopPriceMin: min,
                        pastTopPriceMax: max,
                      }));
                    }
                  }}
                  onClose={() => setFilterPopupOpen(false)}
                />
              </div>
            </>
          );
        }

        // 전고가 필터
        if (isPrevHighPrice) {
          const currentValue = appliedFilters.prevHighPriceMin || appliedFilters.prevHighPriceMax
            ? `${appliedFilters.prevHighPriceMin || ''}~${appliedFilters.prevHighPriceMax || ''}`
            : '';

          return (
            <>
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 999,
                }}
                onClick={() => setFilterPopupOpen(false)}
              />
              <div
                style={{
                  position: 'absolute',
                  top: `${filterPopupPosition.top}px`,
                  left: `${filterPopupPosition.left}px`,
                  zIndex: 1000,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <FilterWrapper
                  FilterClass={PriceRangeFilter}
                  value={currentValue}
                  onChange={(value) => {
                    console.log('[PrevHighPriceFilter] onChange value:', value);
                    if (!value || value === '') {
                      setAppliedFilters((prev) => ({
                        ...prev,
                        prevHighPriceMin: null,
                        prevHighPriceMax: null,
                      }));
                      return;
                    }
                    const parts = value.split('~');
                    if (parts.length === 2) {
                      const minStr = (parts[0] || '').trim();
                      const maxStr = (parts[1] || '').trim();
                      const min = minStr && !isNaN(Number(minStr)) ? Number(minStr) : null;
                      const max = maxStr && !isNaN(Number(maxStr)) ? Number(maxStr) : null;
                      setAppliedFilters((prev) => ({
                        ...prev,
                        prevHighPriceMin: min,
                        prevHighPriceMax: max,
                      }));
                    }
                  }}
                  onClose={() => setFilterPopupOpen(false)}
                />
              </div>
            </>
          );
        }

        // 적고가 필터
        if (isProperHighPrice) {
          const currentValue = appliedFilters.properHighPriceMin || appliedFilters.properHighPriceMax
            ? `${appliedFilters.properHighPriceMin || ''}~${appliedFilters.properHighPriceMax || ''}`
            : '';

          return (
            <>
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 999,
                }}
                onClick={() => setFilterPopupOpen(false)}
              />
              <div
                style={{
                  position: 'absolute',
                  top: `${filterPopupPosition.top}px`,
                  left: `${filterPopupPosition.left}px`,
                  zIndex: 1000,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <FilterWrapper
                  FilterClass={PriceRangeFilter}
                  value={currentValue}
                  onChange={(value) => {
                    console.log('[ProperHighPriceFilter] onChange value:', value);
                    if (!value || value === '') {
                      setAppliedFilters((prev) => ({
                        ...prev,
                        properHighPriceMin: null,
                        properHighPriceMax: null,
                      }));
                      return;
                    }
                    const parts = value.split('~');
                    if (parts.length === 2) {
                      const minStr = (parts[0] || '').trim();
                      const maxStr = (parts[1] || '').trim();
                      const min = minStr && !isNaN(Number(minStr)) ? Number(minStr) : null;
                      const max = maxStr && !isNaN(Number(maxStr)) ? Number(maxStr) : null;
                      setAppliedFilters((prev) => ({
                        ...prev,
                        properHighPriceMin: min,
                        properHighPriceMax: max,
                      }));
                    }
                  }}
                  onClose={() => setFilterPopupOpen(false)}
                />
              </div>
            </>
          );
        }

        // 전저가 필터
        if (isPrevLowPrice) {
          const currentValue = appliedFilters.prevLowPriceMin || appliedFilters.prevLowPriceMax
            ? `${appliedFilters.prevLowPriceMin || ''}~${appliedFilters.prevLowPriceMax || ''}`
            : '';

          return (
            <>
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 999,
                }}
                onClick={() => setFilterPopupOpen(false)}
              />
              <div
                style={{
                  position: 'absolute',
                  top: `${filterPopupPosition.top}px`,
                  left: `${filterPopupPosition.left}px`,
                  zIndex: 1000,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <FilterWrapper
                  FilterClass={PriceRangeFilter}
                  value={currentValue}
                  onChange={(value) => {
                    console.log('[PrevLowPriceFilter] onChange value:', value);
                    if (!value || value === '') {
                      setAppliedFilters((prev) => ({
                        ...prev,
                        prevLowPriceMin: null,
                        prevLowPriceMax: null,
                      }));
                      return;
                    }
                    const parts = value.split('~');
                    if (parts.length === 2) {
                      const minStr = (parts[0] || '').trim();
                      const maxStr = (parts[1] || '').trim();
                      const min = minStr && !isNaN(Number(minStr)) ? Number(minStr) : null;
                      const max = maxStr && !isNaN(Number(maxStr)) ? Number(maxStr) : null;
                      setAppliedFilters((prev) => ({
                        ...prev,
                        prevLowPriceMin: min,
                        prevLowPriceMax: max,
                      }));
                    }
                  }}
                  onClose={() => setFilterPopupOpen(false)}
                />
              </div>
            </>
          );
        }

        // 타겟가 필터
        if (isTargetPrice) {
          const currentValue = appliedFilters.targetPriceMin || appliedFilters.targetPriceMax
            ? `${appliedFilters.targetPriceMin || ''}~${appliedFilters.targetPriceMax || ''}`
            : '';

          return (
            <>
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 999,
                }}
                onClick={() => setFilterPopupOpen(false)}
              />
              <div
                style={{
                  position: 'absolute',
                  top: `${filterPopupPosition.top}px`,
                  left: `${filterPopupPosition.left}px`,
                  zIndex: 1000,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <FilterWrapper
                  FilterClass={PriceRangeFilter}
                  value={currentValue}
                  onChange={(value) => {
                    console.log('[TargetPriceFilter] onChange value:', value);
                    if (!value || value === '') {
                      setAppliedFilters((prev) => ({
                        ...prev,
                        targetPriceMin: null,
                        targetPriceMax: null,
                      }));
                      return;
                    }
                    const parts = value.split('~');
                    if (parts.length === 2) {
                      const minStr = (parts[0] || '').trim();
                      const maxStr = (parts[1] || '').trim();
                      const min = minStr && !isNaN(Number(minStr)) ? Number(minStr) : null;
                      const max = maxStr && !isNaN(Number(maxStr)) ? Number(maxStr) : null;
                      setAppliedFilters((prev) => ({
                        ...prev,
                        targetPriceMin: min,
                        targetPriceMax: max,
                      }));
                    }
                  }}
                  onClose={() => setFilterPopupOpen(false)}
                />
              </div>
            </>
          );
        }

        // 투자금 필터
        if (isInvestPrice) {
          const currentValue = appliedFilters.investPriceMin || appliedFilters.investPriceMax
            ? `${appliedFilters.investPriceMin || ''}~${appliedFilters.investPriceMax || ''}`
            : '';

          return (
            <>
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 999,
                }}
                onClick={() => setFilterPopupOpen(false)}
              />
              <div
                style={{
                  position: 'absolute',
                  top: `${filterPopupPosition.top}px`,
                  left: `${filterPopupPosition.left}px`,
                  zIndex: 1000,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <FilterWrapper
                  FilterClass={PriceRangeFilter}
                  value={currentValue}
                  onChange={(value) => {
                    if (!value || value === '') {
                      setAppliedFilters((prev) => ({
                        ...prev,
                        investPriceMin: null,
                        investPriceMax: null,
                      }));
                      return;
                    }
                    const parts = value.split('~');
                    if (parts.length === 2) {
                      const minStr = (parts[0] || '').trim();
                      const maxStr = (parts[1] || '').trim();
                      const min = minStr && !isNaN(Number(minStr)) ? Number(minStr) : null;
                      const max = maxStr && !isNaN(Number(maxStr)) ? Number(maxStr) : null;
                      setAppliedFilters((prev) => ({
                        ...prev,
                        investPriceMin: min,
                        investPriceMax: max,
                      }));
                    }
                  }}
                  onClose={() => setFilterPopupOpen(false)}
                />
              </div>
            </>
          );
        }

        // 실투자금 필터
        if (isInvestRealInvestPrice) {
          const currentValue = appliedFilters.investRealInvestPriceMin || appliedFilters.investRealInvestPriceMax
            ? `${appliedFilters.investRealInvestPriceMin || ''}~${appliedFilters.investRealInvestPriceMax || ''}`
            : '';

          return (
            <>
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 999,
                }}
                onClick={() => setFilterPopupOpen(false)}
              />
              <div
                style={{
                  position: 'absolute',
                  top: `${filterPopupPosition.top}px`,
                  left: `${filterPopupPosition.left}px`,
                  zIndex: 1000,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <FilterWrapper
                  FilterClass={PriceRangeFilter}
                  value={currentValue}
                  onChange={(value) => {
                    if (!value || value === '') {
                      setAppliedFilters((prev) => ({
                        ...prev,
                        investRealInvestPriceMin: null,
                        investRealInvestPriceMax: null,
                      }));
                      return;
                    }
                    const parts = value.split('~');
                    if (parts.length === 2) {
                      const minStr = (parts[0] || '').trim();
                      const maxStr = (parts[1] || '').trim();
                      const min = minStr && !isNaN(Number(minStr)) ? Number(minStr) : null;
                      const max = maxStr && !isNaN(Number(maxStr)) ? Number(maxStr) : null;
                      setAppliedFilters((prev) => ({
                        ...prev,
                        investRealInvestPriceMin: min,
                        investRealInvestPriceMax: max,
                      }));
                    }
                  }}
                  onClose={() => setFilterPopupOpen(false)}
                />
              </div>
            </>
          );
        }

        // 매매가 필터 (서버 요청 키: tradePriceMin/Max)
        if (isArticleTradePrice) {
          const currentValue = appliedFilters.tradePriceMin || appliedFilters.tradePriceMax
            ? `${appliedFilters.tradePriceMin || ''}~${appliedFilters.tradePriceMax || ''}`
            : '';

          return (
            <>
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 999,
                }}
                onClick={() => setFilterPopupOpen(false)}
              />
              <div
                style={{
                  position: 'absolute',
                  top: `${filterPopupPosition.top}px`,
                  left: `${filterPopupPosition.left}px`,
                  zIndex: 1000,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <FilterWrapper
                  FilterClass={PriceRangeFilter}
                  value={currentValue}
                  onChange={(value) => {
                    if (!value || value === '') {
                      setAppliedFilters((prev) => ({
                        ...prev,
                        tradePriceMin: null,
                        tradePriceMax: null,
                      }));
                      return;
                    }
                    const parts = value.split('~');
                    if (parts.length === 2) {
                      const minStr = (parts[0] || '').trim();
                      const maxStr = (parts[1] || '').trim();
                      const min = minStr && !isNaN(Number(minStr)) ? Number(minStr) : null;
                      const max = maxStr && !isNaN(Number(maxStr)) ? Number(maxStr) : null;
                      setAppliedFilters((prev) => ({
                        ...prev,
                        tradePriceMin: min,
                        tradePriceMax: max,
                      }));
                    }
                  }}
                  onClose={() => setFilterPopupOpen(false)}
                />
              </div>
            </>
          );
        }

        // 매매 최저가 필터
        if (isComplexMinTradePrice) {
          const currentValue = appliedFilters.complexMinTradePriceMin || appliedFilters.complexMinTradePriceMax
            ? `${appliedFilters.complexMinTradePriceMin || ''}~${appliedFilters.complexMinTradePriceMax || ''}`
            : '';

          return (
            <>
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 999,
                }}
                onClick={() => setFilterPopupOpen(false)}
              />
              <div
                style={{
                  position: 'absolute',
                  top: `${filterPopupPosition.top}px`,
                  left: `${filterPopupPosition.left}px`,
                  zIndex: 1000,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <FilterWrapper
                  FilterClass={PriceRangeFilter}
                  value={currentValue}
                  onChange={(value) => {
                    if (!value || value === '') {
                      setAppliedFilters((prev) => ({
                        ...prev,
                        complexMinTradePriceMin: null,
                        complexMinTradePriceMax: null,
                      }));
                      return;
                    }
                    const parts = value.split('~');
                    if (parts.length === 2) {
                      const minStr = (parts[0] || '').trim();
                      const maxStr = (parts[1] || '').trim();
                      const min = minStr && !isNaN(Number(minStr)) ? Number(minStr) : null;
                      const max = maxStr && !isNaN(Number(maxStr)) ? Number(maxStr) : null;
                      setAppliedFilters((prev) => ({
                        ...prev,
                        complexMinTradePriceMin: min,
                        complexMinTradePriceMax: max,
                      }));
                    }
                  }}
                  onClose={() => setFilterPopupOpen(false)}
                />
              </div>
            </>
          );
        }

        if (isComplexMaxTradePrice) {
          const currentValue = appliedFilters.complexMaxTradePriceMin || appliedFilters.complexMaxTradePriceMax
            ? `${appliedFilters.complexMaxTradePriceMin || ''}~${appliedFilters.complexMaxTradePriceMax || ''}`
            : '';
          return (
            <>
              <div
                style={{
                  position: 'fixed',
                  top: filterPopupPosition.top,
                  left: filterPopupPosition.left,
                  zIndex: filterPopupPosition.zIndex,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <FilterWrapper
                  FilterClass={PriceRangeFilter}
                  value={currentValue}
                  onChange={(value) => {
                    if (!value || value === '~') {
                      setAppliedFilters((prev) => ({
                        ...prev,
                        complexMaxTradePriceMin: null,
                        complexMaxTradePriceMax: null,
                      }));
                      return;
                    }
                    const parts = value.split('~');
                    if (parts.length === 2) {
                      const minStr = (parts[0] || '').trim();
                      const maxStr = (parts[1] || '').trim();
                      const min = minStr && !isNaN(Number(minStr)) ? Number(minStr) : null;
                      const max = maxStr && !isNaN(Number(maxStr)) ? Number(maxStr) : null;
                      setAppliedFilters((prev) => ({
                        ...prev,
                        complexMaxTradePriceMin: min,
                        complexMaxTradePriceMax: max,
                      }));
                    }
                  }}
                  onClose={() => setFilterPopupOpen(false)}
                />
              </div>
            </>
          );
        }

        // articleLeasePrice (전세가)
        if (isArticleLeasePrice) {
          const currentValue = appliedFilters.leasePriceMin || appliedFilters.leasePriceMax
            ? `${appliedFilters.leasePriceMin || ''}~${appliedFilters.leasePriceMax || ''}`
            : '';

          return (
            <>
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 999,
                }}
                onClick={() => setFilterPopupOpen(false)}
              />
              <div
                style={{
                  position: 'absolute',
                  top: `${filterPopupPosition.top}px`,
                  left: `${filterPopupPosition.left}px`,
                  zIndex: 1000,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <FilterWrapper
                  FilterClass={PriceRangeFilter}
                  value={currentValue}
                  onChange={(value) => {
                    if (!value || value === '') {
                      setAppliedFilters((prev) => ({
                        ...prev,
                        leasePriceMin: null,
                        leasePriceMax: null,
                      }));
                      return;
                    }
                    const parts = value.split('~');
                    if (parts.length === 2) {
                      const minStr = (parts[0] || '').trim();
                      const maxStr = (parts[1] || '').trim();
                      const min = minStr && !isNaN(Number(minStr)) ? Number(minStr) : null;
                      const max = maxStr && !isNaN(Number(maxStr)) ? Number(maxStr) : null;
                      setAppliedFilters((prev) => ({
                        ...prev,
                        leasePriceMin: min,
                        leasePriceMax: max,
                      }));
                    }
                  }}
                  onClose={() => setFilterPopupOpen(false)}
                />
              </div>
            </>
          );
        }

        // complexMinLeasePrice (전세 최저가)
        if (isComplexMinLeasePrice) {
          const currentValue = appliedFilters.complexMinLeasePriceMin || appliedFilters.complexMinLeasePriceMax
            ? `${appliedFilters.complexMinLeasePriceMin || ''}~${appliedFilters.complexMinLeasePriceMax || ''}`
            : '';

          return (
            <>
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 999,
                }}
                onClick={() => setFilterPopupOpen(false)}
              />
              <div
                style={{
                  position: 'absolute',
                  top: `${filterPopupPosition.top}px`,
                  left: `${filterPopupPosition.left}px`,
                  zIndex: 1000,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <FilterWrapper
                  FilterClass={PriceRangeFilter}
                  value={currentValue}
                  onChange={(value) => {
                    if (!value || value === '') {
                      setAppliedFilters((prev) => ({
                        ...prev,
                        complexMinLeasePriceMin: null,
                        complexMinLeasePriceMax: null,
                      }));
                      return;
                    }
                    const parts = value.split('~');
                    if (parts.length === 2) {
                      const minStr = (parts[0] || '').trim();
                      const maxStr = (parts[1] || '').trim();
                      const min = minStr && !isNaN(Number(minStr)) ? Number(minStr) : null;
                      const max = maxStr && !isNaN(Number(maxStr)) ? Number(maxStr) : null;
                      setAppliedFilters((prev) => ({
                        ...prev,
                        complexMinLeasePriceMin: min,
                        complexMinLeasePriceMax: max,
                      }));
                    }
                  }}
                  onClose={() => setFilterPopupOpen(false)}
                />
              </div>
            </>
          );
        }

        // complexMaxLeasePrice (전세 최고가)
        if (isComplexMaxLeasePrice) {
          const currentValue = appliedFilters.complexMaxLeasePriceMin || appliedFilters.complexMaxLeasePriceMax
            ? `${appliedFilters.complexMaxLeasePriceMin || ''}~${appliedFilters.complexMaxLeasePriceMax || ''}`
            : '';

          return (
            <>
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 999,
                }}
                onClick={() => setFilterPopupOpen(false)}
              />
              <div
                style={{
                  position: 'absolute',
                  top: `${filterPopupPosition.top}px`,
                  left: `${filterPopupPosition.left}px`,
                  zIndex: 1000,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <FilterWrapper
                  FilterClass={PriceRangeFilter}
                  value={currentValue}
                  onChange={(value) => {
                    if (!value || value === '') {
                      setAppliedFilters((prev) => ({
                        ...prev,
                        complexMaxLeasePriceMin: null,
                        complexMaxLeasePriceMax: null,
                      }));
                      return;
                    }
                    const parts = value.split('~');
                    if (parts.length === 2) {
                      const minStr = (parts[0] || '').trim();
                      const maxStr = (parts[1] || '').trim();
                      const min = minStr && !isNaN(Number(minStr)) ? Number(minStr) : null;
                      const max = maxStr && !isNaN(Number(maxStr)) ? Number(maxStr) : null;
                      setAppliedFilters((prev) => ({
                        ...prev,
                        complexMaxLeasePriceMin: min,
                        complexMaxLeasePriceMax: max,
                      }));
                    }
                  }}
                  onClose={() => setFilterPopupOpen(false)}
                />
              </div>
            </>
          );
        }

        // avgSaleRealPrice (매매 실거래가)
        if (isAvgSaleRealPrice) {
          const currentValue = appliedFilters.avgSaleRealPriceMin || appliedFilters.avgSaleRealPriceMax
            ? `${appliedFilters.avgSaleRealPriceMin || ''}~${appliedFilters.avgSaleRealPriceMax || ''}`
            : '';

          return (
            <>
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 999,
                }}
                onClick={() => setFilterPopupOpen(false)}
              />
              <div
                style={{
                  position: 'absolute',
                  top: `${filterPopupPosition.top}px`,
                  left: `${filterPopupPosition.left}px`,
                  zIndex: 1000,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <FilterWrapper
                  FilterClass={PriceRangeFilter}
                  value={currentValue}
                  onChange={(value) => {
                    if (!value || value === '') {
                      setAppliedFilters((prev) => ({
                        ...prev,
                        avgSaleRealPriceMin: null,
                        avgSaleRealPriceMax: null,
                      }));
                      return;
                    }
                    const parts = value.split('~');
                    if (parts.length === 2) {
                      const minStr = (parts[0] || '').trim();
                      const maxStr = (parts[1] || '').trim();
                      const min = minStr && !isNaN(Number(minStr)) ? Number(minStr) : null;
                      const max = maxStr && !isNaN(Number(maxStr)) ? Number(maxStr) : null;
                      setAppliedFilters((prev) => ({
                        ...prev,
                        avgSaleRealPriceMin: min,
                        avgSaleRealPriceMax: max,
                      }));
                    }
                  }}
                  onClose={() => setFilterPopupOpen(false)}
                />
              </div>
            </>
          );
        }

        // avgLeaseRealPrice (전세 실거래가)
        if (isAvgLeaseRealPrice) {
          const currentValue = appliedFilters.avgLeaseRealPriceMin || appliedFilters.avgLeaseRealPriceMax
            ? `${appliedFilters.avgLeaseRealPriceMin || ''}~${appliedFilters.avgLeaseRealPriceMax || ''}`
            : '';

          return (
            <>
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 999,
                }}
                onClick={() => setFilterPopupOpen(false)}
              />
              <div
                style={{
                  position: 'absolute',
                  top: `${filterPopupPosition.top}px`,
                  left: `${filterPopupPosition.left}px`,
                  zIndex: 1000,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <FilterWrapper
                  FilterClass={PriceRangeFilter}
                  value={currentValue}
                  onChange={(value) => {
                    if (!value || value === '') {
                      setAppliedFilters((prev) => ({
                        ...prev,
                        avgLeaseRealPriceMin: null,
                        avgLeaseRealPriceMax: null,
                      }));
                      return;
                    }
                    const parts = value.split('~');
                    if (parts.length === 2) {
                      const minStr = (parts[0] || '').trim();
                      const maxStr = (parts[1] || '').trim();
                      const min = minStr && !isNaN(Number(minStr)) ? Number(minStr) : null;
                      const max = maxStr && !isNaN(Number(maxStr)) ? Number(maxStr) : null;
                      setAppliedFilters((prev) => ({
                        ...prev,
                        avgLeaseRealPriceMin: min,
                        avgLeaseRealPriceMax: max,
                      }));
                    }
                  }}
                  onClose={() => setFilterPopupOpen(false)}
                />
              </div>
            </>
          );
        }

        // tradeArticleCount (매매수)
        if (isTradeArticleCount) {
          const currentValue = appliedFilters.tradeArticleCountMin || appliedFilters.tradeArticleCountMax
            ? `${appliedFilters.tradeArticleCountMin || ''}~${appliedFilters.tradeArticleCountMax || ''}`
            : '';

          return (
            <>
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 999,
                }}
                onClick={() => setFilterPopupOpen(false)}
              />
              <div
                style={{
                  position: 'absolute',
                  top: `${filterPopupPosition.top}px`,
                  left: `${filterPopupPosition.left}px`,
                  zIndex: 1000,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <FilterWrapper
                  FilterClass={CountRangeFilter}
                  value={currentValue}
                  onChange={(value) => {
                    if (!value || value === '') {
                      setAppliedFilters((prev) => ({
                        ...prev,
                        tradeArticleCountMin: null,
                        tradeArticleCountMax: null,
                      }));
                      return;
                    }
                    const parts = value.split('~');
                    if (parts.length === 2) {
                      const minStr = (parts[0] || '').trim();
                      const maxStr = (parts[1] || '').trim();
                      const minNum = parseInt(minStr, 10);
                      const maxNum = parseInt(maxStr, 10);
                      const min = minStr && !isNaN(minNum) ? minNum : null;
                      const max = maxStr && !isNaN(maxNum) ? maxNum : null;
                      setAppliedFilters((prev) => ({
                        ...prev,
                        tradeArticleCountMin: min,
                        tradeArticleCountMax: max,
                      }));
                    }
                  }}
                  onClose={() => setFilterPopupOpen(false)}
                />
              </div>
            </>
          );
        }

        // leaseArticleCount (전세수)
        if (isLeaseArticleCount) {
          const currentValue = appliedFilters.leaseArticleCountMin || appliedFilters.leaseArticleCountMax
            ? `${appliedFilters.leaseArticleCountMin || ''}~${appliedFilters.leaseArticleCountMax || ''}`
            : '';

          return (
            <>
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 999,
                }}
                onClick={() => setFilterPopupOpen(false)}
              />
              <div
                style={{
                  position: 'absolute',
                  top: `${filterPopupPosition.top}px`,
                  left: `${filterPopupPosition.left}px`,
                  zIndex: 1000,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <FilterWrapper
                  FilterClass={CountRangeFilter}
                  value={currentValue}
                  onChange={(value) => {
                    if (!value || value === '') {
                      setAppliedFilters((prev) => ({
                        ...prev,
                        leaseArticleCountMin: null,
                        leaseArticleCountMax: null,
                      }));
                      return;
                    }
                    const parts = value.split('~');
                    if (parts.length === 2) {
                      const minStr = (parts[0] || '').trim();
                      const maxStr = (parts[1] || '').trim();
                      const minNum = parseInt(minStr, 10);
                      const maxNum = parseInt(maxStr, 10);
                      const min = minStr && !isNaN(minNum) ? minNum : null;
                      const max = maxStr && !isNaN(maxNum) ? maxNum : null;
                      setAppliedFilters((prev) => ({
                        ...prev,
                        leaseArticleCountMin: min,
                        leaseArticleCountMax: max,
                      }));
                    }
                  }}
                  onClose={() => setFilterPopupOpen(false)}
                />
              </div>
            </>
          );
        }

        // leasePriceRate (전세가율)
        if (isLeasePriceRate) {
          const currentValue = appliedFilters.leasePriceRateMin || appliedFilters.leasePriceRateMax
            ? `${appliedFilters.leasePriceRateMin || ''}~${appliedFilters.leasePriceRateMax || ''}`
            : '';

          return (
            <>
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 999,
                }}
                onClick={() => setFilterPopupOpen(false)}
              />
              <div
                style={{
                  position: 'absolute',
                  top: `${filterPopupPosition.top}px`,
                  left: `${filterPopupPosition.left}px`,
                  zIndex: 1000,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <FilterWrapper
                  FilterClass={RateRangeFilter}
                  value={currentValue}
                  onChange={(value) => {
                    if (!value || value === '') {
                      setAppliedFilters((prev) => ({
                        ...prev,
                        leasePriceRateMin: null,
                        leasePriceRateMax: null,
                      }));
                      return;
                    }
                    const parts = value.split('~');
                    if (parts.length === 2) {
                      const minStr = (parts[0] || '').trim();
                      const maxStr = (parts[1] || '').trim();
                      const min = minStr && !isNaN(Number(minStr)) ? Number(minStr) : null;
                      const max = maxStr && !isNaN(Number(maxStr)) ? Number(maxStr) : null;
                      setAppliedFilters((prev) => ({
                        ...prev,
                        leasePriceRateMin: min,
                        leasePriceRateMax: max,
                      }));
                    }
                  }}
                  onClose={() => setFilterPopupOpen(false)}
                />
              </div>
            </>
          );
        }

        // riseRate (상승률)
        if (isRiseRate) {
          const currentValue = appliedFilters.riseRateMin || appliedFilters.riseRateMax
            ? `${appliedFilters.riseRateMin || ''}~${appliedFilters.riseRateMax || ''}`
            : '';

          return (
            <>
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 999,
                }}
                onClick={() => setFilterPopupOpen(false)}
              />
              <div
                style={{
                  position: 'absolute',
                  top: `${filterPopupPosition.top}px`,
                  left: `${filterPopupPosition.left}px`,
                  zIndex: 1000,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <FilterWrapper
                  FilterClass={RateRangeFilter}
                  value={currentValue}
                  onChange={(value) => {
                    if (!value || value === '') {
                      setAppliedFilters((prev) => ({
                        ...prev,
                        riseRateMin: null,
                        riseRateMax: null,
                      }));
                      return;
                    }
                    const parts = value.split('~');
                    if (parts.length === 2) {
                      const minStr = (parts[0] || '').trim();
                      const maxStr = (parts[1] || '').trim();
                      const min = minStr && !isNaN(Number(minStr)) ? Number(minStr) : null;
                      const max = maxStr && !isNaN(Number(maxStr)) ? Number(maxStr) : null;
                      setAppliedFilters((prev) => ({
                        ...prev,
                        riseRateMin: min,
                        riseRateMax: max,
                      }));
                    }
                  }}
                  onClose={() => setFilterPopupOpen(false)}
                />
              </div>
            </>
          );
        }

        // risePrice (상승가)
        if (isRisePrice) {
          const currentValue = appliedFilters.risePriceMin || appliedFilters.risePriceMax
            ? `${appliedFilters.risePriceMin || ''}~${appliedFilters.risePriceMax || ''}`
            : '';

          return (
            <>
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 999,
                }}
                onClick={() => setFilterPopupOpen(false)}
              />
              <div
                style={{
                  position: 'absolute',
                  top: `${filterPopupPosition.top}px`,
                  left: `${filterPopupPosition.left}px`,
                  zIndex: 1000,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <FilterWrapper
                  FilterClass={PriceRangeFilter}
                  value={currentValue}
                  onChange={(value) => {
                    if (!value || value === '') {
                      setAppliedFilters((prev) => ({
                        ...prev,
                        risePriceMin: null,
                        risePriceMax: null,
                      }));
                      return;
                    }
                    const parts = value.split('~');
                    if (parts.length === 2) {
                      const minStr = (parts[0] || '').trim();
                      const maxStr = (parts[1] || '').trim();
                      const min = minStr && !isNaN(Number(minStr)) ? Number(minStr) : null;
                      const max = maxStr && !isNaN(Number(maxStr)) ? Number(maxStr) : null;
                      setAppliedFilters((prev) => ({
                        ...prev,
                        risePriceMin: min,
                        risePriceMax: max,
                      }));
                    }
                  }}
                  onClose={() => setFilterPopupOpen(false)}
                />
              </div>
            </>
          );
        }

        // supply3Years (공급량)
        if (isSupply3Years) {
          const currentValue = appliedFilters.supply3YearsMin || appliedFilters.supply3YearsMax
            ? `${appliedFilters.supply3YearsMin || ''}~${appliedFilters.supply3YearsMax || ''}`
            : '';

          return (
            <>
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 999,
                }}
                onClick={() => setFilterPopupOpen(false)}
              />
              <div
                style={{
                  position: 'absolute',
                  top: `${filterPopupPosition.top}px`,
                  left: `${filterPopupPosition.left}px`,
                  zIndex: 1000,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <FilterWrapper
                  FilterClass={CountRangeFilter}
                  value={currentValue}
                  onChange={(value) => {
                    if (!value || value === '') {
                      setAppliedFilters((prev) => ({
                        ...prev,
                        supply3YearsMin: null,
                        supply3YearsMax: null,
                      }));
                      return;
                    }
                    const parts = value.split('~');
                    if (parts.length === 2) {
                      const minStr = (parts[0] || '').trim();
                      const maxStr = (parts[1] || '').trim();
                      const minNum = parseInt(minStr, 10);
                      const maxNum = parseInt(maxStr, 10);
                      const min = minStr && !isNaN(minNum) ? minNum : null;
                      const max = maxStr && !isNaN(maxNum) ? maxNum : null;
                      setAppliedFilters((prev) => ({
                        ...prev,
                        supply3YearsMin: min,
                        supply3YearsMax: max,
                      }));
                    }
                  }}
                  onClose={() => setFilterPopupOpen(false)}
                />
              </div>
            </>
          );
        }

        return null;
        })()}
        </div>
      )}

      {selectedDanjiCodeForModal && (
        <RankingComplexDetailModal
          naverDanjiCode={selectedDanjiCodeForModal}
          summary={selectedDanjiSummaryForModal}
          onClose={() => {
            setSelectedDanjiCodeForModal('');
            setSelectedDanjiSummaryForModal(null);
          }}
          onOpenFavoriteGroupPicker={handleOpenFavoriteGroupPicker}
          isFavorite={favoriteDanjiCodes.has(String(selectedDanjiCodeForModal || '').trim())}
        />
      )}

      {favoriteGroupPickerOpen && (() => {
        // 필터링된 그룹 목록 계산
        const filteredGroups = favoriteGroups.filter((group) => {
          // 즐겨찾기 페이지에서는 현재 선택된 그룹 제외
          if (isFavoritePage && selectedFavoriteGroupId) {
            return Number(group?.groupId || 0) !== Number(selectedFavoriteGroupId);
          }
          return true;
        });

        const hasFilteredGroups = filteredGroups.length > 0;

        return (
          <div className="complex-favorite-picker-backdrop" onClick={handleCloseFavoriteGroupPicker}>
            <div className="complex-favorite-picker-modal" onClick={(event) => event.stopPropagation()}>
              <div className="complex-favorite-picker-head">
                <strong>즐겨찾기 그룹 선택</strong>
                <div className="complex-favorite-picker-head-actions">
                  {hasFilteredGroups && (
                    <button type="button" className="complex-favorite-picker-add-btn" onClick={handleAddFavoriteGroup}>+ 그룹추가</button>
                  )}
                  {hasFilteredGroups && (
                    <button
                      type="button"
                      className="complex-favorite-picker-confirm"
                      onClick={() => handleApplyFavoritesToGroup('add')}
                      disabled={favoriteGroupApplyLoading || !(favoriteTargetGroupId > 0)}
                    >
                      {favoriteGroupApplyLoading ? '처리 중...' : (isFavoritePage ? '복사' : '저장')}
                    </button>
                  )}
                  {hasFilteredGroups && isFavoritePage && (
                    <button
                      type="button"
                      className="complex-favorite-picker-confirm move"
                      onClick={() => handleApplyFavoritesToGroup('move')}
                      disabled={favoriteGroupApplyLoading || !(favoriteTargetGroupId > 0)}
                    >
                      {favoriteGroupApplyLoading ? '처리 중...' : '이동'}
                    </button>
                  )}
                </div>
              </div>
              <div className="complex-favorite-picker-body">
                <div className="complex-favorite-picker-summary">
                  선택된 단지 {favoritePickerTargets.length}개
                </div>
                <div className="complex-favorite-picker-group-list">
                  {!hasFilteredGroups ? (
                    <div className="favorite-group-empty-state">
                      <button
                        type="button"
                        className="favorite-group-empty-add-btn"
                        onClick={handleAddFavoriteGroup}
                      >
                        + 그룹 추가하기
                      </button>
                    </div>
                  ) : (
                    filteredGroups.map((group) => {
                      const groupId = Number(group?.groupId || 0);
                      const active = groupId > 0 && groupId === Number(favoriteTargetGroupId);
                      return (
                        <div key={`picker-group-${groupId}`} className="complex-favorite-picker-group-item">
                          <button
                            type="button"
                            className={`complex-favorite-picker-group-btn ${active ? 'active' : ''}`}
                            onClick={() => setFavoriteTargetGroupId(groupId)}
                            disabled={favoriteGroupApplyLoading}
                          >
                            <span>{group?.groupName || '-'}</span>
                            <strong>{Number(group?.favoriteCount || 0).toLocaleString('ko-KR')}</strong>
                          </button>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {compareModalOpen && (
        <RankingCompareModalV2
          targets={compareTargets}
          onClose={() => setCompareModalOpen(false)}
        />
      )}

      {/* 안전마진(여유매수/매도가) 설정 팝오버 — 개인별, 적용 시 즉시 재조회 */}
      {marginPopover.open && (
        <>
          <div
            onClick={() => setMarginPopover((p) => ({ ...p, open: false }))}
            style={{ position: 'fixed', inset: 0, zIndex: 3000 }}
          />
          <div
            style={{
              position: 'fixed',
              left: Math.max(8, Math.min(marginPopover.x, window.innerWidth - 260)),
              top: marginPopover.y + 10,
              zIndex: 3001,
              width: 240,
              background: '#fff',
              border: '1px solid #e2e8f0',
              borderRadius: 8,
              boxShadow: '0 8px 24px rgba(15,23,42,.12)',
              padding: 12,
            }}
          >
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6, color: '#334155' }}>
              {marginPopover.type === 'buy' ? '여유 매수가 마진' : '여유 매도가 마진'}
            </div>
            <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>
              {marginPopover.type === 'buy' ? '매매가 − (매매가 × 마진%)' : '전저가 + (전저가 × 마진%)'}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
              <input
                key={marginPopover.type}
                ref={marginInputRef}
                type="number"
                step="0.1"
                min="0"
                defaultValue={((marginPopover.type === 'buy' ? safeMargins.buy : safeMargins.sell) * 100)}
                style={{ flex: 1, height: 32, border: '1px solid #e2e8f0', borderRadius: 6, padding: '0 8px', fontSize: 13, boxSizing: 'border-box' }}
              />
              <span style={{ fontSize: 13, color: '#334155' }}>%</span>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                type="button"
                onClick={() => {
                  const pct = Number(marginInputRef.current?.value);
                  if (!Number.isFinite(pct) || pct < 0) return;
                  const ratio = pct / 100;
                  const next = marginPopover.type === 'buy'
                    ? { ...safeMarginsRef.current, buy: ratio }
                    : { ...safeMarginsRef.current, sell: ratio };
                  safeMarginsRef.current = next;
                  setSafeMargins(next);
                  setMarginPopover((p) => ({ ...p, open: false }));
                  load(selectedBjdCodes, null, true);
                }}
                style={{ flex: 1, height: 32, border: 'none', borderRadius: 6, background: '#2563eb', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
              >
                적용
              </button>
              <button
                type="button"
                onClick={() => {
                  const def = marginPopover.type === 'buy' ? 0.02 : 0.20;
                  const next = marginPopover.type === 'buy'
                    ? { ...safeMarginsRef.current, buy: def }
                    : { ...safeMarginsRef.current, sell: def };
                  safeMarginsRef.current = next;
                  setSafeMargins(next);
                  setMarginPopover((p) => ({ ...p, open: false }));
                  load(selectedBjdCodes, null, true);
                }}
                style={{ height: 32, padding: '0 10px', border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff', color: '#475569', fontSize: 13, cursor: 'pointer' }}
              >
                기본값
              </button>
            </div>
          </div>
        </>
      )}

      {taxPopover.open && (
        <>
          <div
            onClick={() => setTaxPopover((p) => ({ ...p, open: false }))}
            style={{ position: 'fixed', inset: 0, zIndex: 3000 }}
          />
          <div
            style={{
              position: 'fixed',
              left: Math.max(8, Math.min(taxPopover.x, window.innerWidth - 280)),
              top: taxPopover.y + 10,
              zIndex: 3001,
              width: 260,
              background: '#fff',
              border: '1px solid #e2e8f0',
              borderRadius: 8,
              boxShadow: '0 8px 24px rgba(15,23,42,.12)',
              padding: 12,
            }}
          >
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10, color: '#334155' }}>
              세금 설정 (취득세 기준)
            </div>
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 12, color: '#475569', fontWeight: 700, marginBottom: 4 }}>주택수</div>
              <div style={{ display: 'flex', gap: 6 }}>
                {[{ v: 1, label: '1주택' }, { v: 2, label: '2주택' }, { v: 3, label: '3주택 이상' }].map((opt) => (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() => { const next = { ...taxSettingsRef.current, homeCount: opt.v }; taxSettingsRef.current = next; setTaxSettings(next); }}
                    style={{
                      flex: 1,
                      height: 30,
                      border: `1px solid ${taxSettings.homeCount === opt.v ? '#2563eb' : '#cbd5e1'}`,
                      borderRadius: 6,
                      background: taxSettings.homeCount === opt.v ? '#eff6ff' : '#fff',
                      color: taxSettings.homeCount === opt.v ? '#1d4ed8' : '#475569',
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={!!taxSettings.firstTime}
                onChange={(e) => { const next = { ...taxSettingsRef.current, firstTime: e.target.checked }; taxSettingsRef.current = next; setTaxSettings(next); }}
              />
              <span style={{ fontSize: 13, color: '#334155', fontWeight: 600 }}>생애최초 주택 구입</span>
            </label>
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                type="button"
                onClick={() => { setTaxPopover((p) => ({ ...p, open: false })); load(selectedBjdCodes, null, true); }}
                style={{ flex: 1, height: 32, border: 'none', borderRadius: 6, background: '#2563eb', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
              >
                적용
              </button>
              <button
                type="button"
                onClick={() => { const next = { homeCount: 1, firstTime: false }; taxSettingsRef.current = next; setTaxSettings(next); load(selectedBjdCodes, null, true); }}
                style={{ height: 32, padding: '0 10px', border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff', color: '#475569', fontSize: 13, cursor: 'pointer' }}
              >
                기본값
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default RankingWorkspace;

